import { useState, useEffect, useMemo, useRef, useDeferredValue } from 'react';
import { 
  CubeIcon,
  AcademicCapIcon, 
  PlusIcon, 
  QrCodeIcon, 
  MagnifyingGlassIcon,
  PencilSquareIcon,
  ListBulletIcon,
  MapIcon,
  ChartPieIcon,
  CameraIcon,
  XMarkIcon,
  Cog6ToothIcon,
  ArrowsPointingOutIcon,
  DocumentDuplicateIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  MapPinIcon,
  TruckIcon,
  HandRaisedIcon,
  ArrowPathIcon,
  EyeIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckIcon,
  ClipboardDocumentListIcon,
  PrinterIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ScaleIcon,
  ArrowsRightLeftIcon,
  ClockIcon,
  CheckCircleIcon,
  SparklesIcon,
  TagIcon,
  InboxStackIcon,
  MinusIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import api from '../../../services/authService';
import { useAuth } from '../../../contextos/autenticacion/ContextoAutenticacion';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { AlmacenDigitalTwin3D } from '../../../componentes/almacen3d';
import { ModalReglasLigie, BotonReglasLigie } from '../../../componentes/modales/ModalReglasLigie';
import { ModalMesaControlAlmacen, type SolicitudOperacionAlmacen } from '../../../componentes/modales/ModalMesaControlAlmacen';
import { ModalImportarAlmacen3D } from '../../../componentes/modales/ModalImportarAlmacen3D';

interface ItemAlmacen {
  id: string;
  sku: string;
  nombre: string;
  cantidad: number;
  ubicacion: string;
  categoria: string;
  peso: number; // en kg
  volumen: number; // en m3
  largo?: number; // en metros
  ancho?: number; // en metros
  alto?: number; // en metros
  proveedor: string;
  lote: string;
  fechaIngreso: string;
  
  // Nuevos campos logísticos avanzados
  codigoBarras: string;
  tipoCodigo: 'EAN' | 'UPC' | 'Code128' | 'QR' | 'Interno';
  unidadMedida: 'unidad' | 'caja' | 'pallet' | 'kg' | 'litro';
  marca: string;
  modelo: string;
  descripcion: string;
  imagenUrl: string;
  estado: 'Disponible' | 'Cuarentena' | 'Dañado' | 'Reservado' | 'Vencido';
  numeroSerie: string;
  
  // Ubicación física estructurada
  zonaOperativa?: string;
  pasillo?: string;
  rack?: string;
  nivel?: string;
  posicion?: string;

  // Auditoría
  fechaActualizacion?: string;
  creadoPor?: string;
  actualizadoPor?: string;

  // Procedencia / Logística Multimodal de Entrada
  tipoEntrada?: 'CONTENEDOR' | 'ALMACEN';
  contenedorNumero?: string;
  contenedorTipo?: string;
  selloFiscal?: string;
  bookingBL?: string;
  naviera?: string;
  puertoOrigen?: string;
  taraContenedorKg?: number;
  almacenOrigen?: string;
  remisionFactura?: string;
  tipoTransporte?: string;
  placasTransporte?: string;
  operadorChofer?: string;
}

interface ZonaOperativa {
  id_zona: number;
  codigo: string;
  nombre: string;
  descripcion?: string | null;
  activo: boolean;
  orden: number;
}

interface CapacidadRackConfig {
  id_capacidad: number;
  alcance: 'INSTITUCION' | 'ZONA' | 'RACK';
  zona_codigo?: string | null;
  pasillo?: string | null;
  rack?: string | null;
  niveles?: number;
  max_unidades: number;
  max_ton: number;
  max_m3: number;
  activo: boolean;
}
interface LogAlmacen {
  id_log: number;
  nombre_usuario: string;
  accion: string;
  sku_producto: string;
  cantidad: number;
  origen_zona?: string;
  origen_pasillo?: string;
  origen_rack?: string;
  origen_nivel?: string;
  origen_posicion?: string;
  destino_zona?: string;
  destino_pasillo?: string;
  destino_rack?: string;
  destino_nivel?: string;
  destino_posicion?: string;
  fecha_movimiento: string;
}

interface ConfiguracionAlmacenInstitucion {
  nombre: string;
  codigo: string;
  descripcion: string;
  politicaCapacidad: 'SUMA_RACKS';
  tema_piso?: string;
  tema_pared?: string;
  dimension_ancho?: number;
  dimension_largo?: number;
  layout_data?: Record<string, any>;
  actualizadoEn: string;
}

function sanitizarCodigoAlmacen(value?: string) {
  const limpio = String(value || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
  return limpio || 'ALM01';
}

function formatearCodigoVisualAlmacen(value?: string) {
  const limpio = sanitizarCodigoAlmacen(value);
  const prefijo = limpio.slice(0, 3);
  const sufijo = limpio.slice(3);
  return sufijo ? `${prefijo}-${sufijo}` : limpio;
}

const TABLA_TARAS_CONTENEDOR: Record<string, number> = {
  '20DC': 2250,
  '40DC': 3700,
  '40HC': 3820,
  '40RF': 4450,
  '40OT': 3900,
  '45HC': 4800,
};

const renderTrayectoBadge = (tipo: 'origen' | 'destino', zona?: string, pasillo?: string, rack?: string, nivel?: string, posicion?: string) => {
  if (!zona) {
    return (
      <span className="px-2.5 py-1 rounded-lg bg-slate-50 dark:bg-white/[0.02] border border-dashed border-slate-200 dark:border-white/10 text-[11px] text-slate-400 italic font-medium">
        {tipo === 'origen' ? 'Recepción Exterior' : 'Despacho Exterior'}
      </span>
    );
  }

  if (zona.startsWith('CONT:')) {
    const contNo = zona.replace('CONT:', '').trim() || 'S/N';
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-500/10 border border-blue-500/25 text-[11px] font-semibold text-blue-700 dark:text-blue-300">
        <span className="text-xs">🚢</span>
        <strong className="font-mono tracking-wide">{contNo}</strong>
        {pasillo && <span className="opacity-80 font-mono">({pasillo})</span>}
        {posicion && <span className="hidden sm:inline opacity-70">· {posicion}</span>}
      </span>
    );
  }

  if (zona.startsWith('ALM:')) {
    const almNombre = zona.replace('ALM:', '').trim() || 'Almacén';
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/25 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">
        <span className="text-xs">🏢</span>
        <strong className="tracking-wide">{almNombre}</strong>
        {rack && <span className="opacity-80 font-mono">· {rack}</span>}
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border font-mono text-[11px] ${
      tipo === 'destino' 
        ? 'bg-blue-50 dark:bg-blue-500/10 border-blue-200/80 dark:border-blue-500/20 font-bold text-blue-700 dark:text-blue-300'
        : 'bg-slate-100 dark:bg-white/5 border-slate-200 dark:border-white/10 font-semibold text-slate-700 dark:text-zinc-300'
    }`}>
      <MapPinIcon className={`h-3 w-3 ${tipo === 'destino' ? 'text-blue-500' : 'text-slate-400'}`} />
      {zona}-{pasillo}-{rack}-{nivel}-{posicion}
    </span>
  );
};

const PaginaAlmacenPractica = () => {
  const { usuario } = useAuth();
  
  // Maestros, jefes, admin, supervisor tienen permisos avanzados
  const esMaestroOSuperior = usuario?.rol_organizacion ? ['maestro', 'jefe', 'admin', 'supervisor'].includes(usuario.rol_organizacion) : false;

  const [zonasOperativas, setZonasOperativas] = useState<ZonaOperativa[]>([]);
  const [cargandoZonas, setCargandoZonas] = useState(false);
  const [mostrarModalZona, setMostrarModalZona] = useState(false);
  const [nuevaZonaNombre, setNuevaZonaNombre] = useState('');
  const [nuevaZonaCodigo, setNuevaZonaCodigo] = useState('');
  const [creandoZona, setCreandoZona] = useState(false);

  const [capacidadesRack, setCapacidadesRack] = useState<CapacidadRackConfig[]>([]);
  const [cargandoCapacidadesRack, setCargandoCapacidadesRack] = useState(false);
  const [mostrarModalCapacidadesRack, setMostrarModalCapacidadesRack] = useState(false);
  const [guardandoCapacidadRack, setGuardandoCapacidadRack] = useState(false);
  const [configuracionAlmacen, setConfiguracionAlmacen] = useState<ConfiguracionAlmacenInstitucion & { nivelesVisuales?: number }>({
    nombre: 'Almacén Central',
    codigo: 'ALM01',
    descripcion: 'Infraestructura principal de la institución para operación logística.',
    politicaCapacidad: 'SUMA_RACKS',
    actualizadoEn: new Date().toISOString(),
    nivelesVisuales: 3,
  });
  const [capacidadForm, setCapacidadForm] = useState<{
    id_capacidad?: number;
    alcance: 'INSTITUCION' | 'ZONA' | 'RACK';
    zona_codigo?: string;
    pasillo?: string;
    rack?: string;
    niveles?: number;
    posiciones_por_nivel?: number;
    ancho?: number;
    max_unidades: number;
    max_ton: number;
    max_m3: number;
    activo: boolean;
  }>({
    alcance: 'INSTITUCION',
    zona_codigo: '',
    pasillo: '',
    rack: '',
    niveles: 3,
    posiciones_por_nivel: 8,
    ancho: 3.8,
    max_unidades: 500,
    max_ton: 1500,
    max_m3: 60,
    activo: true,
  });

  const [items, setItems] = useState<ItemAlmacen[]>([]);
  const [logs, setLogs] = useState<LogAlmacen[]>([]);
  const [busqueda, setBusqueda] = useState('');
  const [scanCodigo, setScanCodigo] = useState('');
  const [mostrarModalNuevo, setMostrarModalNuevo] = useState(false);
  const [mostrarModalReglasLigie, setMostrarModalReglasLigie] = useState(false);
  const [solicitudesPendientes, setSolicitudesPendientes] = useState<SolicitudOperacionAlmacen[]>([]);
  const [mostrarModalMesaControl, setMostrarModalMesaControl] = useState(false);
  const [mostrarModalImportarAlmacen, setMostrarModalImportarAlmacen] = useState(false);
  const [exportandoAlmacen, setExportandoAlmacen] = useState(false);
  const [requiereAprobacion, setRequiereAprobacion] = useState(false);
  const [miembrosInstitucion, setMiembrosInstitucion] = useState<any[]>([]);
  const [supervisoresDelegadosIds, setSupervisoresDelegadosIds] = useState<number[]>([]);
  const [operadorAsignadoEstudianteId, setOperadorAsignadoEstudianteId] = useState<string>('');
  const [operadorAsignadoNombre, setOperadorAsignadoNombre] = useState<string>('');
  const [salidaOperadorEstudianteId, setSalidaOperadorEstudianteId] = useState<string>('');
  const [salidaOperadorNombre, setSalidaOperadorNombre] = useState<string>('');
  const [filtroEstudianteKardex, setFiltroEstudianteKardex] = useState<string>('TODOS');
  const [mostrarModalSalida, setMostrarModalSalida] = useState(false);
  const [salidaItemSeleccionado, setSalidaItemSeleccionado] = useState<ItemAlmacen | null>(null);
  const [salidaCantidad, setSalidaCantidad] = useState<number>(1);
  const [salidaMotivo, setSalidaMotivo] = useState<string>('Despacho a Cliente / Distribución');
  const [salidaBusqueda, setSalidaBusqueda] = useState<string>('');
  const [salidaCategoriaFiltro, setSalidaCategoriaFiltro] = useState<string>('TODAS');

  // Estados de Destino Logístico (Salida a Contenedor o Almacén)
  const [salidaTipoDestino, setSalidaTipoDestino] = useState<'CONTENEDOR' | 'ALMACEN'>('CONTENEDOR');
  const [salidaContenedorNumero, setSalidaContenedorNumero] = useState<string>('');
  const [salidaContenedorTipo, setSalidaContenedorTipo] = useState<string>('');
  const [salidaSelloFiscal, setSalidaSelloFiscal] = useState<string>('');
  const [salidaBooking, setSalidaBooking] = useState<string>('');
  const [salidaNaviera, setSalidaNaviera] = useState<string>('');
  const [salidaTerminalPortuaria, setSalidaTerminalPortuaria] = useState<string>('');
  const [salidaTaraContenedor, setSalidaTaraContenedor] = useState<number>(0);

  // Salida a Almacén / Distribución
  const [salidaAlmacenDestino, setSalidaAlmacenDestino] = useState<string>('');
  const [salidaRemision, setSalidaRemision] = useState<string>('');
  const [salidaTipoTransporte, setSalidaTipoTransporte] = useState<string>('');
  const [salidaPlacas, setSalidaPlacas] = useState<string>('');
  const [salidaOperador, setSalidaOperador] = useState<string>('');

  const obtenerTextoUnidad = (cant: number, unidad?: string) => {
    const u = (unidad || 'unidad').trim().toLowerCase();
    if (cant === 1) return u;
    if (u === 'unidad') return 'unidades';
    if (u === 'caja') return 'cajas';
    if (u === 'pallet') return 'pallets';
    if (u === 'kg') return 'kg';
    if (u === 'litro') return 'litros';
    return `${u}s`;
  };

  const categoriasDisponiblesSalida = useMemo(() => {
    const cats = new Set<string>();
    items.forEach((it) => {
      if (it && it.categoria && typeof it.categoria === 'string' && it.categoria.trim()) {
        cats.add(it.categoria.trim());
      }
    });
    return Array.from(cats);
  }, [items]);

  const itemsFiltradosSalida = useMemo(() => {
    const q = (salidaBusqueda || '').trim().toLowerCase();
    return items.filter((item) => {
      if (!item) return false;
      if (salidaCategoriaFiltro !== 'TODAS' && item.categoria !== salidaCategoriaFiltro) {
        return false;
      }
      if (!q) return true;
      return (
        (item.sku || '').toLowerCase().includes(q) ||
        (item.nombre || '').toLowerCase().includes(q) ||
        (item.lote || '').toLowerCase().includes(q) ||
        (item.codigoBarras || '').toLowerCase().includes(q) ||
        (item.ubicacion || '').toLowerCase().includes(q)
      );
    });
  }, [items, salidaBusqueda, salidaCategoriaFiltro]);
  const [procesandoSalida, setProcesandoSalida] = useState(false);
  const [mostrarModalQR, setMostrarModalQR] = useState<ItemAlmacen | null>(null);
  const [qrAmpliadoModal, setQrAmpliadoModal] = useState<string | null>(null);
  const [mostrarScannerQR, setMostrarScannerQR] = useState(false);
  const [accionPosicion, setAccionPosicion] = useState<{ zonaOperativa: string, pasillo: string, rack: string, nivel: string, posicion: string } | null>(null);
  const [mostrarModalReubicar, setMostrarModalReubicar] = useState(false);
  const scannerRef = useRef<Html5QrcodeScanner | null>(null);
  const [activeTab, setActiveTab] = useState<'lista' | 'mapa' | 'infraestructura' | 'analitica' | 'auditoria'>('lista');

  // Estados para Kardex y Auditoría
  const [kardexBusqueda, setKardexBusqueda] = useState('');
  const [kardexFiltroAccion, setKardexFiltroAccion] = useState<'TODOS' | 'INGRESO' | 'SALIDA' | 'REUBICACION'>('TODOS');
  const [kardexPaginaActual, setKardexPaginaActual] = useState(1);
  const [kardexPorPagina, setKardexPorPagina] = useState(10);
  const [busquedaReubicar, setBusquedaReubicar] = useState('');
  const [modoEdicion, setModoEdicion] = useState(false);
  const [modoManualUbicacion, setModoManualUbicacion] = useState(false);
  const [vistaSlotting, setVistaSlotting] = useState<'movil' | 'completa'>(() => {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      return 'completa';
    }
    return 'movil';
  });
  const [selectorRackAbierto, setSelectorRackAbierto] = useState(false);
  const [selectorZonaAbierto, setSelectorZonaAbierto] = useState(false);
  const [mostrarModalNuevoRackRapido, setMostrarModalNuevoRackRapido] = useState(false);
  const [nuevoRackRapido, setNuevoRackRapido] = useState({
    zona_codigo: '',
    pasillo: '01',
    rack: '01',
    niveles: 3,
    posiciones_por_nivel: 8,
    ancho: 4.8,
    max_ton: 10,
    max_unidades: 400
  });
  const [creandoRackRapido, setCreandoRackRapido] = useState(false);
  const [rackSeleccionado, setRackSeleccionado] = useState<{
    nombre: string;
    zona: string;
    pasillo: string;
    rack: string;
    items: ItemAlmacen[];
    niveles?: number;
    ancho?: number;
  } | null>(null);
  const [rackBusqueda, setRackBusqueda] = useState('');
  const rackBusquedaDeferred = useDeferredValue(rackBusqueda);

  const [zoom, setZoom] = useState(1);
  const [isDraggingState, setIsDraggingState] = useState(false);
  const [modo3D, setModo3D] = useState(true);
  
  // MODO ARQUITECTO (LAYOUT 2D)
  const [modoArquitecto, setModoArquitecto] = useState(false);
  const [dragInfo, setDragInfo] = useState<{ id: string | null, startX: number, startY: number, startObjX: number, startObjY: number }>({ id: null, startX: 0, startY: 0, startObjX: 0, startObjY: 0 });
  const zonaRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const canvasRef = useRef<HTMLDivElement>(null);
  const [herramienta3D, setHerramienta3D] = useState<'pan' | 'orbit'>('pan');
  const [sensibilidad, setSensibilidad] = useState(1);
  
  // Refs para alto rendimiento (60FPS) sin re-renders de React
  const panRef = useRef<HTMLDivElement>(null);
  const rotacionRef = useRef<HTMLDivElement>(null);
  const currentPan = useRef({ x: 0, y: 0 });
  const currentWalk = useRef({ x: 0, y: 0 });
  const keysPressed = useRef<{ [key: string]: boolean }>({});
  const animationFrameId = useRef<number | null>(null);
  const currentRotacion = useRef({ x: 55, z: -45 });
  const dragStart = useRef({ x: 0, y: 0 });
  const isDragging = useRef(false);

  const hasCustomLayout = Object.keys(configuracionAlmacen.layout_data || {}).length > 0;

  const toggleModoArquitecto = () => {
    if (!modoArquitecto) {
       setModo3D(false);
       let newLayout = { ...(configuracionAlmacen.layout_data || {}) };
       let needsUpdate = false;
       
       Object.keys(zonasAlmacen).forEach(zona => {
          if (!newLayout[zona]) {
             const el = zonaRefs.current[zona] as HTMLElement;
             if (el) {
                newLayout[zona] = { x: el.offsetLeft, y: el.offsetTop };
                needsUpdate = true;
             }
          }
       });

       if (needsUpdate) {
          setConfiguracionAlmacen(prev => ({ ...prev, layout_data: newLayout }));
       }
       setModoArquitecto(true);
    } else {
       setModoArquitecto(false);
    }
  };

  const onPointerDownZona = (e: React.PointerEvent, zona: string) => {
     if (!modoArquitecto) return;
     e.stopPropagation();
     const pos = configuracionAlmacen.layout_data?.[zona] || {x: 0, y: 0};
     setDragInfo({
        id: zona,
        startX: e.clientX,
        startY: e.clientY,
        startObjX: pos.x,
        startObjY: pos.y
     });
     (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onPointerMoveCanvas = (e: React.PointerEvent) => {
     if (!dragInfo.id) return;
     const dx = e.clientX - dragInfo.startX;
     const dy = e.clientY - dragInfo.startY;
     
     setConfiguracionAlmacen(prev => ({
        ...prev,
        layout_data: {
           ...(prev.layout_data || {}),
           [dragInfo.id!]: {
              x: dragInfo.startObjX + dx / zoom,
              y: dragInfo.startObjY + dy / zoom
           }
        }
     }));
  };

  const onPointerUpCanvas = (e: React.PointerEvent) => {
     if (dragInfo.id) {
        (e.target as HTMLElement).releasePointerCapture(e.pointerId);
        setDragInfo({ id: null, startX: 0, startY: 0, startObjX: 0, startObjY: 0 });
     }
  };
  const isOrbiting = useRef(false);
  const hasOrbited = useRef(false);
  const hasDragged = useRef(false);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  // Sincronizar estado inicial 3D al cambiar el modo
  useEffect(() => {
    if (rotacionRef.current) {
      if (modo3D) {
        rotacionRef.current.style.transform = `rotateX(${currentRotacion.current.x}deg) rotateZ(${currentRotacion.current.z}deg) translate3d(${currentWalk.current.x}px, ${currentWalk.current.y}px, 0)`;
      } else {
        rotacionRef.current.style.transform = `rotateX(0deg) rotateZ(0deg) translate3d(${currentWalk.current.x}px, ${currentWalk.current.y}px, 0)`;
      }
    }
  }, [modo3D]);

  // Sincronizar el zoom usando el pan actual
  useEffect(() => {
    if (panRef.current) {
      panRef.current.style.transform = `translate(${currentPan.current.x}px, ${currentPan.current.y}px) scale(${zoom})`;
    }
  }, [zoom]);

  const recargarSolicitudesPendientes = async () => {
    try {
      const res = await api.get('/api/institucion/almacen/solicitudes');
      setSolicitudesPendientes(Array.isArray(res.data) ? res.data : []);
      const [prodsRes, logsRes] = await Promise.allSettled([
        api.get('/api/institucion/almacen/productos'),
        api.get('/api/institucion/almacen/logs')
      ]);
      if (prodsRes.status === 'fulfilled') {
        const prods = Array.isArray(prodsRes.value.data) ? prodsRes.value.data : [];
        setItems(prods.map((p: any) => {
          const zCod = p.zona_codigo || 'SEC';
          const pas = normalizarNumero2(p.pasillo || '01');
          const rac = normalizarNumero2(p.rack || '01');
          const niv = normalizarNumero2(p.nivel || '01');
          const pos = normalizarNumero2(p.posicion || '01');
          const ubicacion = `${CODIGO_ALMACEN_COMPACTO}-${zCod}-P${pas}-R${rac}-N${niv}-S${pos}`;
          return {
            id: String(p.id_producto),
            sku: p.sku,
            nombre: p.nombre,
            cantidad: p.cantidad,
            ubicacion,
            categoria: p.categoria || '',
            peso: p.peso_unitario,
            volumen: p.volumen_unitario,
            largo: p.largo,
            ancho: p.ancho,
            alto: p.alto,
            proveedor: p.proveedor || '',
            lote: p.lote || '',
            fechaIngreso: p.fecha_creacion,
            fechaActualizacion: p.fecha_actualizacion,
            creadoPor: p.creado_por_nombre,
            actualizadoPor: p.actualizado_por_nombre,
            codigoBarras: p.codigo_barras || '',
            tipoCodigo: p.tipo_codigo || 'Interno',
            unidadMedida: p.unidad_medida || 'unidad',
            marca: p.marca || '',
            modelo: p.modelo || '',
            descripcion: p.descripcion || '',
            imagenUrl: '',
            estado: p.estado_inventario || 'Disponible',
            numeroSerie: p.numero_serie || '',
            zonaOperativa: zCod,
            pasillo: pas,
            rack: rac,
            nivel: niv,
            posicion: pos,
          };
        }));
      }
      if (logsRes.status === 'fulfilled') {
        setLogs(Array.isArray(logsRes.value.data) ? logsRes.value.data : []);
      }
    } catch (err) {
      console.error('Error al recargar solicitudes pendientes:', err);
    }
  };

  const handleActualizarSupervisoresDelegados = async (nuevosIds: number[]) => {
    setSupervisoresDelegadosIds(nuevosIds);
  };

  const handleCambiarPoliticaAprobacion = async (nuevaPolitica: boolean) => {
    try {
      const res = await api.post('/api/institucion/almacen/politica-aprobacion', {
        requiere_aprobacion: nuevaPolitica
      });
      setRequiereAprobacion(Boolean(res.data?.requiere_aprobacion));
      toast.success(
        nuevaPolitica
          ? 'Auditoría obligatoria activada. Las operaciones requerirán validación docente.'
          : 'Modo libre activado. Las operaciones se asentarán de inmediato en stock.'
      );
    } catch (err: any) {
      toast.error('Error al actualizar la política de aprobación');
    }
  };

  const [nuevoItem, setNuevoItem] = useState<Partial<ItemAlmacen>>({
    nombre: '', sku: '', cantidad: 1, ubicacion: '', categoria: '', peso: 0, volumen: 0, proveedor: '', lote: '',
    codigoBarras: '', tipoCodigo: 'Interno', unidadMedida: 'unidad', marca: '', modelo: '', descripcion: '', imagenUrl: '', estado: 'Disponible', numeroSerie: '',
    largo: 0, ancho: 0, alto: 0,
    zonaOperativa: 'SEC', pasillo: '01', rack: '01', nivel: '01', posicion: '01',
    tipoEntrada: 'CONTENEDOR',
    contenedorNumero: '',
    contenedorTipo: '',
    selloFiscal: '',
    bookingBL: '',
    naviera: '',
    puertoOrigen: '',
    taraContenedorKg: 0,
    almacenOrigen: '',
    remisionFactura: '',
    tipoTransporte: '',
    placasTransporte: '',
    operadorChofer: ''
  });

  useEffect(() => {
    setRackBusqueda('');
  }, [rackSeleccionado?.nombre]);

  // WMS Audio Feedback (Zebra/Honeywell simulation)
  const playBeep = () => {
    try {
        const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
        const oscillator = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioCtx.destination);
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(800, audioCtx.currentTime);
        gainNode.gain.setValueAtTime(0.5, audioCtx.currentTime);
        oscillator.start();
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.15);
        oscillator.stop(audioCtx.currentTime + 0.15);
    } catch (e) {}
  };

  const playErrorBeep = () => {
    try {
        const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
        const oscillator = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioCtx.destination);
        oscillator.type = 'sawtooth';
        oscillator.frequency.setValueAtTime(150, audioCtx.currentTime);
        gainNode.gain.setValueAtTime(0.5, audioCtx.currentTime);
        oscillator.start();
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.5);
        oscillator.stop(audioCtx.currentTime + 0.5);
    } catch (e) {}
  };

  const cargarDatosAlmacen = async () => {
    setCargandoZonas(true);
    setCargandoCapacidadesRack(true);
    try {
      const [zonasRes, capsRes, almacenRes, prodsRes, logsRes, solRes, miembrosRes] = await Promise.allSettled([
        api.get('/api/institucion/almacen/zonas-operativas'),
        api.get('/api/institucion/almacen/capacidades-rack'),
        api.get('/api/institucion/almacen/configuracion'),
        api.get('/api/institucion/almacen/productos'),
        api.get('/api/institucion/almacen/logs'),
        api.get('/api/institucion/almacen/solicitudes'),
        api.get('/api/institucion/miembros'),
      ]);

      if (miembrosRes.status === 'fulfilled' && Array.isArray(miembrosRes.value.data)) {
        setMiembrosInstitucion(miembrosRes.value.data);
      }

      if (solRes.status === 'fulfilled') {
        setSolicitudesPendientes(Array.isArray(solRes.value.data) ? solRes.value.data : []);
      }

      if (zonasRes.status === 'fulfilled') {
        setZonasOperativas(Array.isArray(zonasRes.value.data) ? zonasRes.value.data : []);
      }

      if (capsRes.status === 'fulfilled') {
        setCapacidadesRack(Array.isArray(capsRes.value.data) ? capsRes.value.data : []);
      }
      
      if (almacenRes.status === 'fulfilled' && almacenRes.value.data) {
        const raw = almacenRes.value.data;
        setConfiguracionAlmacen(prev => ({
          ...prev,
          nombre: raw.nombre || 'Almacén Central',
          codigo: sanitizarCodigoAlmacen(raw.codigo || 'ALM01'),
          descripcion: raw.descripcion || '',
          tema_piso: raw.tema_piso || prev.tema_piso || 'concreto',
          tema_pared: raw.tema_pared || prev.tema_pared || 'industrial',
          dimension_ancho: raw.dimension_ancho ? Number(raw.dimension_ancho) : (prev.dimension_ancho || 80),
          dimension_largo: raw.dimension_largo ? Number(raw.dimension_largo) : (prev.dimension_largo || 80),
          layout_data: raw.layout_data || prev.layout_data || {},
          politicaCapacidad: 'SUMA_RACKS',
          actualizadoEn: raw.fecha_actualizacion || new Date().toISOString(),
        }));
        const lData = raw.layout_data;
        if (lData && typeof lData === 'object') {
          setRequiereAprobacion(Boolean(lData.requiere_aprobacion));
          if (Array.isArray(lData.supervisores_delegados_ids)) {
            setSupervisoresDelegadosIds(lData.supervisores_delegados_ids);
          }
        }
      }
      
      if (prodsRes.status === 'fulfilled') {
        const prods = Array.isArray(prodsRes.value.data) ? prodsRes.value.data : [];
        setItems(prods.map((p: any) => {
          const zCod = p.zona_codigo || 'SEC';
          const pas = normalizarNumero2(p.pasillo || '01');
          const rack = normalizarNumero2(p.rack || '01');
          const niv = normalizarNumero2(p.nivel || '01');
          const pos = normalizarNumero2(p.posicion || '01');
          const ubicacion = `${CODIGO_ALMACEN_COMPACTO}-${zCod}-P${pas}-R${rack}-N${niv}-S${pos}`;
          
          return {
            id: String(p.id_producto),
            sku: p.sku,
            nombre: p.nombre,
            cantidad: p.cantidad,
            ubicacion,
            categoria: p.categoria || '',
            peso: p.peso_unitario,
            volumen: p.volumen_unitario,
            largo: p.largo,
            ancho: p.ancho,
            alto: p.alto,
            proveedor: p.proveedor || '',
            lote: p.lote || '',
            fechaIngreso: p.fecha_creacion,
            fechaActualizacion: p.fecha_actualizacion,
            creadoPor: p.creado_por_nombre,
            actualizadoPor: p.actualizado_por_nombre,
            codigoBarras: p.codigo_barras || '',
            tipoCodigo: p.tipo_codigo || 'Interno',
            unidadMedida: p.unidad_medida || 'unidad',
            marca: p.marca || '',
            modelo: p.modelo || '',
            descripcion: p.descripcion || '',
            imagenUrl: '',
            estado: p.estado_inventario || 'Disponible',
            numeroSerie: p.numero_serie || '',
            zonaOperativa: zCod,
            pasillo: p.pasillo || '01',
            rack: p.rack || '01',
            nivel: p.nivel || '01',
            posicion: p.posicion || '01',
          };
        }));
      }

      if (logsRes.status === 'fulfilled') {
        setLogs(Array.isArray(logsRes.value.data) ? logsRes.value.data : []);
      }
    } catch (error) {
      console.error('Error al cargar datos del almacén:', error);
      toast.error('No se pudieron cargar datos del almacén');
    } finally {
      setCargandoZonas(false);
      setCargandoCapacidadesRack(false);
    }
  };

  useEffect(() => {
    cargarDatosAlmacen();
  }, []);

  const exportarAlmacenCompleto = async () => {
    setExportandoAlmacen(true);
    const toastId = toast.loading('Generando respaldo del gemelo digital 3D...');
    try {
      let snapshotData: any = null;
      try {
        const res = await api.get('/api/institucion/almacen/gemelo-digital/exportar');
        if (res.data?.formato) {
          snapshotData = res.data;
        }
      } catch (err) {
        console.warn('Backend export fallback to live frontend state', err);
      }

      if (!snapshotData) {
        snapshotData = {
          formato: 'LOGIXPORT_ALMACEN_DIGITAL_TWIN',
          version: '1.0',
          fecha_exportacion: new Date().toISOString(),
          almacen: {
            nombre: configuracionAlmacen.nombre || 'Almacén Central',
            codigo: configuracionAlmacen.codigo || 'ALM01',
            descripcion: configuracionAlmacen.descripcion || '',
            tema_piso: configuracionAlmacen.tema_piso || 'concreto',
            tema_pared: configuracionAlmacen.tema_pared || 'industrial',
            dimension_ancho: configuracionAlmacen.dimension_ancho || 80,
            dimension_largo: configuracionAlmacen.dimension_largo || 80,
            layout_data: configuracionAlmacen.layout_data || {},
          },
          zonas_operativas: zonasOperativas,
          capacidades_rack: capacidadesRack,
          inventario: items.map(it => ({
            sku: it.sku,
            nombre: it.nombre,
            descripcion: it.descripcion,
            marca: it.marca,
            modelo: it.modelo,
            numero_serie: it.numeroSerie,
            cantidad: it.cantidad,
            unidad_medida: it.unidadMedida,
            categoria: it.categoria,
            proveedor: it.proveedor,
            estado_inventario: it.estado,
            zona_codigo: it.zonaOperativa,
            pasillo: it.pasillo,
            rack: it.rack,
            nivel: it.nivel,
            posicion: it.posicion,
            peso_unitario: it.peso,
            volumen_unitario: it.volumen,
            largo: it.largo,
            ancho: it.ancho,
            alto: it.alto,
            lote: it.lote,
            codigo_barras: it.codigoBarras,
            tipo_codigo: it.tipoCodigo,
            tipo_entrada: it.tipoEntrada,
            contenedor_numero: it.contenedorNumero,
            contenedor_tipo: it.contenedorTipo,
            sello_fiscal: it.selloFiscal,
            booking_bl: it.bookingBL,
            naviera: it.naviera,
            puerto_origen: it.puertoOrigen,
            tara_contenedor_kg: it.taraContenedorKg,
            almacen_origen: it.almacenOrigen,
            remision_factura: it.remisionFactura,
            tipo_transporte: it.tipoTransporte,
            placas_transporte: it.placasTransporte,
            operador_chofer: it.operadorChofer,
          })),
          resumen: {
            total_productos: items.length,
            total_unidades: items.reduce((acc, it) => acc + (Number(it.cantidad) || 0), 0),
            total_zonas: zonasOperativas.length,
            total_capacidades: capacidadesRack.length,
            total_racks_3d: Object.keys((configuracionAlmacen.layout_data as any)?.racks3d || {}).length,
          }
        };
      } else {
        if (configuracionAlmacen.layout_data) {
          snapshotData.almacen.layout_data = {
            ...(snapshotData.almacen.layout_data || {}),
            ...configuracionAlmacen.layout_data,
          };
        }
      }

      const jsonStr = JSON.stringify(snapshotData, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const cod = (configuracionAlmacen.codigo || 'ALM01').replace(/[^a-zA-Z0-9]/g, '');
      const fecha = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.download = `LOGIXPORT_GEMELO_DIGITAL_${cod}_${fecha}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('¡Gemelo digital 3D y datos exportados con éxito!', { id: toastId });
    } catch (err: any) {
      console.error('Error al exportar gemelo digital:', err);
      toast.error('Error al generar la exportación del almacén', { id: toastId });
    } finally {
      setExportandoAlmacen(false);
    }
  };





  useEffect(() => {
    if (!zonasOperativas.length) return;

    setItems(prev =>
      prev.map(item => ({
        ...item,
        zonaOperativa: obtenerCodigoZona(item.zonaOperativa),
      }))
    );

    setNuevoItem(prev => {
      const zonaCodigo = obtenerCodigoZona(prev.zonaOperativa);
      const existe = zonasOperativas.some(z => z.codigo === zonaCodigo);
      return {
        ...prev,
        zonaOperativa: existe ? zonaCodigo : zonasOperativas[0].codigo,
      };
    });
  }, [zonasOperativas]);



  // Helper para reutilización de SKU (mismo producto y mismo tamaño)
  const skuBuscadoModal = String(nuevoItem.sku || '').trim().toUpperCase();
  const itemConMismoSku = useMemo(() => {
    if (!skuBuscadoModal) return null;
    return items.find(i => (!modoEdicion || i.id !== nuevoItem.id) && String(i.sku || '').trim().toUpperCase() === skuBuscadoModal) || null;
  }, [skuBuscadoModal, items, modoEdicion, nuevoItem.id]);

  const validacionSkuReutilizable = useMemo(() => {
    if (!itemConMismoSku) {
      return { esExistente: false, coincideNombre: true, coincideTamano: true, tieneDiferenciaNombre: false, tieneDiferenciaTamano: false, esNombreVacio: true, itemExistente: null };
    }
    const nombreActual = String(nuevoItem.nombre || '').trim().toLowerCase();
    const nombreExist = String(itemConMismoSku.nombre || '').trim().toLowerCase();
    const esNombreVacio = !nombreActual;
    const coincideNombre = esNombreVacio || nombreActual === nombreExist;
    const tieneDiferenciaNombre = !esNombreVacio && nombreActual !== nombreExist;

    const largoActual = Number(nuevoItem.largo || 0);
    const anchoActual = Number(nuevoItem.ancho || 0);
    const altoActual = Number(nuevoItem.alto || 0);
    const tieneMedidasActuales = largoActual > 0 || anchoActual > 0 || altoActual > 0;

    const largoExist = Number(itemConMismoSku.largo || 0);
    const anchoExist = Number(itemConMismoSku.ancho || 0);
    const altoExist = Number(itemConMismoSku.alto || 0);
    const tieneMedidasExistentes = largoExist > 0 || anchoExist > 0 || altoExist > 0;

    let coincideTamano = true;
    let tieneDiferenciaTamano = false;

    if (tieneMedidasActuales && tieneMedidasExistentes) {
      const difL = Math.abs(largoActual - largoExist);
      const difAn = Math.abs(anchoActual - anchoExist);
      const difAl = Math.abs(altoActual - altoExist);
      if (difL > 0.05 || difAn > 0.05 || difAl > 0.05) {
        coincideTamano = false;
        tieneDiferenciaTamano = true;
      }
    }

    return {
      esExistente: true,
      itemExistente: itemConMismoSku,
      coincideNombre,
      coincideTamano,
      tieneDiferenciaNombre,
      tieneDiferenciaTamano,
      esNombreVacio,
    };
  }, [itemConMismoSku, nuevoItem.nombre, nuevoItem.largo, nuevoItem.ancho, nuevoItem.alto]);

  const handleAutoCompletarDesdeSku = (existente: ItemAlmacen) => {
    setNuevoItem(prev => ({
      ...prev,
      nombre: existente.nombre,
      categoria: existente.categoria || prev.categoria,
      marca: existente.marca || prev.marca,
      modelo: existente.modelo || prev.modelo,
      unidadMedida: existente.unidadMedida || prev.unidadMedida,
      peso: existente.peso || prev.peso,
      largo: existente.largo || prev.largo,
      ancho: existente.ancho || prev.ancho,
      alto: existente.alto || prev.alto,
      volumen: existente.volumen || prev.volumen,
      tipoCodigo: existente.tipoCodigo || prev.tipoCodigo,
    }));
    toast.success('Datos sincronizados con SKU ' + existente.sku);
  };

  const handleAgregar = async () => {
    if (!infraestructuraTieneConfiguracionBase) {
      toast.error('Primero configura la infraestructura: almacén, zonas y al menos una capacidad.');
      setActiveTab('infraestructura');
      return;
    }

    const skuInput = String(nuevoItem.sku || '').trim();
    const nombreInput = String(nuevoItem.nombre || '').trim();
    const cantidadInput = Number(nuevoItem.cantidad);
    const zonaInput = String(nuevoItem.zonaOperativa || '').trim();
    const pasilloInput = String(nuevoItem.pasillo || '').trim();
    const rackInput = String(nuevoItem.rack || '').trim();
    const nivelInput = String(nuevoItem.nivel || '').trim();
    const posicionInput = String(nuevoItem.posicion || '').trim();

    const faltantes: string[] = [];
    if (!skuInput) faltantes.push('SKU');
    if (!nombreInput) faltantes.push('Nombre');
    if (!Number.isFinite(cantidadInput) || cantidadInput <= 0) faltantes.push('Cantidad (>0)');
    if (!zonaInput) faltantes.push('Zona operativa');
    if (!pasilloInput) faltantes.push('Pasillo');
    if (!rackInput) faltantes.push('Rack');
    if (!nivelInput) faltantes.push('Nivel');
    if (!posicionInput) faltantes.push('Posición');

    if (faltantes.length) {
      toast.error(
        `${modoEdicion ? 'No puedes guardar cambios con campos vacíos.' : 'No puedes registrar un producto vacío.'} Completa: ${faltantes.join(', ')}`
      );
      return;
    }

    // Validación de reutilización de SKU: solo si es el mismo producto y mismo tamaño
    const skuKey = skuInput.toUpperCase();
    const itemExistenteSku = items.find(i => {
      if (modoEdicion && nuevoItem.id && i.id === nuevoItem.id) return false;
      return String(i.sku || '').trim().toUpperCase() === skuKey;
    });

    if (itemExistenteSku) {
      const nombreExistente = String(itemExistenteSku.nombre || '').trim().toLowerCase();
      if (nombreInput.toLowerCase() !== nombreExistente) {
        playErrorBeep();
        toast.error(
          `El SKU '${skuInput}' ya está asignado al producto "${itemExistenteSku.nombre}". Para reutilizar el SKU debe ser exactamente el mismo producto.`
        );
        return;
      }

      const dimNuevaLargo = normalizarMedida(nuevoItem.largo);
      const dimNuevaAncho = normalizarMedida(nuevoItem.ancho);
      const dimNuevaAlto = normalizarMedida(nuevoItem.alto);
      const tieneMedidasNuevas = (dimNuevaLargo > 0 || dimNuevaAncho > 0 || dimNuevaAlto > 0);

      const dimExistLargo = Number(itemExistenteSku.largo || 0);
      const dimExistAncho = Number(itemExistenteSku.ancho || 0);
      const dimExistAlto = Number(itemExistenteSku.alto || 0);
      const tieneMedidasExistentes = (dimExistLargo > 0 || dimExistAncho > 0 || dimExistAlto > 0);

      if (tieneMedidasNuevas && tieneMedidasExistentes) {
        const difLargo = Math.abs(dimNuevaLargo - dimExistLargo);
        const difAncho = Math.abs(dimNuevaAncho - dimExistAncho);
        const difAlto = Math.abs(dimNuevaAlto - dimExistAlto);
        if (difLargo > 0.05 || difAncho > 0.05 || difAlto > 0.05) {
          playErrorBeep();
          toast.error(
            `El SKU '${skuInput}' pertenece a "${itemExistenteSku.nombre}" pero con dimensiones ${dimExistLargo}×${dimExistAncho}×${dimExistAlto} m. Para reutilizar el SKU deben tener el mismo tamaño físico.`
          );
          return;
        }
      }
    }

    const codigoBarrasInput = String(nuevoItem.codigoBarras || '').trim();
    if (codigoBarrasInput) {
      const codigoKey = codigoBarrasInput.toLowerCase();
      const duplicado = items.some(i => {
        if (i.id === nuevoItem.id) return false;
        const otherKey = String(i.codigoBarras || '').trim().toLowerCase();
        return otherKey && otherKey === codigoKey;
      });
      if (duplicado) {
        toast.error('Ya existe un producto con ese código de barras');
        return;
      }
      const tipo = (nuevoItem.tipoCodigo as any) || 'Interno';
      if (tipo === 'EAN') {
        if (!/^\d{13}$/.test(codigoBarrasInput)) {
          toast.error('EAN debe tener 13 dígitos');
          return;
        }
        if (!esEan13Valido(codigoBarrasInput)) {
          toast.error('EAN inválido (check digit incorrecto). Corrige el número o cambia el tipo a Code128.');
          return;
        }
      }
      if (tipo === 'UPC') {
        if (!/^\d{12}$/.test(codigoBarrasInput)) {
          toast.error('UPC debe tener 12 dígitos');
          return;
        }
        if (!esUpcAValido(codigoBarrasInput)) {
          toast.error('UPC inválido (check digit incorrecto). Corrige el número o cambia el tipo a Code128.');
          return;
        }
      }
    }

    const zonaCodigo = obtenerCodigoZona(zonaInput || zonasOperativas[0]?.codigo || 'SEC');
    const pasilloCodigo = normalizarNumero2(pasilloInput || '01');
    const rackCodigo = normalizarNumero2(rackInput || '01');
    const cantidadNueva = cantidadInput || 0;
    const pesoUnitarioKg = Number(nuevoItem.peso) || 0;
    const volumenUnitarioM3 = calcularVolumenUnitarioM3(nuevoItem);

    const itemsMismoRack = items.filter(i => {
      if (modoEdicion && nuevoItem.id && i.id === nuevoItem.id) return false;
      return (
        obtenerCodigoZona(i.zonaOperativa) === zonaCodigo &&
        normalizarNumero2(i.pasillo) === pasilloCodigo &&
        normalizarNumero2(i.rack) === rackCodigo
      );
    });

    const unidadesActuales = itemsMismoRack.reduce((acc, item) => acc + (Number(item.cantidad) || 0), 0);
    const pesoActualTon = itemsMismoRack.reduce((acc, item) => acc + ((Number(item.peso) || 0) * (Number(item.cantidad) || 0)), 0) / 1000;
    const volumenActualM3 = itemsMismoRack.reduce((acc, item) => acc + (calcularVolumenUnitarioM3(item) * (Number(item.cantidad) || 0)), 0);

    const unidadesFuturas = unidadesActuales + cantidadNueva;
    const pesoFuturoTon = pesoActualTon + ((pesoUnitarioKg * cantidadNueva) / 1000);
    const volumenFuturoM3 = volumenActualM3 + (volumenUnitarioM3 * cantidadNueva);

    const capRack = obtenerCapacidadRack({ zonaOperativa: zonaCodigo, pasillo: pasilloCodigo, rack: rackCodigo });
    const nivelNum = Number(nuevoItem.nivel || '1');
    const posNum = Number(nuevoItem.posicion || '1');
    const maxNiveles = capRack.niveles || 3;
    const maxPos = capRack.posiciones_por_nivel || 8;

    if (nivelNum > maxNiveles) {
      playErrorBeep();
      toast.error(`El nivel ${nivelNum} excede la altura física del rack seleccionado (máximo ${maxNiveles} niveles).`);
      return;
    }
    if (posNum > maxPos) {
      playErrorBeep();
      toast.error(`La posición ${posNum} excede el ancho físico de la viga del rack (máximo ${maxPos} pallets de ancho).`);
      return;
    }

    const excedeUnidades = unidadesFuturas > capRack.max_unidades;
    const excedePeso = pesoFuturoTon > capRack.max_ton;
    const excedeVolumen = volumenFuturoM3 > capRack.max_m3;
    const excede = excedeUnidades || excedePeso || excedeVolumen;

    if (excede) {
      playErrorBeep();
      mostrarAlertaCapacidadExcedida({
        rackLabel: `${CODIGO_ALMACEN_VISUAL} · ${zonaCodigo} · P${pasilloCodigo} · R${rackCodigo}`,
        unidades: excedeUnidades ? { actual: unidadesActuales, futuro: unidadesFuturas, max: capRack.max_unidades } : undefined,
        peso: excedePeso ? { actual: pesoActualTon, futuro: pesoFuturoTon, max: capRack.max_ton } : undefined,
        volumen: excedeVolumen ? { actual: volumenActualM3, futuro: volumenFuturoM3, max: capRack.max_m3 } : undefined,
      });
      return;
    }
    
    let itemParaMostrar: ItemAlmacen | null = null;
    try {
      const codigoBarrasExistente = String(nuevoItem.codigoBarras || '').trim();
      const codigoBarras = codigoBarrasExistente || (modoEdicion ? '' : generarCodigoBarrasUnico(items));
      const tipoCodigo = codigoBarrasExistente ? ((nuevoItem.tipoCodigo as any) || 'Interno') : (modoEdicion ? nuevoItem.tipoCodigo : 'Code128');

      const payload = {
        sku: skuInput.toUpperCase(),
        nombre: nombreInput,
        descripcion: nuevoItem.descripcion || '',
        marca: nuevoItem.marca || 'S/M',
        modelo: nuevoItem.modelo || 'S/M',
        numero_serie: nuevoItem.numeroSerie || '',
        cantidad: cantidadNueva,
        unidad_medida: (nuevoItem.unidadMedida as any) || 'unidad',
        categoria: nuevoItem.categoria || 'General',
        proveedor: nuevoItem.proveedor || 'Sin proveedor',
        estado_inventario: (nuevoItem.estado as any) || 'Disponible',
        zona_codigo: zonaCodigo,
        pasillo: pasilloCodigo,
        rack: rackCodigo,
        nivel: normalizarNumero2(nuevoItem.nivel || '01'),
        posicion: normalizarNumero2(nuevoItem.posicion || '01'),
        peso_unitario: pesoUnitarioKg,
        volumen_unitario: volumenUnitarioM3,
        largo: normalizarMedida(nuevoItem.largo),
        ancho: normalizarMedida(nuevoItem.ancho),
        alto: normalizarMedida(nuevoItem.alto),
        lote: nuevoItem.lote || (nuevoItem.tipoEntrada === 'CONTENEDOR' ? nuevoItem.contenedorNumero : nuevoItem.remisionFactura) || 'N/A',
        codigo_barras: codigoBarras,
        tipo_codigo: tipoCodigo,
        tipo_entrada: nuevoItem.tipoEntrada || 'CONTENEDOR',
        contenedor_numero: nuevoItem.contenedorNumero || '',
        contenedor_tipo: nuevoItem.contenedorTipo || '40HC',
        sello_fiscal: nuevoItem.selloFiscal || '',
        booking_bl: nuevoItem.bookingBL || '',
        naviera: nuevoItem.naviera || '',
        puerto_origen: nuevoItem.puertoOrigen || '',
        tara_contenedor: nuevoItem.taraContenedorKg || 3820,
        almacen_origen: nuevoItem.almacenOrigen || '',
        remision_factura: nuevoItem.remisionFactura || '',
        tipo_transporte: nuevoItem.tipoTransporte || '',
        placas_transporte: nuevoItem.placasTransporte || '',
        operador_chofer: nuevoItem.operadorChofer || '',
        solicitante_id: operadorAsignadoEstudianteId ? Number(operadorAsignadoEstudianteId) : usuario?.id,
        solicitante_nombre: operadorAsignadoNombre || (usuario?.nombre ? `${usuario.nombre} ${usuario.apellido || ''}`.trim() : (usuario?.email || 'Estudiante'))
      };

      let res;
      if (modoEdicion && nuevoItem.id) {
        res = await api.put(`/api/institucion/almacen/productos/${nuevoItem.id}`, payload);
      } else {
        res = await api.post('/api/institucion/almacen/productos', payload);
      }

      const p = res.data;
      if (p?.pendiente_aprobacion) {
        toast.success(p.message || 'Solicitud de ingreso registrada en Mesa de Control para validación docente.', { duration: 5000, icon: '🛡️' });
        await recargarSolicitudesPendientes();
        setMostrarModalNuevo(false);
        return;
      }
      const niv = normalizarNumero2(p.nivel || '01');
      const pos = normalizarNumero2(p.posicion || '01');
      const zCod = p.zona_codigo || 'SEC';
      const pas = normalizarNumero2(p.pasillo || '01');
      const rac = normalizarNumero2(p.rack || '01');
      const ubicacion = `${CODIGO_ALMACEN_COMPACTO}-${zCod}-P${pas}-R${rac}-N${niv}-S${pos}`;

      const itemTransformado: ItemAlmacen = {
        id: String(p.id_producto),
        sku: p.sku,
        nombre: p.nombre,
        cantidad: p.cantidad,
        ubicacion,
        categoria: p.categoria || '',
        peso: p.peso_unitario,
        volumen: p.volumen_unitario,
        largo: p.largo,
        ancho: p.ancho,
        alto: p.alto,
        proveedor: p.proveedor || '',
        lote: p.lote || '',
        fechaIngreso: p.fecha_creacion,
        codigoBarras: p.codigo_barras || '',
        tipoCodigo: p.tipo_codigo || 'Interno',
        unidadMedida: p.unidad_medida || 'unidad',
        marca: p.marca || '',
        modelo: p.modelo || '',
        descripcion: p.descripcion || '',
        imagenUrl: '',
        estado: p.estado_inventario || 'Disponible',
        numeroSerie: p.numero_serie || '',
        zonaOperativa: zCod,
        pasillo: pas,
        rack: rac,
        nivel: niv,
        posicion: pos,
      };

      if (modoEdicion && nuevoItem.id) {
        setItems(items.map(i => i.id === nuevoItem.id ? itemTransformado : i));
        toast.success('Producto actualizado');
      } else {
        setItems([itemTransformado, ...items]);
        toast.success('Producto ingresado al almacén');
        itemParaMostrar = itemTransformado;
      }
      
      // Fetch fresh logs
      const logsRes = await api.get('/api/institucion/almacen/logs');
      if (logsRes.data) {
        setLogs(Array.isArray(logsRes.data) ? logsRes.data : []);
      }
      
      setMostrarModalNuevo(false);
      setNuevoItem({ 
        nombre: '', sku: '', cantidad: 1, ubicacion: '', categoria: '', peso: 0, volumen: 0, proveedor: '', lote: '',
        codigoBarras: '', tipoCodigo: 'Interno', unidadMedida: 'unidad', marca: '', modelo: '', descripcion: '', imagenUrl: '', estado: 'Disponible', numeroSerie: '',
        largo: 0, ancho: 0, alto: 0,
        zonaOperativa: zonasOperativas[0]?.codigo || 'SEC', pasillo: '01', rack: '01', nivel: '01', posicion: '01'
      });
      setModoEdicion(false);
      if (itemParaMostrar) {
        setMostrarModalQR(itemParaMostrar);
      }
    } catch (error) {
      console.error('Error al guardar producto:', error);
      const detail = (error as any)?.response?.data?.detail;
      if (typeof detail === 'string') {
        if (detail.includes('sku_producto_diferente')) {
          toast.error(detail.replace(/^sku_producto_diferente:\s*/, ''));
        } else if (detail.includes('sku_tamano_diferente')) {
          toast.error(detail.replace(/^sku_tamano_diferente:\s*/, ''));
        } else if (detail.includes('posicion_ocupada')) {
          toast.error(detail.replace(/^posicion_ocupada:\s*/, ''));
        } else if (detail.includes('sku_duplicado')) {
          toast.error('El SKU ya está registrado en este almacén con diferentes características');
        } else {
          toast.error(`No se pudo guardar el producto: ${detail}`);
        }
      } else {
        toast.error('No se pudo guardar el producto');
      }
    } finally {
      }
  };

  const handleCrearZonaOperativa = async () => {
    if (!esMaestroOSuperior) return;
    const normalizarNombre = (value: string) => value.trim().replace(/\s+/g, ' ');
    const normalizarCodigo = (value: string) =>
      value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 20);

    const nombre = normalizarNombre(nuevaZonaNombre);
    if (!nombre) {
      toast.error('Ingresa el nombre de la zona');
      return;
    }
    if (nombre.length < 2) {
      toast.error('El nombre es demasiado corto');
      return;
    }

    setCreandoZona(true);
    try {
      const payload: { nombre: string; codigo?: string } = { nombre };
      const codigo = normalizarCodigo(nuevaZonaCodigo);
      if (nuevaZonaCodigo !== codigo) setNuevaZonaCodigo(codigo);
      if (codigo) {
        if (codigo.length < 2) {
          toast.error('El código debe tener al menos 2 caracteres');
          return;
        }
        payload.codigo = codigo;
      }

      const nombreKey = nombre.toLowerCase();
      const codigoKey = codigo.toUpperCase();
      const existeNombre = zonasOperativas.some(z => z.nombre.trim().toLowerCase() === nombreKey);
      const existeCodigo = codigoKey ? zonasOperativas.some(z => z.codigo.toUpperCase() === codigoKey) : false;
      if (existeNombre) {
        toast.error('Ya existe una zona con ese nombre');
        return;
      }
      if (existeCodigo) {
        toast.error('Ya existe una zona con ese código');
        return;
      }

      const response = await api.post('/api/institucion/almacen/zonas-operativas', payload);
      const zonaCreada = response.data as ZonaOperativa;

      setZonasOperativas(prev => {
        const sinDuplicado = prev.filter(z => z.codigo !== zonaCreada.codigo);
        return [...sinDuplicado, zonaCreada].slice().sort((a, b) => (a.orden - b.orden) || a.nombre.localeCompare(b.nombre));
      });
      setNuevoItem(prev => ({ ...prev, zonaOperativa: zonaCreada.codigo }));
      setMostrarModalZona(false);
      toast.success('Zona creada');
    } catch (error) {
      console.error('Error al crear zona operativa:', error);
      const rawDetail = (error as any)?.response?.data?.detail;
      const detail =
        typeof rawDetail === 'string'
          ? rawDetail
          : Array.isArray(rawDetail)
            ? rawDetail.map((d: any) => String(d?.msg ?? '')).join(' | ')
            : '';

      if (detail.includes('zona_nombre_duplicado')) {
        toast.error('Ya existe una zona con ese nombre');
      } else if (detail.includes('zona_codigo_duplicado')) {
        toast.error('Ya existe una zona con ese código');
      } else if (detail.includes('codigo_invalido')) {
        toast.error('Código inválido (usa letras/números, 2–20 caracteres)');
      } else if (detail.includes('nombre_invalido') || detail.includes('nombre_requerido')) {
        toast.error('Nombre inválido');
      } else {
        toast.error('No se pudo crear la zona');
      }
    } finally {
      setCreandoZona(false);
    }
  };

  const handleCrearRackRapido = async () => {
    const zona = String(nuevoRackRapido.zona_codigo || nuevoItem.zonaOperativa || zonasOperativas[0]?.codigo || 'SEC').toUpperCase();
    const pasillo = normalizarNumero2(nuevoRackRapido.pasillo || '01');
    const rack = normalizarNumero2(nuevoRackRapido.rack || '01');
    const niveles = Math.max(1, Math.min(10, Number(nuevoRackRapido.niveles) || 3));
    const posPorNivel = Math.max(1, Math.min(12, Number(nuevoRackRapido.posiciones_por_nivel) || 8));
    const anchoCalculado = posPorNivel <= 2 ? 2.8 : posPorNivel <= 4 ? 3.8 : 4.8;
    const maxTon = Number(nuevoRackRapido.max_ton) || 10;
    const maxUnidades = Number(nuevoRackRapido.max_unidades) || 400;

    const payload: any = {
      alcance: 'RACK',
      zona_codigo: zona,
      pasillo,
      rack,
      niveles,
      posiciones_por_nivel: posPorNivel,
      ancho: anchoCalculado,
      max_unidades: maxUnidades,
      max_ton: maxTon,
      max_m3: 30,
      activo: true,
    };

    setCreandoRackRapido(true);
    try {
      let guardada: CapacidadRackConfig;
      try {
        const response = await api.post('/api/institucion/almacen/capacidades-rack', payload);
        guardada = response.data as CapacidadRackConfig;
      } catch (errApi) {
        console.warn('Endpoint API capacidades no respondió, usando persistencia local:', errApi);
        guardada = {
          id_capacidad: Date.now(),
          ...payload
        };
      }

      setCapacidadesRack(prev => {
        const sin = prev.filter(c => !(c.alcance === 'RACK' && String(c.zona_codigo || '').toUpperCase() === zona && normalizarNumero2(c.pasillo || '') === pasillo && normalizarNumero2(c.rack || '') === rack));
        return [...sin, guardada].sort((a, b) => a.id_capacidad - b.id_capacidad);
      });

      // Sincronizar en caliente layout 3D (Three.js)
      const rackKey1 = `${zona}-Pasillo ${pasillo}-Rack ${rack}`;
      const rackKey2 = `${zona}-${pasillo}-${rack}`;
      const layoutDataPrev = configuracionAlmacen?.layout_data || {};
      const racks3dPrev = (layoutDataPrev as any)?.racks3d || {};
      const rackObj = {
        ...(racks3dPrev[rackKey1] || racks3dPrev[rackKey2] || {}),
        id: rackKey1,
        zona,
        pasillo: `Pasillo ${pasillo}`,
        rack: `Rack ${rack}`,
        niveles,
        posiciones_por_nivel: posPorNivel,
        ancho: anchoCalculado,
        max_ton: maxTon,
        max_unidades: maxUnidades,
      };

      const nuevoLayout = {
        ...layoutDataPrev,
        racks3d: {
          ...racks3dPrev,
          [rackKey1]: rackObj,
          [rackKey2]: rackObj,
        },
      };
      setConfiguracionAlmacen(prev => ({ ...prev, layout_data: nuevoLayout }));

      // Asignar inmediatamente este rack al nuevo item
      setNuevoItem(prev => ({
        ...prev,
        zonaOperativa: zona,
        pasillo,
        rack,
        nivel: '01',
        posicion: '01'
      }));

      setMostrarModalNuevoRackRapido(false);
      playBeep();
      toast.success(`Rack creado y seleccionado: ${zona} · Pasillo ${pasillo} · Rack ${rack}`);
    } catch (err) {
      console.error('Error al crear rack rápido:', err);
      toast.error('No se pudo crear el rack');
    } finally {
      setCreandoRackRapido(false);
    }
  };

  const abrirModalCapacidadesRack = () => {
    if (!esMaestroOSuperior) return;
    const inst = capacidadesRack
      .filter(c => c.alcance === 'INSTITUCION')
      .slice()
      .sort((a, b) => b.id_capacidad - a.id_capacidad)[0];

    const base = inst || ({
      id_capacidad: 0,
      alcance: 'INSTITUCION',
      zona_codigo: null,
      pasillo: null,
      rack: null,
      max_unidades: DEFAULT_CAPACIDAD_RACK_UNIDADES,
      max_ton: DEFAULT_CAPACIDAD_RACK_TON,
      max_m3: DEFAULT_CAPACIDAD_RACK_M3,
      activo: true,
    } as CapacidadRackConfig);

    setCapacidadForm({
      id_capacidad: base.id_capacidad || undefined,
      alcance: base.alcance,
      zona_codigo: String(base.zona_codigo || ''),
      pasillo: String(base.pasillo || ''),
      rack: String(base.rack || ''),
      niveles: base.niveles || 3,
      posiciones_por_nivel: (base as any).posiciones_por_nivel || 8,
      ancho: 3.8,
      max_unidades: Number(base.max_unidades) || DEFAULT_CAPACIDAD_RACK_UNIDADES,
      max_ton: Number(base.max_ton) || DEFAULT_CAPACIDAD_RACK_TON,
      max_m3: Number(base.max_m3) || DEFAULT_CAPACIDAD_RACK_M3,
      activo: Boolean(base.activo),
    });
    setMostrarModalCapacidadesRack(true);
  };

  const handleSeleccionarCapacidad = (config: CapacidadRackConfig) => {
    const layoutRacks3d = (configuracionAlmacen?.layout_data as any)?.racks3d;
    const rackKey = `${config.zona_codigo}-${config.pasillo}-${config.rack}`;
    const rack3d = layoutRacks3d?.[rackKey] ||
      Object.values(layoutRacks3d || {}).find(
        (r: any) =>
          r.zona?.toUpperCase() === String(config.zona_codigo || '').toUpperCase() &&
          normalizarNumero2(r.pasillo) === normalizarNumero2(config.pasillo || '') &&
          normalizarNumero2(r.rack) === normalizarNumero2(config.rack || '')
      ) as any;

    setCapacidadForm({
      id_capacidad: config.id_capacidad || undefined,
      alcance: config.alcance,
      zona_codigo: String(config.zona_codigo || ''),
      pasillo: String(config.pasillo || ''),
      rack: String(config.rack || ''),
      niveles: rack3d?.niveles || config.niveles || 3,
      posiciones_por_nivel: rack3d?.posiciones_por_nivel || (config as any).posiciones_por_nivel || 8,
      ancho: rack3d?.ancho || 3.8,
      max_unidades: Number(rack3d?.max_unidades || config.max_unidades) || DEFAULT_CAPACIDAD_RACK_UNIDADES,
      max_ton: Number(rack3d?.max_ton || config.max_ton) || DEFAULT_CAPACIDAD_RACK_TON,
      max_m3: Number(config.max_m3) || DEFAULT_CAPACIDAD_RACK_M3,
      activo: Boolean(config.activo),
    });
  };
  const handleAplicarPlantilla = (plantillaId: string) => {
    if (!plantillaId) return;
    
    let updates = {};
    switch(plantillaId) {
      // Plantillas MICRO (Racks Individuales)
      case 'r_mecalux_std': updates = { max_ton: 10, niveles: 4, posiciones_por_nivel: 8, ancho: 4.0, max_m3: 20, max_unidades: 400 }; break;
      case 'r_mecalux_pesado': updates = { max_ton: 16, niveles: 4, posiciones_por_nivel: 8, ancho: 4.4, max_m3: 20, max_unidades: 400 }; break;
      case 'r_drivein_schafer': updates = { max_ton: 30, niveles: 5, posiciones_por_nivel: 8, ancho: 4.8, max_m3: 50, max_unidades: 1000 }; break;
      case 'r_flow_dematic': updates = { max_ton: 20, niveles: 4, posiciones_por_nivel: 8, ancho: 4.0, max_m3: 40, max_unidades: 800 }; break;
      case 'r_pushback_interlake': updates = { max_ton: 15, niveles: 4, posiciones_por_nivel: 8, ancho: 4.0, max_m3: 30, max_unidades: 600 }; break;
      case 'r_cantilever_jung': updates = { max_ton: 6, niveles: 5, posiciones_por_nivel: 4, ancho: 3.6, max_m3: 25, max_unidades: 100 }; break;
      case 'r_miniload_mecalux': updates = { max_ton: 1.5, niveles: 6, posiciones_por_nivel: 8, ancho: 3.8, max_m3: 5, max_unidades: 1000 }; break;
      case 'r_auto_asrs': updates = { max_ton: 40, niveles: 10, posiciones_por_nivel: 10, ancho: 5.6, max_m3: 80, max_unidades: 2000 }; break;
      
      // Plantillas MACRO (Institución o Zona)
      case 'g_micro': updates = { max_ton: 500, niveles: 3, posiciones_por_nivel: 8, ancho: 3.8, max_m3: 1000, max_unidades: 50000 }; break;
      case 'g_cedis_regional': updates = { max_ton: 2500, niveles: 4, posiciones_por_nivel: 8, ancho: 4.0, max_m3: 15000, max_unidades: 200000 }; break;
      case 'g_hub_nacional': updates = { max_ton: 10000, niveles: 5, posiciones_por_nivel: 8, ancho: 4.4, max_m3: 80000, max_unidades: 1000000 }; break;
      case 'g_cold_storage': updates = { max_ton: 4000, niveles: 5, posiciones_por_nivel: 8, ancho: 4.4, max_m3: 20000, max_unidades: 300000 }; break;
      case 'g_mega_dc': updates = { max_ton: 25000, niveles: 6, posiciones_por_nivel: 8, ancho: 4.8, max_m3: 200000, max_unidades: 5000000 }; break;
    }
    
    if (Object.keys(updates).length > 0) {
      setCapacidadForm(prev => ({ ...prev, ...updates }));
      toast.success('Plantilla estructural aplicada. Puedes ajustar los valores si lo deseas.');
    }
  };

  const handleGuardarCapacidadRack = async () => {
    if (!esMaestroOSuperior) return;
    const alcance = capacidadForm.alcance;
    const zona_codigo = String(capacidadForm.zona_codigo || '').trim().toUpperCase();
    const pasillo = normalizarNumero2(capacidadForm.pasillo || '');
    const rack = normalizarNumero2(capacidadForm.rack || '');

    if (alcance === 'ZONA' && !zona_codigo) {
      toast.error('Selecciona la zona');
      return;
    }
    if (alcance === 'RACK' && (!zona_codigo || !pasillo || !rack || pasillo === '00' || rack === '00')) {
      toast.error('Completa zona, pasillo y rack');
      return;
    }

    const payload: any = {
      alcance,
      niveles: Number(capacidadForm.niveles) || 3,
      posiciones_por_nivel: Number(capacidadForm.posiciones_por_nivel) || 8,
      max_unidades: Math.max(0, Number(capacidadForm.max_unidades) || 0),
      max_ton: Math.max(0, Number(capacidadForm.max_ton) || 0),
      max_m3: Math.max(0, Number(capacidadForm.max_m3) || 0),
      activo: Boolean(capacidadForm.activo),
    };
    if (alcance === 'ZONA' || alcance === 'RACK') payload.zona_codigo = zona_codigo;
    if (alcance === 'RACK') {
      payload.pasillo = pasillo;
      payload.rack = rack;
    }

    setGuardandoCapacidadRack(true);
    try {
      const response = await api.post('/api/institucion/almacen/capacidades-rack', payload);
      const guardada = response.data as CapacidadRackConfig;
      setCapacidadesRack(prev => {
        const sin = prev.filter(c => c.id_capacidad !== guardada.id_capacidad);
        return [...sin, guardada].slice().sort((a, b) => a.id_capacidad - b.id_capacidad);
      });

      // Sincronizar en caliente con el layout 3D
      if (alcance === 'RACK' && zona_codigo && pasillo && rack) {
        const rackKey1 = `${zona_codigo}-Pasillo ${pasillo}-Rack ${rack}`;
        const rackKey2 = `${zona_codigo}-${pasillo}-${rack}`;
        const layoutDataPrev = configuracionAlmacen?.layout_data || {};
        const racks3dPrev = (layoutDataPrev as any)?.racks3d || {};
        const rackPrev = racks3dPrev[rackKey1] || racks3dPrev[rackKey2] || {};
        const posPorNivel = Number(capacidadForm.posiciones_por_nivel) || 8;
        const anchoCalculado = Number(capacidadForm.ancho) || (posPorNivel <= 2 ? 2.8 : posPorNivel <= 4 ? 3.8 : 4.8);
        const rackObj = {
          ...rackPrev,
          id: rackKey1,
          zona: zona_codigo,
          pasillo: `Pasillo ${pasillo}`,
          rack: `Rack ${rack}`,
          niveles: Number(capacidadForm.niveles) || 3,
          posiciones_por_nivel: posPorNivel,
          ancho: anchoCalculado,
          max_ton: Number(capacidadForm.max_ton) || DEFAULT_CAPACIDAD_RACK_TON,
          max_unidades: Number(capacidadForm.max_unidades) || DEFAULT_CAPACIDAD_RACK_UNIDADES,
        };

        const nuevoLayout = {
          ...layoutDataPrev,
          racks3d: {
            ...racks3dPrev,
            [rackKey1]: rackObj,
            [rackKey2]: rackObj,
          },
        };
        setConfiguracionAlmacen(prev => ({ ...prev, layout_data: nuevoLayout }));
        try {
          await api.post('/api/institucion/almacen/configuracion', {
            nombre: configuracionAlmacen.nombre || 'Almacén Central',
            codigo: sanitizarCodigoAlmacen(configuracionAlmacen.codigo),
            descripcion: configuracionAlmacen.descripcion || '',
            tema_piso: configuracionAlmacen.tema_piso,
            tema_pared: configuracionAlmacen.tema_pared,
            dimension_ancho: configuracionAlmacen.dimension_ancho,
            dimension_largo: configuracionAlmacen.dimension_largo,
            layout_data: nuevoLayout,
          });
        } catch (err) {
          console.warn('Advertencia al sincronizar layout 3D:', err);
        }
      }

      handleSeleccionarCapacidad(guardada);
      toast.success('Capacidad guardada y sincronizada con el modelo 3D');
    } catch (error) {
      console.error('Error al guardar capacidad del rack:', error);
      const rawDetail = (error as any)?.response?.data?.detail;
      const detail =
        typeof rawDetail === 'string'
          ? rawDetail
          : Array.isArray(rawDetail)
            ? rawDetail.map((d: any) => String(d?.msg ?? '')).join(' | ')
            : '';

      if (detail.includes('capacidad_duplicada')) {
        toast.error('Ya existe una configuración para ese alcance');
      } else if (detail.includes('zona_codigo_requerido')) {
        toast.error('Zona requerida');
      } else if (detail.includes('rack_requiere_zona_pasillo_rack')) {
        toast.error('Rack requiere zona, pasillo y rack');
      } else {
        toast.error('No se pudo guardar la capacidad');
      }
    } finally {
      setGuardandoCapacidadRack(false);
    }
  };

  const infraestructuraTieneConfiguracionBase = useMemo(() => {
    const nombre = String(configuracionAlmacen.nombre || '').trim();
    const codigo = sanitizarCodigoAlmacen(configuracionAlmacen.codigo);
    const zonasActivas = zonasOperativas.filter(z => z.activo);
    const capacidadesActivas = capacidadesRack.filter(c => c.activo);
    return Boolean(nombre && codigo && zonasActivas.length > 0 && capacidadesActivas.length > 0);
  }, [configuracionAlmacen, zonasOperativas, capacidadesRack]);

  const handleGuardarConfiguracionAlmacen = async () => {
    if (!esMaestroOSuperior) {
      toast.error('Solo los docentes y directivos pueden modificar la infraestructura.');
      return;
    }
    const nombre = String(configuracionAlmacen.nombre || '').trim();
    const descripcion = String(configuracionAlmacen.descripcion || '').trim();
    const codigo = sanitizarCodigoAlmacen(configuracionAlmacen.codigo);

    if (!nombre) {
      toast.error('Define primero el nombre del almacén institucional');
      return;
    }

    try {
      const savePromise = api.post('/api/institucion/almacen/configuracion', {
        nombre,
        codigo,
        descripcion,
        tema_piso: configuracionAlmacen.tema_piso,
        tema_pared: configuracionAlmacen.tema_pared,
        dimension_ancho: configuracionAlmacen.dimension_ancho,
        dimension_largo: configuracionAlmacen.dimension_largo,
        layout_data: configuracionAlmacen.layout_data
      });

      await toast.promise(savePromise, {
        loading: 'Guardando configuración 3D...',
        success: '¡Entorno del almacén actualizado con éxito!',
        error: 'Hubo un problema al guardar la configuración'
      });

      setConfiguracionAlmacen(prev => ({
        ...prev,
        nombre,
        codigo,
        descripcion,
        politicaCapacidad: 'SUMA_RACKS',
        actualizadoEn: new Date().toISOString(),
      }));
    } catch (error) {
      console.error('Error al guardar configuración de almacén:', error);
    } finally {
      }
  };

  const abrirModalNuevoProducto = (preset?: Partial<ItemAlmacen>) => {
    if (!infraestructuraTieneConfiguracionBase) {
      toast.error('Configura primero la infraestructura institucional antes de registrar inventario.');
      setActiveTab('infraestructura');
      return;
    }

    setModoEdicion(false);
    setNuevoItem({
      nombre: '',
      sku: '',
      cantidad: 1,
      ubicacion: '',
      categoria: '',
      peso: 0,
      volumen: 0,
      proveedor: '',
      lote: '',
      codigoBarras: '',
      tipoCodigo: 'Interno',
      unidadMedida: 'unidad',
      marca: '',
      modelo: '',
      descripcion: '',
      imagenUrl: '',
      estado: 'Disponible',
      numeroSerie: '',
      largo: 0,
      ancho: 0,
      alto: 0,
      zonaOperativa: zonasOperativas[0]?.codigo || 'SEC',
      pasillo: '01',
      rack: '01',
      nivel: '01',
      posicion: '01',
      tipoEntrada: 'CONTENEDOR',
      contenedorNumero: '',
      contenedorTipo: '',
      selloFiscal: '',
      bookingBL: '',
      naviera: '',
      puertoOrigen: '',
      taraContenedorKg: 0,
      almacenOrigen: '',
      remisionFactura: '',
      tipoTransporte: '',
      placasTransporte: '',
      operadorChofer: '',
      ...preset,
    });
    setMostrarModalNuevo(true);
  };

  const resumenInfraestructura = useMemo(() => {
    const inst = capacidadesRack.find(c => c.activo && c.alcance === 'INSTITUCION');
    const zonasActivas = zonasOperativas.filter(z => z.activo);
    
    let total = { max_unidades: 0, max_ton: 0, max_m3: 0 };
    if (inst) {
      total = {
        max_unidades: Number(inst.max_unidades) || 0,
        max_ton: Number(inst.max_ton) || 0,
        max_m3: Number(inst.max_m3) || 0,
      };
    } else {
      const racksActivos = capacidadesRack.filter(c => c.activo && c.alcance === 'RACK');
      total = racksActivos.reduce(
        (acc, rack) => {
          acc.max_unidades += Number(rack.max_unidades) || 0;
          acc.max_ton += Number(rack.max_ton) || 0;
          acc.max_m3 += Number(rack.max_m3) || 0;
          return acc;
        },
        { max_unidades: 0, max_ton: 0, max_m3: 0 }
      );
    }

    return {
      zonas: zonasActivas.length,
      racks: capacidadesRack.filter(c => c.activo && c.alcance === 'RACK').length,
      max_unidades: total.max_unidades,
      max_ton: total.max_ton,
      max_m3: total.max_m3,
    };
  }, [capacidadesRack, zonasOperativas]);

  const mostrarAlertaCapacidadExcedida = (detalle: {
    rackLabel: string;
    unidades?: { actual: number; futuro: number; max: number };
    peso?: { actual: number; futuro: number; max: number };
    volumen?: { actual: number; futuro: number; max: number };
  }) => {
    const bloques = [
      detalle.unidades
        ? {
            label: 'Unidades',
            actual: `${detalle.unidades.actual}/${detalle.unidades.max}`,
            futuro: `${detalle.unidades.futuro}/${detalle.unidades.max}`,
            exceso: `+${Math.max(detalle.unidades.futuro - detalle.unidades.max, 0).toLocaleString()}`
          }
        : null,
      detalle.peso
        ? {
            label: 'Carga',
            actual: `${detalle.peso.actual.toLocaleString(undefined, { maximumFractionDigits: 2 })}/${detalle.peso.max.toLocaleString(undefined, { maximumFractionDigits: 2 })} t`,
            futuro: `${detalle.peso.futuro.toLocaleString(undefined, { maximumFractionDigits: 2 })}/${detalle.peso.max.toLocaleString(undefined, { maximumFractionDigits: 2 })} t`,
            exceso: `+${Math.max(detalle.peso.futuro - detalle.peso.max, 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} t`
          }
        : null,
      detalle.volumen
        ? {
            label: 'Volumen',
            actual: `${detalle.volumen.actual.toLocaleString(undefined, { maximumFractionDigits: 2 })}/${detalle.volumen.max.toLocaleString(undefined, { maximumFractionDigits: 2 })} m³`,
            futuro: `${detalle.volumen.futuro.toLocaleString(undefined, { maximumFractionDigits: 2 })}/${detalle.volumen.max.toLocaleString(undefined, { maximumFractionDigits: 2 })} m³`,
            exceso: `+${Math.max(detalle.volumen.futuro - detalle.volumen.max, 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} m³`
          }
        : null,
    ].filter(Boolean) as Array<{ label: string; actual: string; futuro: string; exceso: string }>;

    toast.custom((t) => (
      <div
        className={`w-[420px] max-w-[calc(100vw-24px)] rounded-2xl border border-red-400/40 bg-slate-900 text-white shadow-2xl transition-all ${
          t.visible ? 'animate-enter' : 'animate-leave'
        }`}
      >
        <div className="border-b border-white/10 bg-gradient-to-r from-red-600/90 to-orange-500/90 px-5 py-4">
          <p className="text-[11px] font-black uppercase tracking-[0.24em] text-red-100">Bloqueo de Capacidad</p>
          <h4 className="mt-1 text-lg font-black">No se puede registrar el producto</h4>
          <p className="mt-1 text-sm text-red-50/90">La ubicación {detalle.rackLabel} rebasa los límites configurados de infraestructura.</p>
        </div>
        <div className="space-y-3 px-5 py-4">
          {bloques.map((bloque) => (
            <div key={bloque.label} className="rounded-xl border border-white/10 bg-white/5 px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-black uppercase tracking-widest text-red-200">{bloque.label}</span>
                <span className="rounded-full bg-red-500/20 px-2.5 py-1 text-[11px] font-black text-red-200">{bloque.exceso}</span>
              </div>
              <p className="mt-2 text-xs text-slate-300">Actual: {bloque.actual}</p>
              <p className="text-sm font-bold text-white">Intento de registro: {bloque.futuro}</p>
            </div>
          ))}
          <p className="text-xs text-slate-300">
            Ajusta la cantidad, cambia de rack o amplía la capacidad desde <span className="font-bold text-white">Infraestructura</span>.
          </p>
        </div>
      </div>
    ), { duration: 6500 });
  };

  const handleCopiarTexto = async (texto: string) => {
    try {
      await navigator.clipboard.writeText(texto);
      toast.success('Copiado');
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  const obtenerBarcodeUrls = (item: ItemAlmacen) => {
    const text = String(item.codigoBarras || '').trim();
    if (!text) return null;

    const tipoSolicitado = item.tipoCodigo;
    const eanValido = tipoSolicitado === 'EAN' && esEan13Valido(text);
    const upcValido = tipoSolicitado === 'UPC' && esUpcAValido(text);
    const tipo = (tipoSolicitado === 'EAN' && !eanValido) || (tipoSolicitado === 'UPC' && !upcValido) ? 'Code128' : tipoSolicitado;

    const tecCode = tipo === 'EAN' ? 'EAN13' : tipo === 'UPC' ? 'UPCA' : 'Code128';
    const tecData = text;

    const primary = `https://barcode.tec-it.com/barcode.ashx?data=${encodeURIComponent(tecData)}&code=${tecCode}&translate-esc=false`;

    const bcid = tipo === 'EAN' ? 'ean13' : tipo === 'UPC' ? 'upca' : 'code128';
    const bwip = `https://bwipjs-api.metafloor.com/?bcid=${bcid}&text=${encodeURIComponent(text)}&scale=3&includetext`;

    const detalle = tipo !== tipoSolicitado ? `${tipoSolicitado} inválido` : null;
    return { primary, fallback: bwip, tipo, detalle };
  };

  const bytesToBase64Url = (bytes: Uint8Array) => {
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  };

  const base64UrlToBytes = (input: string) => {
    const normalized = input.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
  };

  const encodeEtiquetaPayload = (payload: unknown) => {
    const json = JSON.stringify(payload);
    const bytes = new TextEncoder().encode(json);
    return bytesToBase64Url(bytes);
  };

  const decodeEtiquetaPayload = (d: string) => {
    try {
      const bytes = base64UrlToBytes(d);
      const json = new TextDecoder().decode(bytes);
      const parsed = JSON.parse(json);
      if (!parsed || typeof parsed !== 'object') return null;
      return parsed as any;
    } catch {
      return null;
    }
  };

  const obtenerUrlEtiquetaQR = (payload: unknown) => {
    const url = new URL('/qr/almacen/etiqueta', window.location.origin);
    url.searchParams.set('d', encodeEtiquetaPayload(payload));
    return url.toString();
  };

  const obtenerQrUrl = (item: ItemAlmacen) => {
    const payload = {
      sku: item.sku,
      codigoBarras: item.codigoBarras,
      tipoCodigo: item.tipoCodigo,
      numeroSerie: item.numeroSerie,
      nombre: item.nombre,
      marca: item.marca,
      modelo: item.modelo,
      estado: item.estado,
      cantidad: `${item.cantidad} ${item.unidadMedida}`,
      peso: item.peso,
      volumen: calcularVolumenUnitarioM3(item),
      largo: item.largo,
      ancho: item.ancho,
      alto: item.alto,
      lote: item.lote,
      proveedor: item.proveedor,
      ubicacion: item.ubicacion,
    };
    const etiquetaUrl = obtenerUrlEtiquetaQR(payload);
    return `https://api.qrserver.com/v1/create-qr-code/?size=600x600&data=${encodeURIComponent(etiquetaUrl)}`;
  };

  const handleImprimirEtiqueta = (item: ItemAlmacen) => {
    const qrUrl = obtenerQrUrl(item);
    const barcode = obtenerBarcodeUrls(item);
    const w = window.open('', '_blank');
    if (!w) {
      toast.error('Por favor permite las ventanas emergentes (pop-ups) en tu navegador para imprimir');
      return;
    }
    const titulo = `${item.sku} · ${item.nombre}`;
    w.document.open();
    w.document.write(`
      <!doctype html>
      <html lang="es">
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width,initial-scale=1" />
          <title>${titulo}</title>
          <style>
            @page { margin: 0; size: 100mm 50mm; } /* Tamaño estándar etiqueta térmica 4x2 pulgadas */
            body { 
              font-family: ui-sans-serif, system-ui, -apple-system, sans-serif; 
              margin: 0;
              padding: 4mm;
              color: #000;
              width: 100mm;
              height: 50mm;
              box-sizing: border-box;
              display: flex;
              flex-direction: column;
              justify-content: space-between;
              background: #fff;
            }
            .header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              border-bottom: 1px solid #000;
              padding-bottom: 2mm;
              margin-bottom: 2mm;
            }
            .sku { font-size: 14px; font-weight: 900; line-height: 1; }
            .name { font-size: 9px; font-weight: 600; text-transform: uppercase; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 55mm; }
            .loc { font-size: 11px; font-weight: 800; font-family: monospace; background: #000; color: #fff; padding: 1px 4px; border-radius: 2px; }
            .content {
              display: flex;
              justify-content: space-between;
              flex: 1;
            }
            .info {
              flex: 1;
              font-size: 8px;
              line-height: 1.3;
              display: flex;
              flex-direction: column;
              gap: 1px;
            }
            .info-row { display: flex; justify-content: space-between; padding-right: 4mm; }
            .label { font-weight: 700; }
            .val { font-family: monospace; }
            .codes {
              display: flex;
              flex-direction: column;
              align-items: flex-end;
              justify-content: center;
              gap: 2mm;
              width: 35mm;
            }
            .qr-box { display: flex; align-items: center; gap: 2mm; }
            .qr-img { width: 18mm; height: 18mm; }
            .barcode-box { display: flex; flex-direction: column; align-items: center; }
            .barcode-img { height: 12mm; max-width: 35mm; }
            .barcode-text { font-size: 7px; font-family: monospace; letter-spacing: 1px; margin-top: 1px; }
            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <div class="sku">${item.sku}</div>
              <div class="name">${item.nombre}</div>
            </div>
            <div class="loc">${item.ubicacion || 'SIN-UBICACION'}</div>
          </div>
          <div class="content">
            <div class="info">
              <div class="info-row"><span class="label">CANT:</span><span class="val">${item.cantidad} ${item.unidadMedida}</span></div>
              <div class="info-row"><span class="label">PESO:</span><span class="val">${normalizarMedida(item.peso)} kg</span></div>
              <div class="info-row"><span class="label">VOL:</span><span class="val">${calcularVolumenUnitarioM3(item).toFixed(4)} m³</span></div>
              <div class="info-row"><span class="label">LOTE:</span><span class="val">${item.lote || 'N/A'}</span></div>
              <div class="info-row"><span class="label">EST:</span><span class="val">${item.estado}</span></div>
            </div>
            <div class="codes">
              ${barcode ? `
                <div class="barcode-box">
                  <img class="barcode-img" src="${barcode.primary}" alt="Barras" ${barcode.fallback ? `onerror="this.onerror=null;this.src='${barcode.fallback}'"` : ''} />
                  <div class="barcode-text">${String(item.codigoBarras || '').trim()}</div>
                </div>
              ` : ''}
              <div class="qr-box">
                <img class="qr-img" src="${qrUrl}" alt="QR" />
              </div>
            </div>
          </div>
          <script>
            window.onload = () => {
              setTimeout(() => {
                window.print();
                // Opcional: window.close() después de imprimir
              }, 500);
            };
          </script>
        </body>
      </html>
    `);
    w.document.close();
  };

  const handleIngresarEnRack = () => {
    if (!rackSeleccionado) return;
    abrirModalNuevoProducto({
      zonaOperativa: rackSeleccionado.zona,
      pasillo: normalizarNumero2(rackSeleccionado.pasillo),
      rack: normalizarNumero2(rackSeleccionado.rack),
      nivel: '01',
      posicion: '01',
    });
  };

  const handleIngresarEnPosicion = (nivel: string, posicion: string) => {
    if (!rackSeleccionado) return;
    setAccionPosicion({
      zonaOperativa: rackSeleccionado.zona,
      pasillo: normalizarNumero2(rackSeleccionado.pasillo),
      rack: normalizarNumero2(rackSeleccionado.rack),
      nivel: normalizarNumero2(nivel),
      posicion: normalizarNumero2(posicion),
    });
  };

  const handleProcederNuevoProducto = () => {
    if (!accionPosicion) return;
    abrirModalNuevoProducto(accionPosicion);
    setAccionPosicion(null);
  };

  const handleAbrirModalReubicacion = () => {
    if (!accionPosicion) return;
    setBusquedaReubicar('');
    setMostrarModalReubicar(true);
  };

  const handleReubicarProducto = async (item: ItemAlmacen) => {
    if (!accionPosicion) return;
    
    const zonaCodigo = obtenerCodigoZona(accionPosicion.zonaOperativa || zonasOperativas[0]?.codigo || 'SEC');
    const pasilloCodigo = normalizarNumero2(accionPosicion.pasillo || '01');
    const rackCodigo = normalizarNumero2(accionPosicion.rack || '01');
    
    // Validaciones WMS (Constraints Engine)
    const capRack = obtenerCapacidadRack({ zonaOperativa: zonaCodigo, pasillo: pasilloCodigo, rack: rackCodigo });
    
    const itemsMismoRack = items.filter(i => {
      if (i.id === item.id) return false; // Exclude the item being moved
      return (
        obtenerCodigoZona(i.zonaOperativa) === zonaCodigo &&
        normalizarNumero2(i.pasillo) === pasilloCodigo &&
        normalizarNumero2(i.rack) === rackCodigo
      );
    });

    const unidadesActuales = itemsMismoRack.reduce((acc, i) => acc + (Number(i.cantidad) || 0), 0);
    const pesoActualTon = itemsMismoRack.reduce((acc, i) => acc + ((Number(i.peso) || 0) * (Number(i.cantidad) || 0)), 0) / 1000;
    const volumenActualM3 = itemsMismoRack.reduce((acc, i) => acc + (calcularVolumenUnitarioM3(i) * (Number(i.cantidad) || 0)), 0);

    const cantidadNueva = item.cantidad || 0;
    const pesoUnitarioKg = Number(item.peso) || 0;
    const volumenUnitarioM3 = calcularVolumenUnitarioM3(item);

    const unidadesFuturas = unidadesActuales + cantidadNueva;
    const pesoFuturoTon = pesoActualTon + ((pesoUnitarioKg * cantidadNueva) / 1000);
    const volumenFuturoM3 = volumenActualM3 + (volumenUnitarioM3 * cantidadNueva);

    const excedeUnidades = unidadesFuturas > capRack.max_unidades;
    const excedePeso = pesoFuturoTon > capRack.max_ton;
    const excedeVolumen = volumenFuturoM3 > capRack.max_m3;
    
    if (excedeUnidades || excedePeso || excedeVolumen) {
       playErrorBeep();
       mostrarAlertaCapacidadExcedida({
          rackLabel: `${CODIGO_ALMACEN_VISUAL} · ${zonaCodigo} · P${pasilloCodigo} · R${rackCodigo}`,
          unidades: excedeUnidades ? { actual: unidadesActuales, futuro: unidadesFuturas, max: capRack.max_unidades } : undefined,
          peso: excedePeso ? { actual: pesoActualTon, futuro: pesoFuturoTon, max: capRack.max_ton } : undefined,
          volumen: excedeVolumen ? { actual: volumenActualM3, futuro: volumenFuturoM3, max: capRack.max_m3 } : undefined,
       });
       return;
    }

    try {
      const actualizacion = {
         ...item,
         zonaOperativa: accionPosicion.zonaOperativa,
         pasillo: accionPosicion.pasillo,
         rack: accionPosicion.rack,
         nivel: accionPosicion.nivel,
         posicion: accionPosicion.posicion
      };

      const payload = {
        sku: item.sku,
        nombre: item.nombre,
        cantidad: cantidadNueva,
        zona_codigo: zonaCodigo,
        pasillo: pasilloCodigo,
        rack: rackCodigo,
        nivel: normalizarNumero2(accionPosicion.nivel || '01'),
        posicion: normalizarNumero2(accionPosicion.posicion || '01'),
        unidad_medida: item.unidadMedida || 'unidad',
        peso_unitario: pesoUnitarioKg,
        volumen_unitario: volumenUnitarioM3,
        estado_inventario: item.estado || 'Disponible',
        descripcion: item.descripcion || '',
        marca: item.marca || '',
        modelo: item.modelo || '',
        numero_serie: item.numeroSerie || '',
        categoria: item.categoria || '',
        proveedor: item.proveedor || '',
        lote: item.lote || '',
        codigo_barras: item.codigoBarras || '',
        tipo_codigo: item.tipoCodigo || 'Interno',
        largo: item.largo || 0,
        ancho: item.ancho || 0,
        alto: item.alto || 0
      };
      
      await api.put(`/api/institucion/almacen/productos/${item.id}`, payload);
      
      // Fetch fresh logs to update the kardex table in real time
      const logsRes = await api.get('/api/institucion/almacen/logs');
      if (logsRes.data) {
         setLogs(Array.isArray(logsRes.data) ? logsRes.data : []);
      }
      
      // Actualizar estado local
      setItems(prev => prev.map(p => p.id === item.id ? actualizacion : p));
      toast.success('Producto reubicado exitosamente');
      setMostrarModalReubicar(false);
      setAccionPosicion(null);
      
      // Si el rack seleccionado está abierto, actualizarlo también
      if (rackSeleccionado) {
         const movingToThisRack = 
            obtenerCodigoZona(accionPosicion.zonaOperativa) === obtenerCodigoZona(rackSeleccionado.zona) &&
            normalizarNumero2(accionPosicion.pasillo) === normalizarNumero2(rackSeleccionado.pasillo) &&
            normalizarNumero2(accionPosicion.rack) === normalizarNumero2(rackSeleccionado.rack);
         
         let newItems = [...rackSeleccionado.items];
         const itemIndex = newItems.findIndex(p => p.id === item.id);
         
         if (movingToThisRack) {
            if (itemIndex >= 0) {
                newItems[itemIndex] = actualizacion; // Just changing position within the same rack
            } else {
                newItems.push(actualizacion); // Coming from another rack
            }
         } else {
            if (itemIndex >= 0) {
                newItems.splice(itemIndex, 1); // Leaving this rack
            }
         }
         
         setRackSeleccionado({
            ...rackSeleccionado,
            items: newItems
         });
      }
    } catch (error) {
       toast.error('Error al reubicar el producto');
    }
  };

  const handleAbrirItemDesdeRack = (item: ItemAlmacen) => {
    handleEditar(item);
  };

  const handleEditar = (item: ItemAlmacen) => {
    setNuevoItem(item);
    setModoEdicion(true);
    setMostrarModalNuevo(true);
  };

  const abrirModalSalida = (item?: ItemAlmacen) => {
    if (item) {
      setSalidaItemSeleccionado(item);
      setSalidaCantidad(1);
    } else {
      setSalidaItemSeleccionado(null);
      setSalidaCantidad(1);
    }
    setSalidaBusqueda('');
    setSalidaCategoriaFiltro('TODAS');
    setSalidaMotivo('Despacho a Cliente / Distribución');
    setSalidaTipoDestino('CONTENEDOR');
    setSalidaContenedorNumero('');
    setSalidaContenedorTipo('');
    setSalidaSelloFiscal('');
    setSalidaBooking('');
    setSalidaNaviera('');
    setSalidaTerminalPortuaria('');
    setSalidaTaraContenedor(0);
    setSalidaAlmacenDestino('');
    setSalidaRemision('');
    setSalidaTipoTransporte('');
    setSalidaPlacas('');
    setSalidaOperador('');
    setMostrarModalSalida(true);
  };

  const handleConfirmarSalida = async () => {
    if (!salidaItemSeleccionado) {
      toast.error('Selecciona un producto para retirar');
      return;
    }
    const cant = Number(salidaCantidad);
    if (!cant || cant <= 0) {
      toast.error('La cantidad a retirar debe ser mayor a 0');
      return;
    }
    if (cant > salidaItemSeleccionado.cantidad) {
      toast.error(`La cantidad no puede superar el stock disponible (${salidaItemSeleccionado.cantidad})`);
      return;
    }

    try {
      setProcesandoSalida(true);
      const payloadSalida = {
        cantidad: cant,
        motivo: salidaMotivo,
        tipo_destino: salidaTipoDestino,
        contenedor_numero: salidaTipoDestino === 'CONTENEDOR' ? (salidaContenedorNumero.trim().toUpperCase() || undefined) : undefined,
        contenedor_tipo: salidaTipoDestino === 'CONTENEDOR' ? salidaContenedorTipo : undefined,
        sello_fiscal: salidaTipoDestino === 'CONTENEDOR' ? (salidaSelloFiscal.trim().toUpperCase() || undefined) : undefined,
        booking: salidaTipoDestino === 'CONTENEDOR' ? (salidaBooking.trim().toUpperCase() || undefined) : undefined,
        naviera: salidaTipoDestino === 'CONTENEDOR' ? salidaNaviera : undefined,
        terminal_puerto: salidaTipoDestino === 'CONTENEDOR' ? salidaTerminalPortuaria : undefined,
        tara_contenedor: salidaTipoDestino === 'CONTENEDOR' ? salidaTaraContenedor : undefined,
        vgm_total: salidaTipoDestino === 'CONTENEDOR' ? (salidaTaraContenedor + ((salidaItemSeleccionado.peso || 0) * cant)) : undefined,
        almacen_destino: salidaTipoDestino === 'ALMACEN' ? (salidaAlmacenDestino.trim() || undefined) : undefined,
        remision: salidaTipoDestino === 'ALMACEN' ? (salidaRemision.trim().toUpperCase() || undefined) : undefined,
        tipo_transporte: salidaTipoDestino === 'ALMACEN' ? salidaTipoTransporte : undefined,
        placas: salidaTipoDestino === 'ALMACEN' ? (salidaPlacas.trim().toUpperCase() || undefined) : undefined,
        operador: salidaTipoDestino === 'ALMACEN' ? (salidaOperador.trim() || undefined) : undefined,
        solicitante_id: salidaOperadorEstudianteId ? Number(salidaOperadorEstudianteId) : usuario?.id,
        solicitante_nombre: salidaOperadorNombre || (usuario?.nombre ? `${usuario.nombre} ${usuario.apellido || ''}`.trim() : (usuario?.email || 'Estudiante'))
      };
      const res = await api.post(`/api/institucion/almacen/productos/${salidaItemSeleccionado.id}/salida`, payloadSalida);

      if (res.data) {
        if (res.data.pendiente_aprobacion) {
          toast.success(res.data.message || 'Solicitud de retiro registrada en Mesa de Control para validación docente.', { duration: 5000, icon: '🛡️' });
          await recargarSolicitudesPendientes();
          setMostrarModalSalida(false);
          setSalidaItemSeleccionado(null);
          return;
        }
        if (res.data.eliminado) {
          setItems((prev) => prev.filter((i) => i.id !== salidaItemSeleccionado.id));
          if (rackSeleccionado) {
            setRackSeleccionado((prev) =>
              prev
                ? {
                    ...prev,
                    items: prev.items.filter((i) => i.id !== salidaItemSeleccionado.id),
                  }
                : null
            );
          }
          toast.success(`Producto ${salidaItemSeleccionado.sku} retirado completamente del almacén`);
        } else {
          const restante = Number(res.data.cantidad_restante);
          setItems((prev) =>
            prev.map((i) => (i.id === salidaItemSeleccionado.id ? { ...i, cantidad: restante } : i))
          );
          if (rackSeleccionado) {
            setRackSeleccionado((prev) =>
              prev
                ? {
                    ...prev,
                    items: prev.items.map((i) =>
                      i.id === salidaItemSeleccionado.id ? { ...i, cantidad: restante } : i
                    ),
                  }
                : null
            );
          }
          toast.success(`Salida de ${cant} ${salidaItemSeleccionado.unidadMedida}(s) registrada. Quedan ${restante}.`);
        }

        setMostrarModalSalida(false);
        setSalidaItemSeleccionado(null);

        // Actualizar logs del Kardex
        const logsRes = await api.get('/api/institucion/almacen/logs');
        if (logsRes.data) {
          setLogs(Array.isArray(logsRes.data) ? logsRes.data : []);
        }
      }
    } catch (error: any) {
      console.error('Error al registrar salida de mercancía:', error);
      const msg = error.response?.data?.detail || 'Error al registrar la salida de mercancía';
      toast.error(msg);
    } finally {
      setProcesandoSalida(false);
    }
  };


  // Inicializar/Destruir Escáner QR
  useEffect(() => {
    if (mostrarScannerQR) {
      // Necesita un pequeño retraso para asegurar que el div del scanner existe en el DOM
      setTimeout(() => {
        scannerRef.current = new Html5QrcodeScanner(
          "qr-reader",
          { fps: 10, qrbox: { width: 250, height: 250 }, rememberLastUsedCamera: true },
          /* verbose= */ false
        );
        
        scannerRef.current.render(
          (decodedText) => {
            // Éxito al escanear
            const raw = String(decodedText || '').trim();

            let skuDetectado = '';
            let codigoDetectado = '';

            try {
              const u = new URL(raw);
              const d = u.searchParams.get('d');
              if (d) {
                const p = decodeEtiquetaPayload(d);
                skuDetectado = String(p?.sku || '').trim();
                codigoDetectado = String(p?.codigoBarras || '').trim();
              }
            } catch {}

            if (!skuDetectado && !codigoDetectado && raw.startsWith('{')) {
              try {
                const p = JSON.parse(raw);
                skuDetectado = String(p?.sku || '').trim();
                codigoDetectado = String(p?.codigoBarras || '').trim();
              } catch {}
            }

            if (!skuDetectado && !codigoDetectado) {
              skuDetectado = raw;
              codigoDetectado = raw;
            }

            toast.success('Código detectado');

            const skuKey = skuDetectado.toLowerCase();
            const codigoKey = codigoDetectado.toLowerCase();
            const productoEncontrado = items.find(i => {
              const sku = String(i.sku || '').trim().toLowerCase();
              const code = String(i.codigoBarras || '').trim().toLowerCase();
              return (skuKey && sku === skuKey) || (codigoKey && code === codigoKey);
            });

            // Detener el escáner automáticamente tras lectura exitosa
            if (scannerRef.current) {
              scannerRef.current.clear().catch(error => console.error("Error clearing scanner", error));
            }
            setMostrarScannerQR(false);

            if (productoEncontrado) {
              playBeep();
              // Si lo encuentra, abre el modal de edición
              if (esMaestroOSuperior) {
                toast.success('Abriendo detalles del producto...');
                handleEditar(productoEncontrado);
              } else {
                toast('Producto encontrado en inventario', { icon: '📦' });
                // Solo muestra el QR si es estudiante
                setMostrarModalQR(productoEncontrado);
              }
            } else {
              playErrorBeep();
              toast.error('El código no pertenece a ningún producto registrado en este almacén.');
            }
          },
          () => {}
        );
      }, 300);
    } else {
      if (scannerRef.current) {
        scannerRef.current.clear().catch(error => console.error("Error clearing scanner", error));
      }
    }
    
    return () => {
      if (scannerRef.current) {
        scannerRef.current.clear().catch(error => console.error("Error clearing scanner on unmount", error));
      }
    };
  }, [mostrarScannerQR, items, esMaestroOSuperior]);

  // Lógica para Drag and Drop del mapa (Hardware Accelerated)
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (activeTab !== 'mapa') return;
    hasDragged.current = false;
    
    // Click derecho o con Shift para orbitar forzado
    if (e.button === 2 || e.shiftKey) {
       isOrbiting.current = true;
    } else {
       if (modo3D && herramienta3D === 'orbit') {
           isOrbiting.current = true;
       } else {
           isDragging.current = true;
           setIsDraggingState(true);
       }
    }
    
    dragStart.current = { 
       x: e.clientX - (isOrbiting.current ? (currentRotacion.current.z / sensibilidad) : (currentPan.current.x / sensibilidad)), 
       y: e.clientY - (isOrbiting.current ? (currentRotacion.current.x / sensibilidad) : (currentPan.current.y / sensibilidad)) 
    };
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDragging.current) {
      hasDragged.current = true;
      // Pan
      currentPan.current = {
        x: (e.clientX - dragStart.current.x) * sensibilidad,
        y: (e.clientY - dragStart.current.y) * sensibilidad
      };
      if (panRef.current) {
        // Usamos requestAnimationFrame implícito al actualizar style directo
        panRef.current.style.transform = `translate(${currentPan.current.x}px, ${currentPan.current.y}px) scale(${zoom})`;
      }
    } else if (isOrbiting.current && modo3D) {
      hasDragged.current = true;
      hasOrbited.current = true;
      // Rotación por Dragging (Órbita)
      const moveX = (e.clientX - dragStart.current.x) * sensibilidad;
      const moveY = (e.clientY - dragStart.current.y) * sensibilidad;
      currentRotacion.current = {
        x: Math.min(80, Math.max(0, moveY)), // Clamp X rotation between 0 and 80 to prevent flipping
        z: moveX
      };
      if (rotacionRef.current) {
        rotacionRef.current.style.transform = `rotateX(${currentRotacion.current.x}deg) rotateZ(${currentRotacion.current.z}deg) translate3d(${currentWalk.current.x}px, ${currentWalk.current.y}px, 0)`;
      }
    } else if (modo3D && mapContainerRef.current && !hasOrbited.current) {
      // Parallax por Hover (Suave) - Solo si no han orbitado manualmente
      const rect = mapContainerRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const mouseX = e.clientX - centerX;
      const mouseY = e.clientY - centerY;
      
      currentRotacion.current = {
        x: 55 - (mouseY / rect.height) * 20,
        z: -45 + (mouseX / rect.width) * 25
      };
      
      if (rotacionRef.current) {
        rotacionRef.current.style.transform = `rotateX(${currentRotacion.current.x}deg) rotateZ(${currentRotacion.current.z}deg) translate3d(${currentWalk.current.x}px, ${currentWalk.current.y}px, 0)`;
      }
    }
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    isOrbiting.current = false;
    setIsDraggingState(false);
  };

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.2, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.2, 0.4));
  
  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (e.deltaY < 0) {
      handleZoomIn();
    } else {
      handleZoomOut();
    }
  };
  // Soporte para mover la cámara con el teclado (WASD / Flechas)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName)) return;
      if (activeTab !== 'mapa') return;
      keysPressed.current[e.key.toLowerCase()] = true;
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      keysPressed.current[e.key.toLowerCase()] = false;
    };

    const gameLoop = () => {
      const walkSpeed = 12; // Velocidad de caminata en el espacio 3D
      let changed = false;

      // Direcciones deseadas de la cámara en la pantalla
      let screenDx = 0;
      let screenDy = 0;

      if (keysPressed.current['w'] || keysPressed.current['arrowup']) { screenDy += walkSpeed; } // W: Mapa baja, cámara avanza
      if (keysPressed.current['s'] || keysPressed.current['arrowdown']) { screenDy -= walkSpeed; } // S: Mapa sube, cámara retrocede
      if (keysPressed.current['a'] || keysPressed.current['arrowleft']) { screenDx += walkSpeed; } // A: Mapa derecha, cámara izq
      if (keysPressed.current['d'] || keysPressed.current['arrowright']) { screenDx -= walkSpeed; } // D: Mapa izq, cámara derecha

      if (screenDx !== 0 || screenDy !== 0) {
        // Matriz inversa de rotación para que W SIEMPRE sea "hacia adelante" según a dónde mires
        const rad = (currentRotacion.current.z * Math.PI) / 180;
        
        const walkX = screenDx * Math.cos(rad) + screenDy * Math.sin(rad);
        const walkY = -screenDx * Math.sin(rad) + screenDy * Math.cos(rad);

        currentWalk.current.x += walkX;
        currentWalk.current.y += walkY;
        
        // Clamping para evitar salirse del almacén y crashear la cámara
        const maxLimit = 12000;
        const padding = 300; // Evitar acercarse demasiado a la pared
        const limiteX = (Math.min((configuracionAlmacen.dimension_ancho || 80) * 100, maxLimit) / 2) - padding;
        const limiteY = (Math.min((configuracionAlmacen.dimension_largo || 80) * 100, maxLimit) / 2) - padding;

        currentWalk.current.x = Math.max(-limiteX, Math.min(limiteX, currentWalk.current.x));
        currentWalk.current.y = Math.max(-limiteY, Math.min(limiteY, currentWalk.current.y));

        changed = true;
      }

      if (changed && rotacionRef.current) {
        // Aplicar la caminata dentro del espacio rotado
        rotacionRef.current.style.transform = modo3D 
          ? `rotateX(${currentRotacion.current.x}deg) rotateZ(${currentRotacion.current.z}deg) translate3d(${currentWalk.current.x}px, ${currentWalk.current.y}px, 0)` 
          : `rotateX(0deg) rotateZ(0deg) translate3d(${currentWalk.current.x}px, ${currentWalk.current.y}px, 0)`;
      }

      animationFrameId.current = requestAnimationFrame(gameLoop);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    animationFrameId.current = requestAnimationFrame(gameLoop);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
    };
  }, [zoom, activeTab, configuracionAlmacen.dimension_ancho, configuracionAlmacen.dimension_largo]);

  const handleResetView = () => {
    setZoom(1);
    currentPan.current = { x: 0, y: 0 };
    currentWalk.current = { x: 0, y: 0 };
    currentRotacion.current = { x: 55, z: -45 };
    hasOrbited.current = false;
    if (panRef.current) panRef.current.style.transform = `translate(0px, 0px) scale(1)`;
    if (rotacionRef.current) rotacionRef.current.style.transform = modo3D ? `rotateX(55deg) rotateZ(-45deg) translate3d(0px, 0px, 0)` : `rotateX(0deg) rotateZ(0deg) translate3d(0px, 0px, 0)`;
  };

  const CODIGO_ALMACEN_COMPACTO = sanitizarCodigoAlmacen(configuracionAlmacen.codigo);
  const CODIGO_ALMACEN_VISUAL = formatearCodigoVisualAlmacen(configuracionAlmacen.codigo);
  const DEFAULT_CAPACIDAD_RACK_UNIDADES = 500;
  const DEFAULT_CAPACIDAD_RACK_TON = 1500;
  const DEFAULT_CAPACIDAD_RACK_M3 = 60;
  const normalizarNumero2 = (value?: string) => {
    const digits = String(value ?? '').replace(/\D/g, '');
    if (!digits) return '00';
    return digits.padStart(2, '0').slice(-2);
  };

  const normalizarMedida = (value: unknown) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0) return 0;
    return n;
  };

  const esEan13Valido = (code: string) => {
    const s = String(code || '').trim();
    if (!/^\d{13}$/.test(s)) return false;
    const d12 = s.slice(0, 12);
    const check = Number(s[12]);
    let suma = 0;
    for (let i = 0; i < d12.length; i++) {
      const n = Number(d12[i]);
      const pos = i + 1;
      suma += pos % 2 === 0 ? n * 3 : n;
    }
    const esperado = (10 - (suma % 10)) % 10;
    return check === esperado;
  };

  const esUpcAValido = (code: string) => {
    const s = String(code || '').trim();
    if (!/^\d{12}$/.test(s)) return false;
    const d11 = s.slice(0, 11);
    const check = Number(s[11]);
    let suma = 0;
    for (let i = 0; i < d11.length; i++) {
      const n = Number(d11[i]);
      const pos = i + 1;
      suma += pos % 2 === 1 ? n * 3 : n;
    }
    const esperado = (10 - (suma % 10)) % 10;
    return check === esperado;
  };

  const calcularVolumenUnitarioM3 = (data: Partial<ItemAlmacen>) => {
    const volumen = normalizarMedida(data.volumen);
    if (volumen > 0) return volumen;
    const largo = normalizarMedida(data.largo);
    const ancho = normalizarMedida(data.ancho);
    const alto = normalizarMedida(data.alto);
    if (largo > 0 && ancho > 0 && alto > 0) return largo * ancho * alto;
    return 0;
  };

  const generarCodigoBarrasUnico = (existentes: ItemAlmacen[]) => {
    const usados = new Set(existentes.map(i => String(i.codigoBarras || '').trim()).filter(Boolean));
    for (let i = 0; i < 20; i++) {
      const candidato = `LP${Date.now()}${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`;
      if (!usados.has(candidato)) return candidato;
    }
    return `LP${Date.now()}${Math.floor(Math.random() * 1_000_000).toString().padStart(6, '0')}`;
  };

  const obtenerCodigoZona = (zonaOperativa?: string) => {
    const raw = String(zonaOperativa ?? '').trim();
    if (!raw) return 'GEN';

    const upper = raw.toUpperCase();
    const matchCodigo = zonasOperativas.find(z => z.codigo.toUpperCase() === upper);
    if (matchCodigo) return matchCodigo.codigo;

    const lower = raw.toLowerCase();
    const matchNombre = zonasOperativas.find(z => z.nombre.toLowerCase().trim() === lower);
    if (matchNombre) return matchNombre.codigo;

    if (lower.includes('recep') || lower.includes('inbound')) return 'INB';
    if (lower.includes('seco')) return 'SEC';
    if (lower.includes('picking')) return 'PIC';
    if (lower.includes('refriger') || lower.includes('frío') || lower.includes('frio')) return 'REF';
    if (lower.includes('cuarenten')) return 'CUA';
    if (lower.includes('bulk')) return 'BLK';
    if (lower.includes('despach') || lower.includes('outbound')) return 'DES';
    if (lower.includes('devoluc') || lower.includes('inversa')) return 'RET';
    if (lower.includes('cross')) return 'XDK';
    if (lower.includes('segur') || lower.includes('caja fuerte')) return 'SEG';
    return 'GEN';
  };

  const obtenerNombreZona = (zonaOperativa?: string) => {
    const codigo = obtenerCodigoZona(zonaOperativa);
    return zonasOperativas.find(z => z.codigo === codigo)?.nombre || codigo;
  };

  const construirClaveCapacidad = (alcance: 'INSTITUCION' | 'ZONA' | 'RACK', zona?: string, pasillo?: string, rack?: string) => {
    if (alcance === 'INSTITUCION') return 'INSTITUCION';
    if (alcance === 'ZONA') return `ZONA:${String(zona || '').toUpperCase()}`;
    return `RACK:${String(zona || '').toUpperCase()}:${normalizarNumero2(pasillo)}:${normalizarNumero2(rack)}`;
  };

  const capacidadesRackActivasMap = useMemo(() => {
    const map = new Map<string, CapacidadRackConfig>();
    capacidadesRack
      .filter(c => c.activo)
      .slice()
      .sort((a, b) => a.id_capacidad - b.id_capacidad)
      .forEach(c => {
        const key =
          c.alcance === 'INSTITUCION'
            ? construirClaveCapacidad('INSTITUCION')
            : c.alcance === 'ZONA'
              ? construirClaveCapacidad('ZONA', String(c.zona_codigo || '').toUpperCase())
              : construirClaveCapacidad('RACK', String(c.zona_codigo || '').toUpperCase(), String(c.pasillo || ''), String(c.rack || ''));
        map.set(key, c);
      });
    return map;
  }, [capacidadesRack]);

  const obtenerCapacidadRack = (data?: { zonaOperativa?: string; pasillo?: string; rack?: string }) => {
    const zona = obtenerCodigoZona(data?.zonaOperativa);
    const pasillo = normalizarNumero2(data?.pasillo);
    const rack = normalizarNumero2(data?.rack);

    const keyRack = construirClaveCapacidad('RACK', zona, pasillo, rack);
    const keyZona = construirClaveCapacidad('ZONA', zona);
    const keyInst = construirClaveCapacidad('INSTITUCION');

    const cRack = capacidadesRackActivasMap.get(keyRack);
    const cZona = capacidadesRackActivasMap.get(keyZona);
    const cInst = capacidadesRackActivasMap.get(keyInst);

    const base = cInst || ({
      id_capacidad: 0,
      alcance: 'INSTITUCION',
      zona_codigo: null,
      pasillo: null,
      rack: null,
      max_unidades: DEFAULT_CAPACIDAD_RACK_UNIDADES,
      max_ton: DEFAULT_CAPACIDAD_RACK_TON,
      max_m3: DEFAULT_CAPACIDAD_RACK_M3,
      activo: true,
    } as CapacidadRackConfig);

    const resuelta = cRack || cZona || base;

    // Sincronización con personalizaciones 3D del Modo Arquitecto
    const layoutRacks3d = (configuracionAlmacen?.layout_data as any)?.racks3d;
    const rackKey = `${zona}-${pasillo}-${rack}`;
    const rack3d = (layoutRacks3d?.[rackKey] ||
      Object.values(layoutRacks3d || {}).find(
        (r: any) =>
          r.zona?.toUpperCase() === zona.toUpperCase() &&
          normalizarNumero2(r.pasillo) === pasillo &&
          normalizarNumero2(r.rack) === rack
      )) as any;

    return {
      max_unidades: Number(rack3d?.max_unidades || resuelta.max_unidades) || DEFAULT_CAPACIDAD_RACK_UNIDADES,
      max_ton: Number(rack3d?.max_ton || resuelta.max_ton) || DEFAULT_CAPACIDAD_RACK_TON,
      max_m3: Number(resuelta.max_m3) || DEFAULT_CAPACIDAD_RACK_M3,
      fuente: rack3d ? 'LAYOUT_3D' : resuelta.alcance,
      niveles: rack3d?.niveles || resuelta.niveles || 3,
      posiciones_por_nivel: rack3d?.posiciones_por_nivel || (resuelta as any).posiciones_por_nivel || 8,
    };
  };

  const formatearCodigoUbicacion = (
    data: {
      zonaOperativa?: string;
      pasillo?: string;
      rack?: string;
      nivel?: string;
      posicion?: string;
    },
    opts?: {
      incluirNivel?: boolean;
      incluirPosicion?: boolean;
      formato?: 'compacto' | 'visual';
    }
  ) => {
    const incluirNivel = opts?.incluirNivel ?? true;
    const incluirPosicion = opts?.incluirPosicion ?? true;
    const formato = opts?.formato ?? 'visual';

    const zonaCodigo = obtenerCodigoZona(data.zonaOperativa);
    const pasilloCodigo = normalizarNumero2(data.pasillo);
    const rackCodigo = normalizarNumero2(data.rack);
    const nivelCodigo = normalizarNumero2(data.nivel);
    const posicionCodigo = normalizarNumero2(data.posicion);

    if (formato === 'compacto') {
      const partes = [
        CODIGO_ALMACEN_COMPACTO,
        zonaCodigo,
        `P${pasilloCodigo}`,
        `R${rackCodigo}`,
        ...(incluirNivel ? [`N${nivelCodigo}`] : []),
        ...(incluirPosicion ? [`S${posicionCodigo}`] : []),
      ];
      return partes.join('-');
    }

    const partes = [
      CODIGO_ALMACEN_VISUAL,
      zonaCodigo,
      `P${pasilloCodigo}`,
      `R${rackCodigo}`,
      ...(incluirNivel ? [`N${nivelCodigo}`] : []),
      ...(incluirPosicion ? [`S${posicionCodigo}`] : []),
    ];
    return partes.join(' · ');
  };

  const filtrados = items.filter(i => 
    i.nombre.toLowerCase().includes(busqueda.toLowerCase()) || 
    i.sku.toLowerCase().includes(busqueda.toLowerCase())
  );

  // Jerarquía avanzada para el Plano 2D (Zona -> Pasillo -> Rack)
  const zonasAlmacen = useMemo(() => {
    const zonas: Record<string, Record<string, Record<string, ItemAlmacen[]>>> = {};
    
    // PRIMERO: Inyectar racks configurados explícitamente en la Infraestructura
    capacidadesRack.forEach(cap => {
       if (cap.alcance === 'RACK' && cap.zona_codigo && cap.pasillo && cap.rack) {
           const zona = cap.zona_codigo;
           const pasillo = `Pasillo ${normalizarNumero2(cap.pasillo)}`;
           const rackKey = `Rack ${normalizarNumero2(cap.rack)}`;
           
           if (!zonas[zona]) zonas[zona] = {};
           if (!zonas[zona][pasillo]) zonas[zona][pasillo] = {};
           if (!zonas[zona][pasillo][rackKey]) zonas[zona][pasillo][rackKey] = [];
        }
    });

    // SEGUNDO: Inyectar racks del Layout 3D (para sincronización con gemelo digital)
    const layoutRacks3d = (configuracionAlmacen?.layout_data as any)?.racks3d || {};
    Object.values(layoutRacks3d).forEach((r3d: any) => {
       if (r3d?.zona && r3d?.pasillo && r3d?.rack) {
           const zona = String(r3d.zona).toUpperCase();
           const pasillo = `Pasillo ${normalizarNumero2(r3d.pasillo)}`;
           const rackKey = `Rack ${normalizarNumero2(r3d.rack)}`;
           if (!zonas[zona]) zonas[zona] = {};
           if (!zonas[zona][pasillo]) zonas[zona][pasillo] = {};
           if (!zonas[zona][pasillo][rackKey]) zonas[zona][pasillo][rackKey] = [];
       }
    });

    items.forEach(item => {
      const zona = obtenerCodigoZona(item.zonaOperativa);
      const pasillo = item.pasillo ? `Pasillo ${normalizarNumero2(item.pasillo)}` : 'Sin Pasillo';
      const rackKey = item.rack ? `Rack ${normalizarNumero2(item.rack)}` : (item.ubicacion || 'Sin Asignar');
      
      if (!zonas[zona]) zonas[zona] = {};
      if (!zonas[zona][pasillo]) zonas[zona][pasillo] = {};
      if (!zonas[zona][pasillo][rackKey]) zonas[zona][pasillo][rackKey] = [];
      
      zonas[zona][pasillo][rackKey].push(item);
    });
    return zonas;
  }, [items, capacidadesRack, configuracionAlmacen?.layout_data]);

  // Resumen estructural y ocupación de todos los racks del almacén (Sincronizado con 3D)
  const racksResumen = useMemo(() => {
    const list: Array<{
      zona: string;
      pasillo: string;
      rack: string;
      nombreVisual: string;
      niveles: number;
      posicionesPorNivel: number;
      ancho: number;
      totalSlots: number;
      palletsOcupados: number;
      pesoTotalTon: number;
      maxTon: number;
      maxUnidades: number;
      items: ItemAlmacen[];
    }> = [];

    const layoutRacks3d = (configuracionAlmacen?.layout_data as any)?.racks3d || {};
    const procesados = new Set<string>();

    Object.entries(zonasAlmacen).forEach(([zonaKey, pasillos]) => {
      Object.entries(pasillos).forEach(([pasilloKey, racks]) => {
        Object.entries(racks).forEach(([rackKey, itemsRack]) => {
          const cleanPasillo = normalizarNumero2(pasilloKey);
          const cleanRack = normalizarNumero2(rackKey);
          const keyUnica = `${zonaKey}-${cleanPasillo}-${cleanRack}`;
          if (procesados.has(keyUnica)) return;
          procesados.add(keyUnica);

          const r3d = layoutRacks3d[keyUnica] ||
            Object.values(layoutRacks3d).find(
              (r: any) =>
                r.zona?.toUpperCase() === zonaKey.toUpperCase() &&
                normalizarNumero2(r.pasillo) === cleanPasillo &&
                normalizarNumero2(r.rack) === cleanRack
            ) as any;

          const cap = obtenerCapacidadRack({ zonaOperativa: zonaKey, pasillo: cleanPasillo, rack: cleanRack });
          const numNiveles = r3d?.niveles || cap.niveles || 3;
          const posPorNivel = r3d?.posiciones_por_nivel || cap.posiciones_por_nivel || 8;
          const ancho = r3d?.ancho || (posPorNivel <= 2 ? 2.8 : posPorNivel <= 4 ? 3.8 : 4.8);
          const totalSlots = numNiveles * posPorNivel;

          const pesoTotalKg = itemsRack.reduce((acc, it) => acc + ((Number(it.peso) || 0) * (Number(it.cantidad) || 0)), 0);
          const pesoTotalTon = pesoTotalKg / 1000;

          const nombreVisual = formatearCodigoUbicacion(
            { zonaOperativa: zonaKey, pasillo: cleanPasillo, rack: cleanRack },
            { incluirNivel: false, incluirPosicion: false, formato: 'visual' }
          );

          list.push({
            zona: zonaKey,
            pasillo: cleanPasillo,
            rack: cleanRack,
            nombreVisual,
            niveles: numNiveles,
            posicionesPorNivel: posPorNivel,
            ancho,
            totalSlots,
            palletsOcupados: itemsRack.length,
            pesoTotalTon,
            maxTon: cap.max_ton,
            maxUnidades: cap.max_unidades,
            items: itemsRack,
          });
        });
      });
    });

    // Incluir también racks del layout 3D que aún no tengan items ni entrada en zonasAlmacen
    Object.values(layoutRacks3d).forEach((r3d: any) => {
      if (!r3d || !r3d.zona) return;
      const cleanPasillo = normalizarNumero2(r3d.pasillo);
      const cleanRack = normalizarNumero2(r3d.rack);
      const keyUnica = `${r3d.zona}-${cleanPasillo}-${cleanRack}`;
      if (procesados.has(keyUnica)) return;
      procesados.add(keyUnica);

      const itemsRack = items.filter(
        i =>
          obtenerCodigoZona(i.zonaOperativa) === r3d.zona &&
          normalizarNumero2(i.pasillo) === cleanPasillo &&
          normalizarNumero2(i.rack) === cleanRack
      );
      const cap = obtenerCapacidadRack({ zonaOperativa: r3d.zona, pasillo: cleanPasillo, rack: cleanRack });
      const numNiveles = r3d?.niveles || cap.niveles || 3;
      const posPorNivel = r3d?.posiciones_por_nivel || cap.posiciones_por_nivel || 8;
      const ancho = r3d?.ancho || (posPorNivel <= 2 ? 2.8 : posPorNivel <= 4 ? 3.8 : 4.8);
      const totalSlots = numNiveles * posPorNivel;

      const pesoTotalKg = itemsRack.reduce((acc, it) => acc + ((Number(it.peso) || 0) * (Number(it.cantidad) || 0)), 0);
      const pesoTotalTon = pesoTotalKg / 1000;

      const nombreVisual = formatearCodigoUbicacion(
        { zonaOperativa: r3d.zona, pasillo: cleanPasillo, rack: cleanRack },
        { incluirNivel: false, incluirPosicion: false, formato: 'visual' }
      );

      list.push({
        zona: r3d.zona,
        pasillo: cleanPasillo,
        rack: cleanRack,
        nombreVisual,
        niveles: numNiveles,
        posicionesPorNivel: posPorNivel,
        ancho,
        totalSlots,
        palletsOcupados: itemsRack.length,
        pesoTotalTon,
        maxTon: cap.max_ton,
        maxUnidades: cap.max_unidades,
        items: itemsRack,
      });
    });

    return list;
  }, [zonasAlmacen, configuracionAlmacen, items, capacidadesRack]);

  const totalStock = items.reduce((acc, item) => acc + item.cantidad, 0);
  const totalPesoKg = items.reduce((acc, item) => acc + ((Number(item.peso) || 0) * (Number(item.cantidad) || 0)), 0);
  const totalVolumenM3 = items.reduce((acc, item) => acc + (calcularVolumenUnitarioM3(item) * (Number(item.cantidad) || 0)), 0);
  const totalPesoTon = totalPesoKg / 1000;

  const capacidadTotalTon = resumenInfraestructura.max_ton;
  const capacidadDisponibleTon = Math.max(capacidadTotalTon - totalPesoTon, 0);
  const capacidadTotalM3 = resumenInfraestructura.max_m3;
  const capacidadDisponibleM3 = Math.max(capacidadTotalM3 - totalVolumenM3, 0);

  // Datos para gráfica circular de categorías
  const categoriasData = items.reduce((acc, item) => {
    const cat = item.categoria || 'General';
    if (!acc[cat]) acc[cat] = 0;
    acc[cat] += item.cantidad;
    return acc;
  }, {} as Record<string, number>);

  const coloresCategorias = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#6366f1', '#ec4899'];
  const categoriasArray = Object.entries(categoriasData).map(([name, value], idx) => ({
    name,
    value,
    color: coloresCategorias[idx % coloresCategorias.length]
  })).sort((a, b) => b.value - a.value);

  // Componente auxiliar para metadatos (Tiempo transcurrido)
  const getDaysAgo = (dateString: string) => {
    const days = Math.floor((new Date().getTime() - new Date(dateString).getTime()) / (1000 * 3600 * 24));
    return days === 0 ? 'Hoy' : days === 1 ? 'Hace 1 día' : `Hace ${days} días`;
  };

  const formatearTiempoRelativo = (dateString: string) => {
    try {
      const now = new Date().getTime();
      const past = new Date(dateString).getTime();
      const diffMs = now - past;
      if (isNaN(diffMs)) return 'Reciente';
      const diffMin = Math.floor(diffMs / (1000 * 60));
      if (diffMin < 1) return 'Justo ahora';
      if (diffMin < 60) return `Hace ${diffMin} min`;
      const diffHoras = Math.floor(diffMin / 60);
      if (diffHoras < 24) return `Hace ${diffHoras} h`;
      const diffDias = Math.floor(diffHoras / 24);
      if (diffDias === 1) return 'Ayer';
      if (diffDias < 30) return `Hace ${diffDias} d`;
      return new Date(dateString).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
    } catch {
      return 'Reciente';
    }
  };

  const copiarCoordenada = (coord: string) => {
    try {
      navigator.clipboard.writeText(coord);
      toast.success(`Coordenada ${coord} copiada`);
    } catch {
      toast('Coordenada: ' + coord);
    }
  };

  // Filtro reactivo para reubicación de productos
  const itemsFiltradosReubicacion = useMemo(() => {
    if (!busquedaReubicar.trim()) return items;
    const q = busquedaReubicar.toLowerCase().trim();
    return items.filter(item => {
      const sku = (item.sku || '').toLowerCase();
      const nombre = (item.nombre || '').toLowerCase();
      const marca = (item.marca || '').toLowerCase();
      const modelo = (item.modelo || '').toLowerCase();
      const categoria = (item.categoria || '').toLowerCase();
      const codigoBarras = (item.codigoBarras || '').toLowerCase();
      const ubicacion = `${item.zonaOperativa || ''} ${item.pasillo || ''} ${item.rack || ''} ${item.nivel || ''} ${item.posicion || ''}`.toLowerCase();
      return sku.includes(q) || nombre.includes(q) || marca.includes(q) || modelo.includes(q) || categoria.includes(q) || codigoBarras.includes(q) || ubicacion.includes(q);
    });
  }, [items, busquedaReubicar]);

  // Métricas avanzadas para el Centro de Telemetría WMS (Analítica)
  const totalSlotsCapacidad = racksResumen.reduce((acc, r) => acc + r.totalSlots, 0);
  const slotsOcupados = items.length;
  const porcentajeSlotsOcupados = totalSlotsCapacidad > 0 ? Math.min(Math.round((slotsOcupados / totalSlotsCapacidad) * 100), 100) : 0;
  const slotsDisponibles = Math.max(totalSlotsCapacidad - slotsOcupados, 0);

  const porcentajeCargaTon = capacidadTotalTon > 0 ? Math.min(Math.round((totalPesoTon / capacidadTotalTon) * 100), 100) : 0;
  const porcentajeVolumenM3 = capacidadTotalM3 > 0 ? Math.min(Math.round((totalVolumenM3 / capacidadTotalM3) * 100), 100) : 0;

  // Desglose de salud del inventario por estado normativo
  const stockDisponible = items.filter(i => (i.estado || 'Disponible') === 'Disponible').reduce((acc, i) => acc + (Number(i.cantidad) || 0), 0);
  const stockCuarentena = items.filter(i => i.estado === 'Cuarentena').reduce((acc, i) => acc + (Number(i.cantidad) || 0), 0);
  const stockDanado = items.filter(i => i.estado === 'Dañado').reduce((acc, i) => acc + (Number(i.cantidad) || 0), 0);
  const stockReservado = items.filter(i => i.estado === 'Reservado').reduce((acc, i) => acc + (Number(i.cantidad) || 0), 0);

  // Top SKUs de Mayor Concentración / Carga
  const topSkusConcentracion = useMemo(() => {
    return [...items]
      .sort((a, b) => ((Number(b.cantidad) || 0) * (Number(b.peso) || 1)) - ((Number(a.cantidad) || 0) * (Number(a.peso) || 1)))
      .slice(0, 5);
  }, [items]);

  // Diversidad logística
  const totalProveedoresUnicos = new Set(items.map(i => i.proveedor).filter(Boolean)).size;
  const totalZonasActivas = new Set(items.map(i => i.zonaOperativa).filter(Boolean)).size;

  // Métricas y filtros para Kardex / Auditoría
  const kardexTotalMovimientos = logs.length;
  const kardexTotalIngresos = logs.filter(l => l.accion === 'INGRESO').length;
  const kardexTotalSalidas = logs.filter(l => l.accion === 'SALIDA').length;
  const kardexTotalReubicaciones = logs.filter(l => l.accion === 'REUBICACION' || l.accion === 'AJUSTE').length;

  const logsFiltrados = useMemo(() => {
    return logs.filter(log => {
      // Filtro por pastillas de acción
      if (kardexFiltroAccion !== 'TODOS') {
        if (kardexFiltroAccion === 'REUBICACION') {
          if (log.accion !== 'REUBICACION' && log.accion !== 'AJUSTE') return false;
        } else if (log.accion !== kardexFiltroAccion) {
          return false;
        }
      }

      // Filtro por estudiante seleccionado
      if (filtroEstudianteKardex !== 'TODOS') {
        const nom = (log.nombre_usuario || '').toLowerCase();
        if (!nom.includes(filtroEstudianteKardex.toLowerCase())) return false;
      }

      // Filtro por buscador de texto
      if (!kardexBusqueda.trim()) return true;
      const q = kardexBusqueda.toLowerCase().trim();
      const sku = (log.sku_producto || '').toLowerCase();
      const usuario = (log.nombre_usuario || '').toLowerCase();
      const accion = (log.accion || '').toLowerCase();
      const origen = `${log.origen_zona || ''} ${log.origen_pasillo || ''} ${log.origen_rack || ''} ${log.origen_nivel || ''} ${log.origen_posicion || ''}`.toLowerCase();
      const destino = `${log.destino_zona || ''} ${log.destino_pasillo || ''} ${log.destino_rack || ''} ${log.destino_nivel || ''} ${log.destino_posicion || ''}`.toLowerCase();

      return sku.includes(q) || usuario.includes(q) || accion.includes(q) || origen.includes(q) || destino.includes(q);
    });
  }, [logs, kardexFiltroAccion, kardexBusqueda, filtroEstudianteKardex]);

  // Paginación para Kardex / Auditoría
  const totalPaginasKardex = Math.max(1, Math.ceil(logsFiltrados.length / kardexPorPagina));
  const paginaKardexValida = Math.min(Math.max(1, kardexPaginaActual), totalPaginasKardex);
  const indiceInicioKardex = (paginaKardexValida - 1) * kardexPorPagina;
  const indiceFinKardex = Math.min(indiceInicioKardex + kardexPorPagina, logsFiltrados.length);

  const logsPaginados = useMemo(() => {
    return logsFiltrados.slice(indiceInicioKardex, indiceFinKardex);
  }, [logsFiltrados, indiceInicioKardex, indiceFinKardex]);

  const paginasVisiblesKardex = useMemo(() => {
    const paginas: (number | string)[] = [];
    if (totalPaginasKardex <= 7) {
      for (let i = 1; i <= totalPaginasKardex; i++) paginas.push(i);
    } else {
      if (paginaKardexValida <= 3) {
        paginas.push(1, 2, 3, 4, '...', totalPaginasKardex);
      } else if (paginaKardexValida >= totalPaginasKardex - 2) {
        paginas.push(1, '...', totalPaginasKardex - 3, totalPaginasKardex - 2, totalPaginasKardex - 1, totalPaginasKardex);
      } else {
        paginas.push(1, '...', paginaKardexValida - 1, paginaKardexValida, paginaKardexValida + 1, '...', totalPaginasKardex);
      }
    }
    return paginas;
  }, [totalPaginasKardex, paginaKardexValida]);

  const exportarKardexCSV = () => {
    if (logsFiltrados.length === 0) {
      toast.error('No hay registros en el filtro actual para exportar');
      return;
    }

    const headers = ['ID Log', 'Fecha y Hora', 'Operador / Usuario', 'Accion', 'SKU Producto', 'Cantidad', 'Ubicacion Origen', 'Ubicacion Destino'];
    
    const rows = logsFiltrados.map(log => {
      const fecha = new Date(log.fecha_movimiento).toLocaleString();
      const origen = log.origen_zona ? `${log.origen_zona}-${log.origen_pasillo}-${log.origen_rack}-${log.origen_nivel}-${log.origen_posicion}` : 'Exterior';
      const destino = log.destino_zona ? `${log.destino_zona}-${log.destino_pasillo}-${log.destino_rack}-${log.destino_nivel}-${log.destino_posicion}` : 'Exterior';
      
      return [
        log.id_log,
        `"${fecha}"`,
        `"${(log.nombre_usuario || 'Desconocido').replace(/"/g, '""')}"`,
        `"${log.accion}"`,
        `"${log.sku_producto}"`,
        log.cantidad,
        `"${origen}"`,
        `"${destino}"`
      ].join(',');
    });

    const csvContent = '\uFEFF' + [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `WMS_Kardex_Auditoria_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Kardex exportado exitosamente a CSV');
  };

  // Componente del Donut Chart Modernizado
  const CustomDonutChart = ({ data }: { data: { name: string, value: number, color: string }[] }) => {
    const total = data.reduce((acc, curr) => acc + curr.value, 0);
    let accumulatedPercent = 0;
    const r = 15.91549430918954;
    const circ = 100;

    return (
      <div className="relative w-56 h-56 flex items-center justify-center">
        <svg viewBox="0 0 42 42" className="w-full h-full transform -rotate-90 filter drop-shadow-sm">
          <circle cx="21" cy="21" r={r} fill="transparent" stroke="currentColor" strokeWidth="6" className="text-slate-100 dark:text-white/5" />
          {data.map((slice, i) => {
            const percent = total > 0 ? (slice.value / total) * 100 : 0;
            const strokeDasharray = `${percent} ${circ - percent}`;
            const strokeDashoffset = -accumulatedPercent;
            accumulatedPercent += percent;

            return (
              <circle
                key={i}
                cx="21"
                cy="21"
                r={r}
                fill="transparent"
                stroke={slice.color}
                strokeWidth="6"
                strokeDasharray={strokeDasharray}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="round"
                className="transition-all duration-700 ease-out hover:opacity-80 hover:stroke-[7px] cursor-pointer"
              />
            );
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">{total.toLocaleString()}</span>
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-500">Unidades</span>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col flex-1 h-full space-y-6 pb-10 min-h-screen bg-slate-50 dark:bg-transparent -m-4 lg:-m-8 p-4 lg:p-8">
      {/* Header Interactivo Glassmórfico Premium */}
      <div className="border-b border-slate-200/50 dark:border-white/[0.05] pb-6 mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-slate-900 to-slate-500 bg-clip-text text-transparent dark:from-white dark:to-zinc-500 tracking-tight flex items-center gap-2.5 sm:gap-3">
              <div className="p-2 sm:p-2.5 rounded-2xl bg-blue-100/50 dark:bg-white/[0.03] border border-transparent dark:border-white/[0.05] shadow-inner shrink-0">
                <CubeIcon className="h-6 w-6 sm:h-7 sm:w-7 text-blue-600 dark:text-zinc-300" />
              </div>
              <span className="truncate">Almacén de Práctica</span>
            </h1>
            <p className="text-xs sm:text-sm font-medium text-slate-500 dark:text-zinc-500 mt-1 sm:mt-2">
              {configuracionAlmacen.nombre} · Infraestructura y control de inventario institucional.
            </p>
          </div>
          <div className="flex items-center gap-2.5 w-full sm:w-auto shrink-0">
            <button 
              onClick={() => setMostrarScannerQR(true)}
              className="flex-1 sm:flex-none justify-center group relative flex items-center gap-2 overflow-hidden rounded-xl border border-slate-200/50 bg-white/50 px-4 sm:px-5 py-2.5 text-xs sm:text-sm font-bold text-slate-700 shadow-sm backdrop-blur-sm transition-all hover:border-slate-300 hover:bg-white dark:border-white/10 dark:bg-white/[0.02] dark:text-zinc-300 dark:hover:bg-white/[0.05]"
            >
              <CameraIcon className="h-4 w-4 sm:h-5 sm:w-5 transition-transform group-hover:scale-110 text-slate-500 dark:text-zinc-400" />
              <span>Escanear</span>
            </button>
            <button 
              onClick={() => abrirModalNuevoProducto()}
              className="flex-1 sm:flex-none justify-center group relative flex items-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-white dark:to-gray-200 px-4 sm:px-5 py-2.5 text-xs sm:text-sm font-bold text-white dark:text-black shadow-lg shadow-blue-500/25 dark:shadow-[0_0_20px_rgba(255,255,255,0.15)] transition-all hover:scale-[1.02] hover:shadow-blue-500/40 dark:hover:shadow-[0_0_25px_rgba(255,255,255,0.25)]"
            >
              <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5 transition-transform group-hover:rotate-90 shrink-0" />
              <span className="whitespace-nowrap">Ingresar Producto</span>
            </button>
            <button
              onClick={() => setMostrarModalMesaControl(true)}
              className="flex-1 sm:flex-none justify-center group relative flex items-center gap-2 overflow-hidden rounded-xl border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 px-4 sm:px-5 py-2.5 text-xs sm:text-sm font-bold text-amber-700 dark:text-amber-300 shadow-sm transition-all hover:scale-[1.02] cursor-pointer"
              title="Mesa de Control y Supervisión Docente de Operaciones WMS"
            >
              <ShieldCheckIcon className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 dark:text-amber-400 shrink-0" />
              <span className="whitespace-nowrap">Mesa de Control</span>
              {solicitudesPendientes.length > 0 && (
                <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-black bg-amber-500 text-black">
                  {solicitudesPendientes.length}
                </span>
              )}
            </button>
            <BotonReglasLigie 
              onClick={() => setMostrarModalReglasLigie(true)}
              variante="amber"
              texto="Reglas LIGIE (Art. 2)"
            />
                        <button
              type="button"
              onClick={exportarAlmacenCompleto}
              disabled={exportandoAlmacen}
              className="flex-1 sm:flex-none justify-center group relative flex items-center gap-1.5 sm:gap-2 overflow-hidden rounded-xl border border-slate-200/80 dark:border-white/10 bg-white/70 dark:bg-white/[0.03] hover:bg-slate-100 dark:hover:bg-white/[0.08] px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-bold text-slate-700 dark:text-zinc-200 shadow-sm backdrop-blur-sm transition-all hover:scale-[1.02] cursor-pointer"
              title="Exportar Respaldo del Gemelo Digital 3D y su Inventario (JSON)"
            >
              <ArrowDownTrayIcon className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-500 dark:text-indigo-400 group-hover:-translate-y-0.5 transition-transform" />
              <span className="whitespace-nowrap">Exportar 3D</span>
            </button>
            <button
              type="button"
              onClick={() => setMostrarModalImportarAlmacen(true)}
              className="flex-1 sm:flex-none justify-center group relative flex items-center gap-1.5 sm:gap-2 overflow-hidden rounded-xl border border-blue-500/30 bg-blue-500/10 hover:bg-blue-500/20 px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-bold text-blue-700 dark:text-blue-300 shadow-sm backdrop-blur-sm transition-all hover:scale-[1.02] cursor-pointer"
              title="Importar Gemelo Digital 3D, Racks e Inventario desde Archivo JSON"
            >
              <ArrowUpTrayIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 dark:text-blue-400 group-hover:-translate-y-0.5 transition-transform" />
              <span className="whitespace-nowrap">Importar 3D</span>
            </button>
<button 
              onClick={() => abrirModalSalida()}
              className="flex-1 sm:flex-none justify-center group relative flex items-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-rose-600 to-red-600 dark:from-rose-500 dark:to-red-600 px-4 sm:px-5 py-2.5 text-xs sm:text-sm font-bold text-white shadow-lg shadow-rose-500/25 dark:shadow-[0_0_20px_rgba(244,63,94,0.25)] transition-all hover:scale-[1.02] hover:shadow-rose-500/40"
              title="Registrar salida o retiro de mercancía del inventario"
            >
              <ArrowUpTrayIcon className="h-4 w-4 sm:h-5 sm:w-5 transition-transform group-hover:-translate-y-0.5 shrink-0" />
              <span className="whitespace-nowrap">Retiro de Mercancía</span>
            </button>
          </div>
        </div>
      </div>

      {/* Navegación por pestañas (Pills) Segmentadas */}
      <div className="flex overflow-x-auto gap-1 bg-slate-100/80 dark:bg-white/[0.03] p-1.5 rounded-2xl sm:rounded-full w-full sm:w-fit border border-slate-200/50 dark:border-white/[0.05] shadow-sm mb-6 no-scrollbar touch-pan-x">
        <button
          onClick={() => setActiveTab('lista')}
          className={`flex items-center gap-1.5 px-3.5 sm:px-5 py-2 text-xs sm:text-sm font-bold rounded-xl sm:rounded-full transition-all whitespace-nowrap shrink-0 ${
            activeTab === 'lista' 
              ? 'bg-white dark:bg-white/10 text-blue-600 dark:text-white shadow-md border border-slate-200/50 dark:border-transparent' 
              : 'text-slate-600 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-zinc-300 hover:bg-white/50 dark:hover:bg-white/5'
          }`}
        >
          <ListBulletIcon className="h-4 w-4 sm:h-5 sm:w-5" />
          <span>Listado</span>
        </button>
        <button
          onClick={() => setActiveTab('mapa')}
          className={`flex items-center gap-1.5 px-3.5 sm:px-5 py-2 text-xs sm:text-sm font-bold rounded-xl sm:rounded-full transition-all whitespace-nowrap shrink-0 ${
            activeTab === 'mapa' 
              ? 'bg-white dark:bg-white/10 text-blue-600 dark:text-white shadow-md border border-slate-200/50 dark:border-transparent' 
              : 'text-slate-600 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-zinc-300 hover:bg-white/50 dark:hover:bg-white/5'
          }`}
        >
          <MapIcon className="h-4 w-4 sm:h-5 sm:w-5" />
          <span>Mapa de Ubicaciones</span>
        </button>
        <button
          onClick={() => setActiveTab('infraestructura')}
          className={`flex items-center gap-1.5 px-3.5 sm:px-5 py-2 text-xs sm:text-sm font-bold rounded-xl sm:rounded-full transition-all whitespace-nowrap shrink-0 ${
            activeTab === 'infraestructura'
              ? 'bg-white dark:bg-white/10 text-blue-600 dark:text-white shadow-md border border-slate-200/50 dark:border-transparent'
              : 'text-slate-600 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-zinc-300 hover:bg-white/50 dark:hover:bg-white/5'
          }`}
        >
          <Cog6ToothIcon className="h-4 w-4 sm:h-5 sm:w-5" />
          <span>Infraestructura</span>
        </button>
        <button
          onClick={() => setActiveTab('analitica')}
          className={`flex items-center gap-1.5 px-3.5 sm:px-5 py-2 text-xs sm:text-sm font-bold rounded-xl sm:rounded-full transition-all whitespace-nowrap shrink-0 ${
            activeTab === 'analitica' 
              ? 'bg-white dark:bg-white/10 text-blue-600 dark:text-white shadow-md border border-slate-200/50 dark:border-transparent' 
              : 'text-slate-600 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-zinc-300 hover:bg-white/50 dark:hover:bg-white/5'
          }`}
        >
          <ChartPieIcon className="h-4 w-4 sm:h-5 sm:w-5" />
          <span>Analítica</span>
        </button>
        <button
          onClick={() => setActiveTab('auditoria')}
          className={`flex items-center gap-1.5 px-3.5 sm:px-5 py-2 text-xs sm:text-sm font-bold rounded-xl sm:rounded-full transition-all whitespace-nowrap shrink-0 ${
            activeTab === 'auditoria' 
              ? 'bg-white dark:bg-white/10 text-blue-600 dark:text-white shadow-md border border-slate-200/50 dark:border-transparent' 
              : 'text-slate-600 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-zinc-300 hover:bg-white/50 dark:hover:bg-white/5'
          }`}
        >
          <ClipboardDocumentListIcon className="h-4 w-4 sm:h-5 sm:w-5" />
          <span>Kardex / Auditoría</span>
          {logs.length > 0 && (
            <span className="ml-0.5 px-1.5 py-0.5 text-[10px] font-black rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-300 border border-blue-500/20">
              {logs.length}
            </span>
          )}
        </button>
      </div>

      {activeTab === 'lista' && (
        <div className="space-y-6 animate-fadeIn">
          {/* Panel de Estructura de Racks y Capacidad de Pallets */}
          {racksResumen.length > 0 && (
            <div className="bg-white/80 dark:bg-[#0c0c0c] backdrop-blur-xl rounded-[24px] p-4 sm:p-6 border border-slate-200/80 dark:border-white/[0.08] shadow-xl">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-5 border-b border-slate-200/60 dark:border-white/[0.06]">
                <div className="flex items-start sm:items-center gap-3">
                  <div className="p-2 sm:p-2.5 rounded-2xl bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20 shrink-0 mt-0.5 sm:mt-0">
                    <CubeIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm sm:text-base font-extrabold text-slate-900 dark:text-white">
                        Estructura de Racks y Capacidad de Pallets
                      </h3>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                        Sincronizado 3D
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1">
                      Visualiza dimensiones físicas por rack: cuántos pallets caben a lo ancho (viga), altura en niveles y ocupación en tiempo real.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-3 sm:flex sm:items-center gap-2 text-xs w-full md:w-auto">
                  <div className="px-2.5 py-1.5 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200/60 dark:border-white/5 flex flex-col sm:flex-row items-center sm:gap-2 text-center sm:text-left">
                    <span className="text-[10px] sm:text-xs text-slate-500 dark:text-zinc-400">Total:</span>
                    <span className="font-extrabold text-slate-900 dark:text-white">{racksResumen.length} Racks</span>
                  </div>
                  <div className="px-2.5 py-1.5 rounded-xl bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 flex flex-col sm:flex-row items-center sm:gap-2 text-center sm:text-left">
                    <span className="text-[10px] sm:text-xs text-blue-600 dark:text-blue-300">Slots:</span>
                    <span className="font-extrabold text-blue-700 dark:text-blue-200 truncate">
                      {racksResumen.reduce((acc, r) => acc + r.totalSlots, 0)} pallets
                    </span>
                  </div>
                  <div className="px-2.5 py-1.5 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 flex flex-col sm:flex-row items-center sm:gap-2 text-center sm:text-left">
                    <span className="text-[10px] sm:text-xs text-emerald-600 dark:text-emerald-300">Ocupados:</span>
                    <span className="font-extrabold text-emerald-700 dark:text-emerald-200 truncate">
                      {racksResumen.reduce((acc, r) => acc + r.palletsOcupados, 0)} pallets
                    </span>
                  </div>
                </div>
              </div>

              {/* Grid de Racks */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-5">
                {racksResumen.map(rack => {
                  const pctOcupacion = rack.totalSlots > 0 ? Math.min(100, Math.round((rack.palletsOcupados / rack.totalSlots) * 100)) : 0;
                  const pctPeso = rack.maxTon > 0 ? Math.min(100, Math.round((rack.pesoTotalTon / rack.maxTon) * 100)) : 0;
                  const slotsLibres = Math.max(0, rack.totalSlots - rack.palletsOcupados);

                  return (
                    <div
                      key={`${rack.zona}-${rack.pasillo}-${rack.rack}`}
                      className="group relative bg-slate-50/70 hover:bg-slate-50 dark:bg-white/[0.02] dark:hover:bg-white/[0.04] p-4 rounded-2xl border border-slate-200/80 dark:border-white/[0.06] hover:border-blue-500/40 transition-all duration-200 shadow-sm"
                    >
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-black text-sm text-slate-900 dark:text-white tracking-wider">
                              {rack.nombreVisual}
                            </span>
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400 border border-blue-200 dark:border-blue-500/20 uppercase">
                              {obtenerNombreZona(rack.zona)}
                            </span>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => {
                            setRackSeleccionado({
                              nombre: rack.nombreVisual,
                              zona: rack.zona,
                              pasillo: rack.pasillo,
                              rack: rack.rack,
                              items: rack.items,
                              niveles: rack.niveles,
                              ancho: rack.ancho,
                            });
                          }}
                          className="p-1.5 rounded-lg bg-white dark:bg-white/5 hover:bg-blue-50 dark:hover:bg-blue-500/20 text-slate-500 hover:text-blue-600 dark:text-zinc-400 dark:hover:text-blue-300 border border-slate-200 dark:border-white/10 transition-colors shadow-sm"
                          title="Inspeccionar Rack 3D y Slots"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Especificaciones Dimensionales */}
                      <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                        <div className="p-2 rounded-xl bg-white dark:bg-white/[0.03] border border-slate-200/60 dark:border-white/[0.05]">
                          <div className="text-[10px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-wide">Altura</div>
                          <div className="font-extrabold text-slate-800 dark:text-zinc-200 mt-0.5">
                            {rack.niveles} Niveles
                          </div>
                        </div>
                        <div className="p-2 rounded-xl bg-white dark:bg-white/[0.03] border border-slate-200/60 dark:border-white/[0.05]">
                          <div className="text-[10px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-wide">Ancho Viga</div>
                          <div className="font-extrabold text-blue-600 dark:text-blue-400 mt-0.5 flex items-center justify-between">
                            <span>{rack.posicionesPorNivel} Pallets</span>
                            <span className="text-[10px] font-normal text-slate-400 dark:text-zinc-500 font-mono">({rack.ancho}m)</span>
                          </div>
                        </div>
                      </div>

                      {/* Barras de Ocupación */}
                      <div className="space-y-2">
                        <div>
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-0.5 text-[11px] mb-1">
                            <span className="text-slate-500 dark:text-zinc-400">Ocupación Pallets:</span>
                            <span className="font-bold text-slate-800 dark:text-zinc-200 font-mono text-[10px] sm:text-[11px]">
                              {rack.palletsOcupados} / {rack.totalSlots} ({pctOcupacion}%) · {slotsLibres} libres
                            </span>
                          </div>
                          <div className="h-2 w-full bg-slate-200 dark:bg-white/10 rounded-full overflow-hidden">
                            <div
                              className={`h-full transition-all duration-500 rounded-full ${
                                pctOcupacion >= 90 ? 'bg-red-500' : pctOcupacion >= 70 ? 'bg-amber-500' : 'bg-emerald-500'
                              }`}
                              style={{ width: `${pctOcupacion}%` }}
                            />
                          </div>
                        </div>

                        <div>
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-0.5 text-[11px] mb-1">
                            <span className="text-slate-500 dark:text-zinc-400">Carga Estructural:</span>
                            <span className="font-bold text-slate-800 dark:text-zinc-200 font-mono text-[10px] sm:text-[11px]">
                              {rack.pesoTotalTon.toFixed(2)} / {rack.maxTon} ton ({pctPeso}%)
                            </span>
                          </div>
                          <div className="h-1.5 w-full bg-slate-200 dark:bg-white/10 rounded-full overflow-hidden">
                            <div
                              className={`h-full transition-all duration-500 rounded-full ${
                                pctPeso >= 90 ? 'bg-red-500' : 'bg-blue-500'
                              }`}
                              style={{ width: `${pctPeso}%` }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Botón rápido inspeccionar */}
                      <div className="mt-3 pt-2.5 border-t border-slate-200/60 dark:border-white/[0.05] flex items-center justify-between text-xs">
                        <button
                          type="button"
                          onClick={() => {
                            setRackSeleccionado({
                              nombre: rack.nombreVisual,
                              zona: rack.zona,
                              pasillo: rack.pasillo,
                              rack: rack.rack,
                              items: rack.items,
                              niveles: rack.niveles,
                              ancho: rack.ancho,
                            });
                          }}
                          className="font-bold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-1"
                        >
                          <span>Inspeccionar Huecos</span>
                          <span>→</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setRackSeleccionado({
                              nombre: rack.nombreVisual,
                              zona: rack.zona,
                              pasillo: rack.pasillo,
                              rack: rack.rack,
                              items: rack.items,
                              niveles: rack.niveles,
                              ancho: rack.ancho,
                            });
                            setActiveTab('mapa');
                          }}
                          className="text-[11px] text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white"
                        >
                          Ver en 3D 🌐
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tabla de Inventario */}
          <div className="bg-white dark:bg-[#080808] rounded-[24px] shadow-2xl border border-gray-200 dark:border-white/[0.08] overflow-hidden animate-fadeIn">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 p-5 bg-gray-50/50 dark:bg-white/[0.02] border-b border-gray-200 dark:border-white/[0.05]">
          <div className="flex items-center gap-3 bg-white dark:bg-[#0f0f0f] px-4 py-3 rounded-2xl border border-gray-200 dark:border-white/[0.08] w-full focus-within:ring-2 focus-within:ring-blue-500/30 transition-all shadow-sm">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 dark:text-zinc-500" />
            <input 
              id="almacen-practica-busqueda"
              name="busqueda"
              type="text" 
              placeholder="Buscar por SKU o Nombre..." 
              className="bg-transparent border-none outline-none w-full text-sm text-gray-900 dark:text-zinc-200 placeholder-gray-400 dark:placeholder-zinc-600"
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-3 bg-white dark:bg-[#0f0f0f] px-4 py-3 rounded-2xl border border-gray-200 dark:border-white/[0.08] w-full sm:max-w-md focus-within:ring-2 focus-within:ring-blue-500/30 transition-all shadow-sm">
            <CameraIcon className="h-5 w-5 text-gray-400 dark:text-zinc-500" />
            <input
              id="almacen-practica-scan-codigo"
              name="scanCodigo"
              type="text"
              placeholder="Escanear código (Enter)..."
              className="bg-transparent border-none outline-none w-full text-sm text-gray-900 dark:text-zinc-200 placeholder-gray-400 dark:placeholder-zinc-600"
              value={scanCodigo}
              onChange={e => setScanCodigo(e.target.value)}
              onKeyDown={e => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                const code = scanCodigo.trim();
                if (!code) return;
                const match = items.find(i =>
                  i.sku.toLowerCase() === code.toLowerCase() ||
                  String(i.codigoBarras || '').trim() === code
                );
                if (match) {
                  setMostrarModalQR(match);
                } else {
                  toast.error('No se encontró producto con ese código');
                }
                setScanCodigo('');
              }}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-[10px] text-gray-500 dark:text-zinc-500 uppercase tracking-widest bg-gray-50 dark:bg-transparent border-b border-gray-200 dark:border-white/[0.08]">
              <tr>
                <th className="px-6 py-4 font-bold">SKU / Identificador</th>
                <th className="px-6 py-4 font-bold">Producto</th>
                <th className="px-6 py-4 font-bold">Estado</th>
                <th className="px-6 py-4 font-bold">Ubicación y Dimensiones</th>
                <th className="px-6 py-4 font-bold">Logística</th>
                <th className="px-6 py-4 font-bold">Stock</th>
                <th className="px-6 py-4 font-bold text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
              {filtrados.length > 0 ? filtrados.map(item => (
                <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors group">
                  <td className="px-6 py-5 font-medium text-gray-900 dark:text-white">
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center gap-2.5">
                        <button 
                          onClick={() => setMostrarModalQR(item)}
                          className="p-1.5 bg-blue-50 text-blue-600 dark:bg-white/[0.05] dark:text-zinc-300 rounded-lg hover:bg-blue-100 dark:hover:bg-white/[0.1] transition-colors shadow-sm"
                          title="Ver Código QR"
                        >
                          <QrCodeIcon className="h-4 w-4" />
                        </button>
                        <span className="font-bold tracking-tight">{item.sku}</span>
                      </div>
                      {item.codigoBarras && (
                        <span className="text-[10px] text-gray-400 dark:text-zinc-500 uppercase tracking-wider pl-9">{item.tipoCodigo}: {item.codigoBarras}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-5">
                    <div className="text-sm font-bold text-gray-900 dark:text-zinc-200 tracking-tight">{item.nombre}</div>
                    <div className="text-xs text-gray-500 dark:text-zinc-500 mt-0.5">{item.marca} {item.modelo !== 'S/M' && `- ${item.modelo}`}</div>
                    <div className="text-xs text-gray-400 dark:text-zinc-600 mt-1 truncate w-36" title={item.descripcion}>{item.descripcion}</div>
                  </td>
                  <td className="px-6 py-5">
                    <span className={`px-2.5 py-1.5 text-[11px] font-bold rounded-md flex w-fit items-center gap-1.5 border ${
                      item.estado === 'Disponible' ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-500/10 dark:text-green-400 dark:border-green-500/20' :
                      item.estado === 'Reservado' ? 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-500/10 dark:text-yellow-400 dark:border-yellow-500/20' :
                      item.estado === 'Cuarentena' ? 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-500/10 dark:text-orange-400 dark:border-orange-500/20' :
                      item.estado === 'Dañado' ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20' :
                      'bg-gray-50 text-gray-700 border-gray-200 dark:bg-white/5 dark:text-zinc-400 dark:border-white/10'
                    }`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${item.estado === 'Disponible' ? 'bg-green-500' : item.estado === 'Reservado' ? 'bg-yellow-500' : item.estado === 'Cuarentena' ? 'bg-orange-500' : item.estado === 'Dañado' ? 'bg-red-500' : 'bg-gray-500'}`}></div>
                      {item.estado.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-5 text-gray-500 dark:text-zinc-400">
                    {(() => {
                      const cleanPasillo = normalizarNumero2(item.pasillo);
                      const cleanRack = normalizarNumero2(item.rack);
                      const zonaCod = obtenerCodigoZona(item.zonaOperativa);
                      const rInfo = racksResumen.find(r => r.zona === zonaCod && r.pasillo === cleanPasillo && r.rack === cleanRack);
                      const numNiv = rInfo?.niveles || 3;
                      const numPos = rInfo?.posicionesPorNivel || 8;
                      const anchoM = rInfo?.ancho || (numPos <= 2 ? 2.8 : numPos <= 4 ? 3.8 : 4.8);
                      const nivelNum = Number(item.nivel || '1');
                      const posNum = Number(item.posicion || '1');
                      const desbordaNivel = nivelNum > numNiv;
                      const desbordaPos = posNum > numPos;

                      return (
                        <div className="flex flex-col gap-1.5">
                          <button
                            type="button"
                            onClick={() => {
                              setRackSeleccionado({
                                nombre: rInfo?.nombreVisual || `${zonaCod} · P${cleanPasillo} · R${cleanRack}`,
                                zona: zonaCod,
                                pasillo: cleanPasillo,
                                rack: cleanRack,
                                items: rInfo?.items || items.filter(i => obtenerCodigoZona(i.zonaOperativa) === zonaCod && normalizarNumero2(i.pasillo) === cleanPasillo && normalizarNumero2(i.rack) === cleanRack),
                                niveles: numNiv,
                                ancho: anchoM,
                              });
                            }}
                            className="text-left group/badge"
                            title="Haz clic para inspeccionar este rack en 3D"
                          >
                            <span className="font-bold text-gray-800 dark:text-zinc-200 text-[11px] bg-gray-100 hover:bg-blue-50 dark:bg-white/[0.05] dark:hover:bg-blue-500/10 border border-gray-200/80 dark:border-white/[0.08] group-hover/badge:border-blue-400 py-1 px-2.5 rounded-md inline-flex items-center gap-1.5 font-mono shadow-inner tracking-widest transition-all">
                              <span>
                                {formatearCodigoUbicacion(
                                  {
                                    zonaOperativa: item.zonaOperativa,
                                    pasillo: item.pasillo,
                                    rack: item.rack,
                                    nivel: item.nivel,
                                    posicion: item.posicion,
                                  },
                                  { formato: 'visual' }
                                )}
                              </span>
                              <span className="text-[9px] text-blue-500 font-sans opacity-0 group-hover/badge:opacity-100 transition-opacity">↗</span>
                            </span>
                          </button>

                          <div className="flex flex-wrap items-center gap-1 text-[10px]">
                            <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-white/5 font-medium text-slate-700 dark:text-zinc-300">
                              {numNiv} Niveles
                            </span>
                            <span className="px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-500/10 font-medium text-blue-700 dark:text-blue-300">
                              ↔ {numPos} pallets de ancho ({anchoM}m)
                            </span>
                          </div>

                          <div className="text-[10px] text-gray-400 dark:text-zinc-500">
                            Hueco: Nivel <span className="font-bold text-zinc-700 dark:text-zinc-300">{item.nivel}</span> · Posición <span className="font-bold text-zinc-700 dark:text-zinc-300">{item.posicion}</span> de {numPos}
                          </div>

                          {(desbordaNivel || desbordaPos) && (
                            <span className="text-[9px] font-bold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 px-1.5 py-0.5 rounded border border-amber-200 dark:border-amber-500/20 w-fit">
                              ⚠️ Excede dimensiones físicas del rack
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-5 text-gray-500 dark:text-zinc-400 text-xs">
                    <div className="font-mono text-[11px]">P: {item.peso} kg / V: {item.volumen} m³</div>
                    <div className="truncate w-32 mt-1" title={item.proveedor}>Prov: <span className="font-semibold text-gray-700 dark:text-zinc-300">{item.proveedor}</span></div>
                    {item.lote !== 'N/A' && <div className="mt-0.5">Lote: <span className="font-mono text-[10px]">{item.lote}</span></div>}
                    <div className="text-[10px] mt-2 p-1.5 bg-gray-50 dark:bg-white/[0.03] rounded-md border border-gray-100 dark:border-white/[0.05]">
                      {item.creadoPor && <div className="text-gray-500 dark:text-zinc-500" title={`Registrado: ${getDaysAgo(item.fechaIngreso || '')}`}>➕ {item.creadoPor}</div>}
                      {item.actualizadoPor && item.fechaActualizacion !== item.fechaIngreso && (
                        <div className="text-blue-500 dark:text-blue-400 mt-1" title={`Actualizado: ${getDaysAgo(item.fechaActualizacion || '')}`}>✏️ {item.actualizadoPor}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-5">
                    <div className="flex flex-col items-start">
                      <span className="px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20 rounded-lg font-black text-sm shadow-sm">
                        {item.cantidad}
                      </span>
                      <span className="text-[10px] text-gray-400 dark:text-zinc-500 uppercase mt-1.5 tracking-wider font-bold">{item.unidadMedida}s</span>
                    </div>
                  </td>
                  <td className="px-6 py-5 text-right">
                    <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button 
                        onClick={() => handleEditar(item)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:text-zinc-500 dark:hover:text-blue-400 dark:hover:bg-blue-500/10 rounded-lg transition-colors"
                        title="Editar Producto"
                      >
                        <PencilSquareIcon className="h-5 w-5" />
                      </button>
                      <button 
                        onClick={() => abrirModalSalida(item)}
                        className="p-2 text-gray-400 hover:text-rose-600 hover:bg-rose-50 dark:text-zinc-500 dark:hover:text-rose-400 dark:hover:bg-rose-500/10 rounded-lg transition-colors"
                        title="Retirar (Salida de Mercancía)"
                      >
                        <ArrowUpTrayIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    No se encontraron productos en el almacén.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
    )}

      {activeTab === 'mapa' && (
        <div className="fixed inset-0 z-[100] bg-[#080808] flex flex-col h-screen w-screen overflow-hidden animate-fadeIn">
          {/* Top Bar Glassmórfica */}
          <div className="absolute top-0 left-0 right-0 z-50 flex flex-col md:flex-row justify-between items-start md:items-center p-2 md:p-4 bg-black/40 backdrop-blur-xl border-b border-white/10 shadow-[0_10px_30px_rgba(0,0,0,0.5)] gap-2 md:gap-4">
            
            <div className="flex w-full md:w-auto items-center gap-2 md:gap-4 pr-20 md:pr-0">
              <button
                onClick={() => setActiveTab('lista')}
                className="flex flex-shrink-0 items-center gap-1.5 px-3 md:px-4 py-1.5 md:py-2 bg-white/5 hover:bg-white/10 text-white text-[10px] md:text-xs font-bold rounded-lg md:rounded-xl border border-white/10 transition-all backdrop-blur-md"
                title="Volver al Dashboard Operativo"
              >
                <XMarkIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Salir del Workspace</span>
                <span className="sm:hidden">Salir</span>
              </button>
              <div className="hidden md:block h-6 w-px bg-white/20 flex-shrink-0"></div>
              <h2 className="text-[11px] sm:text-sm md:text-xl font-black text-white tracking-widest uppercase flex items-center gap-1 md:gap-2 leading-none">
                 <CubeIcon className="h-4 w-4 md:h-6 md:w-6 text-blue-500 animate-pulse flex-shrink-0" />
                 <span>LogisTech</span> <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500 hidden xs:inline">Digital Twin</span>
              </h2>
              
              <button
                onClick={toggleModoArquitecto}
                className={`ml-4 flex items-center gap-2 px-3 py-1 text-[10px] md:text-xs font-bold rounded-lg transition-all ${
                  modoArquitecto 
                    ? 'bg-yellow-400 border border-yellow-500 text-yellow-900 shadow-[0_0_15px_rgba(250,204,21,0.5)]' 
                    : 'bg-white/10 border border-white/20 text-white hover:bg-white/20'
                }`}
              >
                <MapPinIcon className="h-4 w-4" />
                {modoArquitecto ? 'MODO ARQUITECTO ACTIVO' : 'MODO ARQUITECTO'}
              </button>
              
              {modoArquitecto && (
                <button
                  onClick={() => {
                     if(window.confirm('¿Deseas resetear las posiciones a la cuadrícula automática?')) {
                        setConfiguracionAlmacen(prev => ({ ...prev, layout_data: undefined }));
                     }
                  }}
                  className="ml-2 flex items-center gap-1.5 px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-[10px] md:text-xs font-bold rounded-lg transition-all border border-red-700 shadow-md"
                  title="Restaurar Layout Original"
                >
                  <ArrowPathIcon className="h-3 w-3" />
                  Resetear Layout
                </button>
              )}
            </div>
            
            <div className="flex items-center flex-wrap gap-2 md:gap-4 w-full md:w-auto">
              {/* Controles de Navegación 3D */}
              {modo3D && (
                <div className="flex flex-1 md:flex-none items-center justify-between md:justify-start gap-1 bg-white/5 backdrop-blur-md p-1 md:p-1.5 rounded-lg border border-white/10 shadow-inner">
                  <div className="flex items-center gap-1">
                    <button 
                      onClick={() => setHerramienta3D('pan')} 
                      className={`flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1 md:py-1.5 text-[10px] md:text-xs font-bold rounded-md transition-all ${herramienta3D === 'pan' ? 'bg-blue-600/30 text-blue-400 border border-blue-500/30 shadow-sm' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
                      title="Mover (Pan) - Usa el click izquierdo para arrastrar"
                    >
                      <HandRaisedIcon className="h-3 w-3 md:h-4 md:w-4" /> <span className="hidden xs:inline">Mover</span>
                    </button>
                    <button 
                      onClick={() => setHerramienta3D('orbit')} 
                      className={`flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1 md:py-1.5 text-[10px] md:text-xs font-bold rounded-md transition-all ${herramienta3D === 'orbit' ? 'bg-blue-600/30 text-blue-400 border border-blue-500/30 shadow-sm' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
                      title="Rotar 360 - Usa el click izquierdo para orbitar"
                    >
                      <ArrowPathIcon className="h-3 w-3 md:h-4 md:w-4" /> <span className="hidden xs:inline">Rotar</span>
                    </button>
                  </div>
                  <div className="w-px h-4 md:h-5 bg-white/20 mx-1"></div>
                  <div className="flex items-center gap-1 md:gap-2 px-1 md:px-2" title="Sensibilidad del ratón">
                    <span className="text-[9px] md:text-[10px] font-bold text-gray-400 uppercase">Sens</span>
                    <input 
                      type="range" 
                      min="0.2" 
                      max="3" 
                      step="0.1" 
                      value={sensibilidad} 
                      onChange={(e) => setSensibilidad(Number(e.target.value))}
                      className="w-12 md:w-16 h-1 bg-white/20 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                </div>
              )}

              {/* Controles de Zoom y Pan */}
              <div className="flex flex-1 md:flex-none justify-center items-center gap-1 md:gap-2 bg-white/5 backdrop-blur-md p-1 md:p-1.5 rounded-lg border border-white/10 shadow-inner">
                <button onClick={handleZoomOut} className="p-1 md:p-1.5 text-gray-400 hover:text-blue-400 hover:bg-white/10 rounded-md transition-all" title="Alejar">
                  <MagnifyingGlassMinusIcon className="h-4 w-4 md:h-5 md:w-5" />
                </button>
                <span className="text-[10px] md:text-xs font-bold text-gray-300 w-8 md:w-12 text-center">{Math.round(zoom * 100)}%</span>
                <button onClick={handleZoomIn} className="p-1 md:p-1.5 text-gray-400 hover:text-blue-400 hover:bg-white/10 rounded-md transition-all" title="Acercar">
                  <MagnifyingGlassPlusIcon className="h-4 w-4 md:h-5 md:w-5" />
                </button>
                <div className="w-px h-4 md:h-5 bg-white/20 mx-1"></div>
                <button onClick={handleResetView} className="p-1 md:p-1.5 text-gray-400 hover:text-blue-400 hover:bg-white/10 rounded-md transition-all" title="Centrar Mapa">
                  <ArrowsPointingOutIcon className="h-4 w-4 md:h-5 md:w-5" />
                </button>
                <div className="w-px h-4 md:h-5 bg-white/20 mx-1"></div>
                <button
                  type="button"
                  onClick={exportarAlmacenCompleto}
                  disabled={exportandoAlmacen}
                  className="p-1 md:p-1.5 text-gray-400 hover:text-indigo-400 hover:bg-white/10 rounded-md transition-all flex items-center gap-1 text-[10px] md:text-xs font-bold"
                  title="Exportar Gemelo Digital 3D"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 text-indigo-400" />
                  <span className="hidden lg:inline">Exportar 3D</span>
                </button>
                <button
                  type="button"
                  onClick={() => setMostrarModalImportarAlmacen(true)}
                  className="p-1 md:p-1.5 text-gray-400 hover:text-blue-400 hover:bg-white/10 rounded-md transition-all flex items-center gap-1 text-[10px] md:text-xs font-bold"
                  title="Importar Gemelo Digital 3D"
                >
                  <ArrowUpTrayIcon className="h-4 w-4 text-blue-400" />
                  <span className="hidden lg:inline">Importar 3D</span>
                </button>
              </div>

              <div className="hidden xl:flex flex-wrap items-center gap-4 text-[10px] font-bold uppercase tracking-widest text-zinc-400 bg-white/5 backdrop-blur-md p-2.5 rounded-lg border border-white/10">
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-blue-600 rounded-sm shadow-[0_0_8px_rgba(37,99,235,0.8)]"></div> Normal</div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-rose-600 rounded-sm shadow-[0_0_8px_rgba(225,29,72,0.8)]"></div> Lleno</div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-red-600 rounded-sm shadow-[0_0_8px_rgba(220,38,38,0.8)]"></div> Cuarentena/Dañado</div>
              </div>
            </div>

            <button
              onClick={() => { if (!modoArquitecto) setModo3D(!modo3D); }}
              disabled={modoArquitecto}
              className={`absolute top-2 right-2 md:relative md:top-auto md:right-auto flex items-center gap-1.5 md:gap-2 px-2.5 md:px-4 py-1.5 md:py-2.5 text-[10px] md:text-xs font-bold rounded-lg md:rounded-xl shadow-lg border border-white/10 transition-all
                ${modoArquitecto 
                  ? 'bg-gray-800 text-gray-500 cursor-not-allowed opacity-50' 
                  : 'bg-gradient-to-r from-blue-700 to-indigo-800 hover:from-blue-600 hover:to-indigo-700 text-white hover:scale-105'
                }`}
              title={modoArquitecto ? "Desactiva Modo Arquitecto primero" : "Alternar entre Plano 2D y Gemelo Digital 3D"}
            >
              <CubeIcon className="h-3 w-3 md:h-4 md:w-4" />
              <span className="hidden md:inline">{modo3D ? 'Ver Plano 2D (Diseño)' : 'Ver Gemelo Digital 3D (R3F)'}</span>
              <span className="md:hidden">{modo3D ? '2D' : '3D'}</span>
            </button>
          </div>

          {Object.keys(zonasAlmacen).length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-zinc-500 font-medium">No hay productos ubicados en el almacén.</p>
            </div>
          ) : modo3D ? (
            <AlmacenDigitalTwin3D
              zonasAlmacen={zonasAlmacen as any}
              configuracionAlmacen={configuracionAlmacen}
              obtenerCapacidadRack={obtenerCapacidadRack}
              formatearCodigoUbicacion={formatearCodigoUbicacion}
              obtenerNombreZona={obtenerNombreZona}
              onSelectRack={(data) => {
                setRackSeleccionado(data as any);
              }}
              modoArquitectoInicial={modoArquitecto}
              onGuardarConfiguracion={async (nuevoLayoutData) => {
                setConfiguracionAlmacen((prev) => ({
                  ...prev,
                  layout_data: nuevoLayoutData,
                }));
                const nombre = String(configuracionAlmacen.nombre || 'Almacén Central').trim();
                const codigo = sanitizarCodigoAlmacen(configuracionAlmacen.codigo);
                await api.post('/api/institucion/almacen/configuracion', {
                  nombre,
                  codigo,
                  descripcion: configuracionAlmacen.descripcion || '',
                  tema_piso: configuracionAlmacen.tema_piso,
                  tema_pared: configuracionAlmacen.tema_pared,
                  dimension_ancho: configuracionAlmacen.dimension_ancho,
                  dimension_largo: configuracionAlmacen.dimension_largo,
                  layout_data: nuevoLayoutData,
                });
              }}
              onMoverProductosSlot={async (itemsAMover, destino) => {
                const nivStr = normalizarNumero2(String(destino.nivel));
                const posStr = normalizarNumero2(String(destino.posicion));
                const zCod = destino.zona;
                const pas = normalizarNumero2(destino.pasillo);
                const rac = normalizarNumero2(destino.rack);

                for (const it of itemsAMover) {
                  try {
                    await api.put(`/api/institucion/almacen/productos/${it.id}`, {
                      zona_codigo: zCod,
                      pasillo: pas,
                      rack: rac,
                      nivel: nivStr,
                      posicion: posStr,
                    });
                  } catch (e) {
                    console.error('Error al actualizar posición de producto:', e);
                  }
                }

                setItems((prev) =>
                  prev.map((it) => {
                    if (itemsAMover.some((m) => m.id === it.id)) {
                      const ubicacion = `${CODIGO_ALMACEN_COMPACTO}-${zCod}-P${pas}-R${rac}-N${nivStr}-S${posStr}`;
                      return {
                        ...it,
                        zonaOperativa: zCod,
                        pasillo: pas,
                        rack: rac,
                        nivel: nivStr,
                        posicion: posStr,
                        ubicacion,
                      };
                    }
                    return it;
                  })
                );
              }}
              onAjustarStockItem={async (itemId, nuevoStock) => {
                try {
                  await api.put(`/api/institucion/almacen/productos/${itemId}`, {
                    cantidad: nuevoStock,
                  });
                  setItems((prev) =>
                    prev.map((it) => (it.id === itemId ? { ...it, cantidad: nuevoStock } : it))
                  );
                } catch (e) {
                  console.error('Error al ajustar stock:', e);
                }
              }}
              onDespacharSlot={async (itemsADespachar) => {
                for (const it of itemsADespachar) {
                  try {
                    await api.delete(`/api/institucion/almacen/productos/${it.id}`);
                  } catch (e) {
                    console.error('Error al despachar producto:', e);
                  }
                }
                setItems((prev) => prev.filter((it) => !itemsADespachar.some((m) => m.id === it.id)));
                try {
                  const logsRes = await api.get('/api/institucion/almacen/logs');
                  if (logsRes.data) {
                    setLogs(Array.isArray(logsRes.data) ? logsRes.data : []);
                  }
                  toast.success('Pallet retirado del almacén correctamente');
                } catch (err) {
                  console.error('Error al actualizar logs de salida:', err);
                }
              }}
            />
          ) : (
            <div 
              ref={mapContainerRef}
              className={`flex-1 relative overflow-hidden ${isDraggingState ? 'cursor-grabbing' : 'cursor-grab'}`}
              style={{
                backgroundColor: configuracionAlmacen.tema_pared === 'blanco' ? '#f8fafc' : configuracionAlmacen.tema_pared === 'metalico' ? '#94a3b8' : (configuracionAlmacen.tema_piso === 'epoxico_azul' ? '#1e3a8a' : configuracionAlmacen.tema_piso === 'epoxico_verde' ? '#064e3b' : configuracionAlmacen.tema_piso === 'madera' ? '#8B5A2B' : '#080808'),
                backgroundImage: modo3D ? (
                  configuracionAlmacen.tema_piso === 'epoxico_azul' ? 'linear-gradient(rgba(255,255,255,0.1) 2px, transparent 2px), linear-gradient(90deg, rgba(255,255,255,0.1) 2px, transparent 2px), linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)' :
                  configuracionAlmacen.tema_piso === 'epoxico_verde' ? 'linear-gradient(rgba(255,255,255,0.1) 2px, transparent 2px), linear-gradient(90deg, rgba(255,255,255,0.1) 2px, transparent 2px), linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)' :
                  configuracionAlmacen.tema_piso === 'madera' ? 'repeating-linear-gradient(90deg, transparent, transparent 40px, rgba(0,0,0,0.2) 40px, rgba(0,0,0,0.2) 42px), linear-gradient(rgba(0,0,0,0.1) 2px, transparent 2px)' :
                  'linear-gradient(rgba(255,255,255,0.03) 2px, transparent 2px), linear-gradient(90deg, rgba(255,255,255,0.03) 2px, transparent 2px), linear-gradient(rgba(255,255,255,0.01) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.01) 1px, transparent 1px)'
                ) : 'none',
                backgroundSize: modo3D ? '100px 100px, 100px 100px, 20px 20px, 20px 20px' : 'auto'
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onWheel={handleWheel}
              onContextMenu={(e) => {
                 // Prevenir click derecho para permitir Orbitar
                 if (activeTab === 'mapa' && modo3D) e.preventDefault();
              }}
            >
              {/* Contenedor Transformable (Pan & Zoom) */}

              {/* Instrucciones flotantes del Modo 3D */}
              {modo3D && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-md px-4 md:px-6 py-2 md:py-3 rounded-full border border-white/10 shadow-2xl z-40 flex items-center gap-4 md:gap-6 pointer-events-none">
                   <div className="flex items-center gap-1.5 md:gap-2">
                      <div className="w-4 h-4 md:w-5 md:h-5 bg-white/20 rounded flex items-center justify-center text-white text-[8px] md:text-[10px] font-bold shadow-inner">🖱️</div>
                      <span className="text-white text-[10px] md:text-xs font-medium">Orbitar / Mover</span>
                   </div>
                   <div className="flex items-center gap-1.5 md:gap-2">
                      <div className="w-4 h-4 md:w-5 md:h-5 bg-white/20 rounded flex items-center justify-center text-white text-[8px] md:text-[10px] font-bold shadow-inner">⚙️</div>
                      <span className="text-white text-[10px] md:text-xs font-medium">Zoom</span>
                   </div>
                   <div className="flex items-center gap-1.5 md:gap-2">
                      <div className="w-4 h-4 md:w-5 md:h-5 bg-blue-500/50 rounded flex items-center justify-center text-white text-[8px] md:text-[10px] font-bold shadow-inner animate-pulse">👆</div>
                      <span className="text-blue-200 text-[10px] md:text-xs font-bold tracking-wide">Clic en Rack: Inspeccionar</span>
                   </div>
                </div>
              )}

              <div 
                ref={panRef}
                className="absolute inset-0 origin-center"
                style={{ 
                  transform: `translate(${currentPan.current.x}px, ${currentPan.current.y}px) scale(${zoom})`,
                  willChange: 'transform'
                }}
              >
                {/* Floor pattern (Grid lines) */}
                <div 
                  className="absolute inset-[-5000px] pointer-events-none opacity-20 dark:opacity-10" 
                  style={{ 
                    backgroundImage: 'linear-gradient(#64748b 1px, transparent 1px), linear-gradient(90deg, #64748b 1px, transparent 1px)', 
                    backgroundSize: '40px 40px',
                    backgroundPosition: 'center center'
                  }}
                ></div>
                
                <div 
                  className="relative z-10 flex flex-col gap-24 min-w-max p-24 items-center justify-center min-h-full"
                  style={{ perspective: '1500px' }}
                >
                  <div
                    ref={rotacionRef}
                    style={{
                      transform: modo3D ? `rotateX(${currentRotacion.current.x}deg) rotateZ(${currentRotacion.current.z}deg) translate3d(${currentWalk.current.x}px, ${currentWalk.current.y}px, 0)` : `rotateX(0deg) rotateZ(0deg) translate3d(${currentWalk.current.x}px, ${currentWalk.current.y}px, 0)`,
                      transformStyle: 'preserve-3d',
                      transition: isDraggingState ? 'none' : 'transform 0.05s ease-out'
                    }}
                    className={`flex flex-col gap-24 relative ${(modoArquitecto || hasCustomLayout) ? '!flex-none !items-start !justify-start !w-full !h-full' : ''}`}
                    onPointerMove={modoArquitecto ? onPointerMoveCanvas : undefined}
                    onPointerUp={modoArquitecto ? onPointerUpCanvas : undefined}
                  >
                    
                    {/* ENTORNO 3D FÍSICO (PISO Y PAREDES) */}
                    {modo3D && (() => {
                      // 1 metro = 100px para simplificar la escala visual
                      const rawAnchoPx = (configuracionAlmacen.dimension_ancho || 80) * 100;
                      const rawLargoPx = (configuracionAlmacen.dimension_largo || 80) * 100;
                      
                      // LÍMITE DE SEGURIDAD GPU: Los navegadores colapsan si renderizan CSS 3D > 15,000px.
                      // Limitamos el renderizado visual a 12,000px (120 metros visuales) para evitar que se trabe.
                      const maxLimit = 12000; 
                      const anchoPx = Math.min(rawAnchoPx, maxLimit);
                      const largoPx = Math.min(rawLargoPx, maxLimit);
                      const altoPx = 1000; // 10 metros fijos
                      
                      return (
                      <div ref={canvasRef} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none" style={{ width: `${anchoPx}px`, height: `${largoPx}px`, transformStyle: 'preserve-3d' }}>
                         {/* PISO 3D FÍSICO */}
                         <div 
                           className="absolute inset-0 shadow-[inset_0_0_800px_rgba(0,0,0,0.8)]"
                           style={{
                              transform: 'translateZ(-5px)',
                              backgroundColor: configuracionAlmacen.tema_piso === 'epoxico_azul' ? '#1e3a8a' : configuracionAlmacen.tema_piso === 'epoxico_verde' ? '#064e3b' : configuracionAlmacen.tema_piso === 'madera' ? '#8B5A2B' : '#1e293b',
                              backgroundImage: configuracionAlmacen.tema_piso === 'epoxico_azul' || configuracionAlmacen.tema_piso === 'epoxico_verde' ? 'linear-gradient(rgba(255,255,255,0.1) 2px, transparent 2px), linear-gradient(90deg, rgba(255,255,255,0.1) 2px, transparent 2px)' : configuracionAlmacen.tema_piso === 'madera' ? 'repeating-linear-gradient(90deg, transparent, transparent 40px, rgba(0,0,0,0.2) 40px, rgba(0,0,0,0.2) 42px)' : 'linear-gradient(rgba(0,0,0,0.5) 2px, transparent 2px), linear-gradient(90deg, rgba(0,0,0,0.5) 2px, transparent 2px)',
                              backgroundSize: '100px 100px'
                           }}
                         ></div>

                         {/* MUROS PERIMETRALES */}
                         {configuracionAlmacen.tema_pared !== 'industrial' && (
                           <>
                             {/* MURO NORTE (Fondo) */}
                             <div className="absolute top-0 left-0 w-full flex items-end"
                                  style={{
                                    height: `${altoPx}px`,
                                    transformOrigin: 'top',
                                    transform: 'rotateX(90deg) translateZ(-5px)',
                                    backgroundColor: configuracionAlmacen.tema_pared === 'blanco' ? '#f8fafc' : '#94a3b8',
                                    backgroundImage: configuracionAlmacen.tema_pared === 'metalico' ? 'repeating-linear-gradient(90deg, transparent, transparent 100px, rgba(0,0,0,0.1) 100px, rgba(0,0,0,0.1) 105px)' : 'none',
                                    boxShadow: 'inset 0 -50px 100px rgba(0,0,0,0.5)'
                                  }}>
                                  <div className="w-full h-8 shadow-md" style={{ backgroundImage: 'repeating-linear-gradient(45deg, #fbbf24 0, #fbbf24 20px, #000 20px, #000 40px)' }}></div>
                             </div>

                             {/* MURO SUR (Frente) - Transparente pero oscurece un poco */}
                             <div className="absolute bottom-0 left-0 w-full opacity-5"
                                  style={{
                                    height: `${altoPx}px`,
                                    transformOrigin: 'bottom',
                                    transform: 'rotateX(-90deg) translateZ(5px)',
                                    backgroundColor: configuracionAlmacen.tema_pared === 'blanco' ? '#f8fafc' : '#94a3b8'
                                  }}>
                             </div>

                             {/* MURO OESTE (Izquierda) */}
                             <div className="absolute top-0 left-0 h-full flex items-end justify-end"
                                  style={{
                                    width: `${altoPx}px`,
                                    transformOrigin: 'left',
                                    transform: 'rotateY(-90deg) translateZ(-5px)',
                                    backgroundColor: configuracionAlmacen.tema_pared === 'blanco' ? '#e2e8f0' : '#64748b',
                                    backgroundImage: configuracionAlmacen.tema_pared === 'metalico' ? 'repeating-linear-gradient(0deg, transparent, transparent 100px, rgba(0,0,0,0.1) 100px, rgba(0,0,0,0.1) 105px)' : 'none',
                                    boxShadow: 'inset -50px 0 100px rgba(0,0,0,0.5)'
                                  }}>
                                  <div className="h-full w-8 shadow-md" style={{ backgroundImage: 'repeating-linear-gradient(45deg, #fbbf24 0, #fbbf24 20px, #000 20px, #000 40px)' }}></div>
                             </div>

                             {/* MURO ESTE (Derecha) */}
                             <div className="absolute top-0 right-0 h-full flex items-end justify-start"
                                  style={{
                                    width: `${altoPx}px`,
                                    transformOrigin: 'right',
                                    transform: 'rotateY(90deg) translateZ(5px)',
                                    backgroundColor: configuracionAlmacen.tema_pared === 'blanco' ? '#e2e8f0' : '#64748b',
                                    backgroundImage: configuracionAlmacen.tema_pared === 'metalico' ? 'repeating-linear-gradient(0deg, transparent, transparent 100px, rgba(0,0,0,0.1) 100px, rgba(0,0,0,0.1) 105px)' : 'none',
                                    boxShadow: 'inset 50px 0 100px rgba(0,0,0,0.5)'
                                  }}>
                                  <div className="h-full w-8 shadow-md" style={{ backgroundImage: 'repeating-linear-gradient(45deg, #fbbf24 0, #fbbf24 20px, #000 20px, #000 40px)' }}></div>
                             </div>
                           </>
                         )}
                      </div>
                      );
                    })()}
                    {Object.entries(zonasAlmacen).map(([zona, pasillos]) => {
                      const pos = configuracionAlmacen.layout_data?.[zona];
                      const isAbsolute = modoArquitecto || hasCustomLayout;
                      return (
                      <div 
                        key={zona} 
                        ref={el => zonaRefs.current[zona] = el}
                        className={`bg-white/60 dark:bg-gray-800/60 ${!modo3D ? 'backdrop-blur-md' : ''} border-2 border-dashed border-gray-400 dark:border-gray-600 rounded-2xl p-16 relative mt-4 min-w-[800px] flex flex-col gap-16 transition-all duration-700 ${modo3D ? 'shadow-[20px_30px_50px_rgba(0,0,0,0.3)]' : 'shadow-sm'} ${isAbsolute ? 'absolute' : ''}`}
                        style={{ 
                          transformStyle: 'preserve-3d',
                          left: isAbsolute && pos ? `${pos.x}px` : undefined,
                          top: isAbsolute && pos ? `${pos.y}px` : undefined,
                          transition: dragInfo.id === zona ? 'none' : 'all 0.5s ease'
                        }}
                      >
                        {/* Título de la Zona Operativa */}
                        <div 
                          className={`absolute -top-4 left-6 bg-yellow-400 text-yellow-900 font-black px-4 py-1 rounded shadow-lg border-2 border-yellow-500 z-20 text-xs uppercase tracking-widest flex items-center gap-2 ${modoArquitecto ? 'cursor-grab hover:scale-105 hover:bg-yellow-300 ring-4 ring-yellow-500/50' : ''}`}
                          style={{ transform: modo3D ? 'translateZ(20px)' : 'translateZ(0)' }}
                          onPointerDown={modoArquitecto ? (e) => onPointerDownZona(e, zona) : undefined}
                        >
                           <MapIcon className="h-4 w-4 pointer-events-none" />
                           <span className="pointer-events-none">ZONA: {obtenerNombreZona(zona)} · {zona}</span>
                           {modoArquitecto && <span className="ml-2 bg-black/20 px-2 rounded-full text-[10px] pointer-events-none">ARRÁSTRAME</span>}
                        </div>

                      {Object.entries(pasillos).map(([pasillo, racks]) => (
                        <div key={pasillo} className="relative pt-12 pb-12 px-8 bg-gray-500/5 dark:bg-gray-400/5 rounded-xl border border-gray-300/50 dark:border-gray-600/50" style={{ transformStyle: 'preserve-3d' }}>
                          {/* Título del Pasillo */}
                          <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-yellow-400 text-slate-900 text-[10px] font-black px-4 py-1 rounded shadow-md uppercase tracking-widest z-10 whitespace-nowrap border-b-2 border-yellow-600">
                            {pasillo}
                          </div>

                          {/* Líneas de Seguridad del Pasillo (Suelo) */}
                          <div className="absolute top-[20%] bottom-[20%] left-0 right-0 border-y-2 border-yellow-400/60 pointer-events-none" aria-hidden="true"></div>
                          
                          {/* Camino central */}
                          <div className="absolute top-1/2 left-0 right-0 flex items-center pointer-events-none" aria-hidden="true">
                            <div className="w-full border-t-[4px] border-slate-300/40 border-dashed"></div>
                          </div>
                        
                          {/* Racks inside the pasillo */}
                          <div className="flex flex-wrap gap-12 justify-center relative z-10" style={{ transformStyle: 'preserve-3d' }}>
                            {Object.entries(racks).map(([rack, itemsRack]) => {
                              const hasDamaged = itemsRack.some(i => i.estado === 'Dañado' || i.estado === 'Cuarentena');
                              const capRack = obtenerCapacidadRack({ zonaOperativa: zona, pasillo, rack });
                              const totalStock = itemsRack.reduce((acc, item) => acc + (Number(item.cantidad) || 0), 0);
                              const currentTon = itemsRack.reduce((acc, item) => acc + ((Number(item.peso) || 0) * (Number(item.cantidad) || 0)), 0) / 1000;
                              const occupancyPercent = Math.min(Math.round((currentTon / (capRack.max_ton || 1)) * 100), 100);
                              
                              // Determinar color del rack
                              let rackColorClass = "bg-blue-600 border-blue-700";
                              let topColorClass = "bg-blue-500";
                              
                              const hasReservado = itemsRack.some(i => i.estado === 'Reservado');
                              const isBloqueado = occupancyPercent >= 100 || currentTon >= capRack.max_ton;

                              if (hasDamaged) {
                                rackColorClass = "bg-red-600 border-red-700";
                                topColorClass = "bg-red-500";
                              } else if (isBloqueado) {
                                rackColorClass = "bg-rose-600 border-rose-700";
                                topColorClass = "bg-rose-500";
                              } else if (occupancyPercent >= 80) {
                                rackColorClass = "bg-orange-500 border-orange-600";
                                topColorClass = "bg-orange-400";
                              } else if (occupancyPercent > 50) {
                                rackColorClass = "bg-amber-500 border-amber-600";
                                topColorClass = "bg-amber-400";
                              }
                              
                              
                              // Simulando espacios del rack
                              const nivelesVisuales = (capRack as any).niveles || configuracionAlmacen.nivelesVisuales || 3;
                              const maxSlots = nivelesVisuales * 2;
                              const height_per_level = 99 / nivelesVisuales;
                              const alturas_estantes = Array.from({ length: nivelesVisuales }).map((_, i) => 5 + (i * height_per_level));
                              
                              const filledSlots = totalStock === 0 ? 0 : Math.max(1, Math.ceil((occupancyPercent / 100) * maxSlots));

                              const codigoRack = formatearCodigoUbicacion(
                                {
                                  zonaOperativa: zona,
                                  pasillo,
                                  rack,
                                },
                                { incluirNivel: false, incluirPosicion: false, formato: 'visual' }
                              );

                              return (
                                <div 
                                  key={rack} 
                                  onClick={(e) => { 
                                    e.stopPropagation(); 
                                    if (hasDragged.current) return;
                                    const layoutRacks3d = (configuracionAlmacen?.layout_data as any)?.racks3d;
                                    const layoutRack = layoutRacks3d?.[`${zona}-${pasillo}-${rack}`] ||
                                      Object.values(layoutRacks3d || {}).find(
                                        (r: any) =>
                                          r.zona?.toUpperCase() === zona.toUpperCase() &&
                                          normalizarNumero2(r.pasillo) === normalizarNumero2(pasillo) &&
                                          normalizarNumero2(r.rack) === normalizarNumero2(rack)
                                      ) as any;
                                    setRackSeleccionado({
                                      nombre: codigoRack,
                                      zona,
                                      pasillo,
                                      rack,
                                      items: itemsRack,
                                      niveles: layoutRack?.niveles || capRack.niveles,
                                      ancho: layoutRack?.ancho,
                                    }); 
                                  }}
                                  className="group relative cursor-pointer"
                                  style={{
                                    width: '140px',
                                    height: '60px',
                                    transformStyle: 'preserve-3d',
                                    transition: 'all 0.5s ease-out'
                                  }}
                                >
                                   {/* Sombra en el piso Direccional (AO) */}
                                   <div className={`absolute inset-0 bg-black/40 blur-md rounded transition-all duration-500 origin-bottom-left pointer-events-none ${modo3D ? 'scale-y-125 skew-x-[-20deg] translate-y-4 opacity-70' : ''}`} style={{ transform: 'translateZ(-1px)' }}></div>
                                   
                                   {/* Rack en Modo 2D */}
                                   {!modo3D && (
                                     <div className={`absolute inset-0 p-2 flex flex-col items-center justify-center border-2 border-black/20 rounded shadow-inner ${rackColorClass}`}>
                                        <div className="w-full bg-black/30 rounded-full h-1.5 mb-1 overflow-hidden">
                                          <div className={`h-full ${hasDamaged ? 'bg-red-400' : hasReservado ? 'bg-blue-400' : isBloqueado ? 'bg-gray-400' : 'bg-green-400'}`} style={{ width: `${occupancyPercent}%` }}></div>
                                        </div>
                                        <span className="text-white font-black text-xs bg-black/20 px-1 py-0.5 rounded w-full text-center truncate">{rack}</span>
                                        <div className="mt-1 px-1 bg-white/90 rounded text-center w-full">
                                           <span className="text-gray-900 text-[10px] font-bold">{totalStock} u</span>
                                        </div>
                                     </div>
                                   )}

                                   {/* Estructura del Rack 3D (Rack Metálico Realista con Pallets) */}
                                   {modo3D && (
                                     <div className="absolute inset-0" style={{ transformStyle: 'preserve-3d' }}>
                                        {/* Wrapper Vertical: Se levanta 90 grados desde el piso */}
                                        <div className="absolute bottom-0 left-0 w-full"
                                             style={{ 
                                                 height: '110px', 
                                                 transformOrigin: 'bottom', 
                                                 transform: 'rotateX(-90deg)', 
                                                 transformStyle: 'preserve-3d' 
                                             }}>
                                             
                                             {/* --- HITBOX 3D INVISIBLE (Para facilitar el Clic y Hover en todo el volumen) --- */}
                                             <div className="absolute inset-0 z-50 cursor-pointer" style={{ backgroundColor: 'rgba(255,255,255,0)' }}></div>
                                             
                                             {/* --- MARCO TRASERO INDUSTRIAL (Z = -40px) --- */}
                                             <div className="absolute inset-0 border-x-[6px] border-t-[6px] border-blue-900 flex flex-col justify-evenly shadow-2xl"
                                                  style={{ transform: 'translateZ(-40px)', transformStyle: 'preserve-3d' }}>
                                                  {Array.from({ length: nivelesVisuales }).map((_, i) => (
                                                      <div key={i} className="w-full h-[4px] bg-blue-900/80"></div>
                                                  ))}
                                             </div>

                                             {/* --- MARCO LATERAL IZQUIERDO (Bracing Azul) --- */}
                                             <div className="absolute top-0 left-0 flex items-center justify-center pointer-events-none"
                                                  style={{ width: '40px', height: '110px', transformOrigin: 'left', transform: 'rotateY(90deg)', transformStyle: 'preserve-3d' }}>
                                                  <div className="absolute inset-0 border-y-[4px] border-l-[4px] border-blue-800 bg-blue-900/10"></div>
                                                  <div className="w-full h-full opacity-60" style={{ background: 'linear-gradient(35deg, transparent 46%, #1e3a8a 46%, #1e3a8a 54%, transparent 54%), linear-gradient(-35deg, transparent 46%, #1e3a8a 46%, #1e3a8a 54%, transparent 54%)' }}></div>
                                             </div>

                                             {/* --- MARCO LATERAL DERECHO (Bracing Azul) --- */}
                                             <div className="absolute top-0 right-0 flex items-center justify-center pointer-events-none"
                                                  style={{ width: '40px', height: '110px', transformOrigin: 'right', transform: 'rotateY(-90deg)', transformStyle: 'preserve-3d' }}>
                                                  <div className="absolute inset-0 border-y-[4px] border-r-[4px] border-blue-800 bg-blue-900/10"></div>
                                                  <div className="w-full h-full opacity-60" style={{ background: 'linear-gradient(35deg, transparent 46%, #1e3a8a 46%, #1e3a8a 54%, transparent 54%), linear-gradient(-35deg, transparent 46%, #1e3a8a 46%, #1e3a8a 54%, transparent 54%)' }}></div>
                                             </div>

                                             {/* --- VIGAS FRONTALES Y TRASERAS NARANJAS (Planos Horizontales) --- */}
                                             {alturas_estantes.map((bottom, idx) => (
                                                <div key={idx} className="absolute left-[6px] right-[6px] bg-orange-500 border-y-2 border-orange-600 shadow-[0_5px_10px_rgba(0,0,0,0.3)]"
                                                     style={{ height: '40px', bottom: `${bottom}px`, transformOrigin: 'bottom', transform: 'rotateX(90deg)' }}>
                                                </div>
                                             ))}

                                             {/* --- CARGA / PALLETS (Cajas con base de madera) --- */}
                                             <div className="absolute inset-0" style={{ transformStyle: 'preserve-3d' }}>
                                                {Array.from({ length: filledSlots }).map((_, i) => {
                                                   const nivel = Math.floor(i / 2); // 0, 1, 2...
                                                   const pos = i % 2; // 0 (izq), 1 (der)
                                                   const bottom = 5 + (nivel * height_per_level); // Descansan sobre el estante
                                                   const left = pos === 0 ? 12 : 75;
                                                   return (
                                                      <div key={i} className="absolute transition-all duration-700 ease-in-out"
                                                           style={{ bottom: `${bottom}px`, left: `${left}px`, width: '45px', height: '28px', transformStyle: 'preserve-3d' }}>
                                                           
                                                           {/* PARIHUELA (Base de Madera Realista) */}
                                                           <div className="absolute bottom-0 left-0 w-full" style={{ height: '4px', transformStyle: 'preserve-3d' }}>
                                                              <div className="absolute inset-0 bg-[#8B5A2B] shadow-sm" style={{ transform: 'translateZ(-5px)', backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 4px, rgba(0,0,0,0.15) 4px, rgba(0,0,0,0.15) 6px)' }}></div>
                                                              <div className="absolute top-0 left-0 w-full bg-[#724A22] border-b border-black/20" style={{ height: '30px', transformOrigin: 'top', transform: 'translateZ(-5px) rotateX(-90deg)', backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 4px, rgba(0,0,0,0.1) 4px, rgba(0,0,0,0.1) 6px)' }}></div>
                                                              <div className="absolute top-0 left-0 h-full bg-[#59391A] border-r border-black/20" style={{ width: '30px', transformOrigin: 'left', transform: 'translateZ(-5px) rotateY(90deg)' }}></div>
                                                              <div className="absolute top-0 right-0 h-full bg-[#59391A] border-l border-black/20" style={{ width: '30px', transformOrigin: 'right', transform: 'translateZ(-5px) rotateY(-90deg)' }}></div>
                                                           </div>

                                                           {/* CAJA DE CARGA ENVUELTA (Arriba de la madera) */}
                                                           <div className="absolute left-0 w-[41px] ml-[2px]" style={{ bottom: '4px', height: '22px', transformStyle: 'preserve-3d' }}>
                                                               {/* Cara Frontal */}
                                                               <div className={`absolute inset-0 ${rackColorClass} shadow-xl border border-white/20 flex items-center justify-center rounded-sm overflow-hidden`}
                                                                    style={{ transform: 'translateZ(-5px)' }}>
                                                                    <div className="absolute inset-0 opacity-20 mix-blend-overlay" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(255,255,255,0.5) 2px, rgba(255,255,255,0.5) 4px)' }}></div>
                                                                    <div className="absolute inset-0 bg-gradient-to-tr from-black/20 to-transparent"></div>
                                                                    {/* Etiqueta de despacho simulada */}
                                                                    <div className="absolute top-1.5 right-1.5 w-2.5 h-1.5 bg-white rounded-[1px] shadow-sm flex flex-col justify-between p-[1px] opacity-90"><div className="w-full h-[1px] bg-black/40"></div><div className="w-3/4 h-[1px] bg-black/40"></div></div>
                                                                    {i === 0 && (
                                                                      <span className="text-white font-black text-[9px] bg-black/50 backdrop-blur-sm px-1.5 py-0.5 rounded shadow-lg z-10 border border-white/10">{totalStock} u</span>
                                                                    )}
                                                               </div>
                                                               {/* Cara Superior */}
                                                               <div className={`absolute top-0 left-0 w-full ${topColorClass} border border-white/10 rounded-sm`}
                                                                    style={{ height: '30px', transformOrigin: 'top', transform: 'translateZ(-5px) rotateX(-90deg)' }}>
                                                                    <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 5px, #000 5px, #000 6px)' }}></div>     
                                                               </div>
                                                               {/* Cara Izquierda */}
                                                               <div className={`absolute top-0 left-0 h-full ${rackColorClass} brightness-75 border border-black/20`}
                                                                    style={{ width: '30px', transformOrigin: 'left', transform: 'translateZ(-5px) rotateY(90deg)' }}></div>
                                                               {/* Cara Derecha */}
                                                               <div className={`absolute top-0 right-0 h-full ${rackColorClass} brightness-75 border border-black/20`}
                                                                    style={{ width: '30px', transformOrigin: 'right', transform: 'translateZ(-5px) rotateY(-90deg)' }}></div>
                                                           </div>
                                                      </div>
                                                   )
                                                })}
                                             </div>

                                             {/* --- ETIQUETA RACK --- */}
                                             <div className="absolute top-[-25px] left-1/2 -translate-x-1/2 bg-black/80 text-white font-black text-[10px] px-2 py-0.5 rounded shadow-lg border border-white/20 whitespace-nowrap"
                                                  style={{ transform: 'translateZ(10px)' }}>
                                                {rack}
                                             </div>

                                             {/* --- HINT INTERACTIVO HOVER --- */}
                                             {modo3D && (
                                                <div className="absolute -top-[55px] left-1/2 -translate-x-1/2 bg-blue-600/90 text-white px-2 py-1 rounded-md shadow-[0_0_15px_rgba(37,99,235,0.8)] opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none whitespace-nowrap flex flex-col items-center z-50 animate-bounce border border-blue-400/50"
                                                     style={{ transform: 'translateZ(20px)' }}>
                                                    <span className="font-bold text-[9px] uppercase tracking-wider">☝️ Inspeccionar Rack</span>
                                                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-blue-600/90 rotate-45 border-r border-b border-blue-400/50"></div>
                                                </div>
                                             )}

                                             {/* --- MARCO FRONTAL (Z = 0px) --- */}
                                             <div className="absolute inset-0 border-x-[5px] border-t-[5px] border-slate-500 flex flex-col justify-evenly pointer-events-none"
                                                  style={{ transform: 'translateZ(0px)', transformStyle: 'preserve-3d' }}>
                                                  {Array.from({ length: nivelesVisuales }).map((_, i) => (
                                                      <div key={i} className="w-full h-[5px] bg-slate-500 shadow-[0_2px_4px_rgba(0,0,0,0.4)] relative">
                                                         <div className="absolute top-0 left-2 w-3 h-[3px] bg-white rounded-sm flex gap-[1px] p-[1px]">
                                                             <div className="w-[1px] h-full bg-black"></div>
                                                             <div className={`w-[${(i % 2) + 1}px] h-full bg-black`}></div>
                                                         </div>
                                                      </div>
                                                  ))}
                                             </div>
                                        </div>
                                     </div>
                                   )}
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal de Detalles del Rack */}
      {rackSeleccionado && (
        <div 
          className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/75 dark:bg-black/85 backdrop-blur-md p-2 sm:p-4 lg:p-6 overflow-y-auto animate-fadeIn" 
          onClick={() => setRackSeleccionado(null)}
        >
          <div 
            className="w-full max-w-[96vw] xl:max-w-[1360px] 2xl:max-w-[1440px] my-auto max-h-[96vh] bg-white dark:bg-[#0f1015] shadow-2xl rounded-2xl sm:rounded-[2rem] flex flex-col border border-slate-200/80 dark:border-white/10 relative overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Header del Modal */}
            <div className="px-5 sm:px-8 py-4 sm:py-5 border-b border-slate-100 dark:border-white/5 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3.5 bg-white/95 dark:bg-[#0f1015]/95 backdrop-blur-xl sticky top-0 z-30">
              <div className="flex items-center gap-3.5">
                <div className="p-2.5 rounded-2xl bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 shrink-0">
                  <CubeIcon className="h-6 w-6" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight font-mono">
                      {rackSeleccionado.nombre}
                    </h3>
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
                      Zona {rackSeleccionado.zona} · {obtenerNombreZona(rackSeleccionado.zona)}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-zinc-400 mt-0.5">
                    Estantería de paletización convencional · {rackSeleccionado.items.length} productos en inventario
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 self-end sm:self-center">
                <button
                  type="button"
                  onClick={() => handleCopiarTexto(rackSeleccionado.nombre)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 border border-slate-200/60 dark:border-white/10 text-xs font-bold text-slate-700 dark:text-zinc-200 transition-colors cursor-pointer"
                  title="Copiar código del rack"
                >
                  <DocumentDuplicateIcon className="h-4 w-4" />
                  <span>Copiar</span>
                </button>
                <button
                  type="button"
                  onClick={abrirModalCapacidadesRack}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 border border-slate-200/60 dark:border-white/10 text-xs font-bold text-slate-700 dark:text-zinc-200 transition-colors cursor-pointer"
                  title="Capacidades y límites estructurales"
                >
                  <Cog6ToothIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">Capacidades</span>
                </button>
                  <button
                    type="button"
                    onClick={exportarAlmacenCompleto}
                    disabled={exportandoAlmacen}
                    className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-800 dark:text-zinc-200 font-bold rounded-xl transition-all border border-slate-200 dark:border-white/10 shadow-sm hover:scale-[1.02]"
                    title="Descargar copia del gemelo digital 3D y su inventario"
                  >
                    <ArrowDownTrayIcon className="w-4 h-4 text-indigo-500" />
                    <span>Exportar Gemelo Digital (JSON 3D)</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMostrarModalImportarAlmacen(true)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-bold rounded-xl transition-all border border-blue-200 dark:border-blue-800/40 shadow-sm hover:scale-[1.02]"
                    title="Cargar maqueta 3D y datos desde un respaldo JSON"
                  >
                    <ArrowUpTrayIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <span>Importar Gemelo Digital (JSON 3D)</span>
                  </button>
                <button
                  type="button"
                  onClick={handleIngresarEnRack}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-xs font-bold text-white shadow-md shadow-amber-500/20 transition-all cursor-pointer"
                >
                  <PlusIcon className="h-4 w-4" />
                  <span>Ingresar aquí</span>
                </button>
                <button 
                  onClick={() => setRackSeleccionado(null)}
                  className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors cursor-pointer ml-1"
                  title="Cerrar modal"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            {/* Contenido con scroll */}
            <div className="flex-1 overflow-y-auto p-5 sm:p-7 space-y-6">
              {/* Tarjetas KPI de Estado Estructural y Capacidad */}
              {(() => {
                const unidadesRack = rackSeleccionado.items.reduce((acc, item) => acc + (Number(item.cantidad) || 0), 0);
                const pesoRackTon = rackSeleccionado.items.reduce((acc, item) => acc + ((Number(item.peso) || 0) * (Number(item.cantidad) || 0)), 0) / 1000;
                const volumenRackM3 = rackSeleccionado.items.reduce((acc, item) => acc + (calcularVolumenUnitarioM3(item) * (Number(item.cantidad) || 0)), 0);
                const capRack = obtenerCapacidadRack({ zonaOperativa: rackSeleccionado.zona, pasillo: rackSeleccionado.pasillo, rack: rackSeleccionado.rack });
                const pctUnidades = unidadesRack / capRack.max_unidades;
                const pctPeso = pesoRackTon / capRack.max_ton;
                const pctVol = volumenRackM3 / capRack.max_m3;

                const maxNivelEnItems = Math.max(
                  1,
                  ...rackSeleccionado.items.map((it) =>
                    parseInt(String(it.nivel || '1').replace(/\D/g, ''), 10) || 1
                  )
                );
                const numNiveles = Math.max(
                  rackSeleccionado.niveles || capRack.niveles || 3,
                  maxNivelEnItems
                );
                const posicionesPorNivel = (capRack as any).posiciones_por_nivel || 8;
                const totalSlots = numNiveles * posicionesPorNivel;
                const palletsOcupados = rackSeleccionado.items.length;
                const libresSlots = Math.max(totalSlots - palletsOcupados, 0);
                const pctSlots = totalSlots > 0 ? Math.round((palletsOcupados / totalSlots) * 100) : 0;

                const ultimoItem = [...rackSeleccionado.items].sort((a,b) => new Date(b.fechaIngreso).getTime() - new Date(a.fechaIngreso).getTime())[0];
                const hasDamaged = rackSeleccionado.items.some(i => i.estado === 'Dañado' || i.estado === 'Cuarentena');
                const hasReservado = rackSeleccionado.items.some(i => i.estado === 'Reservado');
                
                let estadoRack = "Operativo";
                let estadoColor = "text-emerald-500 bg-emerald-500/10 border-emerald-500/20";
                let dotColor = "bg-emerald-500";

                if (pctUnidades > 1 || pctPeso > 1 || pctVol > 1) {
                  estadoRack = "Sobrecarga";
                  estadoColor = "text-rose-500 bg-rose-500/10 border-rose-500/20";
                  dotColor = "bg-rose-500";
                } else if (hasDamaged) {
                  estadoRack = "Cuarentena";
                  estadoColor = "text-amber-500 bg-amber-500/10 border-amber-500/20";
                  dotColor = "bg-amber-500";
                } else if (hasReservado) {
                  estadoRack = "En Picking";
                  estadoColor = "text-blue-500 bg-blue-500/10 border-blue-500/20";
                  dotColor = "bg-blue-500";
                }

                return (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3.5">
                      {/* 1. Stock Total */}
                      <div className="bg-slate-50/80 dark:bg-white/[0.03] p-4 rounded-2xl border border-slate-200/80 dark:border-white/5 transition-all hover:border-amber-500/30">
                        <span className="text-[10px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wider block">
                          Stock Total
                        </span>
                        <p className="text-2xl font-black text-slate-900 dark:text-white mt-1">
                          {unidadesRack.toLocaleString()}
                        </p>
                        <span className="text-[11px] font-medium text-slate-500 dark:text-zinc-500 mt-1 block">
                          En {palletsOcupados} {palletsOcupados === 1 ? 'pallet' : 'pallets'}
                        </span>
                      </div>

                      {/* 2. Ocupación de Huecos */}
                      <div className="bg-slate-50/80 dark:bg-white/[0.03] p-4 rounded-2xl border border-slate-200/80 dark:border-white/5 transition-all hover:border-amber-500/30">
                        <span className="text-[10px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wider block">
                          Ocupación Slots
                        </span>
                        <p className="text-2xl font-black text-slate-900 dark:text-white mt-1">
                          {palletsOcupados} / {totalSlots}
                        </p>
                        <div className="mt-2 h-1.5 w-full rounded-full bg-slate-200 dark:bg-white/10 overflow-hidden">
                          <div
                            className={`h-full transition-all ${pctSlots > 90 ? 'bg-rose-500' : pctSlots > 70 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                            style={{ width: `${Math.min(pctSlots, 100)}%` }}
                          />
                        </div>
                        <span className="text-[10px] font-medium text-slate-500 dark:text-zinc-500 mt-1.5 block">
                          {libresSlots} libres ({pctSlots}%)
                        </span>
                      </div>

                      {/* 3. Carga Estructural (Peso) */}
                      <div className="bg-slate-50/80 dark:bg-white/[0.03] p-4 rounded-2xl border border-slate-200/80 dark:border-white/5 transition-all hover:border-amber-500/30">
                        <span className="text-[10px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wider block">
                          Carga (t)
                        </span>
                        <p className="text-xl font-black text-slate-900 dark:text-white mt-1">
                          {pesoRackTon.toLocaleString(undefined, { maximumFractionDigits: 2 })} / {capRack.max_ton.toLocaleString()}
                        </p>
                        <div className="mt-2 h-1.5 w-full rounded-full bg-slate-200 dark:bg-white/10 overflow-hidden">
                          <div
                            className={`h-full transition-all ${pctPeso > 1 ? 'bg-rose-500' : pctPeso > 0.85 ? 'bg-amber-500' : 'bg-blue-500'}`}
                            style={{ width: `${Math.min(pctPeso * 100, 100)}%` }}
                          />
                        </div>
                        <span className="text-[10px] font-medium text-slate-500 dark:text-zinc-500 mt-1.5 block">
                          Disp: {Math.max(capRack.max_ton - pesoRackTon, 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} t
                        </span>
                      </div>

                      {/* 4. Volumen Cúbico */}
                      <div className="bg-slate-50/80 dark:bg-white/[0.03] p-4 rounded-2xl border border-slate-200/80 dark:border-white/5 transition-all hover:border-amber-500/30">
                        <span className="text-[10px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wider block">
                          Volumen (m³)
                        </span>
                        <p className="text-xl font-black text-slate-900 dark:text-white mt-1">
                          {volumenRackM3.toLocaleString(undefined, { maximumFractionDigits: 1 })} / {capRack.max_m3.toLocaleString()}
                        </p>
                        <div className="mt-2 h-1.5 w-full rounded-full bg-slate-200 dark:bg-white/10 overflow-hidden">
                          <div
                            className={`h-full transition-all ${pctVol > 1 ? 'bg-rose-500' : pctVol > 0.85 ? 'bg-amber-500' : 'bg-cyan-500'}`}
                            style={{ width: `${Math.min(pctVol * 100, 100)}%` }}
                          />
                        </div>
                        <span className="text-[10px] font-medium text-slate-500 dark:text-zinc-500 mt-1.5 block">
                          Disp: {Math.max(capRack.max_m3 - volumenRackM3, 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} m³
                        </span>
                      </div>

                      {/* 5. Último Movimiento */}
                      <div className="bg-slate-50/80 dark:bg-white/[0.03] p-4 rounded-2xl border border-slate-200/80 dark:border-white/5 transition-all hover:border-amber-500/30">
                        <span className="text-[10px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wider block">
                          Último Movimiento
                        </span>
                        <p className="text-sm font-bold text-slate-800 dark:text-zinc-200 mt-1.5 truncate">
                          {ultimoItem ? getDaysAgo(ultimoItem.fechaIngreso) : 'Sin registros'}
                        </p>
                        <span className="text-[10px] text-slate-400 dark:text-zinc-500 mt-1 block truncate">
                          {ultimoItem ? `SKU: ${ultimoItem.sku}` : 'Listo para operar'}
                        </span>
                      </div>

                      {/* 6. Estado Operativo */}
                      <div className="bg-slate-50/80 dark:bg-white/[0.03] p-4 rounded-2xl border border-slate-200/80 dark:border-white/5 transition-all hover:border-amber-500/30 flex flex-col justify-between">
                        <span className="text-[10px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wider block">
                          Estado del Rack
                        </span>
                        <div className="mt-1">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${estadoColor}`}>
                            <span className={`h-2 w-2 rounded-full ${dotColor} animate-pulse`} />
                            {estadoRack}
                          </span>
                        </div>
                        <span className="text-[9px] text-slate-400 dark:text-zinc-500 mt-1 block">
                          {pctPeso > 1 ? '¡Exceso de carga!' : 'Estructura apta'}
                        </span>
                      </div>
                    </div>

                    {/* Fila Dividida: Elevación Estructural a la Izquierda y Tabla de Inventario a la Derecha */}
                    <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 mt-6">
                      
                      {/* Columna Izquierda (7 cols): Elevación Estructural Realista del Rack */}
                      <div className="col-span-1 xl:col-span-7 flex flex-col space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <h4 className="text-xs font-black text-slate-500 dark:text-zinc-400 uppercase tracking-widest">
                              Elevación Estructural y Huecos Físicos
                            </h4>
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-zinc-300 font-mono">
                              {numNiveles} Niv · {posicionesPorNivel} Pallets
                            </span>
                          </div>
                          <span className="text-[10px] text-slate-400 dark:text-zinc-500 hidden sm:inline">
                            Toca cualquier hueco para interactuar
                          </span>
                        </div>

                        {/* Estructura Física del Rack */}
                        <div className="relative rounded-2xl border-2 border-slate-700/80 bg-gradient-to-b from-slate-900 via-slate-950 to-[#0a0a0f] p-3 sm:p-5 shadow-2xl overflow-hidden">
                          {/* Arriostramiento de fondo */}
                          <div className="absolute inset-0 pointer-events-none opacity-[0.06] flex items-center justify-center">
                            <svg className="w-full h-full" preserveAspectRatio="none" viewBox="0 0 100 100">
                              <line x1="0" y1="0" x2="100" y2="100" stroke="#38bdf8" strokeWidth="1" />
                              <line x1="100" y1="0" x2="0" y2="100" stroke="#38bdf8" strokeWidth="1" />
                            </svg>
                          </div>

                          {/* Puntal izquierdo */}
                          <div className="absolute top-0 bottom-6 left-0 w-2.5 sm:w-3 bg-gradient-to-r from-blue-700 via-blue-600 to-blue-800 shadow-lg border-r border-blue-400/40 z-10 flex flex-col justify-around py-3">
                            {Array.from({ length: 12 }).map((_, i) => (
                              <div key={i} className="w-1.5 h-1.5 mx-auto bg-black/60 rounded-full" />
                            ))}
                          </div>

                          {/* Puntal derecho */}
                          <div className="absolute top-0 bottom-6 right-0 w-2.5 sm:w-3 bg-gradient-to-l from-blue-700 via-blue-600 to-blue-800 shadow-lg border-l border-blue-400/40 z-10 flex flex-col justify-around py-3">
                            {Array.from({ length: 12 }).map((_, i) => (
                              <div key={i} className="w-1.5 h-1.5 mx-auto bg-black/60 rounded-full" />
                            ))}
                          </div>

                          {/* Niveles del Rack */}
                          {(() => {
                            const nivelesLista = Array.from({ length: numNiveles }, (_, i) => numNiveles - i);

                            return (
                              <div className="space-y-4 px-2 sm:px-3 relative z-10">
                                {nivelesLista.map(nivelNum => {
                                  const nivelStr = `0${nivelNum}`;
                                  const itemsEnEsteNivel = rackSeleccionado.items.filter(
                                    i => normalizarNumero2(i.nivel || '01') === nivelStr
                                  );

                                  return (
                                    <div key={nivelNum} className="space-y-1.5">
                                      {/* Header del Nivel */}
                                      <div className="flex items-center justify-between px-1">
                                        <span className="text-[10px] font-black font-mono tracking-widest text-slate-400 uppercase">
                                          Nivel {nivelStr} {nivelNum === numNiveles ? '(Superior)' : nivelNum === 1 ? '(Suelo)' : ''}
                                        </span>
                                        <span className="text-[9px] text-slate-500 font-mono">
                                          {itemsEnEsteNivel.length}/{posicionesPorNivel} pallets
                                        </span>
                                      </div>

                                      {/* Huecos del Nivel */}
                                      <div 
                                        className="grid gap-1.5 sm:gap-2"
                                        style={{ gridTemplateColumns: `repeat(${posicionesPorNivel}, minmax(0, 1fr))` }}
                                      >
                                        {Array.from({ length: posicionesPorNivel }, (_, idx) => {
                                          const pos = normalizarNumero2(String(idx + 1));
                                          const item = rackSeleccionado.items.find(
                                            i => normalizarNumero2(i.nivel || '01') === nivelStr && normalizarNumero2(i.posicion || '01') === pos
                                          );

                                          if (!item) {
                                            {/* HUECO LIBRE */}
                                            return (
                                              <button
                                                key={pos}
                                                type="button"
                                                onClick={() => handleIngresarEnPosicion(nivelStr, pos)}
                                                title={`Posición N${nivelStr}-P${pos}: Libre (Clic para ingresar producto)`}
                                                className="group/slot relative flex flex-col items-center justify-between p-1 sm:p-1.5 rounded-xl border border-dashed border-emerald-500/40 hover:border-emerald-400 bg-emerald-500/[0.04] hover:bg-emerald-500/[0.14] transition-all cursor-pointer min-h-[68px] sm:min-h-[76px]"
                                              >
                                                <span className="text-[9px] font-mono font-bold text-slate-400 group-hover/slot:text-emerald-300">
                                                  {pos}
                                                </span>
                                                <div className="flex flex-col items-center gap-0.5">
                                                  <PlusIcon className="h-4 w-4 text-emerald-500/60 group-hover/slot:text-emerald-400 group-hover/slot:scale-110 transition-all" />
                                                  <span className="text-[8px] font-bold text-emerald-500/70 group-hover/slot:text-emerald-300 uppercase">
                                                    Libre
                                                  </span>
                                                </div>
                                                <div className="w-full h-1 bg-emerald-500/20 rounded-full" />
                                              </button>
                                            );
                                          }

                                          {/* HUECO CON PALLET OCUPADO */}
                                          const isDamaged = item.estado === 'Dañado' || item.estado === 'Cuarentena';
                                          const isReserved = item.estado === 'Reservado';

                                          return (
                                            <button
                                              key={pos}
                                              type="button"
                                              onClick={() => handleAbrirItemDesdeRack(item)}
                                              title={`N${nivelStr}-P${pos}: ${item.sku} - ${item.nombre} (${item.cantidad} ${item.unidadMedida || 'u'}) · Clic para ver / editar`}
                                              className={`group/slot relative flex flex-col items-center justify-between p-1 sm:p-1.5 rounded-xl border transition-all cursor-pointer min-h-[68px] sm:min-h-[76px] shadow-sm hover:scale-[1.03] hover:shadow-lg ${
                                                isDamaged 
                                                  ? 'border-rose-500/60 bg-gradient-to-b from-rose-950/40 to-rose-900/20 hover:border-rose-400' 
                                                  : isReserved 
                                                    ? 'border-amber-500/60 bg-gradient-to-b from-amber-950/40 to-amber-900/20 hover:border-amber-400' 
                                                    : 'border-blue-500/50 bg-gradient-to-b from-blue-950/40 to-slate-900/40 hover:border-blue-400'
                                              }`}
                                            >
                                              {/* Badge de posición y estado */}
                                              <div className="w-full flex items-center justify-between">
                                                <span className="text-[9px] font-mono font-bold text-slate-300">
                                                  {pos}
                                                </span>
                                                <span className={`h-1.5 w-1.5 rounded-full ${isDamaged ? 'bg-rose-500' : isReserved ? 'bg-amber-500' : 'bg-blue-400'}`} />
                                              </div>

                                              {/* Representación visual realista del Pallet y Cajas */}
                                              <div className="flex flex-col items-center w-full px-0.5 my-0.5">
                                                {/* Cajas de cartón corrugado */}
                                                <div className="w-full max-w-[46px] rounded bg-gradient-to-b from-[#ca8a04] to-[#a16207] p-0.5 sm:p-1 border border-[#eab308]/40 shadow-sm flex flex-col items-center justify-center">
                                                  <span className="text-[7px] sm:text-[7.5px] font-mono font-bold text-black bg-white/90 px-0.5 rounded-xs truncate max-w-full">
                                                    {item.sku}
                                                  </span>
                                                  <span className="text-[6.5px] sm:text-[7px] font-bold text-white/90 truncate max-w-full mt-0.5">
                                                    {item.cantidad}u
                                                  </span>
                                                </div>
                                                {/* Pallet de madera */}
                                                <div className="w-full max-w-[50px] h-2 bg-gradient-to-r from-[#78350f] via-[#92400e] to-[#78350f] rounded-xs border-t border-[#b45309] mt-0.5 flex justify-around items-center px-0.5">
                                                  <div className="w-1 h-1 bg-[#451a03] rounded-xs" />
                                                  <div className="w-1 h-1 bg-[#451a03] rounded-xs" />
                                                  <div className="w-1 h-1 bg-[#451a03] rounded-xs" />
                                                </div>
                                              </div>

                                              <div className="w-full text-center">
                                                <span className="text-[7px] font-medium text-slate-400 truncate block max-w-full">
                                                  {item.categoria || 'Genérico'}
                                                </span>
                                              </div>
                                            </button>
                                          );
                                        })}
                                      </div>

                                      {/* Viga de Carga Naranja de Seguridad con remaches */}
                                      <div className="h-2.5 w-full bg-gradient-to-r from-orange-600 via-orange-500 to-orange-600 rounded-sm shadow-md flex items-center justify-between px-2 border-t border-orange-300/40 border-b border-orange-800">
                                        <div className="flex items-center gap-1">
                                          <div className="w-1 h-1 bg-black/60 rounded-full" />
                                          <div className="w-1 h-1 bg-black/60 rounded-full" />
                                        </div>
                                        <span className="text-[7px] font-mono font-bold text-black/70 tracking-widest hidden sm:inline">
                                          LARGUERO ACERO INDUSTRIAL · NIVEL {nivelStr}
                                        </span>
                                        <div className="flex items-center gap-1">
                                          <div className="w-1 h-1 bg-black/60 rounded-full" />
                                          <div className="w-1 h-1 bg-black/60 rounded-full" />
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}

                          {/* Losa de Concreto en la base */}
                          <div className="mt-4 pt-2 border-t-4 border-slate-700/80 bg-gradient-to-b from-zinc-800 to-zinc-900 -mx-3 -mb-5 sm:-mx-5 sm:-mb-5 px-4 py-2 flex items-center justify-between text-[9px] font-mono text-slate-400">
                            <div className="flex items-center gap-1.5">
                              <span className="h-1.5 w-1.5 bg-emerald-400 rounded-full" />
                              <span>LOSA DE CONCRETO REFORZADO · CAPACIDAD SUELO: OK</span>
                            </div>
                            <span className="hidden sm:inline">ESTRUCTURA CERTIFICADA</span>
                          </div>
                        </div>

                        {/* Leyenda Visual */}
                        <div className="flex flex-wrap items-center justify-center gap-4 text-[10px] font-bold text-slate-500 dark:text-zinc-400 pt-1">
                          <div className="flex items-center gap-1.5">
                            <div className="w-3 h-3 rounded border border-dashed border-emerald-500 bg-emerald-500/10" />
                            <span>Hueco Libre (Clic para ingresar)</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <div className="w-3 h-3 rounded bg-blue-500" />
                            <span>Pallet con Stock</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <div className="w-3 h-3 rounded bg-amber-500" />
                            <span>Reservado / Picking</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <div className="w-3 h-3 rounded bg-rose-500" />
                            <span>Observado / Cuarentena</span>
                          </div>
                        </div>
                      </div>

                      {/* Columna Derecha (5 cols): Inventario en Rack */}
                      <div className="col-span-1 xl:col-span-5 flex flex-col space-y-3">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2.5">
                          <h4 className="text-xs font-black text-slate-500 dark:text-zinc-400 uppercase tracking-widest">
                            Inventario en Rack ({rackSeleccionado.items.length})
                          </h4>
                          <div className="relative w-full sm:w-56">
                            <MagnifyingGlassIcon className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                              type="text"
                              value={rackBusqueda}
                              onChange={e => setRackBusqueda(e.target.value)}
                              placeholder="Filtrar producto o SKU..."
                              className="w-full pl-9 pr-3 py-1.5 bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-xl text-xs font-medium outline-none focus:border-amber-500 transition-colors"
                            />
                          </div>
                        </div>
                        
                        <div className="flex-1 bg-white dark:bg-zinc-900/60 border border-slate-200/80 dark:border-white/10 rounded-2xl overflow-hidden flex flex-col shadow-sm min-h-[300px]">
                          {rackSeleccionado.items.length === 0 ? (
                            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-slate-50/50 dark:bg-white/[0.01]">
                              <div className="p-3.5 rounded-2xl bg-amber-500/10 text-amber-600 dark:text-amber-400 mb-3 border border-amber-500/20">
                                <CubeIcon className="h-8 w-8" />
                              </div>
                              <h5 className="text-sm font-bold text-slate-800 dark:text-white">
                                Rack completamente disponible
                              </h5>
                              <p className="text-xs text-slate-500 dark:text-zinc-400 max-w-xs mt-1 mb-4">
                                Esta estantería no tiene mercancía registrada. Puedes tocar cualquier hueco libre en la elevación o usar el botón directo.
                              </p>
                              <button
                                type="button"
                                onClick={handleIngresarEnRack}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold text-xs shadow-md shadow-amber-500/20 transition-all cursor-pointer"
                              >
                                <PlusIcon className="h-4 w-4" />
                                <span>Ingresar producto aquí</span>
                              </button>
                            </div>
                          ) : (
                            <div className="overflow-x-auto flex-1">
                              <table className="w-full text-left border-collapse">
                                <thead>
                                  <tr className="bg-slate-50 dark:bg-zinc-800/50 border-b border-slate-200 dark:border-white/5">
                                    <th className="py-2.5 px-3 text-[10px] font-black text-slate-500 dark:text-zinc-400 uppercase tracking-widest">Posición</th>
                                    <th className="py-2.5 px-3 text-[10px] font-black text-slate-500 dark:text-zinc-400 uppercase tracking-widest">SKU / Producto</th>
                                    <th className="py-2.5 px-3 text-[10px] font-black text-slate-500 dark:text-zinc-400 uppercase tracking-widest text-right">Cantidad</th>
                                    <th className="py-2.5 px-3 text-[10px] font-black text-slate-500 dark:text-zinc-400 uppercase tracking-widest text-center">Estado</th>
                                    <th className="py-2.5 px-3 text-[10px] font-black text-slate-500 dark:text-zinc-400 uppercase tracking-widest text-right">Acción</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                                  {rackSeleccionado.items
                                    .filter(item => {
                                      const q = rackBusquedaDeferred.trim().toLowerCase();
                                      if (!q) return true;
                                      return (
                                        item.sku.toLowerCase().includes(q) ||
                                        item.nombre.toLowerCase().includes(q) ||
                                        (item.categoria || '').toLowerCase().includes(q)
                                      );
                                    })
                                    .sort((a, b) => {
                                      const na = String(a.nivel || '').padStart(2, '0').slice(-2);
                                      const nb = String(b.nivel || '').padStart(2, '0').slice(-2);
                                      if (na !== nb) return nb.localeCompare(na);
                                      const pa = String(a.posicion || '').padStart(2, '0').slice(-2);
                                      const pb = String(b.posicion || '').padStart(2, '0').slice(-2);
                                      return pa.localeCompare(pb);
                                    })
                                    .map(item => (
                                      <tr 
                                        key={item.id} 
                                        className="hover:bg-amber-500/[0.04] transition-colors group"
                                      >
                                        <td className="py-2.5 px-3 whitespace-nowrap">
                                          <div className="flex items-center gap-1">
                                            <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-white/10 text-[10px] font-bold font-mono text-slate-700 dark:text-zinc-300">
                                              N{normalizarNumero2(item.nivel || '01')}
                                            </span>
                                            <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-white/10 text-[10px] font-bold font-mono text-slate-700 dark:text-zinc-300">
                                              P{normalizarNumero2(item.posicion || '01')}
                                            </span>
                                          </div>
                                        </td>
                                        <td className="py-2.5 px-3">
                                          <div className="flex flex-col">
                                            <span className="text-[10px] font-mono font-bold text-amber-600 dark:text-amber-400">{item.sku}</span>
                                            <span className="text-xs font-bold text-slate-900 dark:text-white truncate max-w-[140px] sm:max-w-[200px]" title={item.nombre}>
                                              {item.nombre}
                                            </span>
                                          </div>
                                        </td>
                                        <td className="py-2.5 px-3 text-right whitespace-nowrap">
                                          <span className="text-xs font-black text-slate-900 dark:text-white">{item.cantidad}</span>
                                          <span className="text-[9px] font-bold text-slate-400 ml-1 uppercase">{item.unidadMedida || 'u'}</span>
                                        </td>
                                        <td className="py-2.5 px-3 whitespace-nowrap text-center">
                                          <span className={`inline-flex px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                                            item.estado === 'Dañado' || item.estado === 'Cuarentena'
                                              ? 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20'
                                              : item.estado === 'Reservado'
                                                ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20'
                                                : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                                          }`}>
                                            {item.estado}
                                          </span>
                                        </td>
                                        <td className="py-2.5 px-3 whitespace-nowrap text-right">
                                          <div className="flex items-center justify-end gap-1">
                                            <button
                                              type="button"
                                              onClick={() => setMostrarModalQR(item)}
                                              title="Ver código QR / Etiqueta"
                                              className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-600 dark:text-zinc-300 transition-colors cursor-pointer"
                                            >
                                              <QrCodeIcon className="h-3.5 w-3.5" />
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => handleAbrirItemDesdeRack(item)}
                                              title="Ver detalles o editar"
                                              className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-600 dark:text-zinc-300 transition-colors cursor-pointer"
                                            >
                                              <PencilSquareIcon className="h-3.5 w-3.5" />
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => abrirModalSalida(item)}
                                              title="Retiro / Salida de este producto"
                                              className="p-1.5 rounded-lg bg-rose-50 hover:bg-rose-100 dark:bg-rose-500/10 dark:hover:bg-rose-500/20 text-rose-600 dark:text-rose-400 transition-colors cursor-pointer"
                                            >
                                              <ArrowUpTrayIcon className="h-3.5 w-3.5" />
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'infraestructura' && (
        <div className="space-y-6 animate-fadeIn">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {esMaestroOSuperior ? (
              <div className="xl:col-span-2 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-start justify-between gap-4 mb-6">
                  <div>
                    <h2 className="text-xl font-black text-gray-900 dark:text-white">Configuración de Almacén</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      La institución define primero su infraestructura y después registra inventario dentro de ella.
                    </p>
                  </div>
                  <span className="px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-widest bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">
                    Jerarquía: Institución &gt; Zona &gt; Pasillo &gt; Rack
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="almacen-practica-config-nombre" className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1 uppercase">Nombre del almacén</label>
                    <input
                      id="almacen-practica-config-nombre"
                      name="configuracionAlmacen.nombre"
                      type="text"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 text-sm outline-none"
                      value={configuracionAlmacen.nombre}
                      onChange={e => setConfiguracionAlmacen(prev => ({ ...prev, nombre: e.target.value }))}
                      placeholder="Ej. Almacén Central"
                    />
                  </div>
                  <div>
                    <label htmlFor="almacen-practica-config-codigo" className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1 uppercase">Código maestro</label>
                    <input
                      id="almacen-practica-config-codigo"
                      name="configuracionAlmacen.codigo"
                      type="text"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 text-sm outline-none font-mono"
                      value={configuracionAlmacen.codigo}
                      onChange={e => setConfiguracionAlmacen(prev => ({ ...prev, codigo: sanitizarCodigoAlmacen(e.target.value) }))}
                      placeholder="ALM01"
                      maxLength={10}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label htmlFor="almacen-practica-config-descripcion" className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1 uppercase">Descripción operativa</label>
                    <textarea
                      id="almacen-practica-config-descripcion"
                      name="configuracionAlmacen.descripcion"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 text-sm outline-none min-h-[96px]"
                      value={configuracionAlmacen.descripcion}
                      onChange={e => setConfiguracionAlmacen(prev => ({ ...prev, descripcion: e.target.value }))}
                      placeholder="Describe el rol del almacén, su alcance y restricciones de operación."
                    />
                  </div>

                  <div className="md:col-span-2 mt-2 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <h4 className="text-sm font-black text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <CubeIcon className="h-5 w-5 text-blue-500" />
                      Maquetación 3D (Visualización)
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="almacen-practica-config-niveles" className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1 uppercase">Estructura Metálica (Niveles por Rack)</label>
                        <select
                          id="almacen-practica-config-niveles"
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm font-semibold outline-none cursor-pointer"
                          value={configuracionAlmacen.nivelesVisuales || 3}
                          onChange={e => setConfiguracionAlmacen(prev => ({ ...prev, nivelesVisuales: Number(e.target.value) }))}
                        >
                          <option value={2}>2 Niveles (Estructura Baja)</option>
                          <option value={3}>3 Niveles (Estándar Industrial)</option>
                          <option value={4}>4 Niveles (Alta Densidad)</option>
                          <option value={5}>5 Niveles (Torre Completa)</option>
                          <option value={6}>6 Niveles (Automatizado)</option>
                        </select>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2">Personaliza cómo se dibuja físicamente la estantería en la pestaña del Mapa de Ubicaciones.</p>
                      </div>
                      <div>
                        <label htmlFor="almacen-practica-config-piso" className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1 uppercase">Diseño de Piso (Plano 3D)</label>
                        <select
                          id="almacen-practica-config-piso"
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm font-semibold outline-none cursor-pointer"
                          value={configuracionAlmacen.tema_piso || 'concreto'}
                          onChange={e => setConfiguracionAlmacen(prev => ({ ...prev, tema_piso: e.target.value }))}
                        >
                          <option value="concreto">Concreto Pulido (Gris Oscuro)</option>
                          <option value="epoxico_azul">Epóxico Azul Industrial</option>
                          <option value="epoxico_verde">Epóxico Verde Clínico</option>
                          <option value="madera">Madera / Tarima</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="almacen-practica-config-pared" className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1 uppercase">Muros Perimetrales</label>
                        <select
                          id="almacen-practica-config-pared"
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm font-semibold outline-none cursor-pointer"
                          value={configuracionAlmacen.tema_pared || 'industrial'}
                          onChange={e => setConfiguracionAlmacen(prev => ({ ...prev, tema_pared: e.target.value }))}
                        >
                          <option value="industrial">Abierto (Fondo Oscuro sin muros)</option>
                          <option value="blanco">Muros Blancos</option>
                          <option value="metalico">Paneles Metálicos (Aluminio)</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="almacen-practica-config-ancho" className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1 uppercase">Ancho del Almacén (m)</label>
                        <input
                          id="almacen-practica-config-ancho"
                          type="number"
                          min="10"
                          max="120"
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm font-semibold outline-none"
                          value={configuracionAlmacen.dimension_ancho || 80}
                          onChange={e => {
                            let val = Number(e.target.value);
                            if (val > 120) val = 120;
                            setConfiguracionAlmacen(prev => ({ ...prev, dimension_ancho: val }));
                          }}
                        />
                      </div>
                      <div>
                        <label htmlFor="almacen-practica-config-largo" className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1 uppercase">Largo del Almacén (m)</label>
                        <input
                          id="almacen-practica-config-largo"
                          type="number"
                          min="10"
                          max="120"
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm font-semibold outline-none"
                          value={configuracionAlmacen.dimension_largo || 80}
                          onChange={e => {
                            let val = Number(e.target.value);
                            if (val > 120) val = 120;
                            setConfiguracionAlmacen(prev => ({ ...prev, dimension_largo: val }));
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={handleGuardarConfiguracionAlmacen}
                    className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-colors"
                  >
                    Guardar configuración base
                  </button>
                  <button
                    type="button"
                    onClick={abrirModalCapacidadesRack}
                    className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-100 font-bold rounded-xl transition-colors"
                  >
                    Administrar zonas y racks
                  </button>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Última actualización: {new Date(configuracionAlmacen.actualizadoEn).toLocaleString()}
                  </span>
                </div>
              </div>
            ) : (
              /* Modo Consulta Académica (Solo Lectura para Alumnos) */
              <div className="xl:col-span-2 bg-white/80 dark:bg-[#0c0c0c] backdrop-blur-xl rounded-[28px] border border-slate-200/80 dark:border-white/[0.08] shadow-xl p-6 sm:p-8 space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-100 dark:border-white/[0.06]">
                  <div>
                    <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400 text-xs font-bold w-fit mb-2">
                      <ShieldCheckIcon className="h-4 w-4" />
                      <span>Ficha Técnica de Infraestructura · Solo Lectura</span>
                    </div>
                    <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">
                      Especificaciones del Centro de Distribución
                    </h2>
                    <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1">
                      Parámetros de ingeniería, layout y capacidades físicas fijadas por la dirección académica.
                    </p>
                  </div>
                  <span className="px-3 py-1.5 rounded-xl text-xs font-mono font-bold bg-slate-100 dark:bg-white/5 text-slate-700 dark:text-zinc-300 border border-slate-200 dark:border-white/10 self-start sm:self-auto">
                    Código: {configuracionAlmacen.codigo || 'ALM01'}
                  </span>
                </div>

                {/* Grid de Especificaciones Físicas Bento */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-4 rounded-2xl bg-slate-50 dark:bg-white/[0.02] border border-slate-200/60 dark:border-white/[0.05]">
                    <span className="text-[11px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-widest block">
                      Nombre de la Instalación
                    </span>
                    <p className="mt-1 text-base font-black text-slate-900 dark:text-white">
                      {configuracionAlmacen.nombre || 'Almacén Central'}
                    </p>
                  </div>

                  <div className="p-4 rounded-2xl bg-slate-50 dark:bg-white/[0.02] border border-slate-200/60 dark:border-white/[0.05]">
                    <span className="text-[11px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-widest block">
                      Huella Operativa Total
                    </span>
                    <p className="mt-1 text-base font-black text-slate-900 dark:text-white">
                      {((configuracionAlmacen.dimension_ancho || 80) * (configuracionAlmacen.dimension_largo || 80)).toLocaleString()} m²
                      <span className="text-xs font-normal text-slate-500 dark:text-zinc-400 ml-2">
                        ({configuracionAlmacen.dimension_ancho || 80}m ancho × {configuracionAlmacen.dimension_largo || 80}m largo)
                      </span>
                    </p>
                  </div>

                  <div className="p-4 rounded-2xl bg-slate-50 dark:bg-white/[0.02] border border-slate-200/60 dark:border-white/[0.05]">
                    <span className="text-[11px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-widest block">
                      Estructura de Racks
                    </span>
                    <p className="mt-1 text-base font-black text-slate-900 dark:text-white">
                      {configuracionAlmacen.nivelesVisuales || 3} Niveles de Carga
                      <span className="text-xs font-normal text-slate-500 dark:text-zinc-400 ml-2">
                        (Estándar Industrial)
                      </span>
                    </p>
                  </div>

                  <div className="p-4 rounded-2xl bg-slate-50 dark:bg-white/[0.02] border border-slate-200/60 dark:border-white/[0.05]">
                    <span className="text-[11px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-widest block">
                      Material del Suelo
                    </span>
                    <p className="mt-1 text-base font-black text-slate-900 dark:text-white">
                      {configuracionAlmacen.tema_piso === 'epoxico_azul' ? 'Epóxico Azul Industrial' : configuracionAlmacen.tema_piso === 'epoxico_verde' ? 'Epóxico Verde Clínico' : configuracionAlmacen.tema_piso === 'madera' ? 'Madera / Tarima' : 'Concreto Pulido'}
                    </p>
                  </div>
                </div>

                {/* Descripción Operativa */}
                <div className="p-4 rounded-2xl bg-slate-50 dark:bg-white/[0.02] border border-slate-200/60 dark:border-white/[0.05]">
                  <span className="text-[11px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-widest block mb-1">
                    Descripción del Almacén y Alcance
                  </span>
                  <p className="text-xs text-slate-700 dark:text-zinc-300 leading-relaxed">
                    {configuracionAlmacen.descripcion || 'Infraestructura principal para simulación de recepción, estiba y despacho logístico.'}
                  </p>
                </div>

                {/* Aviso formativo para el estudiante */}
                <div className="p-4 rounded-2xl bg-blue-50/60 dark:bg-blue-500/5 border border-blue-200/60 dark:border-blue-500/20 flex items-start gap-3">
                  <div className="p-2 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400 shrink-0">
                    <ShieldCheckIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-blue-900 dark:text-blue-300">
                      Parámetros de Ingeniería Bloqueados
                    </h4>
                    <p className="text-[11px] text-blue-700/90 dark:text-blue-400/90 mt-0.5 leading-relaxed">
                      Como operador / practicante puedes consultar estas especificaciones para tus cálculos de cubicaje y estiba. La reconfiguración de muros, niveles y capacidad portante corresponde a los docentes y supervisores.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-widest mb-4">Resumen estructural</h3>
              <div className="space-y-3">
                <div className="rounded-2xl border border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900/30">
                  <p className="text-[11px] uppercase tracking-widest text-gray-400">Institución</p>
                  <p className="mt-1 text-lg font-black text-gray-900 dark:text-white">{configuracionAlmacen.nombre}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">{CODIGO_ALMACEN_VISUAL}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-2xl border border-gray-200 dark:border-gray-700 p-4">
                    <p className="text-[11px] uppercase tracking-widest text-gray-400">Zonas activas</p>
                    <p className="mt-1 text-2xl font-black text-gray-900 dark:text-white">{resumenInfraestructura.zonas}</p>
                  </div>
                  <div className="rounded-2xl border border-gray-200 dark:border-gray-700 p-4">
                    <p className="text-[11px] uppercase tracking-widest text-gray-400">Racks activos</p>
                    <p className="mt-1 text-2xl font-black text-gray-900 dark:text-white">{resumenInfraestructura.racks}</p>
                  </div>
                </div>
                <div className="rounded-2xl border border-green-200 bg-green-50/70 dark:border-green-500/30 dark:bg-green-500/10 p-4">
                  <p className="text-[11px] uppercase tracking-widest text-green-700 dark:text-green-300">Capacidad agregada automática</p>
                  <p className="mt-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
                    {resumenInfraestructura.max_unidades.toLocaleString()} unid · {resumenInfraestructura.max_ton.toLocaleString(undefined, { maximumFractionDigits: 2 })} t · {resumenInfraestructura.max_m3.toLocaleString(undefined, { maximumFractionDigits: 2 })} m³
                  </p>
                  <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                    El dashboard usa la suma de todos los racks configurados para reflejar el techo real de la institución.
                  </p>
                </div>
                <div className={`rounded-2xl border p-4 ${infraestructuraTieneConfiguracionBase ? 'border-blue-200 bg-blue-50/70 dark:border-blue-500/30 dark:bg-blue-500/10' : 'border-amber-200 bg-amber-50/70 dark:border-amber-500/30 dark:bg-amber-500/10'}`}>
                  <p className="text-sm font-bold text-gray-900 dark:text-white">
                    {infraestructuraTieneConfiguracionBase ? 'Infraestructura lista para operar' : 'Falta infraestructura mínima'}
                  </p>
                  <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                    {infraestructuraTieneConfiguracionBase
                      ? 'Ya puedes registrar productos porque existe un almacén definido, zonas activas y racks con capacidad.'
                      : 'Antes de ingresar inventario define el almacén, crea zonas y configura al menos un rack con límites máximos.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'auditoria' && (
        <div className="space-y-6 animate-fadeIn">
          {/* Header Card Glassmórfica con Acción de Exportación */}
          <div className="bg-white/80 dark:bg-[#0c0c0c] backdrop-blur-xl rounded-[28px] p-6 border border-slate-200/80 dark:border-white/[0.08] shadow-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-slate-200/60 dark:border-white/[0.06] relative z-10">
              <div className="flex items-center gap-4">
                <div className="p-3.5 rounded-2xl bg-gradient-to-br from-blue-500/10 to-indigo-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20 shadow-inner">
                  <ClipboardDocumentListIcon className="h-7 w-7" />
                </div>
                <div>
                  <div className="flex items-center gap-2.5">
                    <h2 className="text-xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                      Libro Mayor de Auditoría & Kardex WMS
                    </h2>
                    <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      Inmutable
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1">
                    Trazabilidad de custodia, movimientos de mercancía y auditoría de operadores en tiempo real.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={exportarKardexCSV}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold text-xs shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30 active:scale-95 transition-all"
                  title="Descargar libro mayor en formato CSV para Excel o auditoría formal"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 stroke-[2.5]" />
                  <span>Exportar Kardex (CSV)</span>
                </button>
              </div>
            </div>

            {/* Strip de Telemetría Rápida de Auditoría */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-6 relative z-10">
              <div className="p-4 rounded-2xl bg-slate-50/70 dark:bg-white/[0.02] border border-slate-200/60 dark:border-white/[0.05]">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wider">Total Eventos</span>
                  <ClipboardDocumentListIcon className="h-4 w-4 text-slate-400" />
                </div>
                <p className="text-2xl font-black text-slate-900 dark:text-white mt-1">{kardexTotalMovimientos}</p>
                <p className="text-[10px] text-slate-400 dark:text-zinc-500 mt-0.5">Historial registrado</p>
              </div>

              <div className="p-4 rounded-2xl bg-emerald-50/50 dark:bg-emerald-500/[0.03] border border-emerald-200/60 dark:border-emerald-500/10">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Ingresos</span>
                  <InboxStackIcon className="h-4 w-4 text-emerald-500" />
                </div>
                <p className="text-2xl font-black text-emerald-700 dark:text-emerald-300 mt-1">{kardexTotalIngresos}</p>
                <p className="text-[10px] text-emerald-600/70 dark:text-emerald-400/70 mt-0.5">Entradas de stock</p>
              </div>

              <div className="p-4 rounded-2xl bg-rose-50/50 dark:bg-rose-500/[0.03] border border-rose-200/60 dark:border-rose-500/10">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold text-rose-600 dark:text-rose-400 uppercase tracking-wider">Despachos / Salidas</span>
                  <TruckIcon className="h-4 w-4 text-rose-500" />
                </div>
                <p className="text-2xl font-black text-rose-700 dark:text-rose-300 mt-1">{kardexTotalSalidas}</p>
                <p className="text-[10px] text-rose-600/70 dark:text-rose-400/70 mt-0.5">Extracciones físicas</p>
              </div>

              <div className="p-4 rounded-2xl bg-blue-50/50 dark:bg-blue-500/[0.03] border border-blue-200/60 dark:border-blue-500/10">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider">Reubicaciones</span>
                  <ArrowsRightLeftIcon className="h-4 w-4 text-blue-500" />
                </div>
                <p className="text-2xl font-black text-blue-700 dark:text-blue-300 mt-1">{kardexTotalReubicaciones}</p>
                <p className="text-[10px] text-blue-600/70 dark:text-blue-400/70 mt-0.5">Slotting interno</p>
              </div>
            </div>
          </div>

          {/* Barra de Filtros y Búsqueda */}
          <div className="bg-white/80 dark:bg-[#0c0c0c] backdrop-blur-xl rounded-[24px] p-4 border border-slate-200/80 dark:border-white/[0.08] shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Buscador de auditoría */}
            <div className="relative w-full md:w-96">
              <MagnifyingGlassIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={kardexBusqueda}
                onChange={(e) => {
                  setKardexBusqueda(e.target.value);
                  setKardexPaginaActual(1);
                }}
                placeholder="Buscar por SKU, operador o posición..."
                className="w-full pl-10 pr-9 py-2 rounded-xl text-xs font-medium bg-slate-100 dark:bg-white/5 border border-slate-200/80 dark:border-white/10 text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all"
              />
              {kardexBusqueda && (
                <button
                  onClick={() => {
                    setKardexBusqueda('');
                    setKardexPaginaActual(1);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-white"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Filtro por Estudiante en Kardex */}
            <div className="flex items-center gap-1.5 shrink-0">
              <AcademicCapIcon className="w-4 h-4 text-indigo-500 shrink-0" />
              <select
                value={filtroEstudianteKardex}
                onChange={(e) => {
                  setFiltroEstudianteKardex(e.target.value);
                  setKardexPaginaActual(1);
                }}
                className="text-xs font-bold rounded-xl border border-slate-300 dark:border-white/10 bg-slate-100 dark:bg-zinc-900 py-1.5 pl-2.5 pr-8 text-slate-800 dark:text-zinc-200 outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer shadow-xs"
              >
                <option value="TODOS">👨‍🎓 Todos los Estudiantes</option>
                {miembrosInstitucion.map(m => {
                  const nombreCompleto = `${m.nombre} ${m.apellido}`.trim();
                  return (
                    <option key={m.id_usuario || m.id_miembro} value={nombreCompleto}>
                      {nombreCompleto} ({m.rol_miembro || 'alumno'})
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Pastillas de filtro por acción */}
            <div className="flex items-center gap-1.5 p-1 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200/60 dark:border-white/5 overflow-x-auto w-full md:w-auto">
              <button
                onClick={() => { setKardexFiltroAccion('TODOS'); setKardexPaginaActual(1); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
                  kardexFiltroAccion === 'TODOS'
                    ? 'bg-white dark:bg-white/10 text-slate-900 dark:text-white shadow-sm'
                    : 'text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                Todos ({logs.length})
              </button>
              <button
                onClick={() => { setKardexFiltroAccion('INGRESO'); setKardexPaginaActual(1); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
                  kardexFiltroAccion === 'INGRESO'
                    ? 'bg-emerald-500 text-white shadow-sm'
                    : 'text-slate-500 dark:text-zinc-400 hover:text-emerald-600'
                }`}
              >
                Ingresos ({kardexTotalIngresos})
              </button>
              <button
                onClick={() => { setKardexFiltroAccion('SALIDA'); setKardexPaginaActual(1); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
                  kardexFiltroAccion === 'SALIDA'
                    ? 'bg-rose-500 text-white shadow-sm'
                    : 'text-slate-500 dark:text-zinc-400 hover:text-rose-600'
                }`}
              >
                Salidas ({kardexTotalSalidas})
              </button>
              <button
                onClick={() => { setKardexFiltroAccion('REUBICACION'); setKardexPaginaActual(1); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
                  kardexFiltroAccion === 'REUBICACION'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-slate-500 dark:text-zinc-400 hover:text-blue-500'
                }`}
              >
                Reubicaciones ({kardexTotalReubicaciones})
              </button>
            </div>
          </div>

          {/* Tabla Ledger de Auditoría */}
          <div className="bg-white/80 dark:bg-[#0c0c0c] backdrop-blur-xl rounded-[28px] border border-slate-200/80 dark:border-white/[0.08] shadow-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50/80 dark:bg-white/[0.02] border-b border-slate-200/80 dark:border-white/[0.06] text-slate-500 dark:text-zinc-400 text-[11px] font-bold uppercase tracking-wider">
                    <th className="px-6 py-4">Registro & Hora</th>
                    <th className="px-6 py-4">Operador / Custodio</th>
                    <th className="px-6 py-4">Acción</th>
                    <th className="px-6 py-4">SKU / Producto</th>
                    <th className="px-6 py-4 text-center">Unidades</th>
                    <th className="px-6 py-4">Trayecto Logístico (Origen &rarr; Destino)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-white/[0.04]">
                  {logsFiltrados.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center justify-center max-w-sm mx-auto">
                          <div className="p-4 rounded-full bg-slate-100 dark:bg-white/5 text-slate-400 dark:text-zinc-500 mb-3">
                            <ClipboardDocumentListIcon className="h-8 w-8" />
                          </div>
                          <h4 className="text-base font-bold text-slate-900 dark:text-white">Sin movimientos que mostrar</h4>
                          <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1">
                            {kardexBusqueda || kardexFiltroAccion !== 'TODOS'
                              ? 'No hay registros que coincidan con la búsqueda o filtro aplicado.'
                              : 'Aún no se han ejecutado operaciones de ingreso, salida o reubicación en el almacén.'}
                          </p>
                          {(kardexBusqueda || kardexFiltroAccion !== 'TODOS') && (
                            <button
                              onClick={() => { setKardexBusqueda(''); setKardexFiltroAccion('TODOS'); setKardexPaginaActual(1); }}
                              className="mt-4 px-4 py-2 rounded-xl bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 text-xs font-bold hover:bg-blue-100 transition-all"
                            >
                              Limpiar filtros
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    logsPaginados.map((log) => {
                      const fechaObj = new Date(log.fecha_movimiento);
                      const fechaFormateada = isNaN(fechaObj.getTime()) 
                        ? log.fecha_movimiento 
                        : fechaObj.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
                      const horaFormateada = isNaN(fechaObj.getTime())
                        ? ''
                        : fechaObj.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
                      
                      return (
                        <tr 
                          key={log.id_log} 
                          className="hover:bg-slate-50/60 dark:hover:bg-white/[0.02] transition-colors group"
                        >
                          {/* Fecha & Hora */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <div className="p-2 rounded-xl bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-zinc-400 group-hover:bg-blue-500/10 group-hover:text-blue-500 transition-all">
                                <ClockIcon className="h-4 w-4" />
                              </div>
                              <div>
                                <p className="text-xs font-bold text-slate-900 dark:text-white">
                                  {fechaFormateada} <span className="text-slate-400 font-normal">{horaFormateada}</span>
                                </p>
                                <span className="inline-block mt-0.5 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-zinc-400">
                                  {formatearTiempoRelativo(log.fecha_movimiento)}
                                </span>
                              </div>
                            </div>
                          </td>

                          {/* Operador */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2.5">
                              <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-600 text-white flex items-center justify-center text-[11px] font-extrabold shadow-sm">
                                {(log.nombre_usuario || 'U').charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <p className="text-xs font-bold text-slate-900 dark:text-white">
                                  {log.nombre_usuario || 'Operador Desconocido'}
                                </p>
                                <p className="text-[10px] text-slate-400 dark:text-zinc-500">ID Log #{log.id_log}</p>
                              </div>
                            </div>
                          </td>

                          {/* Acción */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-xl text-[10px] font-black uppercase tracking-wider border ${
                              log.accion === 'INGRESO'
                                ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20'
                                : log.accion === 'SALIDA'
                                ? 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20'
                                : log.accion === 'REUBICACION'
                                ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20'
                                : 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20'
                            }`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${
                                log.accion === 'INGRESO' ? 'bg-emerald-500' :
                                log.accion === 'SALIDA' ? 'bg-rose-500' :
                                log.accion === 'REUBICACION' ? 'bg-blue-500' : 'bg-amber-500'
                              }`} />
                              {log.accion}
                            </span>
                          </td>

                          {/* SKU & Producto */}
                          <td className="px-6 py-4">
                            <div className="flex flex-col">
                              <span className="font-mono text-xs font-extrabold text-slate-900 dark:text-white tracking-wide">
                                {log.sku_producto}
                              </span>
                              <span className="text-[10px] text-slate-400 dark:text-zinc-500">
                                Movimiento físico verificado
                              </span>
                            </div>
                          </td>

                          {/* Unidades */}
                          <td className="px-6 py-4 text-center whitespace-nowrap">
                            <span className={`px-2.5 py-1 rounded-xl font-mono text-xs font-extrabold ${
                              log.accion === 'INGRESO'
                                ? 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300'
                                : log.accion === 'SALIDA'
                                ? 'bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300'
                                : 'bg-slate-100 dark:bg-white/5 text-slate-900 dark:text-white'
                            }`}>
                              {log.accion === 'INGRESO' ? `+${log.cantidad}` : log.accion === 'SALIDA' ? `-${log.cantidad}` : `${log.cantidad}`} uds
                            </span>
                          </td>

                          {/* Trayecto Origen -> Destino */}
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2 flex-wrap text-xs">
                              {renderTrayectoBadge('origen', log.origen_zona, log.origen_pasillo, log.origen_rack, log.origen_nivel, log.origen_posicion)}
                              <span className="text-slate-300 dark:text-zinc-600 font-black">&rarr;</span>
                              {renderTrayectoBadge('destino', log.destino_zona, log.destino_pasillo, log.destino_rack, log.destino_nivel, log.destino_posicion)}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer de la Tabla con Paginación Interactiva y Selector */}
            <div className="p-4 bg-slate-50/80 dark:bg-white/[0.015] border-t border-slate-200/60 dark:border-white/[0.04] flex flex-col md:flex-row items-center justify-between gap-4 text-xs">
              {/* Información de Registros y Selector de Filas */}
              <div className="flex flex-wrap items-center gap-4 text-slate-600 dark:text-zinc-400">
                <div className="flex items-center gap-2">
                  <ShieldCheckIcon className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                  <span>
                    Mostrando <strong className="text-slate-900 dark:text-white font-mono">{logsFiltrados.length === 0 ? 0 : indiceInicioKardex + 1} - {indiceFinKardex}</strong> de <strong className="text-slate-900 dark:text-white font-mono">{logsFiltrados.length}</strong> movimientos
                    {logsFiltrados.length !== logs.length && (
                      <span className="text-slate-400 dark:text-zinc-500 text-[11px] ml-1">
                        (de {logs.length} en total)
                      </span>
                    )}
                  </span>
                </div>

                <div className="flex items-center gap-2 pl-3 border-l border-slate-200 dark:border-white/10">
                  <span className="text-[11px] font-medium text-slate-500 dark:text-zinc-400">Filas:</span>
                  <select
                    value={kardexPorPagina}
                    onChange={(e) => {
                      setKardexPorPagina(Number(e.target.value));
                      setKardexPaginaActual(1);
                    }}
                    className="px-2.5 py-1 rounded-lg text-xs font-bold bg-white dark:bg-[#141414] border border-slate-200/80 dark:border-white/10 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 cursor-pointer shadow-sm"
                  >
                    <option value={5}>5 por pág</option>
                    <option value={10}>10 por pág</option>
                    <option value={25}>25 por pág</option>
                    <option value={50}>50 por pág</option>
                    <option value={100}>100 por pág</option>
                  </select>
                </div>
              </div>

              {/* Botones de Navegación de Páginas */}
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setKardexPaginaActual(prev => Math.max(1, prev - 1))}
                  disabled={paginaKardexValida <= 1}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/5 text-slate-700 dark:text-zinc-300 font-bold text-xs disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-100 dark:hover:bg-white/10 transition-all shadow-sm"
                  title="Página anterior"
                >
                  <ChevronLeftIcon className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Anterior</span>
                </button>

                <div className="flex items-center gap-1">
                  {paginasVisiblesKardex.map((p, idx) => {
                    if (typeof p === 'string') {
                      return (
                        <span key={`ellipsis-${idx}`} className="px-1.5 text-slate-400 dark:text-zinc-600 font-bold select-none">
                          ...
                        </span>
                      );
                    }
                    const esActiva = p === paginaKardexValida;
                    return (
                      <button
                        key={`pag-${p}`}
                        onClick={() => setKardexPaginaActual(p)}
                        className={`w-7 h-7 sm:w-8 sm:h-8 rounded-xl text-xs font-black transition-all flex items-center justify-center ${
                          esActiva
                            ? 'bg-blue-600 text-white shadow-md shadow-blue-500/25 ring-2 ring-blue-500/20'
                            : 'text-slate-600 dark:text-zinc-400 hover:bg-slate-100 dark:hover:bg-white/5 hover:text-slate-900 dark:hover:text-white'
                        }`}
                      >
                        {p}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setKardexPaginaActual(prev => Math.min(totalPaginasKardex, prev + 1))}
                  disabled={paginaKardexValida >= totalPaginasKardex}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/5 text-slate-700 dark:text-zinc-300 font-bold text-xs disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-100 dark:hover:bg-white/10 transition-all shadow-sm"
                  title="Página siguiente"
                >
                  <span className="hidden sm:inline">Siguiente</span>
                  <ChevronRightIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'analitica' && (
        <div className="space-y-6 animate-fadeIn">
          {/* Header Card de Telemetría WMS */}
          <div className="bg-white/80 dark:bg-[#0c0c0c] backdrop-blur-xl rounded-[28px] p-6 border border-slate-200/80 dark:border-white/[0.08] shadow-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/5 rounded-full blur-3xl pointer-events-none" />
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
              <div className="flex items-center gap-4">
                <div className="p-3.5 rounded-2xl bg-gradient-to-br from-indigo-500/10 via-blue-500/10 to-cyan-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20 shadow-inner">
                  <ChartPieIcon className="h-7 w-7" />
                </div>
                <div>
                  <div className="flex items-center gap-2.5">
                    <h2 className="text-xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                      Centro de Telemetría & Rendimiento WMS
                    </h2>
                    <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      Gemelo Digital Activo
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1">
                    Indicadores clave de rendimiento (KPIs), ocupación física de racks y distribución estructural de inventario.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className="px-3.5 py-1.5 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200/60 dark:border-white/5 flex items-center gap-2 text-xs">
                  <span className="text-slate-500 dark:text-zinc-400">Racks Activos:</span>
                  <span className="font-extrabold text-slate-900 dark:text-white">{racksResumen.length}</span>
                </div>
                <div className="px-3.5 py-1.5 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200/60 dark:border-white/5 flex items-center gap-2 text-xs">
                  <span className="text-slate-500 dark:text-zinc-400">Zonas:</span>
                  <span className="font-extrabold text-slate-900 dark:text-white">{totalZonasActivas}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Fila 1: Bento Grid de 4 Hero KPI Cards con Micro-Gauges */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {/* Card 1: Ocupación de Slots (Pallets) */}
            <div className="bg-white/80 dark:bg-[#0c0c0c] backdrop-blur-xl rounded-[24px] p-5 border border-slate-200/80 dark:border-white/[0.08] shadow-lg flex flex-col justify-between relative overflow-hidden group hover:border-blue-500/30 transition-all">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wider">Ocupación de Slots</span>
                <div className="p-2 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
                  <CubeIcon className="h-5 w-5" />
                </div>
              </div>

              <div>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">{slotsOcupados}</span>
                  <span className="text-xs font-semibold text-slate-400 dark:text-zinc-500">/ {totalSlotsCapacidad} slots</span>
                </div>

                <div className="mt-3">
                  <div className="flex items-center justify-between text-[11px] font-bold mb-1.5">
                    <span className="text-slate-500 dark:text-zinc-400">Saturación de Racks</span>
                    <span className="text-blue-600 dark:text-blue-400">{porcentajeSlotsOcupados}%</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-slate-100 dark:bg-white/5 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-700"
                      style={{ width: `${porcentajeSlotsOcupados}%` }}
                    />
                  </div>
                </div>
              </div>

              <div className="pt-3 mt-3 border-t border-slate-100 dark:border-white/[0.04] text-[11px] text-slate-500 dark:text-zinc-400 flex items-center justify-between">
                <span>Espacios libres:</span>
                <span className="font-bold text-emerald-600 dark:text-emerald-400">{slotsDisponibles} slots disponibles</span>
              </div>
            </div>

            {/* Card 2: Estrés Estructural (Carga en Toneladas) */}
            <div className="bg-white/80 dark:bg-[#0c0c0c] backdrop-blur-xl rounded-[24px] p-5 border border-slate-200/80 dark:border-white/[0.08] shadow-lg flex flex-col justify-between relative overflow-hidden group hover:border-amber-500/30 transition-all">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wider">Carga Estructural (t)</span>
                <div className={`p-2 rounded-xl border ${
                  porcentajeCargaTon > 85 
                    ? 'bg-rose-500/10 text-rose-500 border-rose-500/20' 
                    : porcentajeCargaTon > 70 
                    ? 'bg-amber-500/10 text-amber-500 border-amber-500/20' 
                    : 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
                }`}>
                  <ScaleIcon className="h-5 w-5" />
                </div>
              </div>

              <div>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                    {totalPesoTon.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </span>
                  <span className="text-xs font-semibold text-slate-400 dark:text-zinc-500">
                    / {capacidadTotalTon.toLocaleString()} t máx
                  </span>
                </div>

                <div className="mt-3">
                  <div className="flex items-center justify-between text-[11px] font-bold mb-1.5">
                    <span className="text-slate-500 dark:text-zinc-400">Estrés Mecánico</span>
                    <span className={
                      porcentajeCargaTon > 85 ? 'text-rose-500' :
                      porcentajeCargaTon > 70 ? 'text-amber-500' : 'text-emerald-500'
                    }>
                      {porcentajeCargaTon}% {porcentajeCargaTon > 85 ? 'Sobrecarga' : porcentajeCargaTon > 70 ? 'Carga Alta' : 'Segura'}
                    </span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-slate-100 dark:bg-white/5 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${
                        porcentajeCargaTon > 85 ? 'bg-gradient-to-r from-rose-500 to-red-600' :
                        porcentajeCargaTon > 70 ? 'bg-gradient-to-r from-amber-500 to-yellow-500' :
                        'bg-gradient-to-r from-emerald-500 to-teal-500'
                      }`}
                      style={{ width: `${porcentajeCargaTon}%` }}
                    />
                  </div>
                </div>
              </div>

              <div className="pt-3 mt-3 border-t border-slate-100 dark:border-white/[0.04] text-[11px] text-slate-500 dark:text-zinc-400 flex items-center justify-between">
                <span>Remanente:</span>
                <span className="font-bold text-slate-700 dark:text-zinc-300">
                  {capacidadDisponibleTon.toLocaleString(undefined, { maximumFractionDigits: 2 })} t libres
                </span>
              </div>
            </div>

            {/* Card 3: Cubicaje y Volumen (m³) */}
            <div className="bg-white/80 dark:bg-[#0c0c0c] backdrop-blur-xl rounded-[24px] p-5 border border-slate-200/80 dark:border-white/[0.08] shadow-lg flex flex-col justify-between relative overflow-hidden group hover:border-purple-500/30 transition-all">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wider">Cubicaje de Almacén</span>
                <div className="p-2 rounded-xl bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20">
                  <ArrowsPointingOutIcon className="h-5 w-5" />
                </div>
              </div>

              <div>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                    {totalVolumenM3.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </span>
                  <span className="text-xs font-semibold text-slate-400 dark:text-zinc-500">
                    / {capacidadTotalM3.toLocaleString()} m³
                  </span>
                </div>

                <div className="mt-3">
                  <div className="flex items-center justify-between text-[11px] font-bold mb-1.5">
                    <span className="text-slate-500 dark:text-zinc-400">Saturación Volumétrica</span>
                    <span className="text-purple-600 dark:text-purple-400">{porcentajeVolumenM3}%</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-slate-100 dark:bg-white/5 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 transition-all duration-700"
                      style={{ width: `${porcentajeVolumenM3}%` }}
                    />
                  </div>
                </div>
              </div>

              <div className="pt-3 mt-3 border-t border-slate-100 dark:border-white/[0.04] text-[11px] text-slate-500 dark:text-zinc-400 flex items-center justify-between">
                <span>Disponible:</span>
                <span className="font-bold text-slate-700 dark:text-zinc-300">
                  {capacidadDisponibleM3.toLocaleString(undefined, { maximumFractionDigits: 2 })} m³ libres
                </span>
              </div>
            </div>

            {/* Card 4: Total Unidades & Densidad */}
            <div className="bg-white/80 dark:bg-[#0c0c0c] backdrop-blur-xl rounded-[24px] p-5 border border-slate-200/80 dark:border-white/[0.08] shadow-lg flex flex-col justify-between relative overflow-hidden group hover:border-emerald-500/30 transition-all">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wider">Stock & Diversidad</span>
                <div className="p-2 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                  <InboxStackIcon className="h-5 w-5" />
                </div>
              </div>

              <div>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                    {totalStock.toLocaleString()}
                  </span>
                  <span className="text-xs font-semibold text-slate-400 dark:text-zinc-500">unidades</span>
                </div>

                <div className="mt-3">
                  <div className="flex items-center justify-between text-[11px] font-bold mb-1.5">
                    <span className="text-slate-500 dark:text-zinc-400">Variedad de Catálogo</span>
                    <span className="text-emerald-600 dark:text-emerald-400">{items.length} SKUs únicos</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-slate-100 dark:bg-white/5 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-400 transition-all duration-700"
                      style={{ width: `${Math.min(items.length * 10, 100)}%` }}
                    />
                  </div>
                </div>
              </div>

              <div className="pt-3 mt-3 border-t border-slate-100 dark:border-white/[0.04] text-[11px] text-slate-500 dark:text-zinc-400 flex items-center justify-between">
                <span>Proveedores:</span>
                <span className="font-bold text-slate-700 dark:text-zinc-300">
                  {totalProveedoresUnicos} registrados
                </span>
              </div>
            </div>
          </div>

          {/* Fila 2: Bento Grid Intermedio (Salud de Stock + Top SKUs + Donut Chart) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Panel 1: Salud y Calidad de Inventario (Normativa WMS) */}
            <div className="bg-white/80 dark:bg-[#0c0c0c] backdrop-blur-xl rounded-[28px] p-6 border border-slate-200/80 dark:border-white/[0.08] shadow-xl flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between pb-4 border-b border-slate-200/60 dark:border-white/[0.06]">
                  <div>
                    <h3 className="text-base font-extrabold text-slate-900 dark:text-white flex items-center gap-2">
                      <ShieldCheckIcon className="h-5 w-5 text-emerald-500" />
                      Salud & Calidad de Stock
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-zinc-400 mt-0.5">
                      Disponibilidad operativa para picking y despacho.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mt-5">
                  {/* Disponible */}
                  <div className="p-4 rounded-2xl bg-emerald-50/60 dark:bg-emerald-500/[0.04] border border-emerald-200/70 dark:border-emerald-500/10">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">Disponible</span>
                      <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
                    </div>
                    <p className="text-2xl font-black text-emerald-800 dark:text-emerald-200">{stockDisponible.toLocaleString()}</p>
                    <p className="text-[10px] text-emerald-600/70 dark:text-emerald-400/70 mt-0.5">Apto para despacho</p>
                  </div>

                  {/* Cuarentena */}
                  <div className="p-4 rounded-2xl bg-amber-50/60 dark:bg-amber-500/[0.04] border border-amber-200/70 dark:border-amber-500/10">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wider">Cuarentena</span>
                      <ClockIcon className="h-4 w-4 text-amber-500" />
                    </div>
                    <p className="text-2xl font-black text-amber-800 dark:text-amber-200">{stockCuarentena.toLocaleString()}</p>
                    <p className="text-[10px] text-amber-600/70 dark:text-amber-400/70 mt-0.5">Inspección de calidad</p>
                  </div>

                  {/* Dañado / Merma */}
                  <div className="p-4 rounded-2xl bg-rose-50/60 dark:bg-rose-500/[0.04] border border-rose-200/70 dark:border-rose-500/10">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] font-bold text-rose-700 dark:text-rose-400 uppercase tracking-wider">Dañado / Merma</span>
                      <ExclamationTriangleIcon className="h-4 w-4 text-rose-500" />
                    </div>
                    <p className="text-2xl font-black text-rose-800 dark:text-rose-200">{stockDanado.toLocaleString()}</p>
                    <p className="text-[10px] text-rose-600/70 dark:text-rose-400/70 mt-0.5">Bloqueado para picking</p>
                  </div>

                  {/* Reservado */}
                  <div className="p-4 rounded-2xl bg-blue-50/60 dark:bg-blue-500/[0.04] border border-blue-200/70 dark:border-blue-500/10">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] font-bold text-blue-700 dark:text-blue-400 uppercase tracking-wider">Reservado</span>
                      <ShieldCheckIcon className="h-4 w-4 text-blue-500" />
                    </div>
                    <p className="text-2xl font-black text-blue-800 dark:text-blue-200">{stockReservado.toLocaleString()}</p>
                    <p className="text-[10px] text-blue-600/70 dark:text-blue-400/70 mt-0.5">Asignado a órdenes</p>
                  </div>
                </div>
              </div>

              <div className="mt-5 pt-4 border-t border-slate-200/60 dark:border-white/[0.06] flex items-center justify-between text-xs">
                <span className="text-slate-500 dark:text-zinc-400">Eficiencia Comercial:</span>
                <span className="font-extrabold text-emerald-600 dark:text-emerald-400">
                  {totalStock > 0 ? ((stockDisponible / totalStock) * 100).toFixed(1) : 100}% disponible
                </span>
              </div>
            </div>

            {/* Panel 2: Radar de Concentración (Top 5 SKUs con mayor impacto) */}
            <div className="bg-white/80 dark:bg-[#0c0c0c] backdrop-blur-xl rounded-[28px] p-6 border border-slate-200/80 dark:border-white/[0.08] shadow-xl flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between pb-4 border-b border-slate-200/60 dark:border-white/[0.06]">
                  <div>
                    <h3 className="text-base font-extrabold text-slate-900 dark:text-white flex items-center gap-2">
                      <SparklesIcon className="h-5 w-5 text-indigo-500" />
                      Top 5 SKUs de Mayor Concentración
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-zinc-400 mt-0.5">
                      Artículos con mayor impacto en masa y unidades.
                    </p>
                  </div>
                </div>

                <div className="mt-4 space-y-3">
                  {topSkusConcentracion.length === 0 ? (
                    <div className="py-12 text-center text-xs text-slate-400">
                      No hay artículos suficientes para clasificar
                    </div>
                  ) : (
                    topSkusConcentracion.map((item, index) => {
                      const pesoItemTotal = ((Number(item.peso) || 0) * (Number(item.cantidad) || 0));
                      const pesoPorcentaje = totalPesoKg > 0 ? (pesoItemTotal / totalPesoKg) * 100 : 0;
                      
                      return (
                        <div 
                          key={item.id}
                          className="p-3 rounded-2xl bg-slate-50/70 dark:bg-white/[0.02] border border-slate-200/60 dark:border-white/[0.05] hover:border-indigo-500/20 transition-all"
                        >
                          <div className="flex items-center justify-between text-xs">
                            <div className="flex items-center gap-2">
                              <span className="w-5 h-5 rounded-lg bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 font-black text-[10px] flex items-center justify-center">
                                #{index + 1}
                              </span>
                              <div>
                                <span className="font-extrabold text-slate-900 dark:text-white line-clamp-1">
                                  {item.nombre || item.sku}
                                </span>
                                <span className="font-mono text-[10px] text-slate-400 dark:text-zinc-500">
                                  {item.sku}
                                </span>
                              </div>
                            </div>
                            <div className="text-right">
                              <span className="font-extrabold text-slate-900 dark:text-white">
                                {item.cantidad} uds
                              </span>
                              <p className="text-[10px] text-slate-400 dark:text-zinc-500 font-mono">
                                {pesoItemTotal.toLocaleString()} kg
                              </p>
                            </div>
                          </div>

                          <div className="mt-2 h-1.5 w-full rounded-full bg-slate-200 dark:bg-white/5 overflow-hidden">
                            <div 
                              className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-blue-500"
                              style={{ width: `${Math.max(pesoPorcentaje, 8)}%` }}
                            />
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-slate-200/60 dark:border-white/[0.06] text-[11px] text-slate-500 dark:text-zinc-400 flex items-center justify-between">
                <span>Clasificación:</span>
                <span className="font-bold text-slate-700 dark:text-zinc-300">Análisis ABC preliminar</span>
              </div>
            </div>

            {/* Panel 3: Distribución por Categoría (Donut Chart Bento) */}
            <div className="bg-white/80 dark:bg-[#0c0c0c] backdrop-blur-xl rounded-[28px] p-6 border border-slate-200/80 dark:border-white/[0.08] shadow-xl flex flex-col justify-between items-center">
              <div className="w-full">
                <div className="flex items-center justify-between pb-4 border-b border-slate-200/60 dark:border-white/[0.06]">
                  <div>
                    <h3 className="text-base font-extrabold text-slate-900 dark:text-white flex items-center gap-2">
                      <TagIcon className="h-5 w-5 text-blue-500" />
                      Distribución por Categoría
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-zinc-400 mt-0.5">
                      Mix de categorías en el inventario actual.
                    </p>
                  </div>
                </div>

                {categoriasArray.length > 0 ? (
                  <div className="mt-6 flex flex-col items-center">
                    <CustomDonutChart data={categoriasArray} />

                    <div className="w-full mt-6 space-y-2 max-h-48 overflow-y-auto pr-1">
                      {categoriasArray.map((cat) => {
                        const porcentajeCat = totalStock > 0 ? ((cat.value / totalStock) * 100).toFixed(1) : 0;
                        return (
                          <div 
                            key={cat.name} 
                            className="flex items-center justify-between p-2 rounded-xl bg-slate-50/60 dark:bg-white/[0.02] border border-slate-200/50 dark:border-white/[0.04]"
                          >
                            <div className="flex items-center gap-2.5">
                              <div className="w-3 h-3 rounded-full shrink-0 shadow-sm" style={{ backgroundColor: cat.color }} />
                              <span className="text-xs font-bold text-slate-800 dark:text-zinc-200 line-clamp-1">{cat.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-extrabold text-slate-900 dark:text-white">{cat.value}</span>
                              <span className="text-[10px] font-semibold text-slate-400 dark:text-zinc-500">({porcentajeCat}%)</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="py-20 text-center text-xs text-slate-400">
                    Sin categorías registradas en inventario
                  </div>
                )}
              </div>

              <div className="w-full mt-4 pt-3 border-t border-slate-200/60 dark:border-white/[0.06] text-[11px] text-slate-500 dark:text-zinc-400 flex items-center justify-between">
                <span>Categorías únicas:</span>
                <span className="font-bold text-slate-700 dark:text-zinc-300">{categoriasArray.length} activas</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Acción Posición (WMS Menu) */}
      {accionPosicion && !mostrarModalReubicar && !mostrarModalNuevo && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 sm:p-6 bg-slate-950/70 dark:bg-black/85 backdrop-blur-xl animate-in fade-in duration-200">
           <div className="bg-white/95 dark:bg-[#0f1117] rounded-[32px] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.6)] dark:shadow-[0_25px_60px_-15px_rgba(59,130,246,0.2)] w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-200/80 dark:border-white/10 relative">
             {/* Glow top effect */}
             <div className="absolute top-0 inset-x-0 h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-emerald-500"></div>
             
             {/* Header */}
             <div className="p-6 border-b border-slate-100 dark:border-white/[0.06] bg-slate-50/60 dark:bg-white/[0.02] relative">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest px-2.5 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/20">
                        WMS Slotting Matrix
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-emerald-600 dark:text-emerald-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                        Slot Disponible
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <h3 className="font-mono font-black text-slate-900 dark:text-white text-2xl tracking-tight">
                        {accionPosicion.zonaOperativa}-{accionPosicion.pasillo}-{accionPosicion.rack}-{accionPosicion.nivel}-{accionPosicion.posicion}
                      </h3>
                      <button
                        type="button"
                        onClick={() => copiarCoordenada(`${accionPosicion.zonaOperativa}-${accionPosicion.pasillo}-${accionPosicion.rack}-${accionPosicion.nivel}-${accionPosicion.posicion}`)}
                        className="p-1.5 rounded-xl bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white transition-all active:scale-95"
                        title="Copiar coordenada física"
                      >
                        <DocumentDuplicateIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <button 
                    onClick={() => setAccionPosicion(null)}
                    className="p-2 rounded-2xl bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-400 hover:text-slate-700 dark:text-zinc-400 dark:hover:text-white transition-all active:scale-95"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Desglose de Coordenada en Pastillas */}
                <div className="grid grid-cols-5 gap-1.5 mt-4 pt-3 border-t border-slate-200/60 dark:border-white/[0.04] text-center">
                  <div className="p-1.5 rounded-xl bg-white dark:bg-white/[0.03] border border-slate-200/60 dark:border-white/5">
                    <span className="text-[9px] font-bold uppercase text-slate-400 dark:text-zinc-500 block">Zona</span>
                    <span className="font-mono text-xs font-black text-slate-900 dark:text-white">{accionPosicion.zonaOperativa}</span>
                  </div>
                  <div className="p-1.5 rounded-xl bg-white dark:bg-white/[0.03] border border-slate-200/60 dark:border-white/5">
                    <span className="text-[9px] font-bold uppercase text-slate-400 dark:text-zinc-500 block">Pasillo</span>
                    <span className="font-mono text-xs font-black text-slate-900 dark:text-white">{accionPosicion.pasillo}</span>
                  </div>
                  <div className="p-1.5 rounded-xl bg-white dark:bg-white/[0.03] border border-slate-200/60 dark:border-white/5">
                    <span className="text-[9px] font-bold uppercase text-slate-400 dark:text-zinc-500 block">Rack</span>
                    <span className="font-mono text-xs font-black text-slate-900 dark:text-white">{accionPosicion.rack}</span>
                  </div>
                  <div className="p-1.5 rounded-xl bg-white dark:bg-white/[0.03] border border-slate-200/60 dark:border-white/5">
                    <span className="text-[9px] font-bold uppercase text-slate-400 dark:text-zinc-500 block">Nivel</span>
                    <span className="font-mono text-xs font-black text-blue-600 dark:text-blue-400">N-{accionPosicion.nivel}</span>
                  </div>
                  <div className="p-1.5 rounded-xl bg-white dark:bg-white/[0.03] border border-slate-200/60 dark:border-white/5">
                    <span className="text-[9px] font-bold uppercase text-slate-400 dark:text-zinc-500 block">Posición</span>
                    <span className="font-mono text-xs font-black text-indigo-600 dark:text-indigo-400">P-{accionPosicion.posicion}</span>
                  </div>
                </div>
             </div>
             
             {/* Acciones Bento */}
             <div className="p-6 space-y-3 relative z-10 bg-white dark:bg-transparent">
                <p className="text-xs text-slate-500 dark:text-zinc-400 font-medium px-1">
                  Seleccione el flujo operativo que desea ejecutar en este slot:
                </p>

                {/* Opción 1: Ingreso de Carga */}
                <button 
                  type="button"
                  onClick={handleProcederNuevoProducto}
                  className="w-full flex items-center gap-4 bg-slate-50/70 dark:bg-white/[0.02] border-2 border-slate-200/70 hover:border-blue-500 dark:border-white/5 dark:hover:border-blue-500/50 p-4 rounded-2xl transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/10 group relative overflow-hidden cursor-pointer active:scale-[0.98]"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 via-blue-500/0 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
                  <div className="h-12 w-12 bg-blue-500/10 dark:bg-blue-500/20 rounded-2xl flex items-center justify-center text-blue-600 dark:text-blue-400 shrink-0 group-hover:scale-110 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300 shadow-sm border border-blue-500/20">
                    <PlusIcon className="h-6 w-6 stroke-[2.5]" />
                  </div>
                  <div className="text-left flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-extrabold text-slate-900 dark:text-white text-sm group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                        Ingresar Nueva Carga
                      </p>
                      <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded-md bg-blue-500/10 text-blue-600 dark:text-blue-400">
                        Entrada
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-zinc-400 mt-0.5 leading-snug">
                      Registrar lote, pallet o bulto desde cero en esta coordenada.
                    </p>
                  </div>
                  <div className="text-slate-400 dark:text-zinc-600 group-hover:text-blue-500 group-hover:translate-x-1 transition-all shrink-0">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>

                {/* Opción 2: Reubicar / Put-Away */}
                <button 
                  type="button"
                  onClick={handleAbrirModalReubicacion}
                  className="w-full flex items-center gap-4 bg-slate-50/70 dark:bg-white/[0.02] border-2 border-slate-200/70 hover:border-emerald-500 dark:border-white/5 dark:hover:border-emerald-500/50 p-4 rounded-2xl transition-all duration-300 hover:shadow-xl hover:shadow-emerald-500/10 group relative overflow-hidden cursor-pointer active:scale-[0.98]"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/0 via-emerald-500/0 to-emerald-500/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
                  <div className="h-12 w-12 bg-emerald-500/10 dark:bg-emerald-500/20 rounded-2xl flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0 group-hover:scale-110 group-hover:bg-emerald-600 group-hover:text-white transition-all duration-300 shadow-sm border border-emerald-500/20">
                    <ArrowPathIcon className="h-6 w-6 stroke-[2.5]" />
                  </div>
                  <div className="text-left flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-extrabold text-slate-900 dark:text-white text-sm group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                        Reubicar (Put-away)
                      </p>
                      <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                        Transferencia
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-zinc-400 mt-0.5 leading-snug">
                      Mover inventario existente desde otro rack hacia esta posición.
                    </p>
                  </div>
                  <div className="text-slate-400 dark:text-zinc-600 group-hover:text-emerald-500 group-hover:translate-x-1 transition-all shrink-0">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
             </div>

             {/* Footer con info técnica */}
             <div className="px-6 py-3.5 bg-slate-50/80 dark:bg-white/[0.01] border-t border-slate-100 dark:border-white/[0.04] flex items-center justify-between text-[11px] text-slate-500 dark:text-zinc-400">
               <span className="flex items-center gap-1.5">
                 <CubeIcon className="h-4 w-4 text-slate-400" />
                 Posición Tipo Pallet Estándar
               </span>
               <span className="font-mono font-bold text-slate-700 dark:text-zinc-300">
                 WMS Digital Twin
               </span>
             </div>
           </div>
        </div>
      )}

      {/* Modal Reubicar (Selección) */}
      {mostrarModalReubicar && accionPosicion && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 sm:p-6 bg-slate-950/70 dark:bg-black/85 backdrop-blur-xl animate-in fade-in duration-200">
           <div className="bg-white/95 dark:bg-[#0f1117] rounded-[32px] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.6)] dark:shadow-[0_25px_60px_-15px_rgba(16,185,129,0.2)] w-full max-w-xl overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-200/80 dark:border-white/10 flex flex-col max-h-[85vh] relative">
             {/* Glow top effect */}
             <div className="absolute top-0 inset-x-0 h-1.5 bg-gradient-to-r from-emerald-500 via-teal-500 to-blue-500"></div>

             {/* Header */}
             <div className="p-6 border-b border-slate-100 dark:border-white/[0.06] bg-slate-50/60 dark:bg-white/[0.02]">
               <div className="flex justify-between items-start">
                 <div>
                   <div className="flex items-center gap-2 mb-1">
                     <span className="text-[10px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest px-2.5 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                       Consola de Reubicación
                     </span>
                     <span className="text-[10px] font-bold text-slate-400 dark:text-zinc-500">
                       Operación Put-away
                     </span>
                   </div>
                   <h3 className="font-extrabold text-slate-900 dark:text-white text-xl tracking-tight">
                     Seleccionar Mercancía para Transferir
                   </h3>
                   <p className="text-xs text-slate-500 dark:text-zinc-400 mt-0.5">
                     Elige un pallet o lote activo en almacén para reubicarlo a la viga destino.
                   </p>
                 </div>
                 <button 
                   onClick={() => setMostrarModalReubicar(false)}
                   className="p-2 rounded-2xl bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-400 hover:text-slate-700 dark:text-zinc-400 dark:hover:text-white transition-all active:scale-95"
                 >
                   <XMarkIcon className="h-5 w-5" />
                 </button>
               </div>

               {/* Banner de Trayecto Destino Confirmado */}
               <div className="mt-4 p-3.5 rounded-2xl bg-gradient-to-r from-emerald-500/10 via-teal-500/5 to-blue-500/10 border border-emerald-500/20 flex items-center justify-between gap-3 flex-wrap">
                 <div className="flex items-center gap-2">
                   <div className="p-2 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                     <ArrowsRightLeftIcon className="h-4 w-4" />
                   </div>
                   <div>
                     <span className="text-[10px] font-black uppercase text-emerald-600 dark:text-emerald-400 tracking-wider block">
                       Destino Confirmado:
                     </span>
                     <span className="font-mono text-xs font-black text-slate-900 dark:text-white">
                       {accionPosicion.zonaOperativa}-{accionPosicion.pasillo}-{accionPosicion.rack}-{accionPosicion.nivel}-{accionPosicion.posicion}
                     </span>
                   </div>
                 </div>

                 <div className="flex items-center gap-2 text-xs">
                   <span className="text-[11px] font-semibold text-slate-500 dark:text-zinc-400">
                     Nivel {accionPosicion.nivel} · Pos {accionPosicion.posicion}
                   </span>
                 </div>
               </div>
             </div>

             {/* Barra de Búsqueda Reactiva en Tiempo Real */}
             <div className="p-4 border-b border-slate-100 dark:border-white/[0.06] bg-white dark:bg-transparent">
               <div className="relative">
                 <MagnifyingGlassIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                 <input 
                   type="text"
                   value={busquedaReubicar}
                   onChange={(e) => setBusquedaReubicar(e.target.value)}
                   placeholder="Buscar por SKU, nombre, marca o posición actual..."
                   className="w-full pl-10 pr-9 py-2.5 rounded-xl border border-slate-200/80 dark:border-white/10 bg-slate-50 dark:bg-white/5 text-xs font-medium text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 transition-all"
                   autoFocus
                 />
                 {busquedaReubicar && (
                   <button
                     onClick={() => setBusquedaReubicar('')}
                     className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-white"
                   >
                     <XMarkIcon className="h-4 w-4" />
                   </button>
                 )}
               </div>
               <div className="flex items-center justify-between mt-2 px-1 text-[11px] text-slate-400 dark:text-zinc-500">
                 <span>Catálogo disponible: {itemsFiltradosReubicacion.length} de {items.length} productos</span>
                 {busquedaReubicar && <span>Filtrando resultados</span>}
               </div>
             </div>

             {/* Lista de Productos con Diseño Bento */}
             <div className="p-4 flex-1 overflow-y-auto space-y-2.5 max-h-[50vh]">
               {itemsFiltradosReubicacion.length === 0 ? (
                 <div className="py-16 text-center">
                   <div className="p-4 rounded-full bg-slate-100 dark:bg-white/5 text-slate-400 dark:text-zinc-500 w-14 h-14 mx-auto mb-3 flex items-center justify-center">
                     <CubeIcon className="h-7 w-7" />
                   </div>
                   <h4 className="text-sm font-bold text-slate-900 dark:text-white">No se encontraron productos</h4>
                   <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1 max-w-xs mx-auto">
                     {busquedaReubicar 
                       ? 'No hay ningún artículo registrado que coincida con tu búsqueda.' 
                       : 'No hay productos en inventario disponibles para reubicar.'}
                   </p>
                   {busquedaReubicar && (
                     <button
                       onClick={() => setBusquedaReubicar('')}
                       className="mt-3 px-3 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-bold"
                     >
                       Limpiar búsqueda
                     </button>
                   )}
                 </div>
               ) : (
                 itemsFiltradosReubicacion.map((item) => {
                   const yaEnMismaUbicacion = 
                     item.zonaOperativa === accionPosicion.zonaOperativa &&
                     item.pasillo === accionPosicion.pasillo &&
                     item.rack === accionPosicion.rack &&
                     item.nivel === accionPosicion.nivel &&
                     item.posicion === accionPosicion.posicion;

                   return (
                     <button
                       key={item.id}
                       type="button"
                       disabled={yaEnMismaUbicacion}
                       onClick={() => handleReubicarProducto(item)}
                       className={`w-full text-left p-4 rounded-2xl border transition-all duration-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3 group relative overflow-hidden ${
                         yaEnMismaUbicacion 
                           ? 'opacity-50 cursor-not-allowed bg-slate-100/50 dark:bg-white/[0.01] border-slate-200 dark:border-white/5' 
                           : 'bg-white dark:bg-[#151722] border-slate-200/80 dark:border-white/[0.06] hover:border-emerald-500/60 dark:hover:border-emerald-500/50 hover:shadow-lg hover:shadow-emerald-500/5 cursor-pointer active:scale-[0.99]'
                       }`}
                     >
                       <div className="flex items-start gap-3.5 min-w-0">
                         {/* Thumbnail / Icono de producto */}
                         <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-white/5 dark:to-white/10 flex items-center justify-center shrink-0 border border-slate-200/80 dark:border-white/10 group-hover:border-emerald-500/30 transition-all overflow-hidden">
                           {item.imagenUrl ? (
                             <img src={item.imagenUrl} alt={item.nombre} className="w-full h-full object-cover" />
                           ) : (
                             <CubeIcon className="h-6 w-6 text-slate-500 dark:text-zinc-400 group-hover:text-emerald-500 transition-colors" />
                           )}
                         </div>

                         {/* Información del Producto */}
                         <div className="min-w-0 flex-1">
                           <div className="flex items-center gap-2 flex-wrap mb-0.5">
                             <span className="font-mono text-[11px] font-black text-blue-600 dark:text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-md">
                               {item.sku}
                             </span>
                             <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-md border ${
                               item.estado === 'Disponible' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' :
                               item.estado === 'Cuarentena' ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20' :
                               item.estado === 'Dañado' ? 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20' :
                               'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20'
                             }`}>
                               {item.estado || 'Disponible'}
                             </span>
                             {item.categoria && (
                               <span className="text-[10px] font-medium text-slate-500 dark:text-zinc-400">
                                 {item.categoria}
                               </span>
                             )}
                           </div>

                           <h4 className="font-extrabold text-slate-900 dark:text-white text-sm truncate group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                             {item.nombre || item.sku}
                           </h4>

                           {/* Trayecto Origen */}
                           <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-zinc-400 mt-1">
                             <MapPinIcon className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                             <span>Origen actual:</span>
                             <span className="font-mono font-bold text-slate-700 dark:text-zinc-300">
                               {item.zonaOperativa || 'SEC'}-{item.pasillo || '01'}-{item.rack || '01'}-{item.nivel || '01'}-{item.posicion || '01'}
                             </span>
                             {yaEnMismaUbicacion && (
                               <span className="ml-1 text-[10px] font-bold text-amber-600 dark:text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded">
                                 (Ya está aquí)
                               </span>
                             )}
                           </div>
                         </div>
                       </div>

                       {/* Stock & Acción */}
                       <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-center shrink-0 border-t sm:border-t-0 pt-2 sm:pt-0 border-slate-100 dark:border-white/5">
                         <div className="text-left sm:text-right">
                           <span className="text-base font-black text-slate-900 dark:text-white">
                             {item.cantidad} <span className="text-xs font-bold text-slate-400 dark:text-zinc-500">{item.unidadMedida || 'uds'}</span>
                           </span>
                           <p className="text-[11px] font-mono text-slate-400 dark:text-zinc-500">
                             {((Number(item.peso) || 0) * (Number(item.cantidad) || 0)).toLocaleString()} kg
                           </p>
                         </div>

                         {!yaEnMismaUbicacion && (
                           <div className="mt-1 flex items-center gap-1 text-[11px] font-bold text-emerald-600 dark:text-emerald-400 opacity-80 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all">
                             <span>Reubicar aquí</span>
                             <span className="text-sm font-black">&rarr;</span>
                           </div>
                         )}
                       </div>
                     </button>
                   );
                 })
               )}
             </div>

             {/* Footer con Indicador de Seguridad WMS */}
             <div className="p-4 bg-slate-50/80 dark:bg-white/[0.02] border-t border-slate-100 dark:border-white/[0.04] flex items-center justify-between text-xs text-slate-500 dark:text-zinc-400">
               <span className="flex items-center gap-1.5">
                 <ShieldCheckIcon className="h-4 w-4 text-emerald-500" />
                 Se registrará automáticamente en el Kardex y Ledger de Auditoría.
               </span>
               <button
                 type="button"
                 onClick={() => setMostrarModalReubicar(false)}
                 className="px-4 py-1.5 rounded-xl bg-slate-200/70 hover:bg-slate-300 dark:bg-white/5 dark:hover:bg-white/10 text-slate-700 dark:text-zinc-300 font-bold text-xs transition-all"
               >
                 Cancelar
               </button>
             </div>
           </div>
        </div>
      )}

      {/* Modal Escáner QR */}
      {mostrarScannerQR && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-900/50">
              <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <CameraIcon className="h-5 w-5 text-blue-600" />
                Escanear Código (QR / Barras)
              </h3>
              <button 
                onClick={() => setMostrarScannerQR(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 flex flex-col items-center">
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-4">
                Apunta la cámara de tu dispositivo hacia el código de barras o QR del producto.
              </p>
              
              <div className="w-full overflow-hidden rounded-xl border-2 border-dashed border-blue-400/50 relative bg-black">
                <div id="qr-reader" className="w-full"></div>
              </div>
            </div>
          </div>
        </div>
      )}

            {/* Modal Retiro / Salida de Mercancía WMS - Diseño Clean */}
      {mostrarModalSalida && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-2 sm:p-4 lg:p-6 bg-slate-900/75 dark:bg-black/85 backdrop-blur-md overflow-y-auto overflow-x-hidden">
          <div className="relative bg-white dark:bg-[#0f0f10] rounded-2xl sm:rounded-[2rem] shadow-2xl shadow-black/50 w-full max-w-4xl my-auto max-h-[94vh] flex flex-col border border-slate-200/70 dark:border-white/10 overflow-hidden animate-fadeIn">
            
            {/* Header del Modal - Estilo Clean Idéntico a Ingreso a Almacén */}
            <div className="sticky top-0 z-20 bg-white/95 dark:bg-[#0f0f10]/95 backdrop-blur-xl border-b border-slate-100 dark:border-white/5 px-4 sm:px-8 py-3.5 sm:py-5 flex justify-between items-center shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2 sm:p-2.5 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400">
                  <ArrowUpTrayIcon className="h-5 sm:h-6 w-5 sm:w-6" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-2xl font-extrabold bg-gradient-to-r from-slate-900 to-slate-600 bg-clip-text text-transparent dark:from-white dark:to-zinc-400 tracking-tight leading-tight">
                    Retiro de Mercancía
                  </h3>
                  <p className="text-xs sm:text-sm font-medium text-slate-500 dark:text-zinc-500 mt-0.5">
                    Selecciona el producto y define la cantidad a retirar con asiento automático en Kardex.
                  </p>
                </div>
              </div>
              <button 
                type="button"
                onClick={() => {
                  setMostrarModalSalida(false);
                  setSalidaItemSeleccionado(null);
                }}
                className="p-2 sm:p-2.5 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-500 dark:text-zinc-400 transition-colors"
                title="Cerrar modal"
              >
                <XMarkIcon className="h-5 sm:h-6 w-5 sm:w-6" />
              </button>
            </div>

            {/* Contenido Principal con Scroll */}
            <div className="p-4 sm:p-6 lg:p-8 flex-1 overflow-y-auto space-y-6">
              {/* Selector de Estudiante Responsable del Retiro */}
              <div className="bg-indigo-50/70 dark:bg-indigo-950/20 border border-indigo-200/80 dark:border-indigo-500/30 rounded-2xl p-4 shadow-xs space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-indigo-950 dark:text-indigo-200 flex items-center gap-1.5">
                    <AcademicCapIcon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    <span>Estudiante / Operador que Registra el Retiro</span>
                  </label>
                  <span className="text-[10px] font-bold text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/50 px-2 py-0.5 rounded-md">
                    {esMaestroOSuperior ? 'Modo Docente: Asignar a Alumno' : 'Mi Usuario'}
                  </span>
                </div>
                <select
                  value={salidaOperadorEstudianteId}
                  onChange={(e) => {
                    const id = e.target.value;
                    setSalidaOperadorEstudianteId(id);
                    const m = miembrosInstitucion.find(x => String(x.id_usuario || x.id_miembro) === id);
                    setSalidaOperadorNombre(m ? `${m.nombre} ${m.apellido}`.trim() : '');
                  }}
                  className="w-full text-xs font-semibold rounded-xl border border-indigo-200 dark:border-indigo-800 bg-white dark:bg-zinc-900 text-slate-800 dark:text-zinc-200 p-2.5 outline-none focus:ring-2 focus:ring-indigo-500 shadow-xs cursor-pointer"
                >
                  <option value="">👤 Asignar a mi usuario ({usuario?.nombre || usuario?.email})</option>
                  {miembrosInstitucion.map(m => (
                    <option key={m.id_miembro || m.id_usuario} value={m.id_usuario || m.id_miembro}>
                      {m.nombre} {m.apellido} ({m.rol_miembro || 'alumno'}) - {m.email}
                    </option>
                  ))}
                </select>
              </div>
              {/* PASO 1: SELECCIÓN DE PRODUCTO (Si no hay ninguno seleccionado) */}
              {!salidaItemSeleccionado ? (
                <div className="space-y-4">
                  {/* Buscador y Acceso a Escáner */}
                  <div className="flex flex-col sm:flex-row gap-3 items-stretch">
                    <div className="relative flex-1">
                      <MagnifyingGlassIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 dark:text-zinc-500" />
                      <input
                        type="text"
                        placeholder="Buscar por SKU, descripción, lote, ubicación o código de barras..."
                        value={salidaBusqueda}
                        onChange={(e) => setSalidaBusqueda(e.target.value)}
                        className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm font-medium text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-500 outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                      />
                      {salidaBusqueda && (
                        <button
                          type="button"
                          onClick={() => setSalidaBusqueda('')}
                          className="absolute right-3.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-full hover:bg-slate-200 dark:hover:bg-white/10 transition-colors"
                        >
                          <XMarkIcon className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setMostrarModalSalida(false);
                        setMostrarScannerQR(true);
                      }}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-700 dark:text-zinc-300 text-xs sm:text-sm font-bold hover:bg-slate-100 dark:hover:bg-white/10 transition-colors shrink-0"
                      title="Escanear código de barras o QR de producto físico"
                    >
                      <CameraIcon className="h-4 w-4 text-blue-500" />
                      <span>Escanear Código / QR</span>
                    </button>
                  </div>

                  {/* Pills de Filtrado por Categoría */}
                  {categoriasDisponiblesSalida.length > 0 && (
                    <div className="flex items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar touch-pan-x">
                      <button
                        type="button"
                        onClick={() => setSalidaCategoriaFiltro('TODAS')}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
                          salidaCategoriaFiltro === 'TODAS'
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-zinc-400 hover:bg-slate-200 dark:hover:bg-white/10 border border-slate-200/50 dark:border-white/5'
                        }`}
                      >
                        Todas ({items.length})
                      </button>
                      {categoriasDisponiblesSalida.map((cat) => {
                        const cantCat = items.filter((i) => i.categoria === cat).length;
                        return (
                          <button
                            key={cat}
                            type="button"
                            onClick={() => setSalidaCategoriaFiltro(cat)}
                            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
                              salidaCategoriaFiltro === cat
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-zinc-400 hover:bg-slate-200 dark:hover:bg-white/10 border border-slate-200/50 dark:border-white/5'
                            }`}
                          >
                            {cat} ({cantCat})
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Contenedor de Lista de Productos */}
                  <div className="border border-slate-200/70 dark:border-white/5 rounded-2xl sm:rounded-3xl max-h-[48vh] overflow-y-auto bg-slate-50/50 dark:bg-black/20 divide-y divide-slate-100 dark:divide-white/5">
                    {/* CASO A: Inventario Vacío */}
                    {items.length === 0 ? (
                      <div className="p-8 sm:p-12 flex flex-col items-center text-center">
                        <div className="p-3 sm:p-4 rounded-2xl bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20 mb-3">
                          <InboxStackIcon className="h-8 w-8 sm:h-10 sm:w-10 stroke-[1.8]" />
                        </div>
                        <h4 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white">
                          No hay mercancía registrada en este almacén
                        </h4>
                        <p className="text-xs sm:text-sm text-slate-500 dark:text-zinc-400 mt-1.5 max-w-md">
                          Actualmente no existen pallets o bultos asignados a los racks. Para realizar una salida WMS, primero debes registrar un ingreso y ubicarlo en el rack.
                        </p>
                        <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
                          <button
                            type="button"
                            onClick={() => {
                              setMostrarModalSalida(false);
                              abrirModalNuevoProducto();
                            }}
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white text-xs sm:text-sm font-bold shadow-lg shadow-blue-500/25 transition-all hover:scale-[1.02] active:scale-[0.98]"
                          >
                            <PlusIcon className="h-4 w-4" />
                            <span>+ Registrar Ingreso de Mercancía</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              setMostrarModalSalida(false);
                              setMostrarScannerQR(true);
                            }}
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-700 dark:text-zinc-300 text-xs sm:text-sm font-bold hover:bg-slate-50 dark:hover:bg-white/10 transition-colors"
                          >
                            <CameraIcon className="h-4 w-4 text-slate-400" />
                            <span>Escanear Código</span>
                          </button>
                        </div>
                      </div>
                    ) : itemsFiltradosSalida.length === 0 ? (
                      /* CASO B: Sin Coincidencias */
                      <div className="p-8 sm:p-10 flex flex-col items-center text-center">
                        <div className="h-12 w-12 rounded-xl bg-slate-100 dark:bg-white/5 text-slate-400 dark:text-zinc-400 flex items-center justify-center mb-3">
                          <MagnifyingGlassIcon className="h-6 w-6" />
                        </div>
                        <h4 className="font-bold text-sm text-slate-900 dark:text-white">
                          Sin resultados coincidentes
                        </h4>
                        <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1 max-w-sm">
                          No se encontraron artículos con el término &ldquo;{salidaBusqueda}&rdquo; en la categoría seleccionada.
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            setSalidaBusqueda('');
                            setSalidaCategoriaFiltro('TODAS');
                          }}
                          className="mt-4 px-3.5 py-1.5 rounded-xl bg-slate-200/80 dark:bg-white/10 text-xs font-bold text-slate-700 dark:text-zinc-200 hover:bg-slate-300 dark:hover:bg-white/15 transition-colors"
                        >
                          Restablecer Filtros
                        </button>
                      </div>
                    ) : (
                      /* CASO C: Lista de Productos Clean */
                      itemsFiltradosSalida.map((item) => (
                        <div
                          key={item.id}
                          onClick={() => {
                            setSalidaItemSeleccionado(item);
                            setSalidaCantidad(1);
                          }}
                          className="p-3.5 sm:p-4.5 flex items-center justify-between gap-4 hover:bg-blue-500/[0.04] dark:hover:bg-white/[0.03] cursor-pointer transition-all group"
                        >
                          <div className="min-w-0 flex-1 space-y-1.5">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-mono text-xs font-bold text-blue-600 dark:text-blue-400 bg-blue-500/10 border border-blue-500/20 px-2 py-0.5 rounded-md">
                                {item.sku}
                              </span>
                              <span className="font-bold text-sm text-slate-900 dark:text-white truncate">
                                {item.nombre}
                              </span>
                              {item.categoria && (
                                <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-md bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-zinc-400 border border-slate-200 dark:border-white/5">
                                  {item.categoria}
                                </span>
                              )}
                            </div>

                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-zinc-400">
                              <span className="font-mono text-[11px] font-bold text-cyan-600 dark:text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded border border-cyan-500/20 flex items-center gap-1">
                                <MapPinIcon className="h-3 w-3" />
                                {item.ubicacion || 'Sin Asignar'}
                              </span>

                              {item.lote && (
                                <span className="flex items-center gap-1">
                                  <TagIcon className="h-3 w-3 text-slate-400" />
                                  Lote: <strong className="text-slate-700 dark:text-zinc-300 font-mono">{item.lote}</strong>
                                </span>
                              )}

                              {item.codigoBarras && (
                                <span className="font-mono text-[11px] text-slate-400 dark:text-zinc-500 hidden sm:inline">
                                  CB: {item.codigoBarras}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="text-right shrink-0 flex items-center gap-3">
                            <div className="text-right">
                              <span className="font-mono font-bold text-sm text-slate-900 dark:text-white block">
                                {item.cantidad} {obtenerTextoUnidad(item.cantidad, item.unidadMedida)}
                              </span>
                              <span className="text-[11px] text-slate-400 dark:text-zinc-500 font-medium">
                                {((Number(item.peso) || 0) * (Number(item.cantidad) || 0)).toFixed(1)} kg · {((Number(item.volumen) || 0) * (Number(item.cantidad) || 0)).toFixed(2)} m³
                              </span>
                            </div>

                            <div className="h-8 w-8 rounded-xl bg-slate-100 group-hover:bg-blue-600 dark:bg-white/5 dark:group-hover:bg-blue-600 text-slate-400 group-hover:text-white flex items-center justify-center transition-all">
                              <ChevronRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ) : (
                /* PASO 2: CONSOLA DE CONFIGURACIÓN DE SALIDA (Producto Seleccionado) */
                <div className="space-y-5">
                  {/* Fila de Regreso */}
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => setSalidaItemSeleccionado(null)}
                      className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-blue-600 dark:text-blue-400 hover:underline transition-colors"
                    >
                      <ChevronLeftIcon className="h-4 w-4" />
                      <span>Volver a la selección de productos</span>
                    </button>
                    <span className="text-[11px] font-mono uppercase text-slate-400 dark:text-zinc-500">
                      ID ITEM: #{String(salidaItemSeleccionado.id || '').slice(0, 8)}
                    </span>
                  </div>

                  {/* Card de Producto Seleccionado - Estilo Clean */}
                  <div className="bg-slate-50/80 dark:bg-white/[0.03] border border-slate-200/70 dark:border-white/5 rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-200/60 dark:border-white/5">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs font-bold text-blue-600 dark:text-blue-400 bg-blue-500/10 border border-blue-500/20 px-2 py-0.5 rounded-md">
                            SKU: {salidaItemSeleccionado.sku}
                          </span>
                          <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                            En Existencia
                          </span>
                        </div>
                        <h4 className="text-base sm:text-xl font-bold text-slate-900 dark:text-white mt-1.5">
                          {salidaItemSeleccionado.nombre}
                        </h4>
                      </div>

                      <div className="text-left sm:text-right">
                        <span className="text-[11px] uppercase font-bold text-slate-400 dark:text-zinc-500 block">
                          Stock Disponible
                        </span>
                        <span className="font-mono font-extrabold text-lg sm:text-xl text-slate-900 dark:text-white">
                          {salidaItemSeleccionado.cantidad} {obtenerTextoUnidad(salidaItemSeleccionado.cantidad, salidaItemSeleccionado.unidadMedida)}
                        </span>
                      </div>
                    </div>

                    {/* Grilla de Métricas Técnicas */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="p-3 rounded-xl bg-white dark:bg-black/20 border border-slate-200/60 dark:border-white/5">
                        <span className="text-[10px] uppercase font-bold text-slate-400 dark:text-zinc-500 block">
                          Ubicación Física
                        </span>
                        <span className="font-mono text-xs font-bold text-cyan-600 dark:text-cyan-400 truncate block mt-0.5">
                          {salidaItemSeleccionado.ubicacion}
                        </span>
                      </div>

                      <div className="p-3 rounded-xl bg-white dark:bg-black/20 border border-slate-200/60 dark:border-white/5">
                        <span className="text-[10px] uppercase font-bold text-slate-400 dark:text-zinc-500 block">
                          Lote / Control
                        </span>
                        <span className="font-mono text-xs font-bold text-slate-800 dark:text-zinc-200 truncate block mt-0.5">
                          {salidaItemSeleccionado.lote || 'Sin Lote'}
                        </span>
                      </div>

                      <div className="p-3 rounded-xl bg-white dark:bg-black/20 border border-slate-200/60 dark:border-white/5">
                        <span className="text-[10px] uppercase font-bold text-slate-400 dark:text-zinc-500 block">
                          Masa Unitaria
                        </span>
                        <span className="font-mono text-xs font-bold text-slate-800 dark:text-zinc-200 truncate block mt-0.5">
                          {salidaItemSeleccionado.peso ? `${salidaItemSeleccionado.peso} kg` : 'N/D'}
                        </span>
                      </div>

                      <div className="p-3 rounded-xl bg-white dark:bg-black/20 border border-slate-200/60 dark:border-white/5">
                        <span className="text-[10px] uppercase font-bold text-slate-400 dark:text-zinc-500 block">
                          Volumen Unitario
                        </span>
                        <span className="font-mono text-xs font-bold text-slate-800 dark:text-zinc-200 truncate block mt-0.5">
                          {salidaItemSeleccionado.volumen ? `${salidaItemSeleccionado.volumen} m³` : 'N/D'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Panel de Cantidad Clean */}
                  <div className="bg-slate-50/80 dark:bg-white/[0.03] border border-slate-200/70 dark:border-white/5 rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-zinc-300">
                        Cantidad a Retirar ({salidaItemSeleccionado.unidadMedida || 'unidad'}s):
                      </label>
                      <span className="text-xs font-mono font-bold text-blue-600 dark:text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-lg border border-blue-500/20">
                        {salidaItemSeleccionado.cantidad > 0 ? Math.round((salidaCantidad / salidaItemSeleccionado.cantidad) * 100) : 100}% del stock
                      </span>
                    </div>

                    {/* Stepper */}
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setSalidaCantidad((prev) => Math.max(1, Number(prev || 1) - 1))}
                        disabled={salidaCantidad <= 1}
                        className="h-11 w-11 rounded-xl bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-700 dark:text-zinc-200 hover:bg-slate-100 dark:hover:bg-white/10 flex items-center justify-center transition-all disabled:opacity-30 disabled:pointer-events-none"
                        title="Disminuir una unidad"
                      >
                        <MinusIcon className="h-5 w-5 stroke-[2.5]" />
                      </button>

                      <div className="flex-1 relative">
                        <input
                          type="number"
                          min="1"
                          max={salidaItemSeleccionado.cantidad}
                          value={salidaCantidad}
                          onChange={(e) => {
                            const val = Math.max(1, Math.min(salidaItemSeleccionado.cantidad, Number(e.target.value) || 1));
                            setSalidaCantidad(val);
                          }}
                          className="w-full h-11 px-4 text-center font-mono font-extrabold text-xl rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-slate-900 dark:text-white outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400 dark:text-zinc-500 uppercase pointer-events-none">
                          {obtenerTextoUnidad(salidaCantidad, salidaItemSeleccionado.unidadMedida)}
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={() => setSalidaCantidad((prev) => Math.min(salidaItemSeleccionado.cantidad, Number(prev || 1) + 1))}
                        disabled={salidaCantidad >= salidaItemSeleccionado.cantidad}
                        className="h-11 w-11 rounded-xl bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-700 dark:text-zinc-200 hover:bg-slate-100 dark:hover:bg-white/10 flex items-center justify-center transition-all disabled:opacity-30 disabled:pointer-events-none"
                        title="Aumentar una unidad"
                      >
                        <PlusIcon className="h-5 w-5 stroke-[2.5]" />
                      </button>
                    </div>

                    {/* Presets de Cantidad */}
                    <div className="flex flex-wrap items-center gap-1.5 pt-1">
                      <button
                        type="button"
                        onClick={() => setSalidaCantidad(1)}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                          salidaCantidad === 1
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'bg-white dark:bg-white/5 text-slate-600 dark:text-zinc-400 border border-slate-200 dark:border-white/10 hover:bg-slate-100 dark:hover:bg-white/10'
                        }`}
                      >
                        1 {salidaItemSeleccionado.unidadMedida || 'unidad'}
                      </button>

                      {salidaItemSeleccionado.cantidad >= 5 && (
                        <button
                          type="button"
                          onClick={() => setSalidaCantidad(5)}
                          className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                            salidaCantidad === 5
                              ? 'bg-blue-600 text-white shadow-sm'
                              : 'bg-white dark:bg-white/5 text-slate-600 dark:text-zinc-400 border border-slate-200 dark:border-white/10 hover:bg-slate-100 dark:hover:bg-white/10'
                          }`}
                        >
                          5 {salidaItemSeleccionado.unidadMedida || 'unidad'}s
                        </button>
                      )}

                      {salidaItemSeleccionado.cantidad >= 10 && (
                        <button
                          type="button"
                          onClick={() => setSalidaCantidad(10)}
                          className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                            salidaCantidad === 10
                              ? 'bg-blue-600 text-white shadow-sm'
                              : 'bg-white dark:bg-white/5 text-slate-600 dark:text-zinc-400 border border-slate-200 dark:border-white/10 hover:bg-slate-100 dark:hover:bg-white/10'
                          }`}
                        >
                          10 {salidaItemSeleccionado.unidadMedida || 'unidad'}s
                        </button>
                      )}

                      {salidaItemSeleccionado.cantidad > 2 && (
                        <button
                          type="button"
                          onClick={() => setSalidaCantidad(Math.ceil(salidaItemSeleccionado.cantidad / 2))}
                          className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                            salidaCantidad === Math.ceil(salidaItemSeleccionado.cantidad / 2)
                              ? 'bg-blue-600 text-white shadow-sm'
                              : 'bg-white dark:bg-white/5 text-slate-600 dark:text-zinc-400 border border-slate-200 dark:border-white/10 hover:bg-slate-100 dark:hover:bg-white/10'
                          }`}
                        >
                          50% ({Math.ceil(salidaItemSeleccionado.cantidad / 2)})
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => setSalidaCantidad(salidaItemSeleccionado.cantidad)}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ml-auto ${
                          salidaCantidad === salidaItemSeleccionado.cantidad
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20 hover:bg-blue-100 dark:hover:bg-blue-500/20'
                        }`}
                      >
                        Retiro Total ({salidaItemSeleccionado.cantidad})
                      </button>
                    </div>
                  </div>

                  {/* Impacto en Existencias y Carga */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-2xl bg-slate-50/80 dark:bg-white/[0.03] border border-slate-200/70 dark:border-white/5 space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-bold text-slate-700 dark:text-zinc-300 uppercase tracking-wide">
                          Balance de Stock
                        </span>
                        <span className="font-mono text-[11px] text-slate-400 dark:text-zinc-500">
                          {salidaItemSeleccionado.cantidad} → {salidaItemSeleccionado.cantidad - salidaCantidad}
                        </span>
                      </div>
                      <div className="flex items-center justify-between pt-1">
                        <div>
                          <span className="text-[10px] uppercase font-bold text-slate-400 dark:text-zinc-500 block">
                            Stock Remanente
                          </span>
                          <strong className={`font-mono text-base font-extrabold ${
                            salidaItemSeleccionado.cantidad - salidaCantidad === 0 
                              ? 'text-amber-600 dark:text-amber-400' 
                              : 'text-emerald-600 dark:text-emerald-400'
                          }`}>
                            {salidaItemSeleccionado.cantidad - salidaCantidad} {obtenerTextoUnidad(salidaItemSeleccionado.cantidad - salidaCantidad, salidaItemSeleccionado.unidadMedida)}
                          </strong>
                        </div>
                        {salidaItemSeleccionado.cantidad - salidaCantidad === 0 ? (
                          <span className="text-[10px] font-bold uppercase text-amber-600 dark:text-amber-400 bg-amber-500/10 px-2.5 py-1 rounded-lg border border-amber-500/20">
                            Hueco Quedará Libre
                          </span>
                        ) : (
                          <span className="text-[10px] font-bold uppercase text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-lg border border-emerald-500/20">
                            Pallet en Rack
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="p-4 rounded-2xl bg-slate-50/80 dark:bg-white/[0.03] border border-slate-200/70 dark:border-white/5 space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-bold text-slate-700 dark:text-zinc-300 uppercase tracking-wide">
                          Carga Egresada
                        </span>
                        <ScaleIcon className="h-4 w-4 text-blue-500" />
                      </div>
                      <div className="grid grid-cols-2 gap-2 pt-1">
                        <div>
                          <span className="text-[10px] uppercase font-bold text-slate-400 dark:text-zinc-500 block">
                            Peso Total
                          </span>
                          <strong className="font-mono text-sm font-bold text-slate-900 dark:text-white block">
                            {((Number(salidaItemSeleccionado.peso) || 0) * salidaCantidad).toFixed(1)} kg
                          </strong>
                        </div>
                        <div>
                          <span className="text-[10px] uppercase font-bold text-slate-400 dark:text-zinc-500 block">
                            Volumen Libre
                          </span>
                          <strong className="font-mono text-sm font-bold text-slate-900 dark:text-white block">
                            {((Number(salidaItemSeleccionado.volumen) || 0) * salidaCantidad).toFixed(3)} m³
                          </strong>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Destino del Retiro: Contenedor vs Almacén */}
                  <div className="p-4 sm:p-5 rounded-2xl bg-slate-50/80 dark:bg-white/[0.03] border border-slate-200/70 dark:border-white/5 space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-200/60 dark:border-white/5 pb-3">
                      <div className="flex items-center gap-2">
                        <span className="p-1.5 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400">
                          <TruckIcon className="h-4 w-4" />
                        </span>
                        <div>
                          <h4 className="text-xs font-bold text-slate-800 dark:text-white uppercase tracking-wider">
                            Destino del Retiro Logístico
                          </h4>
                          <p className="text-[11px] text-slate-500 dark:text-zinc-400">
                            Especifica si el egreso se carga a un contenedor intermodal o se traslada a otro almacén/cliente.
                          </p>
                        </div>
                      </div>

                      {/* Toggle Selector */}
                      <div className="inline-flex p-1 rounded-xl bg-slate-200/70 dark:bg-white/10 self-start sm:self-auto">
                        <button
                          type="button"
                          onClick={() => setSalidaTipoDestino('CONTENEDOR')}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                            salidaTipoDestino === 'CONTENEDOR'
                              ? 'bg-blue-600 text-white shadow-sm'
                              : 'text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
                          }`}
                        >
                          <span>🚢</span>
                          <span>Salida a Contenedor</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setSalidaTipoDestino('ALMACEN')}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                            salidaTipoDestino === 'ALMACEN'
                              ? 'bg-emerald-600 text-white shadow-sm'
                              : 'text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
                          }`}
                        >
                          <span>🏢</span>
                          <span>Salida a Almacén</span>
                        </button>
                      </div>
                    </div>

                    {/* Formulario Dinámico según Selección - Totalmente Editable por el Usuario */}
                    {salidaTipoDestino === 'CONTENEDOR' ? (
                      <div className="space-y-3.5">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-[10px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1">
                              No. Contenedor (ISO 6346)
                            </label>
                            <input
                              type="text"
                              maxLength={13}
                              placeholder="Escribe el No. (ej. TGHU 901234-5)"
                              value={salidaContenedorNumero}
                              onChange={(e) => setSalidaContenedorNumero(e.target.value.toUpperCase())}
                              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs font-mono font-bold text-slate-900 dark:text-white outline-none focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1">
                              Tipo / Medida Contenedor
                            </label>
                            <input
                              type="text"
                              placeholder="Escribe tipo (ej. 40HC, 20DC, Reefer)"
                              value={salidaContenedorTipo}
                              onChange={(e) => {
                                const val = e.target.value;
                                setSalidaContenedorTipo(val);
                                const cleanKey = val.toUpperCase().replace(/[^A-Z0-9]/g, '');
                                const taraAuto = TABLA_TARAS_CONTENEDOR[cleanKey];
                                if (taraAuto) setSalidaTaraContenedor(taraAuto);
                              }}
                              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs font-medium text-slate-900 dark:text-white outline-none focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1">
                              Candado / Sello Fiscal SAT
                            </label>
                            <input
                              type="text"
                              placeholder="Escribe sello fiscal (ej. SAT-994182)"
                              value={salidaSelloFiscal}
                              onChange={(e) => setSalidaSelloFiscal(e.target.value.toUpperCase())}
                              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs font-mono font-bold text-slate-900 dark:text-white outline-none focus:border-blue-500"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                          <div>
                            <label className="block text-[10px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1">
                              Booking Portuario / Reserva
                            </label>
                            <input
                              type="text"
                              placeholder="Escribe Booking de reserva"
                              value={salidaBooking}
                              onChange={(e) => setSalidaBooking(e.target.value.toUpperCase())}
                              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs font-mono text-slate-900 dark:text-white outline-none focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1">
                              Línea Naviera / Buque
                            </label>
                            <input
                              type="text"
                              placeholder="Escribe naviera / buque"
                              value={salidaNaviera}
                              onChange={(e) => setSalidaNaviera(e.target.value)}
                              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs text-slate-900 dark:text-white outline-none focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1">
                              Terminal Portuaria Destino
                            </label>
                            <input
                              type="text"
                              placeholder="Escribe terminal destino"
                              value={salidaTerminalPortuaria}
                              onChange={(e) => setSalidaTerminalPortuaria(e.target.value)}
                              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs font-medium text-slate-900 dark:text-white outline-none focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1">
                              Tara Contenedor (kg)
                            </label>
                            <input
                              type="number"
                              min="0"
                              placeholder="Escribe tara (ej. 3820)"
                              value={salidaTaraContenedor || ''}
                              onChange={(e) => setSalidaTaraContenedor(Number(e.target.value) || 0)}
                              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs font-mono font-bold text-slate-900 dark:text-white outline-none focus:border-blue-500"
                            />
                          </div>
                        </div>

                        {/* Caja de Cálculo VGM (SOLAS) */}
                        <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs">
                          <div className="flex items-center gap-2">
                            <ScaleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0" />
                            <div>
                              <span className="font-bold text-blue-900 dark:text-blue-200 block">
                                Cálculo VGM (Verified Gross Mass) · OMI / SOLAS
                              </span>
                              <span className="text-[11px] text-blue-700/80 dark:text-blue-300/80">
                                {salidaTaraContenedor > 0 
                                  ? `Tara (${salidaTaraContenedor} kg) + Carga retirada (${((salidaItemSeleccionado?.peso || 0) * salidaCantidad).toFixed(1)} kg)`
                                  : `Carga retirada: ${((salidaItemSeleccionado?.peso || 0) * salidaCantidad).toFixed(1)} kg · Escribe la tara arriba para calcular el VGM final.`}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-[10px] uppercase font-bold text-blue-600 dark:text-blue-400 block">VGM Estimado</span>
                            <span className="font-mono text-sm font-extrabold text-blue-900 dark:text-blue-100">
                              {(salidaTaraContenedor + ((salidaItemSeleccionado?.peso || 0) * salidaCantidad)).toFixed(1)} kg
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3.5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[10px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1">
                              Almacén Destino / Cliente / Sede
                            </label>
                            <input
                              type="text"
                              placeholder="Escribe almacén o cliente destino (ej. CEDIS Monterrey)"
                              value={salidaAlmacenDestino}
                              onChange={(e) => setSalidaAlmacenDestino(e.target.value)}
                              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs font-medium text-slate-900 dark:text-white outline-none focus:border-emerald-500"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1">
                              No. Orden de Salida / Remisión
                            </label>
                            <input
                              type="text"
                              placeholder="Escribe remisión u orden (ej. REM-SAL-2024-912)"
                              value={salidaRemision}
                              onChange={(e) => setSalidaRemision(e.target.value.toUpperCase())}
                              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs font-mono font-bold text-slate-900 dark:text-white outline-none focus:border-emerald-500"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-[10px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1">
                              Tipo de Transporte Terrestre
                            </label>
                            <input
                              type="text"
                              placeholder="Escribe tipo transporte (ej. Caja Seca 53ft, Torton 15T)"
                              value={salidaTipoTransporte}
                              onChange={(e) => setSalidaTipoTransporte(e.target.value)}
                              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs font-medium text-slate-900 dark:text-white outline-none focus:border-emerald-500"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1">
                              Placas de la Unidad
                            </label>
                            <input
                              type="text"
                              placeholder="Escribe placas (ej. 45-AA-7B)"
                              value={salidaPlacas}
                              onChange={(e) => setSalidaPlacas(e.target.value.toUpperCase())}
                              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs font-mono font-bold text-slate-900 dark:text-white outline-none focus:border-emerald-500"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1">
                              Operador / Fletera
                            </label>
                            <input
                              type="text"
                              placeholder="Escribe operador / chofer (ej. Fletes Hesa / R. Morales)"
                              value={salidaOperador}
                              onChange={(e) => setSalidaOperador(e.target.value)}
                              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs text-slate-900 dark:text-white outline-none focus:border-emerald-500"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Motivo de la Operación - Editable */}
                  <div className="space-y-1.5">
                    <label className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wide">
                      Motivo del Retiro / Operación:
                    </label>
                    <input
                      type="text"
                      list="motivos-salida-list"
                      placeholder="Escribe el motivo del retiro (ej. Despacho a Cliente, Embarque a Contenedor, etc.)"
                      value={salidaMotivo}
                      onChange={(e) => setSalidaMotivo(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm font-medium text-slate-900 dark:text-white outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                    />
                    <datalist id="motivos-salida-list">
                      <option value="Despacho a Cliente / Pedido de Venta" />
                      <option value="Embarque a Contenedor / Exportación Portuaria" />
                      <option value="Transferencia Inter-Almacén / Otra Sede" />
                      <option value="Avería / Merma / Calidad" />
                      <option value="Práctica Académica / Simulación WMS" />
                      <option value="Ajuste de Inventario / Corrección de Conteo" />
                    </datalist>
                  </div>
                </div>
              )}
            </div>

            {/* Footer del Modal - Idéntico a Ingreso a Almacén */}
            <div className="sticky bottom-0 z-20 bg-white/95 dark:bg-[#0f0f10]/95 backdrop-blur-xl border-t border-slate-100 dark:border-white/5 px-4 sm:px-8 py-3.5 sm:py-4 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
              <div className="flex items-center gap-2 text-xs font-mono text-slate-500 dark:text-zinc-400">
                {salidaItemSeleccionado ? (
                  <span>
                    Ubicación actual: <strong className="text-slate-800 dark:text-zinc-200 font-bold">{salidaItemSeleccionado.ubicacion}</strong>
                  </span>
                ) : (
                  <span>
                    Almacén activo: <strong className="text-slate-800 dark:text-zinc-200 font-bold">{configuracionAlmacen.codigo}</strong>
                  </span>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => {
                    setMostrarModalSalida(false);
                    setSalidaItemSeleccionado(null);
                  }}
                  disabled={procesandoSalida}
                  className="flex-1 sm:flex-initial px-5 py-2.5 text-sm font-bold text-slate-600 dark:text-zinc-300 bg-white dark:bg-white/5 hover:bg-slate-100 dark:hover:bg-white/10 rounded-xl transition-colors border border-slate-200/50 dark:border-white/10 shadow-sm disabled:opacity-50"
                >
                  Cancelar
                </button>

                <button
                  type="button"
                  onClick={handleConfirmarSalida}
                  disabled={
                    !salidaItemSeleccionado ||
                    salidaCantidad <= 0 ||
                    salidaCantidad > (salidaItemSeleccionado?.cantidad || 0) ||
                    procesandoSalida
                  }
                  className="flex-1 sm:flex-initial px-7 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 rounded-xl shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all hover:-translate-y-0.5 active:scale-95 disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center gap-2"
                >
                  {procesandoSalida ? (
                    <>
                      <ArrowPathIcon className="h-4 w-4 animate-spin" />
                      <span>Procesando Retiro...</span>
                    </>
                  ) : (
                    <>
                      <ArrowUpTrayIcon className="h-4 w-4 stroke-[2]" />
                      <span>
                        {!salidaItemSeleccionado
                          ? 'Seleccionar Producto'
                          : `Confirmar Salida (${salidaCantidad} ${obtenerTextoUnidad(salidaCantidad, salidaItemSeleccionado?.unidadMedida)})`}
                      </span>
                    </>
                  )}
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

{/* Modal Nuevo Ingreso */}
      {mostrarModalNuevo && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-2 sm:p-4 lg:p-6 bg-slate-900/75 dark:bg-black/85 backdrop-blur-md overflow-y-auto overflow-x-hidden">
          <div className="relative bg-white dark:bg-[#0f0f10] rounded-2xl sm:rounded-[2rem] shadow-2xl shadow-black/50 w-full max-w-[95vw] xl:max-w-[92vw] 2xl:max-w-[1500px] my-auto max-h-[96vh] flex flex-col border border-slate-200/70 dark:border-white/10 overflow-hidden animate-fadeIn">
            {/* Header del Modal */}
            <div className="sticky top-0 z-20 bg-white/95 dark:bg-[#0f0f10]/95 backdrop-blur-xl border-b border-slate-100 dark:border-white/5 px-4 sm:px-8 py-3.5 sm:py-5 flex justify-between items-center shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2 sm:p-2.5 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400">
                  <CubeIcon className="h-5 sm:h-6 w-5 sm:w-6" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-2xl font-extrabold bg-gradient-to-r from-slate-900 to-slate-600 bg-clip-text text-transparent dark:from-white dark:to-zinc-400 tracking-tight leading-tight">
                    {modoEdicion ? 'Editar Producto Logístico' : 'Ingreso a Almacén Logístico'}
                  </h3>
                  <p className="text-xs sm:text-sm font-medium text-slate-500 dark:text-zinc-500 mt-0.5">
                    Complete los detalles del producto y seleccione el hueco físico en el rack.
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setMostrarModalNuevo(false)}
                className="p-2 sm:p-2.5 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-500 dark:text-zinc-400 transition-colors"
                title="Cerrar modal"
              >
                <XMarkIcon className="h-5 sm:h-6 w-5 sm:w-6" />
              </button>
            </div>
            
            <div className="p-3 sm:p-6 lg:p-8 flex-1 overflow-y-auto scrollbar-thin">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
                {/* Columna Izquierda - Datos del Producto (lg:col-span-5) */}
                <div className="lg:col-span-5 space-y-5">
                  {/* Selector de Estudiante Asignado a la Práctica */}
                  <div className="bg-indigo-50/70 dark:bg-indigo-950/20 border border-indigo-200/80 dark:border-indigo-500/30 rounded-2xl sm:rounded-3xl p-4 sm:p-5 shadow-xs space-y-2.5">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5">
                      <label className="text-xs font-bold text-indigo-950 dark:text-indigo-200 flex items-center gap-1.5">
                        <AcademicCapIcon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                        <span>Estudiante / Operador de la Práctica</span>
                      </label>
                      <span className="text-[10px] font-bold text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/50 px-2 py-0.5 rounded-md self-start sm:self-auto">
                        {esMaestroOSuperior ? 'Modo Docente: Asignar a Estudiante' : 'Mi Usuario'}
                      </span>
                    </div>
                    <select
                      value={operadorAsignadoEstudianteId}
                      onChange={(e) => {
                        const id = e.target.value;
                        setOperadorAsignadoEstudianteId(id);
                        const m = miembrosInstitucion.find(x => String(x.id_usuario || x.id_miembro) === id);
                        setOperadorAsignadoNombre(m ? `${m.nombre} ${m.apellido}`.trim() : '');
                      }}
                      className="w-full text-xs font-semibold rounded-xl border border-indigo-200 dark:border-indigo-800 bg-white dark:bg-zinc-900 text-slate-800 dark:text-zinc-200 p-2.5 outline-none focus:ring-2 focus:ring-indigo-500 shadow-xs cursor-pointer"
                    >
                      <option value="">👤 Asignar a mi usuario ({usuario?.nombre || usuario?.email})</option>
                      {miembrosInstitucion.map(m => (
                        <option key={m.id_miembro || m.id_usuario} value={m.id_usuario || m.id_miembro}>
                          {m.nombre} {m.apellido} ({m.rol_miembro || 'alumno'}) - {m.email}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Procedencia / Tipo de Ingreso */}
                  <div className="bg-slate-50/80 dark:bg-white/[0.03] border border-slate-200/70 dark:border-white/5 rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-200/60 dark:border-white/5 pb-3">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl text-blue-600 dark:text-blue-400">
                          <TruckIcon className="h-5 w-5" />
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-800 dark:text-white text-sm uppercase tracking-wider">
                            Procedencia del Ingreso
                          </h4>
                          <p className="text-[11px] text-slate-500 dark:text-zinc-400">
                            Define si la mercancía arriba en contenedor marítimo o desde almacén nacional.
                          </p>
                        </div>
                      </div>

                      {/* Toggle Pills */}
                      <div className="inline-flex p-1 rounded-xl bg-slate-200/70 dark:bg-white/10 self-start sm:self-auto">
                        <button
                          type="button"
                          onClick={() => setNuevoItem(prev => ({ ...prev, tipoEntrada: 'CONTENEDOR' }))}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                            (nuevoItem.tipoEntrada || 'CONTENEDOR') === 'CONTENEDOR'
                              ? 'bg-blue-600 text-white shadow-sm'
                              : 'text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
                          }`}
                        >
                          <span>🚢</span>
                          <span>Desde Contenedor</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setNuevoItem(prev => ({ ...prev, tipoEntrada: 'ALMACEN' }))}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                            nuevoItem.tipoEntrada === 'ALMACEN'
                              ? 'bg-emerald-600 text-white shadow-sm'
                              : 'text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
                          }`}
                        >
                          <span>🏢</span>
                          <span>Desde Almacén</span>
                        </button>
                      </div>
                    </div>

                    {/* Formulario Dinámico según Selección - Totalmente Editable por el Usuario */}
                    {(nuevoItem.tipoEntrada || 'CONTENEDOR') === 'CONTENEDOR' ? (
                      <div className="space-y-3.5">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1">
                              No. Contenedor (ISO 6346)
                            </label>
                            <input
                              type="text"
                              maxLength={13}
                              placeholder="Escribe el No. (ej. MSKU 748291-0)"
                              value={nuevoItem.contenedorNumero ?? ''}
                              onChange={(e) => setNuevoItem(prev => ({ ...prev, contenedorNumero: e.target.value.toUpperCase() }))}
                              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs font-mono font-bold text-slate-900 dark:text-white outline-none focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1">
                              Tipo de Contenedor
                            </label>
                            <input
                              type="text"
                              placeholder="Escribe tipo (ej. 40HC, 20DC, Reefer)"
                              value={nuevoItem.contenedorTipo ?? ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                const cleanKey = val.toUpperCase().replace(/[^A-Z0-9]/g, '');
                                const taraAuto = TABLA_TARAS_CONTENEDOR[cleanKey];
                                setNuevoItem(prev => ({
                                  ...prev,
                                  contenedorTipo: val,
                                  taraContenedorKg: taraAuto || prev.taraContenedorKg || 0
                                }));
                              }}
                              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs font-medium text-slate-900 dark:text-white outline-none focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1">
                              Candado / Sello Fiscal
                            </label>
                            <input
                              type="text"
                              placeholder="Escribe sello fiscal (ej. SAT-849102)"
                              value={nuevoItem.selloFiscal ?? ''}
                              onChange={(e) => setNuevoItem(prev => ({ ...prev, selloFiscal: e.target.value.toUpperCase() }))}
                              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs font-mono font-bold text-slate-900 dark:text-white outline-none focus:border-blue-500"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                          <div>
                            <label className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1">
                              Booking / B/L (Bill of Lading)
                            </label>
                            <input
                              type="text"
                              placeholder="Escribe Booking / B/L"
                              value={nuevoItem.bookingBL ?? ''}
                              onChange={(e) => setNuevoItem(prev => ({ ...prev, bookingBL: e.target.value.toUpperCase() }))}
                              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs font-mono text-slate-900 dark:text-white outline-none focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1">
                              Línea Naviera
                            </label>
                            <input
                              type="text"
                              placeholder="Escribe naviera (ej. Maersk, MSC, ONE)"
                              value={nuevoItem.naviera ?? ''}
                              onChange={(e) => setNuevoItem(prev => ({ ...prev, naviera: e.target.value }))}
                              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs text-slate-900 dark:text-white outline-none focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1">
                              Puerto / Terminal de Arribo
                            </label>
                            <input
                              type="text"
                              placeholder="Escribe puerto (ej. SSA Manzanillo)"
                              value={nuevoItem.puertoOrigen ?? ''}
                              onChange={(e) => setNuevoItem(prev => ({ ...prev, puertoOrigen: e.target.value }))}
                              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs text-slate-900 dark:text-white outline-none focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1">
                              Tara Contenedor (kg)
                            </label>
                            <input
                              type="number"
                              min="0"
                              placeholder="Escribe tara (ej. 3820)"
                              value={nuevoItem.taraContenedorKg || ''}
                              onChange={(e) => setNuevoItem(prev => ({ ...prev, taraContenedorKg: Number(e.target.value) || 0 }))}
                              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs font-mono font-bold text-slate-900 dark:text-white outline-none focus:border-blue-500"
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3.5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1">
                              Almacén o Planta de Procedencia
                            </label>
                            <input
                              type="text"
                              placeholder="Escribe el almacén de origen (ej. CEDIS Guadalajara)"
                              value={nuevoItem.almacenOrigen ?? ''}
                              onChange={(e) => setNuevoItem(prev => ({ ...prev, almacenOrigen: e.target.value }))}
                              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs font-medium text-slate-900 dark:text-white outline-none focus:border-emerald-500"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1">
                              Factura / Remisión / Pedimento
                            </label>
                            <input
                              type="text"
                              placeholder="Escribe remisión o factura (ej. REM-2024-8841)"
                              value={nuevoItem.remisionFactura ?? ''}
                              onChange={(e) => setNuevoItem(prev => ({ ...prev, remisionFactura: e.target.value.toUpperCase() }))}
                              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs font-mono font-bold text-slate-900 dark:text-white outline-none focus:border-emerald-500"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1">
                              Tipo de Transporte Terrestre
                            </label>
                            <input
                              type="text"
                              placeholder="Escribe tipo transporte (ej. Caja Seca 53ft, Torton 15T)"
                              value={nuevoItem.tipoTransporte ?? ''}
                              onChange={(e) => setNuevoItem(prev => ({ ...prev, tipoTransporte: e.target.value }))}
                              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs font-medium text-slate-900 dark:text-white outline-none focus:border-emerald-500"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1">
                              Placas de la Unidad
                            </label>
                            <input
                              type="text"
                              placeholder="Escribe placas (ej. 45-AA-7B)"
                              value={nuevoItem.placasTransporte ?? ''}
                              onChange={(e) => setNuevoItem(prev => ({ ...prev, placasTransporte: e.target.value.toUpperCase() }))}
                              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs font-mono font-bold text-slate-900 dark:text-white outline-none focus:border-emerald-500"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1">
                              Operador / Fletera
                            </label>
                            <input
                              type="text"
                              placeholder="Escribe operador / chofer (ej. Transportes Castores / J. Pérez)"
                              value={nuevoItem.operadorChofer ?? ''}
                              onChange={(e) => setNuevoItem(prev => ({ ...prev, operadorChofer: e.target.value }))}
                              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs text-slate-900 dark:text-white outline-none focus:border-emerald-500"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Identificación */}
                  <div className="bg-slate-50/80 dark:bg-white/[0.03] border border-slate-200/70 dark:border-white/5 rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl text-blue-600 dark:text-blue-400">
                        <CubeIcon className="h-5 w-5" />
                      </div>
                      <h4 className="font-bold text-slate-800 dark:text-white text-sm uppercase tracking-wider">Identificación</h4>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                        <div>
                          <label htmlFor="almacen-practica-item-sku" className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1 uppercase tracking-wide">SKU (Código)</label>
                          <input 
                            id="almacen-practica-item-sku"
                            name="nuevoItem.sku"
                            type="text" 
                            className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 font-medium"
                            value={nuevoItem.sku ?? ''}
                            onChange={e => setNuevoItem({...nuevoItem, sku: e.target.value})}
                            placeholder="Ej. PAL-001"
                          />
                        </div>
                        <div>
                          <label htmlFor="almacen-practica-item-numero-serie" className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1 uppercase tracking-wide">Número de Serie / IMEI</label>
                          <input 
                            id="almacen-practica-item-numero-serie"
                            name="nuevoItem.numeroSerie"
                            type="text" 
                            className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                            value={nuevoItem.numeroSerie ?? ''}
                            onChange={e => setNuevoItem({...nuevoItem, numeroSerie: e.target.value})}
                            placeholder="Para electrónicos"
                          />
                        </div>
                      </div>

                      {/* Notificación limpia de reutilización de SKU */}
                      {validacionSkuReutilizable.esExistente && validacionSkuReutilizable.itemExistente && (
                        <div className={`p-3 rounded-xl border text-xs transition-all ${
                          validacionSkuReutilizable.tieneDiferenciaNombre || validacionSkuReutilizable.tieneDiferenciaTamano
                            ? 'bg-amber-500/10 border-amber-500/30 text-amber-900 dark:text-amber-200'
                            : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-900 dark:text-emerald-200'
                        }`}>
                          <div className="flex items-start gap-2.5">
                            <span className={`p-1 rounded-lg flex-shrink-0 mt-0.5 ${
                              validacionSkuReutilizable.tieneDiferenciaNombre || validacionSkuReutilizable.tieneDiferenciaTamano
                                ? 'bg-amber-500/20 text-amber-600 dark:text-amber-400'
                                : 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400'
                            }`}>
                              {validacionSkuReutilizable.tieneDiferenciaNombre || validacionSkuReutilizable.tieneDiferenciaTamano ? (
                                <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                              ) : (
                                <CheckCircleIcon className="w-3.5 h-3.5" />
                              )}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2 flex-wrap">
                                <p className="font-semibold text-xs">
                                  {validacionSkuReutilizable.tieneDiferenciaNombre || validacionSkuReutilizable.tieneDiferenciaTamano ? (
                                    <>SKU {validacionSkuReutilizable.itemExistente.sku} en uso por otro producto o tamaño</>
                                  ) : (
                                    <>SKU {validacionSkuReutilizable.itemExistente.sku} reconocido (Mismo producto y tamaño)</>
                                  )}
                                </p>
                                {(validacionSkuReutilizable.tieneDiferenciaNombre || validacionSkuReutilizable.tieneDiferenciaTamano || validacionSkuReutilizable.esNombreVacio) && (
                                  <button
                                    type="button"
                                    onClick={() => handleAutoCompletarDesdeSku(validacionSkuReutilizable.itemExistente!)}
                                    className="text-[11px] font-semibold px-2 py-0.5 rounded-lg bg-white/80 dark:bg-zinc-800/80 hover:bg-white dark:hover:bg-zinc-700 border border-current shadow-xs transition-colors cursor-pointer"
                                  >
                                    Copiar datos del SKU
                                  </button>
                                )}
                              </div>
                              <p className="text-[11px] opacity-85 mt-1 leading-relaxed">
                                {validacionSkuReutilizable.tieneDiferenciaNombre ? (
                                  <span>Pertenece a <strong>"{validacionSkuReutilizable.itemExistente.nombre}"</strong>. Para reutilizar este SKU debe ser exactamente el mismo producto. </span>
                                ) : null}
                                {validacionSkuReutilizable.tieneDiferenciaTamano ? (
                                  <span>Medidas registradas: <strong>{validacionSkuReutilizable.itemExistente.largo || 0}×{validacionSkuReutilizable.itemExistente.ancho || 0}×{validacionSkuReutilizable.itemExistente.alto || 0} m</strong>. Deben ser del mismo tamaño físico para compartir SKU. </span>
                                ) : null}
                                {!validacionSkuReutilizable.tieneDiferenciaNombre && !validacionSkuReutilizable.tieneDiferenciaTamano && (
                                  <span>Producto válido: <strong>{validacionSkuReutilizable.itemExistente.nombre}</strong> ({validacionSkuReutilizable.itemExistente.largo || 0}×{validacionSkuReutilizable.itemExistente.ancho || 0}×{validacionSkuReutilizable.itemExistente.alto || 0} m). Se registrará una nueva unidad/tarima en el rack.</span>
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div>
                        <label htmlFor="almacen-practica-item-nombre" className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1 uppercase tracking-wide">Nombre del Producto</label>
                        <input 
                          id="almacen-practica-item-nombre"
                          name="nuevoItem.nombre"
                          type="text" 
                          className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 font-medium"
                          value={nuevoItem.nombre ?? ''}
                          onChange={e => setNuevoItem({...nuevoItem, nombre: e.target.value})}
                          placeholder="Ej. Pallet de cajas de cartón"
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <label htmlFor="almacen-practica-item-marca" className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1 uppercase tracking-wide">Marca</label>
                          <input 
                            id="almacen-practica-item-marca"
                            name="nuevoItem.marca"
                            type="text" 
                            className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                            value={nuevoItem.marca ?? ''}
                            onChange={e => setNuevoItem({...nuevoItem, marca: e.target.value})}
                          />
                        </div>
                        <div>
                          <label htmlFor="almacen-practica-item-modelo" className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1 uppercase tracking-wide">Modelo</label>
                          <input 
                            id="almacen-practica-item-modelo"
                            name="nuevoItem.modelo"
                            type="text" 
                            className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                            value={nuevoItem.modelo ?? ''}
                            onChange={e => setNuevoItem({...nuevoItem, modelo: e.target.value})}
                          />
                        </div>
                        <div>
                          <label htmlFor="almacen-practica-item-lote" className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1 uppercase tracking-wide">Lote / Serie</label>
                          <input 
                            id="almacen-practica-item-lote"
                            name="nuevoItem.lote"
                            type="text" 
                            className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                            value={nuevoItem.lote ?? ''}
                            onChange={e => setNuevoItem({...nuevoItem, lote: e.target.value})}
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="almacen-practica-item-descripcion" className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1 uppercase tracking-wide">Descripción</label>
                        <textarea 
                          id="almacen-practica-item-descripcion"
                          name="nuevoItem.descripcion"
                          className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 resize-none h-20"
                          value={nuevoItem.descripcion ?? ''}
                          onChange={e => setNuevoItem({...nuevoItem, descripcion: e.target.value})}
                          placeholder="Observaciones o notas del lote..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* Logística */}
                  <div className="bg-slate-50/80 dark:bg-white/[0.03] border border-slate-200/70 dark:border-white/5 rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl text-emerald-600 dark:text-emerald-400">
                        <TruckIcon className="h-5 w-5" />
                      </div>
                      <h4 className="font-bold text-slate-800 dark:text-white text-sm uppercase tracking-wider">Logística</h4>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-2.5">
                        <div>
                          <label htmlFor="almacen-practica-item-cantidad" className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1 uppercase tracking-wide">Cantidad</label>
                          <input 
                            id="almacen-practica-item-cantidad"
                            name="nuevoItem.cantidad"
                            type="number" 
                            min="1"
                            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm outline-none transition-all focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-center font-bold"
                            value={nuevoItem.cantidad ?? 1}
                            onChange={e => setNuevoItem({...nuevoItem, cantidad: Number(e.target.value)})}
                          />
                        </div>
                        <div>
                          <label htmlFor="almacen-practica-item-unidad-medida" className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1 uppercase tracking-wide">Ud. Medida</label>
                          <select 
                            id="almacen-practica-item-unidad-medida"
                            name="nuevoItem.unidadMedida"
                            className="w-full px-2.5 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs sm:text-sm outline-none transition-all focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 appearance-none font-medium"
                            value={(nuevoItem.unidadMedida as any) ?? 'unidad'}
                            onChange={e => setNuevoItem({...nuevoItem, unidadMedida: e.target.value as any})}
                          >
                            <option value="unidad">Unidad (PZA)</option>
                            <option value="caja">Caja (CJ)</option>
                            <option value="pallet">Pallet (PLT)</option>
                            <option value="kg">Kilogramos (KG)</option>
                            <option value="litro">Litros (L)</option>
                          </select>
                        </div>
                        <div>
                          <label htmlFor="almacen-practica-item-estado" className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1 uppercase tracking-wide">Estado</label>
                          <select 
                            id="almacen-practica-item-estado"
                            name="nuevoItem.estado"
                            className="w-full px-2.5 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs sm:text-sm outline-none transition-all focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 appearance-none font-medium"
                            value={(nuevoItem.estado as any) ?? 'Disponible'}
                            onChange={e => setNuevoItem({...nuevoItem, estado: e.target.value as any})}
                          >
                            <option value="Disponible">🟢 Disponible</option>
                            <option value="Reservado">🟡 Reservado</option>
                            <option value="Cuarentena">🟠 Cuarentena</option>
                            <option value="Dañado">🔴 Dañado</option>
                            <option value="Vencido">⚫ Vencido</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                        <div>
                          <label htmlFor="almacen-practica-item-categoria" className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1 uppercase tracking-wide">Categoría</label>
                          <input 
                            id="almacen-practica-item-categoria"
                            name="nuevoItem.categoria"
                            type="text" 
                            className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm outline-none transition-all focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                            value={nuevoItem.categoria ?? ''}
                            onChange={e => setNuevoItem({...nuevoItem, categoria: e.target.value})}
                            placeholder="Ej. Alimentos / Químicos"
                          />
                        </div>
                        <div>
                          <label htmlFor="almacen-practica-item-proveedor" className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1 uppercase tracking-wide">Proveedor</label>
                          <input 
                            id="almacen-practica-item-proveedor"
                            name="nuevoItem.proveedor"
                            type="text" 
                            className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm outline-none transition-all focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                            value={nuevoItem.proveedor ?? ''}
                            onChange={e => setNuevoItem({...nuevoItem, proveedor: e.target.value})}
                            placeholder="Ej. Logix Supplier S.A."
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Dimensiones y Cubicaje */}
                  <div className="bg-slate-50/80 dark:bg-white/[0.03] border border-slate-200/70 dark:border-white/5 rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-xl text-amber-600 dark:text-amber-400">
                        <ArrowsPointingOutIcon className="h-5 w-5" />
                      </div>
                      <h4 className="font-bold text-slate-800 dark:text-white text-sm uppercase tracking-wider">Dimensiones y Cubicaje</h4>
                    </div>

                    <div className="space-y-3.5">
                      <div className="grid grid-cols-2 gap-3.5">
                        <div>
                          <label htmlFor="almacen-practica-item-peso" className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1 uppercase tracking-wide">Peso (kg)</label>
                          <input 
                            id="almacen-practica-item-peso"
                            name="nuevoItem.peso"
                            type="number" 
                            min="0"
                            step="0.01"
                            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm outline-none transition-all focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-center font-semibold"
                            value={nuevoItem.peso ?? 0}
                            onChange={e => setNuevoItem({...nuevoItem, peso: Number(e.target.value)})}
                          />
                        </div>
                        <div>
                          <label htmlFor="almacen-practica-item-volumen" className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1 uppercase tracking-wide">Volumen (m³)</label>
                          <input 
                            id="almacen-practica-item-volumen"
                            name="nuevoItem.volumen"
                            type="number" 
                            min="0"
                            step="0.01"
                            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm outline-none transition-all focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-center font-semibold"
                            value={nuevoItem.volumen ?? 0}
                            onChange={e => setNuevoItem({...nuevoItem, volumen: Number(e.target.value)})}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1 uppercase tracking-wide">Cubicaje (L × A × H en metros)</label>
                        <div className="grid grid-cols-3 gap-2.5">
                          <input
                            id="almacen-practica-item-largo"
                            name="nuevoItem.largo"
                            type="number"
                            min="0"
                            step="0.01"
                            className="w-full px-2.5 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs sm:text-sm outline-none transition-all focus:border-amber-500 text-center"
                            value={nuevoItem.largo ?? 0}
                            onChange={e => {
                              const largo = Number(e.target.value);
                              const ancho = Number(nuevoItem.ancho) || 0;
                              const alto = Number(nuevoItem.alto) || 0;
                              const volumen = largo > 0 && ancho > 0 && alto > 0 ? Number((largo * ancho * alto).toFixed(6)) : Number(nuevoItem.volumen) || 0;
                              setNuevoItem({ ...nuevoItem, largo, volumen });
                            }}
                            placeholder="Largo"
                          />
                          <input
                            id="almacen-practica-item-ancho"
                            name="nuevoItem.ancho"
                            type="number"
                            min="0"
                            step="0.01"
                            className="w-full px-2.5 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs sm:text-sm outline-none transition-all focus:border-amber-500 text-center"
                            value={nuevoItem.ancho ?? 0}
                            onChange={e => {
                              const ancho = Number(e.target.value);
                              const largo = Number(nuevoItem.largo) || 0;
                              const alto = Number(nuevoItem.alto) || 0;
                              const volumen = largo > 0 && ancho > 0 && alto > 0 ? Number((largo * ancho * alto).toFixed(6)) : Number(nuevoItem.volumen) || 0;
                              setNuevoItem({ ...nuevoItem, ancho, volumen });
                            }}
                            placeholder="Ancho"
                          />
                          <input
                            id="almacen-practica-item-alto"
                            name="nuevoItem.alto"
                            type="number"
                            min="0"
                            step="0.01"
                            className="w-full px-2.5 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs sm:text-sm outline-none transition-all focus:border-amber-500 text-center"
                            value={nuevoItem.alto ?? 0}
                            onChange={e => {
                              const alto = Number(e.target.value);
                              const largo = Number(nuevoItem.largo) || 0;
                              const ancho = Number(nuevoItem.ancho) || 0;
                              const volumen = largo > 0 && ancho > 0 && alto > 0 ? Number((largo * ancho * alto).toFixed(6)) : Number(nuevoItem.volumen) || 0;
                              setNuevoItem({ ...nuevoItem, alto, volumen });
                            }}
                            placeholder="Alto"
                          />
                        </div>
                        <p className="text-[10px] text-slate-400 dark:text-zinc-500 mt-1.5">
                          Calcula el volumen automáticamente al ingresar las 3 medidas.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Trazabilidad */}
                  <div className="bg-slate-50/80 dark:bg-white/[0.03] border border-slate-200/70 dark:border-white/5 rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl text-indigo-600 dark:text-indigo-400">
                        <QrCodeIcon className="h-5 w-5" />
                      </div>
                      <h4 className="font-bold text-slate-800 dark:text-white text-sm uppercase tracking-wider">Trazabilidad</h4>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      <div>
                        <label htmlFor="almacen-practica-item-codigo-barras" className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1 uppercase tracking-wide">Código de Barras</label>
                        <input 
                          id="almacen-practica-item-codigo-barras"
                          name="nuevoItem.codigoBarras"
                          type="text" 
                          className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm outline-none transition-all focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                          value={nuevoItem.codigoBarras ?? ''}
                          onChange={e => setNuevoItem({...nuevoItem, codigoBarras: e.target.value})}
                          placeholder="Generación automática"
                        />
                      </div>
                      <div>
                        <label htmlFor="almacen-practica-item-tipo-codigo" className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1 uppercase tracking-wide">Tipo de Código</label>
                        <select 
                          id="almacen-practica-item-tipo-codigo"
                          name="nuevoItem.tipoCodigo"
                          className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm outline-none transition-all focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 appearance-none disabled:opacity-50"
                          value={(nuevoItem.tipoCodigo as any) ?? 'Interno'}
                          onChange={e => setNuevoItem({...nuevoItem, tipoCodigo: e.target.value as any})}
                          disabled={!String(nuevoItem.codigoBarras || '').trim()}
                        >
                          <option value="Interno">Interno</option>
                          <option value="EAN">EAN</option>
                          <option value="UPC">UPC</option>
                          <option value="Code128">Code128</option>
                          <option value="QR">QR Externo</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Columna Derecha - PANEL DEDICADO A LA UBICACIÓN Y HUECOS DEL RACK (lg:col-span-7) */}
                <div className="lg:col-span-7 space-y-5">
                  <div className="bg-slate-50/80 dark:bg-white/[0.03] border border-slate-200/70 dark:border-white/5 rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm space-y-4">
                    {/* Header de la Ubicación con cambio de vista y accesos rápidos */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-200/60 dark:border-white/5">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-xl text-amber-600 dark:text-amber-400">
                          <MapPinIcon className="h-5 w-5" />
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-800 dark:text-white text-sm uppercase tracking-wider">Ubicación y Matriz de Huecos</h4>
                          <p className="text-[11px] text-slate-500 dark:text-zinc-400">Selecciona o haz clic directo en el hueco del rack</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                        {/* Selector de Modo Fachada vs Táctil */}
                        <div className="inline-flex items-center p-1 rounded-xl bg-slate-200/60 dark:bg-white/5 border border-slate-200/80 dark:border-white/10 text-xs">
                          <button
                            type="button"
                            onClick={() => setVistaSlotting('completa')}
                            className={`px-2.5 py-1 rounded-lg font-bold transition-all text-xs flex items-center gap-1.5 ${
                              vistaSlotting === 'completa'
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
                            }`}
                            title="Ver todos los huecos y niveles del rack a lo ancho"
                          >
                            <span>🏢 Todos los Huecos</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setVistaSlotting('movil')}
                            className={`px-2.5 py-1 rounded-lg font-bold transition-all text-xs flex items-center gap-1.5 ${
                              vistaSlotting === 'movil'
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
                            }`}
                            title="Modo móvil ergonómico por niveles"
                          >
                            <span>📱 Por Nivel</span>
                          </button>
                        </div>

                        {esMaestroOSuperior && (
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => {
                                const z = nuevoItem.zonaOperativa || zonasOperativas[0]?.codigo || 'SEC';
                                const rCount = racksResumen.filter(r => r.zona === z).length + 1;
                                setNuevoRackRapido({
                                  zona_codigo: z,
                                  pasillo: '01',
                                  rack: String(rCount).padStart(2, '0'),
                                  niveles: 3,
                                  posiciones_por_nivel: 8,
                                  ancho: 4.8,
                                  max_ton: 10,
                                  max_unidades: 400
                                });
                                setMostrarModalNuevoRackRapido(true);
                              }}
                              className="px-2.5 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-500/10 hover:bg-blue-100 dark:hover:bg-blue-500/20 text-blue-600 dark:text-blue-400 transition-colors border border-blue-200/50 dark:border-blue-500/20 flex items-center gap-1 text-xs font-bold shadow-sm"
                              title="Crear un nuevo rack en esta zona"
                            >
                              <PlusIcon className="h-3.5 w-3.5" />
                              <span>Rack</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setNuevaZonaNombre('');
                                setNuevaZonaCodigo('');
                                setMostrarModalZona(true);
                              }}
                              className="px-2.5 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-500/10 hover:bg-amber-100 dark:hover:bg-amber-500/20 text-amber-600 dark:text-amber-400 transition-colors border border-amber-200/50 dark:border-amber-500/20 flex items-center gap-1 text-xs font-bold"
                              title="Crear nueva zona operativa"
                            >
                              <PlusIcon className="h-3.5 w-3.5" />
                              <span>Zona</span>
                            </button>
                            <button
                              type="button"
                              onClick={abrirModalCapacidadesRack}
                              className="p-1.5 rounded-lg bg-white dark:bg-white/5 hover:bg-slate-100 dark:hover:bg-white/10 text-slate-500 dark:text-zinc-400 transition-colors border border-slate-200/50 dark:border-white/10"
                              title="Capacidades y límites estructurales"
                            >
                              <Cog6ToothIcon className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Fila de Selectores de Zona y Rack */}
                    <div className="relative">
                      {/* Click outside backdrop for custom dropdowns */}
                      {(selectorZonaAbierto || selectorRackAbierto) && (
                        <div 
                          className="fixed inset-0 z-30" 
                          onClick={() => {
                            setSelectorZonaAbierto(false);
                            setSelectorRackAbierto(false);
                          }} 
                        />
                      )}

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 relative z-40">
                        {/* Selector de Zona */}
                        <div className="relative">
                          <label className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1 uppercase tracking-wide">
                            Zona de Almacenamiento
                          </label>

                          {(() => {
                            const zonaActual = obtenerCodigoZona(nuevoItem.zonaOperativa);
                            const zonaSeleccionadaObj = zonasOperativas.find(z => z.codigo === zonaActual) || zonasOperativas[0];
                            const racksEnZona = racksResumen.filter(r => r.zona === zonaActual);

                            return (
                              <div className="relative">
                                <button
                                  type="button"
                                  id="almacen-practica-item-zona-operativa"
                                  disabled={cargandoZonas || !zonasOperativas.length}
                                  onClick={() => {
                                    setSelectorZonaAbierto(prev => !prev);
                                    setSelectorRackAbierto(false);
                                  }}
                                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-zinc-900 hover:border-amber-500/60 dark:hover:border-amber-500/60 text-left transition-all shadow-sm flex items-center justify-between gap-2 disabled:opacity-50 cursor-pointer"
                                >
                                  <div className="flex items-center gap-2.5 min-w-0">
                                    <span className="px-2 py-0.5 rounded-md text-xs font-mono font-bold bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20 shrink-0">
                                      {zonaSeleccionadaObj?.codigo || 'SEC'}
                                    </span>
                                    <div className="min-w-0">
                                      <div className="text-xs sm:text-sm font-bold text-slate-800 dark:text-zinc-100 truncate">
                                        {cargandoZonas ? 'Cargando zonas...' : (zonaSeleccionadaObj?.nombre || 'Zona General')}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 shrink-0">
                                    <span className="hidden sm:inline-block text-[10px] font-semibold text-slate-400 dark:text-zinc-500">
                                      {racksEnZona.length} {racksEnZona.length === 1 ? 'rack' : 'racks'}
                                    </span>
                                    <ChevronDownIcon className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${selectorZonaAbierto ? 'rotate-180 text-amber-500' : ''}`} />
                                  </div>
                                </button>

                                {/* Popover Dropdown de Zonas */}
                                {selectorZonaAbierto && (
                                  <div className="absolute top-full left-0 right-0 mt-1.5 z-50 rounded-2xl bg-white dark:bg-[#18181b] border border-slate-200 dark:border-zinc-700 shadow-2xl p-1.5 backdrop-blur-xl max-h-64 overflow-y-auto animate-in fade-in zoom-in-95">
                                    <div className="px-2 py-1 text-[10px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-wider">
                                      Zonas del almacén
                                    </div>
                                    <div className="space-y-1 mt-1">
                                      {zonasOperativas
                                        .filter(z => z.activo)
                                        .slice()
                                        .sort((a, b) => (a.orden - b.orden) || a.nombre.localeCompare(b.nombre))
                                        .map(zona => {
                                          const esActiva = zona.codigo === zonaActual;
                                          const cantRacks = racksResumen.filter(r => r.zona === zona.codigo).length;
                                          return (
                                            <button
                                              key={zona.codigo}
                                              type="button"
                                              onClick={() => {
                                                const racksNuevaZ = racksResumen.filter(r => r.zona === zona.codigo);
                                                if (racksNuevaZ.length > 0) {
                                                  setNuevoItem(prev => ({
                                                    ...prev,
                                                    zonaOperativa: zona.codigo,
                                                    pasillo: racksNuevaZ[0].pasillo,
                                                    rack: racksNuevaZ[0].rack,
                                                    nivel: '01',
                                                    posicion: '01'
                                                  }));
                                                } else {
                                                  setNuevoItem(prev => ({
                                                    ...prev,
                                                    zonaOperativa: zona.codigo,
                                                    pasillo: '01',
                                                    rack: '01',
                                                    nivel: '01',
                                                    posicion: '01'
                                                  }));
                                                }
                                                setSelectorZonaAbierto(false);
                                              }}
                                              className={`w-full px-3 py-2 rounded-xl text-left flex items-center justify-between text-xs transition-all cursor-pointer ${
                                                esActiva
                                                  ? 'bg-amber-500/10 text-amber-700 dark:text-amber-300 font-bold border border-amber-500/30 shadow-sm'
                                                  : 'hover:bg-slate-100 dark:hover:bg-white/5 text-slate-700 dark:text-zinc-300 border border-transparent'
                                              }`}
                                            >
                                              <div className="flex items-center gap-2 min-w-0">
                                                <span className="px-1.5 py-0.5 rounded text-[10px] font-mono font-bold bg-slate-100 dark:bg-white/10 text-slate-700 dark:text-zinc-300 shrink-0">
                                                  {zona.codigo}
                                                </span>
                                                <span className="truncate">{zona.nombre}</span>
                                              </div>
                                              <div className="flex items-center gap-2 shrink-0">
                                                <span className="text-[10px] text-slate-400 dark:text-zinc-500">
                                                  {cantRacks} {cantRacks === 1 ? 'rack' : 'racks'}
                                                </span>
                                                {esActiva && <CheckIcon className="h-4 w-4 text-amber-500 shrink-0" />}
                                              </div>
                                            </button>
                                          );
                                        })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>

                        {/* Selector de Rack Existente */}
                        {(() => {
                          const zonaActual = obtenerCodigoZona(nuevoItem.zonaOperativa);
                          const racksDeEstaZona = racksResumen.filter(r => r.zona === zonaActual);
                          const pasilloActual = normalizarNumero2(nuevoItem.pasillo);
                          const rackActual = normalizarNumero2(nuevoItem.rack);
                          const rackActualObj = racksDeEstaZona.find(
                            r => r.pasillo === pasilloActual && r.rack === rackActual
                          ) || racksDeEstaZona[0];

                          return (
                            <div className="relative">
                              <div className="flex items-center justify-between mb-1">
                                <label className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wide">
                                  Rack de Estantería
                                </label>
                                <button
                                  type="button"
                                  onClick={() => setModoManualUbicacion(prev => !prev)}
                                  className="text-[10px] font-bold text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
                                >
                                  {modoManualUbicacion ? '« Usar lista de racks' : '✏️ Escribir manual'}
                                </button>
                              </div>

                              {!modoManualUbicacion && racksDeEstaZona.length > 0 ? (
                                <div className="relative">
                                  {/* Botón selector custom de Rack */}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectorRackAbierto(prev => !prev);
                                      setSelectorZonaAbierto(false);
                                    }}
                                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-zinc-900 hover:border-amber-500/60 dark:hover:border-amber-500/60 text-left transition-all shadow-sm flex items-center justify-between gap-2 cursor-pointer"
                                  >
                                    <div className="flex items-center gap-2.5 min-w-0">
                                      <span className="px-2 py-0.5 rounded-md text-xs font-mono font-bold bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 shrink-0">
                                        P{rackActualObj?.pasillo ?? pasilloActual}-R{rackActualObj?.rack ?? rackActual}
                                      </span>
                                      <div className="min-w-0">
                                        <div className="text-xs sm:text-sm font-bold text-slate-800 dark:text-zinc-100 truncate">
                                          {rackActualObj?.nombreVisual ?? `Rack P${pasilloActual}-R${rackActual}`}
                                        </div>
                                      </div>
                                    </div>

                                    <div className="flex items-center gap-2 shrink-0">
                                      {rackActualObj && (
                                        <span className="hidden sm:inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-zinc-300">
                                          <span className={`h-1.5 w-1.5 rounded-full ${
                                            rackActualObj.totalSlots - rackActualObj.palletsOcupados === 0 
                                              ? 'bg-rose-500' 
                                              : rackActualObj.totalSlots - rackActualObj.palletsOcupados <= 3 
                                                ? 'bg-amber-500' 
                                                : 'bg-emerald-500'
                                          }`} />
                                          {rackActualObj.totalSlots - rackActualObj.palletsOcupados} libres
                                        </span>
                                      )}
                                      <ChevronDownIcon className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${selectorRackAbierto ? 'rotate-180 text-amber-500' : ''}`} />
                                    </div>
                                  </button>

                                  {/* Popover Menú Custom para Racks */}
                                  {selectorRackAbierto && (
                                    <div className="absolute top-full left-0 right-0 mt-1.5 z-50 rounded-2xl bg-white dark:bg-[#18181b] border border-slate-200 dark:border-zinc-700 shadow-2xl p-2 backdrop-blur-xl max-h-72 overflow-y-auto animate-in fade-in zoom-in-95">
                                      <div className="px-2 py-1 text-[10px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-wider flex items-center justify-between">
                                        <span>Racks en Zona {zonaActual} ({racksDeEstaZona.length})</span>
                                        <span className="text-[9px] lowercase font-normal">clic para seleccionar</span>
                                      </div>
                                      <div className="space-y-1 mt-1">
                                        {racksDeEstaZona.map(r => {
                                          const esActivo = pasilloActual === r.pasillo && rackActual === r.rack;
                                          const libres = r.totalSlots - r.palletsOcupados;
                                          return (
                                            <button
                                              key={`${r.pasillo}-${r.rack}`}
                                              type="button"
                                              onClick={() => {
                                                setNuevoItem(prev => ({ ...prev, pasillo: r.pasillo, rack: r.rack }));
                                                setSelectorRackAbierto(false);
                                              }}
                                              className={`w-full p-2.5 rounded-xl text-left transition-all border cursor-pointer ${
                                                esActivo
                                                  ? 'bg-amber-500/10 border-amber-500/30 text-amber-900 dark:text-amber-100 shadow-sm'
                                                  : 'border-transparent hover:bg-slate-100 dark:hover:bg-white/5 text-slate-800 dark:text-zinc-200'
                                              }`}
                                            >
                                              <div className="flex items-center justify-between gap-2">
                                                <div className="flex items-center gap-2 min-w-0">
                                                  <span className={`px-2 py-0.5 rounded-md text-xs font-mono font-bold ${
                                                    esActivo 
                                                      ? 'bg-amber-500 text-white' 
                                                      : 'bg-slate-100 dark:bg-white/10 text-slate-700 dark:text-zinc-300'
                                                  }`}>
                                                    P{r.pasillo}-R{r.rack}
                                                  </span>
                                                  <span className="text-xs font-bold truncate">{r.nombreVisual}</span>
                                                </div>
                                                {esActivo && <CheckIcon className="h-4 w-4 text-amber-500 shrink-0" />}
                                              </div>
                                              <div className="flex items-center justify-between mt-1.5 text-[11px] text-slate-500 dark:text-zinc-400">
                                                <span>{r.niveles} Niv · {r.posicionesPorNivel} Pallets ancho</span>
                                                <div className="flex items-center gap-1.5 font-medium">
                                                  <span className={`h-2 w-2 rounded-full ${
                                                    libres === 0 ? 'bg-rose-500' : libres <= 3 ? 'bg-amber-500' : 'bg-emerald-500'
                                                  }`} />
                                                  <span>{r.palletsOcupados}/{r.totalSlots} ({libres} libres)</span>
                                                </div>
                                              </div>
                                            </button>
                                          );
                                        })}
                                      </div>

                                      {/* Acción para crear nuevo rack */}
                                      <div className="mt-2 pt-2 border-t border-slate-100 dark:border-white/10">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setSelectorRackAbierto(false);
                                            setNuevoRackRapido({
                                              zona_codigo: zonaActual,
                                              pasillo: '01',
                                              rack: String(racksDeEstaZona.length + 1).padStart(2, '0'),
                                              niveles: 3,
                                              posiciones_por_nivel: 8,
                                              ancho: 4.8,
                                              max_ton: 10,
                                              max_unidades: 400
                                            });
                                            setMostrarModalNuevoRackRapido(true);
                                          }}
                                          className="w-full px-3 py-2 rounded-xl text-xs font-bold text-amber-600 dark:text-amber-400 hover:bg-amber-500/10 border border-dashed border-amber-500/30 flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
                                        >
                                          <PlusIcon className="h-4 w-4" />
                                          <span>Crear nuevo rack en zona {zonaActual}</span>
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ) : !modoManualUbicacion && racksDeEstaZona.length === 0 ? (
                                <div className="p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-between gap-2">
                                  <span className="text-xs text-amber-700 dark:text-amber-300 font-medium">
                                    Sin racks en <strong className="font-bold">{zonaActual}</strong>
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setNuevoRackRapido({
                                        zona_codigo: zonaActual,
                                        pasillo: '01',
                                        rack: '01',
                                        niveles: 3,
                                        posiciones_por_nivel: 8,
                                        ancho: 4.8,
                                        max_ton: 10,
                                        max_unidades: 400
                                      });
                                      setMostrarModalNuevoRackRapido(true);
                                    }}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs shadow-sm cursor-pointer"
                                  >
                                    <PlusIcon className="h-3 w-3" />
                                    Crear Rack
                                  </button>
                                </div>
                              ) : (
                                <div className="grid grid-cols-4 gap-1.5">
                                  <div>
                                    <label className="block text-[9px] font-bold text-slate-400 text-center uppercase">Pas</label>
                                    <input
                                      type="text"
                                      className="w-full px-1.5 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs font-mono text-center outline-none focus:border-amber-500"
                                      value={String(nuevoItem.pasillo ?? '01')}
                                      onChange={e => setNuevoItem({...nuevoItem, pasillo: e.target.value})}
                                      placeholder="01"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-[9px] font-bold text-slate-400 text-center uppercase">Rack</label>
                                    <input
                                      type="text"
                                      className="w-full px-1.5 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs font-mono text-center outline-none focus:border-amber-500"
                                      value={String(nuevoItem.rack ?? '01')}
                                      onChange={e => setNuevoItem({...nuevoItem, rack: e.target.value})}
                                      placeholder="01"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-[9px] font-bold text-slate-400 text-center uppercase">Niv</label>
                                    <input
                                      type="text"
                                      className="w-full px-1.5 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs font-mono text-center outline-none focus:border-amber-500"
                                      value={String(nuevoItem.nivel ?? '01')}
                                      onChange={e => setNuevoItem({...nuevoItem, nivel: e.target.value})}
                                      placeholder="01"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-[9px] font-bold text-slate-400 text-center uppercase">Pos</label>
                                    <input
                                      type="text"
                                      className="w-full px-1.5 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-xs font-mono text-center outline-none focus:border-amber-500"
                                      value={String(nuevoItem.posicion ?? '01')}
                                      onChange={e => setNuevoItem({...nuevoItem, posicion: e.target.value})}
                                      placeholder="01"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>

                      {/* Chips de Cambio Rápido de Rack (1-Tap Switcher) */}
                      {(() => {
                        const zonaActual = obtenerCodigoZona(nuevoItem.zonaOperativa);
                        const racksDeEstaZona = racksResumen.filter(r => r.zona === zonaActual);
                        const pasilloActual = normalizarNumero2(nuevoItem.pasillo);
                        const rackActual = normalizarNumero2(nuevoItem.rack);

                        if (modoManualUbicacion || racksDeEstaZona.length <= 1) return null;

                        return (
                          <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
                            <span className="text-[10px] uppercase font-bold text-slate-400 dark:text-zinc-500 tracking-wider">
                              Racks en {zonaActual}:
                            </span>
                            {racksDeEstaZona.map(r => {
                              const esActivo = pasilloActual === r.pasillo && rackActual === r.rack;
                              return (
                                <button
                                  key={`chip-${r.pasillo}-${r.rack}`}
                                  type="button"
                                  onClick={() => setNuevoItem(prev => ({ ...prev, pasillo: r.pasillo, rack: r.rack }))}
                                  className={`px-2.5 py-1 rounded-lg text-xs font-mono font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                                    esActivo
                                      ? 'bg-amber-500 text-white shadow-sm ring-2 ring-amber-500/20'
                                      : 'bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 text-slate-700 dark:text-zinc-300 border border-slate-200/50 dark:border-white/5'
                                  }`}
                                >
                                  <span>🏗️ P{r.pasillo}-R{r.rack}</span>
                                  <span className={`text-[10px] ${esActivo ? 'text-amber-100' : 'text-slate-400 dark:text-zinc-500'}`}>
                                    ({r.palletsOcupados}/{r.totalSlots})
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Matriz y Elevación Física del Rack ("TODOS LOS HUECOS A PARTE") */}
                    {(() => {
                      const zonaActual = obtenerCodigoZona(nuevoItem.zonaOperativa);
                      const pasilloActual = normalizarNumero2(nuevoItem.pasillo);
                      const rackActual = normalizarNumero2(nuevoItem.rack);
                      const nivelActual = normalizarNumero2(nuevoItem.nivel);
                      const posActual = normalizarNumero2(nuevoItem.posicion);

                      const rackTarget = racksResumen.find(
                        r => r.zona === zonaActual && r.pasillo === pasilloActual && r.rack === rackActual
                      ) || (() => {
                        const cap = obtenerCapacidadRack({ zonaOperativa: zonaActual, pasillo: pasilloActual, rack: rackActual });
                        const numNiv = cap.niveles || 3;
                        const posPorNiv = cap.posiciones_por_nivel || 8;
                        const anchoM = posPorNiv <= 2 ? 2.8 : posPorNiv <= 4 ? 3.8 : 4.8;
                        const itemsRack = items.filter(
                          i => obtenerCodigoZona(i.zonaOperativa) === zonaActual &&
                               normalizarNumero2(i.pasillo) === pasilloActual &&
                               normalizarNumero2(i.rack) === rackActual
                        );
                        return {
                          zona: zonaActual,
                          pasillo: pasilloActual,
                          rack: rackActual,
                          nombreVisual: `${zonaActual} · P${pasilloActual} · R${rackActual}`,
                          niveles: numNiv,
                          posicionesPorNivel: posPorNiv,
                          ancho: anchoM,
                          totalSlots: numNiv * posPorNiv,
                          palletsOcupados: itemsRack.length,
                          pesoTotalTon: itemsRack.reduce((acc, it) => acc + ((Number(it.peso) || 0) * (Number(it.cantidad) || 0)), 0) / 1000,
                          maxTon: cap.max_ton,
                          maxUnidades: cap.max_unidades,
                          items: itemsRack,
                        };
                      })();

                      const nivelNum = Number(nuevoItem.nivel || '1');
                      const posNum = Number(nuevoItem.posicion || '1');
                      const excedeNivel = nivelNum > rackTarget.niveles;
                      const excedePosicion = posNum > rackTarget.posicionesPorNivel;
                      const libresCount = rackTarget.totalSlots - rackTarget.palletsOcupados;
                      const ocupacionPct = Math.round((rackTarget.palletsOcupados / (rackTarget.totalSlots || 1)) * 100);

                      // Checar si el hueco seleccionado está ocupado por otro producto
                      const itemEnEseSlot = items.find(
                        i =>
                          i.id !== nuevoItem.id &&
                          obtenerCodigoZona(i.zonaOperativa) === zonaActual &&
                          normalizarNumero2(i.pasillo) === pasilloActual &&
                          normalizarNumero2(i.rack) === rackActual &&
                          normalizarNumero2(i.nivel) === nivelActual &&
                          normalizarNumero2(i.posicion) === posActual
                      );

                      return (
                        <div className="rounded-2xl p-4 sm:p-5 bg-white/80 dark:bg-black/40 border border-slate-200/80 dark:border-white/10 space-y-4 shadow-sm">
                          {/* Barra de título del rack + Botón Auto-asignar */}
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-200/60 dark:border-white/5">
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-mono text-base text-slate-900 dark:text-white font-extrabold tracking-wider">
                                  🏗️ {rackTarget.nombreVisual}
                                </span>
                                <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300 border border-blue-200 dark:border-blue-500/20">
                                  {rackTarget.niveles} Niveles · {rackTarget.posicionesPorNivel} Pallets de frente ({rackTarget.ancho}m)
                                </span>
                              </div>
                              <div className="flex items-center gap-2 mt-1.5">
                                <div className="w-28 sm:w-36 h-2 rounded-full bg-slate-200 dark:bg-white/10 overflow-hidden">
                                  <div 
                                    className={`h-full rounded-full transition-all duration-500 ${
                                      ocupacionPct >= 90 ? 'bg-red-500' : ocupacionPct >= 70 ? 'bg-amber-500' : 'bg-emerald-500'
                                    }`}
                                    style={{ width: `${Math.min(100, ocupacionPct)}%` }}
                                  />
                                </div>
                                <span className="text-[11px] font-bold text-slate-600 dark:text-zinc-300">
                                  {rackTarget.palletsOcupados}/{rackTarget.totalSlots} pallets ({ocupacionPct}%) · <span className="text-emerald-600 dark:text-emerald-400 font-extrabold">{libresCount} libres</span>
                                </span>
                              </div>
                            </div>

                            {/* Botón WMS: Asignar primer hueco libre en 1 toque */}
                            <button
                              type="button"
                              onClick={() => {
                                for (let n = 1; n <= rackTarget.niveles; n++) {
                                  const nStr = String(n).padStart(2, '0');
                                  for (let p = 1; p <= rackTarget.posicionesPorNivel; p++) {
                                    const pStr = String(p).padStart(2, '0');
                                    const ocupado = items.some(
                                      i =>
                                        i.id !== nuevoItem.id &&
                                        obtenerCodigoZona(i.zonaOperativa) === zonaActual &&
                                        normalizarNumero2(i.pasillo) === pasilloActual &&
                                        normalizarNumero2(i.rack) === rackActual &&
                                        normalizarNumero2(i.nivel) === nStr &&
                                        normalizarNumero2(i.posicion) === pStr
                                    );
                                    if (!ocupado) {
                                      setNuevoItem(prev => ({ ...prev, nivel: nStr, posicion: pStr }));
                                      playBeep();
                                      toast.success(`Hueco asignado: Nivel ${nStr}, Posición ${pStr}`);
                                      return;
                                    }
                                  }
                                }
                                playErrorBeep();
                                toast.error('Este rack está al 100% de su capacidad. Selecciona otro rack.');
                              }}
                              className="w-full sm:w-auto text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 px-4 py-2.5 rounded-xl shadow-md shadow-blue-500/20 transition-all flex items-center justify-center gap-1.5 active:scale-95 shrink-0"
                              title="Asigna automáticamente el hueco más ergonómico disponible"
                            >
                              <span>⚡ Auto-asignar hueco libre</span>
                            </button>
                          </div>

                          {/* VISTA 1: FACHADA INTEGRAL 2D (ELEVACIÓN COMPLETA REALISTA Y ADAPTABLE DÍA/NOCHE) */}
                          {vistaSlotting === 'completa' ? (
                            <div className="space-y-2.5">
                              <div className="text-[11px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wide flex items-center justify-between">
                                <span className="flex items-center gap-1.5">
                                  <span>Toca cualquier posición libre para asignarla:</span>
                                </span>
                                <span className="font-mono text-[10px] text-slate-500 dark:text-zinc-400">
                                  Arriba = N{String(rackTarget.niveles).padStart(2, '0')} · Abajo = N01 (Suelo)
                                </span>
                              </div>

                              {/* Marco Industrial de la Estantería (Sin neón, realista día y noche) */}
                              <div className="p-3 sm:p-4 bg-slate-100/90 dark:bg-zinc-950/60 rounded-2xl border border-slate-200/90 dark:border-zinc-800/80 shadow-inner overflow-x-auto">
                                <div className="w-full min-w-[500px] flex gap-2 items-stretch">
                                  {/* Bastidor / Puntal de Acero Izquierdo con troquelado */}
                                  <div className="w-3.5 bg-gradient-to-b from-slate-400 via-blue-700 to-slate-500 dark:from-slate-700 dark:via-blue-800 dark:to-slate-800 border-x border-slate-400/60 dark:border-slate-700 rounded-sm shadow-sm shrink-0 flex flex-col justify-around py-3 items-center">
                                    {Array.from({ length: 8 }, (_, i) => (
                                      <span key={i} className="w-1.5 h-1.5 rounded-sm bg-slate-200/90 dark:bg-zinc-900 border border-slate-400/40 dark:border-zinc-700/60" />
                                    ))}
                                  </div>

                                  {/* Bahía Central con Todos los Niveles y Huecos */}
                                  <div className="flex-1 space-y-3 py-1">
                                    {Array.from({ length: rackTarget.niveles }, (_, idx) => {
                                      const nivelActualLoop = rackTarget.niveles - idx; // De arriba hacia abajo
                                      const nStr = String(nivelActualLoop).padStart(2, '0');

                                      let libresEnEsteNivel = 0;
                                      for (let p = 1; p <= rackTarget.posicionesPorNivel; p++) {
                                        const pStr = String(p).padStart(2, '0');
                                        const oc = items.some(
                                          i =>
                                            i.id !== nuevoItem.id &&
                                            obtenerCodigoZona(i.zonaOperativa) === zonaActual &&
                                            normalizarNumero2(i.pasillo) === pasilloActual &&
                                            normalizarNumero2(i.rack) === rackActual &&
                                            normalizarNumero2(i.nivel) === nStr &&
                                            normalizarNumero2(i.posicion) === pStr
                                        );
                                        if (!oc) libresEnEsteNivel++;
                                      }

                                      return (
                                        <div key={nStr} className="space-y-1.5">
                                          <div className="flex items-center gap-2">
                                            {/* Etiqueta lateral del nivel */}
                                            <div className="w-16 shrink-0 text-right pr-2">
                                              <span className="text-[11px] font-mono font-black text-slate-800 dark:text-zinc-200 block leading-tight">
                                                N{nStr}
                                              </span>
                                              <span className={`text-[9px] font-bold leading-tight ${
                                                libresEnEsteNivel === 0 ? 'text-red-500 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'
                                              }`}>
                                                {libresEnEsteNivel === 0 ? 'Lleno' : `${libresEnEsteNivel} lib`}
                                              </span>
                                            </div>

                                            {/* Matriz de Huecos de este nivel */}
                                            <div
                                              className="flex-1 grid gap-2"
                                              style={{ gridTemplateColumns: `repeat(${rackTarget.posicionesPorNivel}, minmax(0, 1fr))` }}
                                            >
                                              {Array.from({ length: rackTarget.posicionesPorNivel }, (_, pIdx) => {
                                                const pStr = String(pIdx + 1).padStart(2, '0');
                                                const isSelected = nivelActual === nStr && posActual === pStr;
                                                const itemEnEsteHueco = items.find(
                                                  i =>
                                                    i.id !== nuevoItem.id &&
                                                    obtenerCodigoZona(i.zonaOperativa) === zonaActual &&
                                                    normalizarNumero2(i.pasillo) === pasilloActual &&
                                                    normalizarNumero2(i.rack) === rackActual &&
                                                    normalizarNumero2(i.nivel) === nStr &&
                                                    normalizarNumero2(i.posicion) === pStr
                                                );

                                                return (
                                                  <button
                                                    type="button"
                                                    key={pStr}
                                                    onClick={() => {
                                                      if (itemEnEsteHueco) {
                                                        playErrorBeep();
                                                        toast.error(`Hueco N${nStr}-S${pStr} ocupado por: ${itemEnEsteHueco.sku} (${itemEnEsteHueco.nombre})`);
                                                        return;
                                                      }
                                                      setNuevoItem(prev => ({ ...prev, nivel: nStr, posicion: pStr }));
                                                      playBeep();
                                                    }}
                                                    className={`relative flex flex-col items-center justify-between p-1.5 rounded-xl border transition-all text-center min-h-[58px] select-none ${
                                                      isSelected
                                                        ? 'bg-blue-50 dark:bg-blue-950/40 border-2 border-blue-600 dark:border-blue-500 shadow-sm ring-2 ring-blue-500/20 scale-[1.03] z-10'
                                                        : itemEnEsteHueco
                                                          ? 'bg-amber-50/80 dark:bg-amber-950/25 border-amber-200/90 dark:border-amber-800/50 cursor-not-allowed'
                                                          : 'bg-white dark:bg-zinc-900/90 hover:bg-slate-50 dark:hover:bg-zinc-800 border-slate-200 dark:border-zinc-700/80 hover:border-slate-400 dark:hover:border-zinc-500 cursor-pointer shadow-sm'
                                                    }`}
                                                    title={
                                                      itemEnEsteHueco
                                                        ? `⛔ Ocupado: ${itemEnEsteHueco.sku} · ${itemEnEsteHueco.nombre}`
                                                        : `✅ Hueco libre: Nivel ${nStr}, Posición ${pStr} (Clic para asignar)`
                                                    }
                                                  >
                                                    {/* Código del hueco */}
                                                    <span className={`text-[10px] font-mono font-bold ${
                                                      isSelected
                                                        ? 'text-blue-700 dark:text-blue-300'
                                                        : itemEnEsteHueco
                                                          ? 'text-amber-900 dark:text-amber-300'
                                                          : 'text-slate-600 dark:text-zinc-400'
                                                    }`}>
                                                      S{pStr}
                                                    </span>

                                                    {/* Ilustración de Pallet / Carga Realista */}
                                                    {itemEnEsteHueco ? (
                                                      <div className="flex flex-col items-center my-0.5">
                                                        {/* Pallet con caja de cartón corrugado */}
                                                        <svg className="w-7 h-4.5 text-amber-700 dark:text-amber-500" viewBox="0 0 32 20" fill="currentColor">
                                                          <rect x="6" y="2" width="20" height="10" rx="1" fill="#b45309" fillOpacity="0.85" />
                                                          <line x1="16" y1="2" x2="16" y2="12" stroke="#78350f" strokeWidth="1" strokeDasharray="2 1" />
                                                          <rect x="2" y="12" width="28" height="2" rx="0.5" fill="#78350f" />
                                                          <rect x="4" y="14" width="4" height="3" rx="0.5" fill="#78350f" />
                                                          <rect x="14" y="14" width="4" height="3" rx="0.5" fill="#78350f" />
                                                          <rect x="24" y="14" width="4" height="3" rx="0.5" fill="#78350f" />
                                                          <rect x="2" y="17" width="28" height="1.5" rx="0.5" fill="#78350f" />
                                                        </svg>
                                                        <span className="text-[8px] font-mono font-bold text-amber-900 dark:text-amber-200 truncate max-w-[50px] mt-0.5 bg-amber-100/90 dark:bg-amber-900/60 px-1 rounded">
                                                          {itemEnEsteHueco.sku}
                                                        </span>
                                                      </div>
                                                    ) : isSelected ? (
                                                      <div className="flex flex-col items-center my-0.5">
                                                        {/* Pallet resaltado para almacenar */}
                                                        <svg className="w-7 h-4 text-blue-600 dark:text-blue-400" viewBox="0 0 32 16" fill="currentColor">
                                                          <rect x="5" y="2" width="22" height="7" rx="1" fill="#2563eb" fillOpacity="0.25" stroke="#2563eb" strokeWidth="1" strokeDasharray="2 1" />
                                                          <rect x="2" y="9" width="28" height="2" rx="0.5" fill="#2563eb" />
                                                          <rect x="4" y="11" width="4" height="3" rx="0.5" fill="#2563eb" />
                                                          <rect x="14" y="11" width="4" height="3" rx="0.5" fill="#2563eb" />
                                                          <rect x="24" y="11" width="4" height="3" rx="0.5" fill="#2563eb" />
                                                          <rect x="2" y="14" width="28" height="1.5" rx="0.5" fill="#2563eb" />
                                                        </svg>
                                                        <span className="text-[8px] font-black text-blue-700 dark:text-blue-300 uppercase tracking-tighter bg-blue-100/90 dark:bg-blue-900/70 px-1 rounded mt-0.5">
                                                          ✓ Elegido
                                                        </span>
                                                      </div>
                                                    ) : (
                                                      <div className="flex flex-col items-center my-0.5">
                                                        {/* Pallet Euro vacío de madera neutro */}
                                                        <svg className="w-7 h-3.5 text-slate-400 dark:text-zinc-500 opacity-60 group-hover:opacity-100 transition-opacity" viewBox="0 0 32 14" fill="currentColor">
                                                          <rect x="2" y="3" width="28" height="2.5" rx="0.5" />
                                                          <rect x="4" y="5.5" width="4" height="4.5" rx="0.5" />
                                                          <rect x="14" y="5.5" width="4" height="4.5" rx="0.5" />
                                                          <rect x="24" y="5.5" width="4" height="4.5" rx="0.5" />
                                                          <rect x="2" y="10" width="28" height="2" rx="0.5" />
                                                        </svg>
                                                        <span className="text-[8px] font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/50 px-1 rounded mt-0.5 border border-emerald-200/60 dark:border-emerald-800/40">
                                                          + Libre
                                                        </span>
                                                      </div>
                                                    )}
                                                  </button>
                                                );
                                              })}
                                            </div>
                                          </div>
                                          
                                          {/* Larguero / Viga de Carga Horizontal en Naranja Industrial con Garras de Conexión */}
                                          <div className="flex items-center gap-2">
                                            <div className="w-16 shrink-0" />
                                            <div className="flex-1 flex items-center">
                                              {/* Garra de fijación izquierda con perno */}
                                              <div className="w-2 h-3.5 bg-slate-500 dark:bg-slate-600 rounded-l-sm border border-slate-600 dark:border-slate-500 flex items-center justify-center shrink-0">
                                                <span className="w-1 h-1 rounded-full bg-slate-200 dark:bg-zinc-800" />
                                              </div>
                                              {/* Perfil del larguero de carga */}
                                              <div className="flex-1 h-3 bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 dark:from-amber-700 dark:via-amber-600 dark:to-amber-700 border-t border-amber-300/80 dark:border-amber-400/60 border-b border-amber-800/80 shadow-sm flex items-center justify-center">
                                                <span className="text-[7.5px] font-mono font-bold text-amber-950/80 dark:text-amber-100/80 uppercase tracking-widest hidden sm:inline select-none">
                                                  Larguero de Carga · Acero Estructural {rackTarget.ancho}m
                                                </span>
                                              </div>
                                              {/* Garra de fijación derecha */}
                                              <div className="w-2 h-3.5 bg-slate-500 dark:bg-slate-600 rounded-r-sm border border-slate-600 dark:border-slate-500 flex items-center justify-center shrink-0">
                                                <span className="w-1 h-1 rounded-full bg-slate-200 dark:bg-zinc-800" />
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}

                                    {/* Suelo de Almacén (Losa de Hormigón) */}
                                    <div className="pt-1.5 flex items-center gap-2">
                                      <div className="w-16 shrink-0 text-right pr-2 text-[9px] font-mono font-bold text-slate-500 dark:text-zinc-500">
                                        SUELO
                                      </div>
                                      <div className="flex-1 h-4 bg-slate-200 dark:bg-zinc-800 rounded-sm border-t-2 border-slate-300 dark:border-zinc-700 flex items-center justify-between px-3 shadow-inner">
                                        <span className="text-[8px] font-mono font-black text-slate-600 dark:text-zinc-400 uppercase tracking-widest select-none">
                                          Losa de Hormigón Pulido · Cota 0.00m
                                        </span>
                                        <span className="text-[8px] font-mono font-bold text-slate-500 dark:text-zinc-500 select-none hidden sm:inline">
                                          Capacidad: 5.0 Ton/m²
                                        </span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Bastidor / Puntal de Acero Derecho */}
                                  <div className="w-3.5 bg-gradient-to-b from-slate-400 via-blue-700 to-slate-500 dark:from-slate-700 dark:via-blue-800 dark:to-slate-800 border-x border-slate-400/60 dark:border-slate-700 rounded-sm shadow-sm shrink-0 flex flex-col justify-around py-3 items-center">
                                    {Array.from({ length: 8 }, (_, i) => (
                                      <span key={i} className="w-1.5 h-1.5 rounded-sm bg-slate-200/90 dark:bg-zinc-900 border border-slate-400/40 dark:border-zinc-700/60" />
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            /* VISTA 2: MODO ERGONÓMICO TÁCTIL (MÓVIL SIMPLE ADAPTABLE DÍA/NOCHE) */
                            <div className="space-y-3.5">
                              {/* Paso 1: Elegir Nivel */}
                              <div className="space-y-1.5">
                                <span className="text-[11px] font-bold text-slate-600 dark:text-zinc-400 uppercase tracking-wider block">
                                  1. Toca el Nivel (Piso):
                                </span>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                  {Array.from({ length: rackTarget.niveles }, (_, idx) => {
                                    const nNum = idx + 1;
                                    const nStr = String(nNum).padStart(2, '0');
                                    const isNivelSelected = nivelActual === nStr;

                                    let libresEnNivel = 0;
                                    for (let p = 1; p <= rackTarget.posicionesPorNivel; p++) {
                                      const pStr = String(p).padStart(2, '0');
                                      const oc = items.some(
                                        i =>
                                          i.id !== nuevoItem.id &&
                                          obtenerCodigoZona(i.zonaOperativa) === zonaActual &&
                                          normalizarNumero2(i.pasillo) === pasilloActual &&
                                          normalizarNumero2(i.rack) === rackActual &&
                                          normalizarNumero2(i.nivel) === nStr &&
                                          normalizarNumero2(i.posicion) === pStr
                                      );
                                      if (!oc) libresEnNivel++;
                                    }

                                    return (
                                      <button
                                        type="button"
                                        key={nStr}
                                        onClick={() => {
                                          setNuevoItem(prev => ({ ...prev, nivel: nStr }));
                                          playBeep();
                                        }}
                                        className={`py-2.5 px-3 rounded-xl border text-center transition-all min-h-[54px] flex flex-col justify-center items-center ${
                                          isNivelSelected
                                            ? 'bg-blue-600 border-blue-600 text-white shadow-sm ring-2 ring-blue-500/20 scale-[1.02]'
                                            : libresEnNivel === 0
                                              ? 'bg-slate-100 dark:bg-zinc-900/40 border-slate-200 dark:border-zinc-800 text-slate-400 dark:text-zinc-500'
                                              : 'bg-white dark:bg-zinc-900 border-slate-200 dark:border-zinc-700/80 text-slate-700 dark:text-zinc-200 hover:border-slate-300 active:scale-95'
                                        }`}
                                      >
                                        <div className="text-xs font-black">
                                          Nivel {nStr} {nNum === 1 ? '(Suelo)' : nNum === rackTarget.niveles ? '(Alto)' : ''}
                                        </div>
                                        <div className={`text-[10px] font-bold mt-0.5 ${
                                          isNivelSelected ? 'text-blue-100' : libresEnNivel === 0 ? 'text-red-500 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'
                                        }`}>
                                          {libresEnNivel === 0 ? '⛔ Lleno' : `${libresEnNivel} libres`}
                                        </div>
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* Paso 2: Huecos en ese Nivel (Grid táctil, botones grandes) */}
                              <div className="space-y-1.5 pt-1">
                                <span className="text-[11px] font-bold text-slate-600 dark:text-zinc-400 uppercase tracking-wider block">
                                  2. Toca un hueco disponible en Nivel {nivelActual}:
                                </span>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                                  {Array.from({ length: rackTarget.posicionesPorNivel }, (_, pIdx) => {
                                    const pStr = String(pIdx + 1).padStart(2, '0');
                                    const isSelected = nivelActual === nuevoItem.nivel && posActual === pStr;
                                    const itemEnEsteHueco = items.find(
                                      i =>
                                        i.id !== nuevoItem.id &&
                                        obtenerCodigoZona(i.zonaOperativa) === zonaActual &&
                                        normalizarNumero2(i.pasillo) === pasilloActual &&
                                        normalizarNumero2(i.rack) === rackActual &&
                                        normalizarNumero2(i.nivel) === nivelActual &&
                                        normalizarNumero2(i.posicion) === pStr
                                    );

                                    return (
                                      <button
                                        type="button"
                                        key={pStr}
                                        onClick={() => {
                                          if (itemEnEsteHueco) {
                                            playErrorBeep();
                                            toast.error(`Hueco N${nivelActual}-S${pStr} ocupado por: ${itemEnEsteHueco.sku}`);
                                            return;
                                          }
                                          setNuevoItem(prev => ({ ...prev, posicion: pStr }));
                                          playBeep();
                                        }}
                                        className={`p-3 rounded-xl border text-center transition-all flex flex-col items-center justify-center min-h-[60px] active:scale-95 ${
                                          isSelected
                                            ? 'bg-blue-50 dark:bg-blue-950/40 border-2 border-blue-600 dark:border-blue-500 text-blue-900 dark:text-blue-100 font-bold shadow-sm'
                                            : itemEnEsteHueco
                                              ? 'bg-amber-50/80 dark:bg-amber-950/20 border-amber-200/80 dark:border-amber-800/40 text-amber-900 dark:text-amber-200 cursor-not-allowed'
                                              : 'bg-white dark:bg-zinc-900 border-slate-200 dark:border-zinc-700/80 hover:border-slate-300 text-slate-800 dark:text-zinc-200 hover:scale-[1.02] cursor-pointer shadow-sm'
                                        }`}
                                      >
                                        <span className="text-xs font-mono font-black">Posición S{pStr}</span>
                                        {isSelected ? (
                                          <span className="text-[10px] uppercase tracking-wider mt-0.5 text-blue-600 dark:text-blue-400 font-bold">✓ Elegido</span>
                                        ) : itemEnEsteHueco ? (
                                          <span className="text-[9px] text-amber-800 dark:text-amber-300 truncate max-w-[95px] mt-0.5 font-bold" title={itemEnEsteHueco.nombre}>
                                            📦 {itemEnEsteHueco.sku}
                                          </span>
                                        ) : (
                                          <span className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-0.5 font-bold">+ Libre</span>
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Resumen del hueco y código completo */}
                          <div className="flex flex-wrap items-center justify-between text-xs text-slate-600 dark:text-zinc-400 pt-2 border-t border-slate-200/60 dark:border-white/5 gap-2">
                            <div className="flex items-center gap-3">
                              <span className="flex items-center gap-1.5 text-slate-700 dark:text-zinc-300 font-semibold text-[11px]">
                                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" /> Libre
                              </span>
                              <span className="flex items-center gap-1.5 text-slate-700 dark:text-zinc-300 font-semibold text-[11px]">
                                <span className="w-2.5 h-2.5 rounded-full bg-amber-500 inline-block" /> Ocupado
                              </span>
                              <span className="flex items-center gap-1.5 text-slate-700 dark:text-zinc-300 font-semibold text-[11px]">
                                <span className="w-2.5 h-2.5 rounded-full bg-blue-600 inline-block" /> Seleccionado
                              </span>
                            </div>

                            <div className="font-mono text-xs">
                              Hueco elegido: <span className="font-black text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10 px-2.5 py-1 rounded-lg border border-blue-200 dark:border-blue-500/20">Nivel {nivelActual} · Posición {posActual}</span>
                            </div>
                          </div>

                          {/* Validación en tiempo real (Colores armónicos día/noche sin neón) */}
                          <div>
                            {excedeNivel ? (
                              <div className="flex items-center gap-2 text-xs font-bold text-red-800 dark:text-red-300 bg-red-50 dark:bg-red-950/25 p-2.5 rounded-xl border border-red-200 dark:border-red-800/40">
                                <span>❌</span>
                                <span>El nivel {nivelNum} excede la altura física ({rackTarget.niveles} niveles máx).</span>
                              </div>
                            ) : excedePosicion ? (
                              <div className="flex items-center gap-2 text-xs font-bold text-red-800 dark:text-red-300 bg-red-50 dark:bg-red-950/25 p-2.5 rounded-xl border border-red-200 dark:border-red-800/40">
                                <span>❌</span>
                                <span>La posición {posNum} excede el ancho de la viga ({rackTarget.posicionesPorNivel} pallets máx).</span>
                              </div>
                            ) : itemEnEseSlot ? (
                              <div className="flex items-center gap-2 text-xs font-bold text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/25 p-2.5 rounded-xl border border-amber-200 dark:border-amber-800/40">
                                <span>⚠️</span>
                                <span>Hueco N{nivelActual}-S{posActual} ocupado por: <span className="font-mono underline font-bold">{itemEnEseSlot.sku}</span> ({itemEnEseSlot.nombre}). Toca un hueco libre.</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 text-xs font-bold text-emerald-800 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/25 p-2.5 rounded-xl border border-emerald-200 dark:border-emerald-800/40">
                                <span>✅</span>
                                <span>Hueco N{nivelActual}-S{posActual} DISPONIBLE y listo para almacenar el producto.</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer de Acciones */}
            <div className="p-4 sm:p-6 bg-slate-50/90 dark:bg-[#0f0f10]/90 backdrop-blur-xl border-t border-slate-200/50 dark:border-white/5 flex flex-col-reverse sm:flex-row justify-between sm:items-center gap-3 shrink-0">
              <div className="text-xs text-slate-500 dark:text-zinc-400 flex items-center gap-2">
                <span className="font-mono text-xs">
                  Ubicación final: <strong className="text-slate-800 dark:text-white font-extrabold">{obtenerCodigoZona(nuevoItem.zonaOperativa)}-P{normalizarNumero2(nuevoItem.pasillo)}-R{normalizarNumero2(nuevoItem.rack)}-N{normalizarNumero2(nuevoItem.nivel)}-S{normalizarNumero2(nuevoItem.posicion)}</strong>
                </span>
              </div>
              <div className="flex items-center justify-end gap-3 w-full sm:w-auto">
                <button 
                  type="button"
                  onClick={() => setMostrarModalNuevo(false)}
                  className="flex-1 sm:flex-initial px-5 py-2.5 text-sm font-bold text-slate-600 dark:text-zinc-300 bg-white dark:bg-white/5 hover:bg-slate-100 dark:hover:bg-white/10 rounded-xl transition-colors border border-slate-200/50 dark:border-white/10 shadow-sm"
                >
                  Cancelar
                </button>
                <button 
                  type="button"
                  onClick={handleAgregar}
                  className="flex-1 sm:flex-initial px-7 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 rounded-xl shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all hover:-translate-y-0.5 active:scale-95"
                >
                  {modoEdicion ? 'Guardar Cambios' : 'Registrar en Almacén'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {mostrarModalZona && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-900/40 dark:bg-black/60 backdrop-blur-md"
          onClick={() => setMostrarModalZona(false)}
        >
          <div
            className="bg-white/90 dark:bg-[#0a0a0a]/90 backdrop-blur-2xl border border-white/20 dark:border-white/10 rounded-[2rem] shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-8 py-6 border-b border-slate-200/50 dark:border-white/5 flex items-center justify-between bg-slate-50/50 dark:bg-white/5">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-xl text-amber-600 dark:text-amber-400">
                  <MapPinIcon className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 dark:text-white">Nueva Zona Operativa</h3>
              </div>
              <button
                type="button"
                onClick={() => setMostrarModalZona(false)}
                className="p-2 rounded-xl bg-white dark:bg-white/5 hover:bg-slate-100 dark:hover:bg-white/10 text-slate-500 dark:text-zinc-400 transition-colors border border-slate-200/50 dark:border-white/10"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="p-8 space-y-6">
              <div>
                <label htmlFor="almacen-practica-zona-nombre" className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1.5 uppercase tracking-wide">Nombre de la Zona</label>
                <input
                  id="almacen-practica-zona-nombre"
                  name="nuevaZonaNombre"
                  type="text"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm outline-none transition-all focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
                  value={nuevaZonaNombre}
                  onChange={e => setNuevaZonaNombre(e.target.value)}
                  placeholder="Ej. Farmacia / Medicamentos Controlados"
                />
              </div>
              <div>
                <label htmlFor="almacen-practica-zona-codigo" className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1.5 uppercase tracking-wide">Código (Opcional)</label>
                <input
                  id="almacen-practica-zona-codigo"
                  name="nuevaZonaCodigo"
                  type="text"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm outline-none transition-all focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 uppercase"
                  value={nuevaZonaCodigo}
                  onChange={e =>
                    setNuevaZonaCodigo(
                      e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 20)
                    )
                  }
                  placeholder="Ej. FAR"
                />
              </div>
            </div>

            <div className="p-6 bg-slate-50/80 dark:bg-[#111111]/80 backdrop-blur-xl border-t border-slate-200/50 dark:border-white/5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setMostrarModalZona(false)}
                className="px-6 py-3 text-sm font-bold text-slate-600 dark:text-zinc-300 bg-white dark:bg-white/5 hover:bg-slate-100 dark:hover:bg-white/10 rounded-xl transition-colors border border-slate-200/50 dark:border-white/10 shadow-sm"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleCrearZonaOperativa}
                disabled={creandoZona || nuevaZonaNombre.trim().length < 2}
                className="px-8 py-3 text-sm font-bold text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 disabled:from-amber-200 disabled:to-orange-200 rounded-xl shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 transition-all hover:-translate-y-0.5"
              >
                Crear Zona
              </button>
            </div>
          </div>
        </div>
      )}

      {mostrarModalNuevoRackRapido && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-900/40 dark:bg-black/60 backdrop-blur-md"
          onClick={() => setMostrarModalNuevoRackRapido(false)}
        >
          <div
            className="bg-white/95 dark:bg-[#0a0a0a]/95 backdrop-blur-2xl border border-white/20 dark:border-white/10 rounded-[2rem] shadow-2xl w-full max-w-md overflow-hidden flex flex-col max-h-[90vh]"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-5 border-b border-slate-200/50 dark:border-white/5 flex items-center justify-between bg-slate-50/50 dark:bg-white/5">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl text-blue-600 dark:text-blue-400">
                  <CubeIcon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-800 dark:text-white">Nuevo Rack Estructural</h3>
                  <p className="text-xs text-slate-500 dark:text-zinc-400">Configura dimensiones y capacidad de pallets</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setMostrarModalNuevoRackRapido(false)}
                className="p-2 rounded-xl bg-white dark:bg-white/5 hover:bg-slate-100 dark:hover:bg-white/10 text-slate-500 dark:text-zinc-400 transition-colors border border-slate-200/50 dark:border-white/10"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto">
              <div>
                <label className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1.5 uppercase tracking-wide">Zona Operativa</label>
                <select
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm outline-none transition-all focus:border-blue-500 font-bold"
                  value={nuevoRackRapido.zona_codigo || nuevoItem.zonaOperativa || (zonasOperativas[0]?.codigo || 'SEC')}
                  onChange={e => setNuevoRackRapido(prev => ({ ...prev, zona_codigo: e.target.value }))}
                >
                  {zonasOperativas.map(z => (
                    <option key={z.codigo} value={z.codigo}>
                      {z.nombre} ({z.codigo})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1.5 uppercase tracking-wide">Pasillo</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm font-mono text-center outline-none focus:border-blue-500 font-bold"
                    value={nuevoRackRapido.pasillo}
                    onChange={e => setNuevoRackRapido(prev => ({ ...prev, pasillo: normalizarNumero2(e.target.value) }))}
                    placeholder="01"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1.5 uppercase tracking-wide">Número de Rack</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm font-mono text-center outline-none focus:border-blue-500 font-bold"
                    value={nuevoRackRapido.rack}
                    onChange={e => setNuevoRackRapido(prev => ({ ...prev, rack: normalizarNumero2(e.target.value) }))}
                    placeholder="01"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1.5 uppercase tracking-wide">Altura (Niveles)</label>
                  <select
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm outline-none focus:border-blue-500 font-medium"
                    value={nuevoRackRapido.niveles}
                    onChange={e => setNuevoRackRapido(prev => ({ ...prev, niveles: Number(e.target.value) }))}
                  >
                    <option value={2}>2 Niveles (Bajo)</option>
                    <option value={3}>3 Niveles (Estándar)</option>
                    <option value={4}>4 Niveles (Alto)</option>
                    <option value={5}>5 Niveles (Torre)</option>
                    <option value={6}>6 Niveles (Extra)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1.5 uppercase tracking-wide">Pallets de Ancho</label>
                  <select
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm outline-none focus:border-blue-500 font-medium"
                    value={nuevoRackRapido.posiciones_por_nivel}
                    onChange={e => {
                      const val = Number(e.target.value);
                      const anchoM = val <= 2 ? 2.8 : val <= 4 ? 3.8 : 4.8;
                      setNuevoRackRapido(prev => ({ ...prev, posiciones_por_nivel: val, ancho: anchoM }));
                    }}
                  >
                    <option value={2}>2 Pallets (2.8m viga)</option>
                    <option value={4}>4 Pallets (3.8m viga)</option>
                    <option value={8}>8 Pallets (4.8m viga)</option>
                  </select>
                </div>
              </div>

              <div className="p-3 bg-blue-50/70 dark:bg-blue-950/20 rounded-xl border border-blue-200/60 dark:border-blue-500/20 text-xs text-blue-900 dark:text-blue-200">
                <span className="font-bold">Capacidad calculada:</span> {Number(nuevoRackRapido.niveles) * Number(nuevoRackRapido.posiciones_por_nivel)} pallets de almacenamiento total. Sincronizado automáticamente con el plano 3D y WMS.
              </div>
            </div>

            <div className="p-5 bg-slate-50/80 dark:bg-[#111111]/80 backdrop-blur-xl border-t border-slate-200/50 dark:border-white/5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setMostrarModalNuevoRackRapido(false)}
                className="px-5 py-2.5 text-xs font-bold text-slate-600 dark:text-zinc-300 bg-white dark:bg-white/5 hover:bg-slate-100 rounded-xl border border-slate-200/50 dark:border-white/10"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleCrearRackRapido}
                disabled={creandoRackRapido}
                className="px-6 py-2.5 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-xl shadow-md shadow-blue-500/20 transition-all"
              >
                {creandoRackRapido ? 'Creando...' : 'Crear y Usar Rack'}
              </button>
            </div>
          </div>
        </div>
      )}

      {mostrarModalCapacidadesRack && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-900/40 dark:bg-black/60 backdrop-blur-md"
          onClick={() => setMostrarModalCapacidadesRack(false)}
        >
          <div
            className="bg-white/90 dark:bg-[#0a0a0a]/90 backdrop-blur-2xl border border-white/20 dark:border-white/10 rounded-[2rem] shadow-2xl w-full max-w-5xl overflow-hidden flex flex-col max-h-[90vh]"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-8 py-6 border-b border-slate-200/50 dark:border-white/5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-50/50 dark:bg-white/5">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-2xl text-blue-600 dark:text-blue-400">
                  <Cog6ToothIcon className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-800 dark:text-white">Capacidades de Almacén y Racks</h3>
                  <p className="text-sm text-slate-500 dark:text-zinc-400 mt-0.5">
                    Gestiona los límites de unidades, peso y volumen por zonas o racks.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setMostrarModalCapacidadesRack(false)}
                className="p-2 rounded-xl bg-white dark:bg-white/5 hover:bg-slate-100 dark:hover:bg-white/10 text-slate-500 dark:text-zinc-400 transition-colors border border-slate-200/50 dark:border-white/10 self-start sm:self-auto"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-hidden flex flex-col lg:flex-row">
              {/* Sidebar: Lista de Capacidades */}
              <div className="w-full lg:w-1/3 border-b lg:border-b-0 lg:border-r border-slate-200/50 dark:border-white/5 flex flex-col bg-slate-50/30 dark:bg-white/5">
                <div className="p-4 border-b border-slate-200/50 dark:border-white/5 flex justify-between items-center bg-white dark:bg-black/20">
                  <h4 className="text-[10px] font-black text-slate-500 dark:text-zinc-400 uppercase tracking-widest">Reglas Activas</h4>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() =>
                        setCapacidadForm({
                          alcance: 'INSTITUCION',
                          zona_codigo: '',
                          pasillo: '',
                          rack: '',
                          max_unidades: DEFAULT_CAPACIDAD_RACK_UNIDADES,
                          max_ton: DEFAULT_CAPACIDAD_RACK_TON,
                          max_m3: DEFAULT_CAPACIDAD_RACK_M3,
                          activo: true,
                        })
                      }
                      className="px-2 py-1 text-[10px] font-bold text-blue-600 bg-blue-50 dark:bg-blue-500/10 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-500/20 transition-colors"
                    >
                      + Inst
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setCapacidadForm({
                          alcance: 'ZONA',
                          zona_codigo: zonasOperativas[0]?.codigo || '',
                          pasillo: '',
                          rack: '',
                          max_unidades: DEFAULT_CAPACIDAD_RACK_UNIDADES,
                          max_ton: DEFAULT_CAPACIDAD_RACK_TON,
                          max_m3: DEFAULT_CAPACIDAD_RACK_M3,
                          activo: true,
                        })
                      }
                      className="px-2 py-1 text-[10px] font-bold text-amber-600 bg-amber-50 dark:bg-amber-500/10 dark:text-amber-400 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-500/20 transition-colors"
                    >
                      + Zona
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setCapacidadForm({
                          alcance: 'RACK',
                          zona_codigo: zonasOperativas[0]?.codigo || '',
                          pasillo: '01',
                          rack: '01',
                          max_unidades: DEFAULT_CAPACIDAD_RACK_UNIDADES,
                          max_ton: DEFAULT_CAPACIDAD_RACK_TON,
                          max_m3: DEFAULT_CAPACIDAD_RACK_M3,
                          activo: true,
                        })
                      }
                      className="px-2 py-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 dark:bg-emerald-500/10 dark:text-emerald-400 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-500/20 transition-colors"
                    >
                      + Rack
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  {cargandoCapacidadesRack ? (
                    <div className="text-sm text-slate-500 dark:text-zinc-400 text-center py-4">Cargando reglas...</div>
                  ) : (
                    capacidadesRack
                      .slice()
                      .sort((a, b) => a.id_capacidad - b.id_capacidad)
                      .map(c => {
                        const titulo =
                          c.alcance === 'INSTITUCION'
                            ? 'Límite Global (Institución)'
                            : c.alcance === 'ZONA'
                              ? `Zona Operativa: ${String(c.zona_codigo || '')}`
                              : `Rack: ${String(c.zona_codigo || '')} · P${normalizarNumero2(String(c.pasillo || ''))} · R${normalizarNumero2(String(c.rack || ''))}`;
                        
                        const subtitulo = `${c.max_unidades} unid · ${Number(c.max_ton).toLocaleString(undefined, { maximumFractionDigits: 2 })} t · ${Number(c.max_m3).toLocaleString(undefined, { maximumFractionDigits: 2 })} m³`;
                        
                        const selected =
                          capacidadForm.alcance === c.alcance &&
                          String(capacidadForm.zona_codigo || '').toUpperCase() === String(c.zona_codigo || '').toUpperCase() &&
                          normalizarNumero2(capacidadForm.pasillo || '') === normalizarNumero2(String(c.pasillo || '')) &&
                          normalizarNumero2(capacidadForm.rack || '') === normalizarNumero2(String(c.rack || ''));

                        return (
                          <button
                            key={c.id_capacidad}
                            type="button"
                            onClick={() => handleSeleccionarCapacidad(c)}
                            className={`w-full text-left p-3.5 rounded-2xl border transition-all ${
                              selected
                                ? 'border-blue-500 bg-white dark:bg-blue-500/10 shadow-md shadow-blue-500/10 ring-1 ring-blue-500/50'
                                : 'border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 hover:border-blue-300 dark:hover:border-white/20 hover:bg-slate-50 dark:hover:bg-white/5'
                            }`}
                          >
                            <div className="flex items-center justify-between gap-3 mb-1.5">
                              <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider ${
                                c.alcance === 'INSTITUCION' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                c.alcance === 'ZONA' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                                'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                              }`}>
                                {c.alcance}
                              </span>
                              <span className={`flex h-2 w-2 rounded-full ${c.activo ? 'bg-green-500' : 'bg-slate-300 dark:bg-slate-600'}`}></span>
                            </div>
                            <p className="text-xs font-bold text-slate-800 dark:text-white truncate mb-0.5">{titulo}</p>
                            <p className="text-[10px] text-slate-500 dark:text-zinc-400 truncate font-mono">{subtitulo}</p>
                          </button>
                        );
                      })
                  )}
                </div>
              </div>

              {/* Formulario Principal */}
              <div className="w-full lg:w-2/3 p-6 lg:p-8 overflow-y-auto">
                <div className="bg-slate-50 dark:bg-white/5 border border-slate-200/50 dark:border-white/5 rounded-3xl p-6 shadow-sm">
                  <h4 className="font-bold text-slate-800 dark:text-white text-sm uppercase tracking-wider mb-6">Detalles de la Regla</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    <div className="lg:col-span-1">
                      <label htmlFor="almacen-practica-capacidad-alcance" className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1.5 uppercase tracking-wide">Alcance</label>
                      <select
                        id="almacen-practica-capacidad-alcance"
                        name="capacidadForm.alcance"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 appearance-none font-bold"
                        value={capacidadForm.alcance}
                        onChange={e => setCapacidadForm(prev => ({ ...prev, alcance: e.target.value as any }))}
                      >
                        <option value="INSTITUCION">Global (Institución)</option>
                        <option value="ZONA">Zona Operativa</option>
                        <option value="RACK">Rack Específico</option>
                      </select>
                    </div>

                    <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="almacen-practica-capacidad-zona" className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1.5 uppercase tracking-wide">Zona</label>
                        <select
                          id="almacen-practica-capacidad-zona"
                          name="capacidadForm.zona_codigo"
                          className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 appearance-none font-medium disabled:opacity-50"
                          value={capacidadForm.zona_codigo}
                          onChange={e => setCapacidadForm(prev => ({ ...prev, zona_codigo: e.target.value }))}
                          disabled={capacidadForm.alcance === 'INSTITUCION' || cargandoZonas || !zonasOperativas.length}
                        >
                          {zonasOperativas.filter(z => z.activo).map(z => (
                            <option key={z.codigo} value={z.codigo}>
                              {z.nombre} ({z.codigo})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label htmlFor="almacen-practica-capacidad-pasillo" className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1.5 uppercase tracking-wide">Pasillo</label>
                          <input
                            id="almacen-practica-capacidad-pasillo"
                            name="capacidadForm.pasillo"
                            type="text"
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 text-center font-mono disabled:opacity-50"
                            value={capacidadForm.pasillo}
                            onChange={e => setCapacidadForm(prev => ({ ...prev, pasillo: normalizarNumero2(e.target.value) }))}
                            disabled={capacidadForm.alcance !== 'RACK'}
                            placeholder="01"
                          />
                        </div>
                        <div>
                          <label htmlFor="almacen-practica-capacidad-rack" className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1.5 uppercase tracking-wide">Rack</label>
                          <input
                            id="almacen-practica-capacidad-rack"
                            name="capacidadForm.rack"
                            type="text"
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 text-center font-mono disabled:opacity-50"
                            value={capacidadForm.rack}
                            onChange={e => setCapacidadForm(prev => ({ ...prev, rack: normalizarNumero2(e.target.value) }))}
                            disabled={capacidadForm.alcance !== 'RACK'}
                            placeholder="01"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-slate-200/50 dark:border-white/5">
                    <label htmlFor="almacen-practica-capacidad-plantilla" className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1.5 uppercase tracking-wide">
                      Plantilla Estructural (Autocompletar Límites Físicos)
                    </label>
                    <select
                      id="almacen-practica-capacidad-plantilla"
                      className="w-full px-4 py-3 rounded-xl border border-blue-200 dark:border-blue-500/30 bg-blue-50/50 dark:bg-blue-500/5 text-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 text-blue-900 dark:text-blue-100 font-medium"
                      onChange={e => handleAplicarPlantilla(e.target.value)}
                      defaultValue=""
                    >
                      <option value="" disabled>-- Selecciona una plantilla industrial --</option>
                      {capacidadForm.alcance === 'RACK' ? (
                        <>
                          <optgroup label="Sistemas Selectivos">
                            <option value="r_mecalux_std">Mecalux Selectivo Estándar (10 Tons, 4 Niveles)</option>
                            <option value="r_mecalux_pesado">Mecalux Selectivo Heavy-Duty (16 Tons, 4 Niveles)</option>
                          </optgroup>
                          <optgroup label="Sistemas de Alta Densidad">
                            <option value="r_drivein_schafer">SSI Schäfer Drive-In / Acumulación (30 Tons, 5 Niveles)</option>
                            <option value="r_pushback_interlake">Interlake Push-Back LIFO (15 Tons, 4 Niveles)</option>
                            <option value="r_flow_dematic">Dematic Pallet Flow Dinámico FIFO (20 Tons, 4 Niveles)</option>
                          </optgroup>
                          <optgroup label="Especiales y Automatizados">
                            <option value="r_cantilever_jung">Jungheinrich Cantilever - Voladizo (6 Tons, 5 Niveles)</option>
                            <option value="r_miniload_mecalux">Mecalux Estantería Picking Manual / Miniload (1.5 Tons, 6 Niveles)</option>
                            <option value="r_auto_asrs">Sistema ASRS Automatizado / Transelevador (40 Tons, 10 Niveles)</option>
                          </optgroup>
                        </>
                      ) : (
                        <>
                          <optgroup label="Instalaciones Logísticas">
                            <option value="g_micro">Micro Fulfillment Center E-commerce / Dark Store (500 Tons)</option>
                            <option value="g_cedis_regional">Centro de Distribución Regional CEDIS Estándar (2,500 Tons)</option>
                            <option value="g_hub_nacional">Hub Logístico Nacional / FMCG (10,000 Tons)</option>
                          </optgroup>
                          <optgroup label="Instalaciones Especiales">
                            <option value="g_cold_storage">Almacén Frigorífico / Cold Storage -20°C (4,000 Tons)</option>
                            <option value="g_mega_dc">Mega DC Internacional (Tipo Amazon/MercadoLibre) (25,000 Tons)</option>
                          </optgroup>
                        </>
                      )}
                    </select>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-4 mt-6 pt-6 border-t border-slate-200/50 dark:border-white/5">
                    <div>
                      <label htmlFor="almacen-practica-capacidad-niveles" className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1.5 uppercase tracking-wide">Niveles Altura</label>
                      <input
                        id="almacen-practica-capacidad-niveles"
                        name="capacidadForm.niveles"
                        type="number"
                        min="1"
                        max="10"
                        step="1"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 font-mono"
                        value={capacidadForm.niveles ?? 3}
                        onChange={e => setCapacidadForm(prev => ({ ...prev, niveles: Math.max(1, Number(e.target.value)) }))}
                      />
                    </div>
                    <div>
                      <label htmlFor="almacen-practica-capacidad-pallets-ancho" className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1.5 uppercase tracking-wide">Pallets de Ancho</label>
                      <select
                        id="almacen-practica-capacidad-pallets-ancho"
                        className="w-full px-3 py-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 font-mono"
                        value={capacidadForm.posiciones_por_nivel ?? 8}
                        onChange={e => {
                          const val = Number(e.target.value);
                          const anchoAuto = val <= 2 ? 2.8 : val <= 4 ? 3.8 : 4.8;
                          setCapacidadForm(prev => ({ ...prev, posiciones_por_nivel: val, ancho: anchoAuto }));
                        }}
                      >
                        <option value={2}>2 Pallets (2.8m)</option>
                        <option value={4}>4 Pallets (3.8m)</option>
                        <option value={8}>8 Pallets (4.8m)</option>
                        <option value={10}>10 Pallets (5.6m)</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="almacen-practica-capacidad-ancho-viga" className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1.5 uppercase tracking-wide">Ancho Viga (m)</label>
                      <input
                        id="almacen-practica-capacidad-ancho-viga"
                        name="capacidadForm.ancho"
                        type="number"
                        min="1.5"
                        max="10"
                        step="0.1"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 font-mono"
                        value={capacidadForm.ancho ?? 3.8}
                        onChange={e => setCapacidadForm(prev => ({ ...prev, ancho: Number(e.target.value) }))}
                      />
                    </div>
                    <div>
                      <label htmlFor="almacen-practica-capacidad-max-unidades" className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1.5 uppercase tracking-wide">Máx. Unidades</label>
                      <input
                        id="almacen-practica-capacidad-max-unidades"
                        name="capacidadForm.max_unidades"
                        type="number"
                        min="0"
                        step="1"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 font-mono"
                        value={capacidadForm.max_unidades}
                        onChange={e => setCapacidadForm(prev => ({ ...prev, max_unidades: Number(e.target.value) }))}
                      />
                    </div>
                    <div>
                      <label htmlFor="almacen-practica-capacidad-max-toneladas" className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1.5 uppercase tracking-wide">Máx. Toneladas</label>
                      <input
                        id="almacen-practica-capacidad-max-toneladas"
                        name="capacidadForm.max_ton"
                        type="number"
                        min="0"
                        step="0.01"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 font-mono"
                        value={capacidadForm.max_ton}
                        onChange={e => setCapacidadForm(prev => ({ ...prev, max_ton: Number(e.target.value) }))}
                      />
                    </div>
                    <div>
                      <label htmlFor="almacen-practica-capacidad-max-m3" className="block text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1.5 uppercase tracking-wide">Máx. m³</label>
                      <input
                        id="almacen-practica-capacidad-max-m3"
                        name="capacidadForm.max_m3"
                        type="number"
                        min="0"
                        step="0.01"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/20 text-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 font-mono"
                        value={capacidadForm.max_m3}
                        onChange={e => setCapacidadForm(prev => ({ ...prev, max_m3: Number(e.target.value) }))}
                      />
                    </div>
                  </div>

                  {/* Cálculo Dinámico de Capacidad y Geometría del Rack */}
                  <div className="mt-4 p-4 rounded-2xl bg-blue-50/70 dark:bg-blue-500/10 border border-blue-200/80 dark:border-blue-500/20 text-xs">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <span className="font-extrabold text-blue-900 dark:text-blue-100 flex items-center gap-1.5 flex-wrap">
                          <span>📐 Geometría Resultante:</span>
                          <span className="font-mono bg-blue-100 dark:bg-blue-500/20 px-2 py-0.5 rounded-lg text-blue-700 dark:text-blue-300 font-bold">
                            {capacidadForm.niveles || 3} Niveles × {capacidadForm.posiciones_por_nivel || 8} Pallets de ancho = {(capacidadForm.niveles || 3) * (capacidadForm.posiciones_por_nivel || 8)} Pallets Totales
                          </span>
                        </span>
                      </div>
                      <div className="text-slate-600 dark:text-zinc-400 font-medium">
                        Viga: <span className="font-mono font-bold text-slate-900 dark:text-white">{capacidadForm.ancho || 3.8}m</span> · Carga media: <span className="font-mono font-bold text-slate-900 dark:text-white">{((capacidadForm.max_ton || 15) / (capacidadForm.niveles || 3)).toFixed(1)}t / nivel</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-8 pt-6 border-t border-slate-200/50 dark:border-white/5">
                    <label htmlFor="almacen-practica-capacidad-activo" className="flex items-center gap-3 cursor-pointer group">
                      <div className="relative inline-flex items-center h-6 w-11">
                        <input
                          id="almacen-practica-capacidad-activo"
                          name="capacidadForm.activo"
                          type="checkbox"
                          className="peer sr-only"
                          checked={capacidadForm.activo}
                          onChange={e => setCapacidadForm(prev => ({ ...prev, activo: e.target.checked }))}
                        />
                        <div className="w-11 h-6 bg-slate-300 dark:bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                      </div>
                      <span className="text-sm font-bold text-slate-700 dark:text-white uppercase tracking-wide group-hover:text-slate-900 dark:group-hover:text-blue-300 transition-colors">
                        Regla Activa
                      </span>
                    </label>
                    <button
                      type="button"
                      onClick={handleGuardarCapacidadRack}
                      disabled={guardandoCapacidadRack}
                      className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 disabled:opacity-50 text-white text-sm font-bold rounded-xl shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all hover:-translate-y-0.5"
                    >
                      Guardar Regla
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Mostrar QR / Etiqueta Térmica Logística */}
      {mostrarModalQR && (
        <div 
          className="fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-6 bg-slate-900/75 dark:bg-black/85 backdrop-blur-md overflow-y-auto animate-fadeIn" 
          onClick={() => setMostrarModalQR(null)}
        >
          <div 
            className="bg-white dark:bg-[#111218] border border-slate-200/80 dark:border-white/10 rounded-2xl sm:rounded-3xl shadow-2xl p-5 sm:p-7 flex flex-col items-center max-w-lg w-full relative overflow-hidden space-y-4 sm:space-y-5 my-auto" 
            onClick={e => e.stopPropagation()}
          >
            {/* Cabecera del Modal */}
            <div className="w-full flex items-center justify-between pb-3 border-b border-slate-100 dark:border-white/5">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                  <QrCodeIcon className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="text-sm sm:text-base font-extrabold text-slate-900 dark:text-white tracking-tight">
                    Etiqueta Logística de Estiba
                  </h4>
                  <p className="text-[10px] sm:text-[11px] text-slate-500 dark:text-zinc-400">
                    Estándar térmico GS1 / Trazabilidad WMS 4x3"
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setMostrarModalQR(null)}
                className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors cursor-pointer"
                title="Cerrar modal"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Simulación Realista de la Etiqueta Térmica Física (Blanca / Tinta Negra de Alta Resolución) */}
            <div className="w-full bg-white text-black rounded-2xl shadow-xl border-2 border-slate-300 dark:border-zinc-700 p-4 sm:p-5 relative overflow-hidden font-sans select-none">
              {/* Header de la etiqueta */}
              <div className="flex items-center justify-between border-b-2 border-black pb-2 mb-2.5">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded bg-black text-white text-[10px] sm:text-[11px] font-black font-sans tracking-widest uppercase leading-tight inline-block pb-0.5">
                    LOGIXPORT WMS
                  </span>
                  <span className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wider text-black leading-tight pt-0.5">
                    CONTROL DE ESTIBA
                  </span>
                </div>
                <span className="text-[9px] sm:text-[10px] font-sans tracking-wider font-bold text-black leading-tight pt-0.5">
                  {new Date(mostrarModalQR.fechaIngreso || Date.now()).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </span>
              </div>

              {/* SKU y Nombre del Producto */}
              <div className="space-y-0.5 mb-2.5">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-2xl sm:text-3xl font-black font-sans tracking-tight text-black leading-none pb-1">
                    {mostrarModalQR.sku}
                  </span>
                  <span className="px-2 py-1 rounded bg-black text-white text-[9px] sm:text-[10px] font-black uppercase font-sans tracking-widest leading-tight inline-block pb-0.5">
                    {mostrarModalQR.estado || 'DISPONIBLE'}
                  </span>
                </div>
                <p className="text-xs font-bold uppercase text-black break-words leading-tight pb-0.5">
                  {mostrarModalQR.nombre}
                </p>
                {mostrarModalQR.categoria && (
                  <span className="text-[9px] text-zinc-600 font-semibold uppercase leading-tight pt-0.5 inline-block">
                    Categoría: {mostrarModalQR.categoria}
                  </span>
                )}
              </div>

              {/* Coordenada Física en Rack (Bloque de Alto Contraste) */}
              <div className="bg-black text-white p-2.5 rounded-xl mb-2.5 flex items-center justify-between font-sans">
                <div>
                  <span className="text-[8px] text-zinc-400 block uppercase font-bold tracking-wider leading-tight">UBICACIÓN FÍSICA</span>
                  <span className="text-xs sm:text-sm font-black tracking-wider text-amber-300 leading-tight pt-0.5 pb-0.5 inline-block">
                    {mostrarModalQR.zonaOperativa || 'SEC'} · P{normalizarNumero2(mostrarModalQR.pasillo)} · R{normalizarNumero2(mostrarModalQR.rack)}
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-[8px] text-zinc-400 block uppercase font-bold tracking-wider leading-tight">NIVEL / POS</span>
                  <span className="text-xs sm:text-sm font-black tracking-wider text-white leading-tight pt-0.5 pb-0.5 inline-block">
                    N{normalizarNumero2(mostrarModalQR.nivel)} · P{normalizarNumero2(mostrarModalQR.posicion)}
                  </span>
                </div>
              </div>

              {/* Grid de Atributos Físicos de la Carga */}
              <div className="grid grid-cols-3 gap-2 py-1.5 border-y border-dashed border-zinc-400 text-center text-black mb-2.5">
                <div>
                  <span className="text-[8px] sm:text-[9px] uppercase font-bold text-zinc-600 block leading-tight">CANTIDAD</span>
                  <span className="text-xs font-black font-sans tracking-wider block overflow-visible leading-normal pt-1 pb-0.5">
                    {mostrarModalQR.cantidad} {mostrarModalQR.unidadMedida || 'PZA'}
                  </span>
                </div>
                <div className="border-x border-zinc-300">
                  <span className="text-[8px] sm:text-[9px] uppercase font-bold text-zinc-600 block leading-tight">PESO TOTAL</span>
                  <span className="text-xs font-black font-sans tracking-wider block overflow-visible leading-normal pt-1 pb-0.5">
                    {((Number(mostrarModalQR.peso) || 0) * (Number(mostrarModalQR.cantidad) || 1)).toLocaleString()} kg
                  </span>
                </div>
                <div>
                  <span className="text-[8px] sm:text-[9px] uppercase font-bold text-zinc-600 block leading-tight">LOTE / BATCH</span>
                  <span className="text-xs font-black font-sans tracking-wider block overflow-visible leading-normal pt-1 pb-0.5">
                    {mostrarModalQR.lote || 'STD-01'}
                  </span>
                </div>
              </div>

              {/* Bloque Dual: Código QR 2D + Código de Barras 1D */}
              <div className="flex items-center justify-between gap-3 pt-1">
                {/* QR 2D */}
                <div 
                  className="flex flex-col items-center shrink-0 cursor-pointer group"
                  onClick={() => setQrAmpliadoModal(obtenerQrUrl(mostrarModalQR))}
                  title="Ampliar QR para escanear"
                >
                  <div className="p-1 rounded-lg border-2 border-black bg-white shadow-xs group-hover:border-blue-500 transition-colors">
                    <img 
                      src={obtenerQrUrl(mostrarModalQR)}
                      alt="QR Code"
                      className="w-32 h-32 sm:w-36 sm:h-36 object-contain group-hover:scale-105 transition-transform"
                    />
                  </div>
                  <span className="text-[7.5px] font-sans tracking-widest font-bold text-black uppercase mt-1 leading-tight group-hover:text-blue-600 transition-colors">ESCÁNER 2D</span>
                </div>

                {/* Código de Barras 1D GS1 / Code 128 */}
                {(() => {
                  const barcode = obtenerBarcodeUrls(mostrarModalQR);
                  return (
                    <div className="flex-1 flex flex-col items-center justify-center min-w-0">
                      <span className="text-[7.5px] font-sans tracking-widest font-bold text-zinc-600 uppercase mb-2 leading-tight">
                        CÓDIGO DE BARRAS ({barcode?.tipo || 'CODE128'})
                      </span>
                      {barcode && (
                        <div className="w-full bg-white py-2 px-1 rounded border border-zinc-200 flex items-center justify-center">
                          <img
                            src={barcode.primary}
                            data-fallback={barcode.fallback}
                            alt="Código de barras"
                            className="w-full h-auto max-h-14 object-contain"
                            onError={(e) => {
                              const img = e.currentTarget;
                              const fallback = img.dataset.fallback;
                              if (fallback && img.src !== fallback) {
                                img.src = fallback;
                                return;
                              }
                              img.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Borde dentado de corte térmico */}
              <div className="mt-2.5 pt-1.5 border-t-2 border-dashed border-zinc-300 flex items-center justify-between text-[8px] text-zinc-500 font-sans tracking-wider leading-tight pb-0.5">
                <span>[CORTE TÉRMICO ESTÁNDAR 100x75mm]</span>
                <span>ID: #{mostrarModalQR.id?.slice(0, 8) || 'AUTO'}</span>
              </div>
            </div>

            {/* Acciones del Modal */}
            <div className="w-full flex items-center gap-2.5 pt-1">
              <button
                type="button"
                onClick={() => handleCopiarTexto(`${mostrarModalQR.sku} | ${mostrarModalQR.nombre} | Ubicación: ${mostrarModalQR.zonaOperativa || 'SEC'}-P${mostrarModalQR.pasillo}-R${mostrarModalQR.rack}-N${mostrarModalQR.nivel}-P${mostrarModalQR.posicion}`)}
                className="flex-1 py-3 px-3.5 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-700 dark:text-zinc-300 font-bold text-xs flex items-center justify-center gap-2 transition-all cursor-pointer border border-slate-200/60 dark:border-white/10"
                title="Copiar datos al portapapeles"
              >
                <DocumentDuplicateIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Copiar Datos</span>
                <span className="sm:hidden">Copiar</span>
              </button>

              <button 
                type="button"
                onClick={() => handleImprimirEtiqueta(mostrarModalQR)}
                className="flex-[2] py-3 px-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-xs sm:text-sm font-bold rounded-xl shadow-lg shadow-amber-500/20 hover:shadow-amber-500/35 transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <PrinterIcon className="h-4 w-4" />
                <span>Imprimir Etiqueta Térmica</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Informativo: Artículo 2 LIGIE - Reglas Generales y Complementarias */}
      <ModalReglasLigie 
        abierto={mostrarModalReglasLigie}
        onCerrar={() => setMostrarModalReglasLigie(false)}
        moduloOrigen="almacen"
      />

      {/* Modal Mesa de Control WMS & Auditoría Docente */}
      <ModalMesaControlAlmacen
        abierto={mostrarModalMesaControl}
        onCerrar={() => setMostrarModalMesaControl(false)}
        solicitudes={solicitudesPendientes}
        onActualizar={recargarSolicitudesPendientes}
        requiereAprobacion={requiereAprobacion}
        onCambiarPolitica={handleCambiarPoliticaAprobacion}
        esGestor={esMaestroOSuperior || (usuario?.id ? supervisoresDelegadosIds.includes(Number(usuario.id)) : false)}
        miembros={miembrosInstitucion}
        supervisoresDelegadosIds={supervisoresDelegadosIds}
        onActualizarSupervisoresDelegados={handleActualizarSupervisoresDelegados}
      />

      {/* Modal Importar Gemelo Digital 3D */}
      <ModalImportarAlmacen3D
        isOpen={mostrarModalImportarAlmacen}
        onClose={() => setMostrarModalImportarAlmacen(false)}
        onImportadoExitoso={() => {
          cargarDatosAlmacen();
        }}
        almacenActualCodigo={configuracionAlmacen.codigo || 'ALM01'}
        totalProductosActuales={items.length}
      />

      {/* Modal QR Ampliado */}
      {qrAmpliadoModal && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="relative bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full mx-auto flex flex-col items-center shadow-2xl animate-in zoom-in-95 duration-200 border-4 border-slate-200">
            <button
              onClick={() => setQrAmpliadoModal(null)}
              className="absolute top-4 right-4 p-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-800 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
            <div className="text-center mb-6 mt-2">
              <h3 className="text-xl font-black text-slate-800 uppercase tracking-wide">
                Escáner WMS
              </h3>
              <p className="text-sm font-medium text-slate-500 mt-1">
                Apunta tu escáner 2D hacia este código
              </p>
            </div>
            
            <div className="w-full aspect-square bg-white border-2 border-slate-100 rounded-2xl p-4 flex items-center justify-center shadow-inner">
              <img 
                src={qrAmpliadoModal}
                alt="QR Ampliado"
                className="w-full h-full object-contain pointer-events-none select-none"
              />
            </div>
            
            <button
              onClick={() => {
                const a = document.createElement('a');
                a.href = qrAmpliadoModal;
                a.download = `QR_LOGIXPORT_${mostrarModalQR?.sku || 'Item'}.png`;
                a.click();
              }}
              className="mt-6 w-full py-3.5 bg-slate-900 hover:bg-black text-white text-sm font-bold rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
            >
              <DocumentDuplicateIcon className="h-5 w-5" />
              <span>Guardar QR (Imagen)</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaginaAlmacenPractica;
